import type { z } from 'zod';
import {
  CallbackBodySchema,
  CallbackResponseSchema,
  MeResponseSchema,
  RefreshResponseSchema,
  SignInQuerySchema,
  SignInResponseSchema,
  SignOutResponseSchema,
} from './auth/auth.schemas';
import {
  SandboxGateCallbackBodySchema,
  SandboxGateCallbackResponseSchema,
  SandboxGateResponseSchema,
  SandboxGateStartQuerySchema,
  SandboxGateStartResponseSchema,
  SandboxMembersResponseSchema,
  SandboxSessionBodySchema,
  SandboxSessionResponseSchema,
} from './auth/sandbox-auth.schemas';
import {
  AdminCallbackBodySchema,
  AdminCallbackResponseSchema,
  AdminMeResponseSchema,
  AdminMobileCallbackQuerySchema,
  AdminRefreshBodySchema,
  AdminRefreshResponseSchema,
  AdminSignInQuerySchema,
  AdminSignInResponseSchema,
  AdminSignOutBodySchema,
  AdminSignOutResponseSchema,
} from './admin/admin-auth.schemas';
import {
  CreateAdminWorkspaceBodySchema,
  CreateAdminWorkspaceResponseSchema,
  CreateAdminWorkspaceInvitationBodySchema,
  CreateAdminWorkspaceInvitationPathParamsSchema,
  CreateAdminWorkspaceInvitationResponseSchema,
  GetAdminWorkspacePathParamsSchema,
  GetAdminWorkspaceResponseSchema,
  ListAdminWorkspacesQuerySchema,
  ListAdminWorkspacesResponseSchema,
  UpdateAdminWorkspaceBodySchema,
  UpdateAdminWorkspacePathParamsSchema,
  UpdateAdminWorkspaceResponseSchema,
} from './admin/admin-workspaces.schemas';
import {
  GetAdminOrganizationPathParamsSchema,
  GetAdminOrganizationResponseSchema,
  ListAdminOrganizationsQuerySchema,
  ListAdminOrganizationsResponseSchema,
  UpdateAdminOrganizationAddressBodySchema,
  UpdateAdminOrganizationAddressPathParamsSchema,
  UpdateAdminOrganizationAddressResponseSchema,
  UpdateAdminOrganizationNameBodySchema,
  UpdateAdminOrganizationNamePathParamsSchema,
  UpdateAdminOrganizationNameResponseSchema,
} from './admin/admin-organizations.schemas';
import {
  ListAdminWorkspaceRolesPathParamsSchema,
  ListAdminWorkspaceRolesResponseSchema,
} from './admin/admin-workspace-roles.schemas';
import {
  EnrolFleetMachineBodySchema,
  EnrolFleetMachineResponseSchema,
  FleetMachinePathParamsSchema,
  FleetMachineSelfResponseSchema,
  FleetMachineViewSchema,
  ListFleetMachinesResponseSchema,
  SetFleetMachineWeeklyLimitBodySchema,
} from './admin/fleet-machines.schemas';
import {
  FleetMetricsSeriesQuerySchema,
  FleetMetricsSeriesSchema,
  FleetRunTotalsQuerySchema,
  FleetRunTotalsSchema,
} from './admin/fleet-runs.schemas';
import {
  FleetTickBodySchema,
  FleetTickResponseSchema,
  TakeFleetCommandsResponseSchema,
  FleetStreamEventSchema,
  CleanFleetLogBodySchema,
  CleanFleetLogResponseSchema,
  ReadFleetLogQuerySchema,
  ReadFleetLogResponseSchema,
  ReadFleetMachineLogsQuerySchema,
  ReadFleetMachineLogsResponseSchema,
} from './admin/fleet-tick.schemas';
import {
  ClaimFleetJobBodySchema,
  FleetJobPathParamsSchema,
  FleetJobViewSchema,
  ListFleetJobsQuerySchema,
  ListFleetJobsResponseSchema,
  ListLiveFleetJobsResponseSchema,
  ListBoundFleetJobsResponseSchema,
  ListFleetJobHistoryBodySchema,
  ListFleetJobHistoryResponseSchema,
  ListResumableFleetJobsBodySchema,
  ListResumableFleetJobsResponseSchema,
  ReleaseFleetJobsByWorkerBodySchema,
  ReleaseFleetJobsByWorkerResponseSchema,
  ReleaseHeldFleetJobBodySchema,
  ReleaseHeldFleetJobResponseSchema,
  ReleaseFleetJobBodySchema,
  ClearFleetJobSuspectResponseSchema,
  SendFleetJobMessageBodySchema,
  FleetJobMessageViewSchema,
  ListFleetJobMessagesResponseSchema,
  ClaimFleetJobResponseSchema,
  EndFleetJobBodySchema,
  EndFleetJobResponseSchema,
  StartFleetJobBodySchema,
  StartFleetJobResponseSchema,
  TopUpFleetJobBodySchema,
  TopUpFleetJobResponseSchema,
} from './admin/fleet-jobs.schemas';
import {
  AppendFleetJobEventBodySchema,
  AppendFleetJobEventResponseSchema,
  ListFleetJobEventsResponseSchema,
} from './admin/fleet-job-events.schemas';
import {
  ReadFleetQueueBodySchema,
  ReadFleetQueueCommentsBodySchema,
  ReadFleetQueueCommentsResponseSchema,
  ReadFleetQueueResponseSchema,
} from './admin/fleet-queue.schemas';
import { ClaimNextFleetJobBodySchema, ClaimNextFleetJobResponseSchema } from './admin/fleet-next-job.schemas';
import {
  FleetBoardQuerySchema,
  FleetBoardTicketPathParamsSchema,
  GetFleetBoardResponseSchema,
  GetFleetBoardTicketResponseSchema,
  RankFleetQueueTicketBodySchema,
  RankFleetQueueTicketResponseSchema,
} from './admin/fleet-board.schemas';
import {
  OrderFleetLaneBlockersBodySchema,
  OrderFleetLaneBlockersResponseSchema,
  ReportFleetLaneBodySchema,
  ReportFleetLaneResponseSchema,
} from './admin/fleet-lanes.schemas';
import {
  AnswerFleetQuestionFromMachineBodySchema,
  AnswerFleetQuestionBodySchema,
  AnswerFleetQuestionResponseSchema,
  AskFleetJobQuestionsBodySchema,
  AskFleetQuestionAgainBodySchema,
  AskFleetQuestionAgainResponseSchema,
  FleetQuestionPathParamsSchema,
  FleetRunQuestionsResponseSchema,
  ListFleetQuestionsResponseSchema,
  ListOpenFleetQuestionsBodySchema,
  MarkFleetAnswerRelayedBodySchema,
  MarkFleetAnswerRelayedResponseSchema,
  ReadFleetJobQuestionsBodySchema,
  TakeFleetAnswersBodySchema,
  TakeFleetAnswersResponseSchema,
} from './admin/fleet-questions.schemas';
import { FleetOverviewResponseSchema, ListFleetPeersResponseSchema } from './admin/fleet-status.schemas';
import { FleetHostHealthSeriesQuerySchema, FleetHostHealthSeriesResponseSchema } from './admin/fleet-health.schemas';
import {
  FleetRegisteredAccountPathParamsSchema,
  FleetRegisteredAccountViewSchema,
  ListFleetRegisteredAccountsResponseSchema,
  UpdateFleetRegisteredAccountBodySchema,
} from './admin/fleet-registered-accounts.schemas';
import {
  AnnounceFleetReleaseBodySchema,
  AnnounceFleetReleaseResponseSchema,
  BlockFleetReleaseBodySchema,
  BlockFleetReleaseResponseSchema,
  ClaimFleetReportBodySchema,
  ClaimFleetReportResponseSchema,
  FinishFleetReportBodySchema,
  FleetReportDetailSchema,
  FleetReportMessageResponseSchema,
  FleetReportPathParamsSchema,
  FleetReportTransitionResponseSchema,
  ListFleetReleasesResponseSchema,
  ListFleetReportsResponseSchema,
  MarkFleetReportUnplaceableBodySchema,
  ParkFleetReportBodySchema,
  ReleaseFleetReportBodySchema,
  ReplyToFleetReportBodySchema,
  SayOnFleetReportBodySchema,
  SubmitFleetReportBodySchema,
} from './admin/fleet-reports.schemas';
import {
  FleetAssistantMessageViewSchema,
  FleetAssistantTurnPathParamsSchema,
  ListFleetAssistantMessagesQuerySchema,
  ListFleetAssistantMessagesResponseSchema,
  PostFleetAssistantMessageBodySchema,
  ReplyToFleetAssistantTurnBodySchema,
  ReplyToFleetAssistantTurnResponseSchema,
  TakeFleetAssistantTurnBodySchema,
  TakeFleetAssistantTurnResponseSchema,
} from './admin/fleet-assistant.schemas';
import {
  GetMessengerChatPathParamsSchema,
  GetMessengerChatResponseSchema,
  ListMessengerChatsResponseSchema,
} from './admin/messenger-chats.schemas';
import {
  ListWhatsAppFailedDispatchesPathParamsSchema,
  ListWhatsAppFailedDispatchesResponseSchema,
  ListWhatsAppUnmatchedMessagesResponseSchema,
  WhatsAppOperatorListQuerySchema,
} from './admin/whatsapp.schemas';
import {
  FleetCommandAcceptedResponseSchema,
  FleetCommandBodySchema,
  FleetCommandOutcomePathParamsSchema,
  FleetCommandPathParamsSchema,
  FleetCommandsPathParamsSchema,
  FleetCommandViewSchema,
  ListFleetCommandsResponseSchema,
  RecordFleetCommandOutcomeBodySchema,
  RecordFleetCommandOutcomeResponseSchema,
  RecordFleetCommandProgressBodySchema,
  RecordFleetCommandProgressResponseSchema,
} from './admin/fleet-commands.schemas';
import {
  GetLandIqDataRunQuerySchema,
  LandIqDataRunDetailSchema,
  LandIqBoardTicketPathParamsSchema,
  LandIqDataRunPathParamsSchema,
  ListLandIqDataRunsResponseSchema,
  MoveLandIqDataRunTicketBodySchema,
  MoveLandIqDataRunTicketResponseSchema,
  StartLandIqDataRunResponseSchema,
  RetryLandIqDataRunFailedChunksResponseSchema,
  ResumeLandIqDataRunResponseSchema,
} from './admin/landiq-data-runs.schemas';
import {
  GetSandboxResetResponseSchema,
  StartSandboxResetBodySchema,
  StartSandboxResetResponseSchema,
} from './admin/sandbox-reset.schemas';
import {
  GetAdminApiKeyPathParamsSchema,
  GetAdminApiKeyResponseSchema,
  ReplayApiKeyRequestResponseSchema,
  IssueAdminApiKeyBodySchema,
  IssueAdminApiKeyResponseSchema,
  IssueAdminKeyBodySchema,
  IssueAdminKeyResponseSchema,
  ListAdminApiKeysQuerySchema,
  ListAdminApiKeysResponseSchema,
  ResolveAdminApiKeyOwnerPathParamsSchema,
  ResolveAdminApiKeyOwnerResponseSchema,
  RevokeAdminApiKeyPathParamsSchema,
  RevokeAdminApiKeyResponseSchema,
  UpdateAdminApiKeyBodySchema,
  UpdateAdminApiKeyPathParamsSchema,
  UpdateAdminApiKeyResponseSchema,
} from './admin/api-key.schemas';
import {
  IssueMcpAccessBodySchema,
  IssueMcpAccessResponseSchema,
  ListMcpAccessResponseSchema,
  RevokeMcpAccessPathParamsSchema,
  RevokeMcpAccessResponseSchema,
  ListAllMcpAccessResponseSchema,
  RevokeAnyMcpAccessResponseSchema,
  McpAccessSwitchResponseSchema,
  SetMcpAccessSwitchBodySchema,
  WorkspaceMcpSwitchResponseSchema,
  SetWorkspaceMcpSwitchBodySchema,
  ListMcpToolExposureQuerySchema,
  ListMcpToolExposureResponseSchema,
  McpToolExposureItemSchema,
  SetMcpToolExposureBatchBodySchema,
  SetMcpToolExposureBatchResponseSchema,
  SetMcpToolExposureBodySchema,
  SetMcpToolExposureParamsSchema,
  DiagnoseMcpToolReachabilityParamsSchema,
  DiagnoseMcpToolReachabilityQuerySchema,
  McpToolReachabilityResponseSchema,
} from './admin/mcp-access.schemas';
import {
  IssuedValuationAgentTokenResponseSchema,
  IssueValuationAgentTokenBodySchema,
  ValuationAgentTokensResponseSchema,
} from './admin/valuation-agent-token.schemas';
import {
  ListAdminConnectedClientsQuerySchema,
  ListAdminConnectedClientsResponseSchema,
} from './admin/connected-client.schemas';
import { McpJsonRpcRequestSchema, McpJsonRpcResponseSchema } from './admin/mcp.schemas';
import { ProtectedResourceMetadataSchema } from './identity/oauth-metadata.schemas';
import {
  GetApiCallPathParamsSchema,
  ReplayApiCallPathParamsSchema,
  GetApiCallResponseSchema,
  ListApiCallsQuerySchema,
  ListApiCallsResponseSchema,
} from './admin/api-call.schemas';
import { ListEmailLogQuerySchema, ListEmailLogResponseSchema } from './admin/email-log.schemas';
import {
  AdminMemberAvatarUploadPathParamsSchema,
  GetAdminMemberPathParamsSchema,
  GetAdminMemberOnboardingResponseSchema,
  GetAdminMemberResponseSchema,
  ListAdminMemberActivityQuerySchema,
  ListAdminMemberActivityResponseSchema,
  ListAdminMemberSignInsResponseSchema,
  UpdateAdminMemberAccessBodySchema,
  UpdateAdminMemberAccessResponseSchema,
  UpdateAdminMemberBodySchema,
  UpdateAdminMemberPathParamsSchema,
  UpdateAdminMemberResponseSchema,
  ListAdminMembersQuerySchema,
  ListAdminMembersResponseSchema,
  ListAdminOrganizationMembersResponseSchema,
} from './admin/admin-members.schemas';
import {
  CreateAdminBodySchema,
  CreateAdminResponseSchema,
  DeactivateAdminPathParamsSchema,
  DeactivateAdminResponseSchema,
  GetAdminPathParamsSchema,
  GetAdminResponseSchema,
  ListAdminsResponseSchema,
} from './admin/admin-admins.schemas';
import {
  AdminPreferencePathParamsSchema,
  GetAdminPreferenceResponseSchema,
  SaveAdminPreferenceBodySchema,
  SaveAdminPreferenceResponseSchema,
} from './admin/admin-preferences.schemas';
import {
  AdminPushEventPathParamsSchema,
  ListAdminPushEventsResponseSchema,
  RegisterAdminPushDeviceBodySchema,
  RegisterAdminPushDeviceResponseSchema,
  SetAdminPushEventBodySchema,
  SetAdminPushEventResponseSchema,
} from './admin/admin-push.schemas';
import {
  DocumentAnalysisSkillPathParamsSchema,
  DocumentAnalysisSkillViewSchema,
  ListDocumentAnalysisSkillsResponseSchema,
  SaveDocumentAnalysisSkillPromptBodySchema,
  SaveSystemSkillPromptBodySchema,
  SetDocumentAnalysisSkillStatusBodySchema,
  SystemSkillViewSchema,
} from './admin/admin-document-analysis-skills.schemas';
import {
  AgentSkillDefinitionPathParamsSchema,
  AgentSkillDefinitionViewSchema,
  CreateAgentSkillDefinitionBodySchema,
  ListAgentSkillDefinitionsResponseSchema,
  SetAgentSkillDefinitionStatusBodySchema,
  UpdateAgentSkillDefinitionBodySchema,
} from './admin/agent-skill-definitions.schemas';
import { ListToolCatalogQuerySchema, ListToolCatalogResponseSchema } from './assistant/tool-catalog.schemas';
import { ListCodificationCandidatesResponseSchema } from './assistant/codification-advisory.schemas';
import {
  ListPendingApprovalsResponseSchema,
  ProposedActionDetailSchema,
  ProposedActionPathParamsSchema,
} from './assistant/proposed-actions.schemas';
import {
  CreateSkillInstanceBodySchema,
  CreateSkillInstanceResponseSchema,
  ListSkillEventCatalogueResponseSchema,
  ListSkillInstancesResponseSchema,
  SkillInstanceActionBodySchema,
  SkillInstanceDetailSchema,
  SkillInstancePathParamsSchema,
  SkillInstanceWorkspaceParamsSchema,
  UpdateSkillInstanceConfigBodySchema,
} from './assistant/skill-instances.schemas';
import {
  GetSkillInstanceStateResponseSchema,
  ListSkillRunsResponseSchema,
  SkillRunDetailSchema,
  SkillRunPathParamsSchema,
  SkillRunSummarySchema,
  TriggerSkillRunBodySchema,
} from './assistant/skill-runs.schemas';
import {
  CreateOrganizationBodySchema,
  CreateOrganizationPathParamsSchema,
  CreateOrganizationResponseSchema,
  GetWorkspaceOrganizationPathParamsSchema,
  GetWorkspaceOrganizationResponseSchema,
  ListOrganizationTreePathParamsSchema,
  ListOrganizationTreeResponseSchema,
  ListMyOrganizationsPathParamsSchema,
  ListMyOrganizationsResponseSchema,
  UpdateWorkspaceOrganizationBodySchema,
  UpdateWorkspaceOrganizationPathParamsSchema,
  UpdateWorkspaceOrganizationResponseSchema,
  UpdateOrganizationProfileBodySchema,
  UpdateOrganizationProfilePathParamsSchema,
  UpdateOrganizationProfileResponseSchema,
  GetMyOrganizationPermissionsPathParamsSchema,
  GetMyOrganizationPermissionsResponseSchema,
} from './identity/organizations.schemas';
import {
  ExchangeAppHandoffBodySchema,
  ExchangeAppHandoffResponseSchema,
  ListLinkedApplicationsPathParamsSchema,
  ListLinkedApplicationsResponseSchema,
  MintAppHandoffBodySchema,
  MintAppHandoffPathParamsSchema,
  MintAppHandoffResponseSchema,
} from './identity/app-handoffs.schemas';
import {
  ListCoveragePostalCodesPathParamsSchema,
  ListCoveragePostalCodesResponseSchema,
  AssignCoveragePostalCodesPathParamsSchema,
  AssignCoveragePostalCodesBodySchema,
  AssignCoveragePostalCodesResponseSchema,
  RemoveCoveragePostalCodePathParamsSchema,
  RemoveCoveragePostalCodeResponseSchema,
} from './identity/coverage-postal-codes.schemas';
import { WorkspaceCoveragePathParamsSchema, WorkspaceCoverageResponseSchema } from './identity/coverage.schemas';
import {
  TriggerPartnerOnboardingBodySchema,
  TriggerPartnerOnboardingPathParamsSchema,
  TriggerPartnerOnboardingResponseSchema,
  ExchangeOnboardingSessionBodySchema,
  ExchangeOnboardingSessionResponseSchema,
  RefreshOnboardingSessionResponseSchema,
  RequestOnboardingSessionCodeBodySchema,
  RequestOnboardingSessionCodeResponseSchema,
  OnboardingPathParamsSchema,
  OnboardingMeResponseSchema,
  StartOnboardingResponseSchema,
  SaveDraftBodySchema,
  SubmitOnboardingBodySchema,
  SubmitOnboardingResponseSchema,
  OnboardingReviewPathParamsSchema,
  RequestOnboardingChangesBodySchema,
  OnboardingReviewResponseSchema,
} from './identity/organization-onboarding.schemas';
import {
  CreateContactBodySchema,
  CreateContactPathParamsSchema,
  CreateContactResponseSchema,
  DeleteContactCommunicationPathParamsSchema,
  DeleteContactCommunicationResponseSchema,
  DeleteContactNotePathParamsSchema,
  DeleteContactNoteResponseSchema,
  DeleteContactPathParamsSchema,
  DeleteContactResponseSchema,
  GetWorkspaceContactPathParamsSchema,
  GetWorkspaceContactResponseSchema,
  GetVisibleContactPathParamsSchema,
  GetVisibleContactResponseSchema,
  ListWorkspaceContactsPathParamsSchema,
  ListWorkspaceContactsQuerySchema,
  ListWorkspaceContactsResponseSchema,
  ListVisibleContactsPathParamsSchema,
  ListVisibleContactsQuerySchema,
  ListVisibleContactsResponseSchema,
  AmendContactCommunicationBodySchema,
  AmendContactCommunicationPathParamsSchema,
  AmendContactCommunicationResponseSchema,
  AmendContactNoteBodySchema,
  AmendContactNotePathParamsSchema,
  AmendContactNoteResponseSchema,
  RecordContactCommunicationBodySchema,
  RecordContactCommunicationPathParamsSchema,
  RecordContactCommunicationResponseSchema,
  RecordContactNoteBodySchema,
  RecordContactNotePathParamsSchema,
  RecordContactNoteResponseSchema,
  UpdateContactAddressBodySchema,
  UpdateContactAddressPathParamsSchema,
  UpdateContactAddressResponseSchema,
  UpdateContactEmailsBodySchema,
  UpdateContactEmailsPathParamsSchema,
  UpdateContactEmailsResponseSchema,
  UpdateContactPhonesBodySchema,
  UpdateContactPhonesPathParamsSchema,
  UpdateContactPhonesResponseSchema,
  UpdateContactProfileBodySchema,
  UpdateContactProfilePathParamsSchema,
  UpdateContactProfileResponseSchema,
  UpdateContactTypesBodySchema,
  UpdateContactTypesPathParamsSchema,
  UpdateContactTypesResponseSchema,
  UpdateContactWarningNoteBodySchema,
  UpdateContactWarningNotePathParamsSchema,
  UpdateContactWarningNoteResponseSchema,
  ReassignContactStandortPathParamsSchema,
  ReassignContactStandortBodySchema,
  ReassignContactStandortResponseSchema,
} from './relationship/contacts.schemas';
import { SearchPathParamsSchema, SearchQuerySchema, SearchResultsResponseSchema } from './search/search.schemas';
import {
  CreateCrmCompanyBodySchema,
  CreateCrmCompanyPathParamsSchema,
  CreateCrmCompanyResponseSchema,
  GetWorkspaceCrmCompanyPathParamsSchema,
  GetWorkspaceCrmCompanyResponseSchema,
  GetVisibleCrmCompanyPathParamsSchema,
  GetVisibleCrmCompanyResponseSchema,
  ListCrmCompaniesPathParamsSchema,
  ListCrmCompaniesQuerySchema,
  ListCrmCompaniesResponseSchema,
  UpdateCrmCompanyBodySchema,
  UpdateCrmCompanyPathParamsSchema,
  UpdateCrmCompanyResponseSchema,
  CrmCompanyLifecyclePathParamsSchema,
  ArchiveCrmCompanyResponseSchema,
  UnarchiveCrmCompanyResponseSchema,
  DeleteCrmCompanyResponseSchema,
} from './relationship/companies.schemas';
import {
  LinkContactToCompanyBodySchema,
  LinkContactToCompanyPathParamsSchema,
  CompanyContactLinkResponseSchema,
  UpdateCompanyLinkBodySchema,
  CompanyLinkPathParamsSchema,
  UnlinkContactFromCompanyResponseSchema,
  ListCompanyContactsPathParamsSchema,
  ListCompanyContactsResponseSchema,
  ListContactCompaniesPathParamsSchema,
  ListContactCompaniesResponseSchema,
} from './relationship/company-contact-links.schemas';
import {
  CreatePropertyBodySchema,
  CreatePropertyPathParamsSchema,
  CreatePropertyResponseSchema,
  GetAdminPropertyPathParamsSchema,
  GetAdminPropertyResponseSchema,
  ReadAdminPropertyDocumentPathParamsSchema,
  GetWorkspaceOrganizationPropertyPathParamsSchema,
  GetWorkspacePropertyPathParamsSchema,
  GetWorkspaceOrganizationPropertyResponseSchema,
  GetVisiblePropertyResponseSchema,
  AttachPropertyContactBodySchema,
  AttachPropertyContactPathParamsSchema,
  AttachPropertyContactResponseSchema,
  UpdatePropertyContactBodySchema,
  UpdatePropertyContactPathParamsSchema,
  UpdatePropertyContactResponseSchema,
  DetachPropertyContactPathParamsSchema,
  DetachPropertyContactResponseSchema,
  ListAdminPropertiesQuerySchema,
  ListAdminPropertiesResponseSchema,
  ListContactPropertiesPathParamsSchema,
  ListContactPropertiesResponseSchema,
  ListPropertyContactsPathParamsSchema,
  ListPropertyContactsQuerySchema,
  ListPropertyContactsResponseSchema,
  ListWorkspaceOrganizationPropertiesPathParamsSchema,
  ListWorkspaceOrganizationPropertiesResponseSchema,
  UpdatePropertyAddressBodySchema,
  UpdatePropertyAddressPathParamsSchema,
  UpdatePropertyAddressResponseSchema,
  UpdatePropertyIdentityBodySchema,
  UpdatePropertyIdentityPathParamsSchema,
  UpdatePropertyIdentityResponseSchema,
  UpdatePropertyBuildingBodySchema,
  UpdatePropertyBuildingPathParamsSchema,
  UpdatePropertyBuildingResponseSchema,
  UpdatePropertyDescriptionsBodySchema,
  UpdatePropertyDescriptionsPathParamsSchema,
  UpdatePropertyDescriptionsResponseSchema,
  UpdatePropertyAmenitiesBodySchema,
  UpdatePropertyAmenitiesPathParamsSchema,
  UpdatePropertyAmenitiesResponseSchema,
  UpdatePropertyAreasBodySchema,
  UpdatePropertyAreasPathParamsSchema,
  UpdatePropertyAreasResponseSchema,
  PropertyLifecyclePathParamsSchema,
  ActivatePropertyResponseSchema,
  DeactivatePropertyResponseSchema,
  ArchivePropertyBodySchema,
  ArchivePropertyRequestBodySchema,
  PropertyArchivePreviewResponseSchema,
  ArchivePropertyResponseSchema,
  ChangeArchiveReasonResponseSchema,
  UpdatePropertyReferenceSettingsBodySchema,
  UpdatePropertyReferenceSettingsResponseSchema,
  UpdatePropertyHandReservationBodySchema,
  UpdatePropertyHandReservationResponseSchema,
  UnarchivePropertyResponseSchema,
  StageInAcquisitionResponseSchema,
  RevertToDraftResponseSchema,
  ReassignPropertyPathParamsSchema,
  ReassignPropertyBodySchema,
  ReassignPropertyResponseSchema,
  ReassignPropertyStandortPathParamsSchema,
  ReassignPropertyStandortBodySchema,
  ReassignPropertyStandortResponseSchema,
  SoftDeletePropertyPathParamsSchema,
  SoftDeletePropertyQuerySchema,
  SoftDeletePropertyResponseSchema,
  UpdatePropertyEnergyBodySchema,
  UpdatePropertyEnergyPathParamsSchema,
  UpdatePropertyEnergyResponseSchema,
  UpdatePropertyInvestmentYieldBodySchema,
  UpdatePropertyInvestmentYieldPathParamsSchema,
  UpdatePropertyInvestmentYieldResponseSchema,
  UpdatePropertyPlotDetailsBodySchema,
  UpdatePropertyPlotDetailsPathParamsSchema,
  UpdatePropertyPlotDetailsResponseSchema,
  UpdatePropertyAdditionalCostsBodySchema,
  UpdatePropertyAdditionalCostsPathParamsSchema,
  UpdatePropertyAdditionalCostsResponseSchema,
  UpdatePropertyPricingSaleBodySchema,
  UpdatePropertyPricingSalePathParamsSchema,
  UpdatePropertyPricingSaleResponseSchema,
  UpdatePropertyPricingRentalBodySchema,
  UpdatePropertyPricingRentalPathParamsSchema,
  UpdatePropertyPricingRentalResponseSchema,
  UpdatePropertyRentalTermsBodySchema,
  UpdatePropertyRentalTermsPathParamsSchema,
  UpdatePropertyRentalTermsResponseSchema,
  ListVisiblePropertiesPathParamsSchema,
  ListVisiblePropertiesQuerySchema,
  ListVisiblePropertiesResponseSchema,
} from './property/properties.schemas';
import {
  PropertyExposesPathParamsSchema,
  PropertyExposeVersionPathParamsSchema,
  CreatePropertyExposeUploadBodySchema,
  CreatePropertyExposeUploadResponseSchema,
  FilePropertyExposeBodySchema,
  FilePropertyExposeResponseSchema,
  ListPropertyExposesResponseSchema,
  ReadPropertyExposeResponseSchema,
  RemovePropertyExposeResponseSchema,
} from './property/exposes.schemas';
import {
  PropertyDocumentsPathParamsSchema,
  CreatePropertyDocumentUploadBodySchema,
  CreatePropertyDocumentUploadResponseSchema,
  ConfirmPropertyDocumentUploadBodySchema,
  ConfirmPropertyDocumentUploadResponseSchema,
  ListPropertyDocumentsQuerySchema,
  ListPropertyDocumentsResponseSchema,
  ReadPropertyDocumentPathParamsSchema,
  ReadPropertyDocumentQuerySchema,
  ReadPropertyDocumentResponseSchema,
  PropertyDocumentPathParamsSchema,
  RenamePropertyDocumentBodySchema,
  RenamePropertyDocumentResponseSchema,
  RecategorizePropertyDocumentBodySchema,
  RecategorizePropertyDocumentResponseSchema,
  SetPropertyDocumentForBuyersBodySchema,
  SetPropertyDocumentForBuyersResponseSchema,
  SetPropertyDocumentsForBuyersBodySchema,
  SetPropertyDocumentsForBuyersResponseSchema,
  DeletePropertyDocumentResponseSchema,
  RequeueDocumentClassificationResponseSchema,
  QueueDocumentAnalysisResponseSchema,
  PropertyDocumentSchema,
} from './property/documents.schemas';
import {
  DocumentRedactionPathParamsSchema,
  DocumentRedactionSchema,
  StartDocumentRedactionBodySchema,
} from './property/document-redaction.schemas';
import {
  ApplyDocumentAnalysisBodySchema,
  ApplyDocumentAnalysisResponseSchema,
  CorrectDocumentAnalysisFieldBodySchema,
  DocumentAnalysisListResponseSchema,
  DocumentAnalysisPathParamsSchema,
  DocumentAnalysisResponseSchema,
} from './property/document-analysis.schemas';
import { PropertyReadinessResponseSchema } from './property/readiness.schemas';
import { PropertyDossierSchema, PropertyFactsSchema } from './property/dossier.schemas';
import {
  WriteValuationAgentFindingsBodySchema,
  WriteValuationAgentFindingsResponseSchema,
} from './property/valuation-agent-findings.schemas';
import { PropertyCompletenessResponseSchema } from './property/completeness.schemas';
import {
  ChangePropertyCaptureViewingKindBodySchema,
  ConfirmCapturePhotoUploadBodySchema,
  CreateCapturePhotoUploadBodySchema,
  GetPropertyCaptureResponseSchema,
  PropertyCaptureFieldsResponseSchema,
  PropertyCapturePhotosResponseSchema,
  PropertyCaptureSchema,
  RecordCaptureFindingBodySchema,
  StartPropertyCaptureBodySchema,
  ValuationAgentPropertyCaptureResponseSchema,
} from './property/property-capture.schemas';
import { OperatorPropertyMemorySchema } from './property/memory.schemas';
import {
  ApplyFactSuggestionsBodySchema,
  ApplyFactSuggestionsResponseSchema,
  PropertyFactSuggestionsSchema,
} from './property/fact-suggestions.schemas';
import { ImmoosIqLoadStatusResponseSchema, LoadImmoosIqDataResponseSchema } from './property/immoos-iq.schemas';
import {
  ConfirmFactsBodySchema,
  ConfirmFactsResponseSchema,
  PropertyFactReviewSchema,
  WithdrawFactConfirmationsBodySchema,
  WithdrawFactConfirmationsResponseSchema,
} from './property/fact-review.schemas';
import {
  FactKeyDetailViewSchema,
  FactKeyPathParamsSchema,
  FactKeyViewSchema,
  ListFactKeysResponseSchema,
  UpdateFactKeyBodySchema,
  CreateFactKeyBodySchema,
} from './property/admin-fact-keys.schemas';
import {
  FactKeyCatalogGroupPathParamsSchema,
  FACT_KEY_GROUP_PAGE_SIZE,
  FactKeyCatalogGroupQuerySchema,
  FactKeyCatalogGroupSchema,
  FactKeyCatalogOverviewSchema,
} from './property/fact-key-catalog.schemas';
import { CreateDocumentRequestBodySchema, DocumentRequestResponseSchema } from './property/document-requests.schemas';
import {
  PropertyMediaPathParamsSchema,
  PropertyMediaItemPathParamsSchema,
  CreatePropertyMediaUploadBodySchema,
  CreatePropertyMediaUploadResponseSchema,
  ConfirmPropertyMediaUploadBodySchema,
  ConfirmPropertyMediaUploadResponseSchema,
  ListPropertyMediaResponseSchema,
  PropertyMediaItemSchema,
  RenamePropertyMediaBodySchema,
  RecategorizePropertyMediaBodySchema,
  PublishPropertyMediaAreaBodySchema,
  ReorderPropertyMediaBodySchema,
  SetPropertyMediaPublishedBodySchema,
  ReadPropertyMediaQuerySchema,
  ReadPropertyMediaResponseSchema,
} from './property/media.schemas';
import { AddPropertyMediaVideoLinkBodySchema } from './property/video-links';
import {
  ListPropertyToursResponseSchema,
  PropertyTourPathParamsSchema,
  PropertyToursPathParamsSchema,
  ReorderPropertyToursBodySchema,
  SavePropertyTourBodySchema,
  SetPropertyTourPublishedBodySchema,
} from './property/tours.schemas';
import {
  ChangeMemberOrganisationRoleBodySchema,
  ChangeMemberOrganisationRolePathParamsSchema,
  ChangeMemberOrganisationRoleResponseSchema,
  SetLeadRecipientBodySchema,
  SetLeadRecipientPathParamsSchema,
  SetLeadRecipientResponseSchema,
  SetMemberOrganisationRolesBodySchema,
  SetMemberOrganisationRolesPathParamsSchema,
  SetMemberOrganisationRolesResponseSchema,
  RemoveMemberOrganisationRolesPathParamsSchema,
  RemoveMemberOrganisationRolesResponseSchema,
  ChangeMemberWorkspaceRoleBodySchema,
  ChangeMemberWorkspaceRolePathParamsSchema,
  ChangeMemberWorkspaceRoleResponseSchema,
  RemoveMemberWorkspaceRolePathParamsSchema,
  RemoveMemberWorkspaceRoleResponseSchema,
  CreateOrganisationMemberBodySchema,
  ListStandortMembersPathParamsSchema,
  ListStandortMembersResponseSchema,
  CreateOrganisationMemberPathParamsSchema,
  CreateOrganisationMemberResponseSchema,
  CreateWorkspaceMemberBodySchema,
  CreateWorkspaceMemberPathParamsSchema,
  CreateWorkspaceMemberResponseSchema,
  DeactivateWorkspaceMemberPathParamsSchema,
  DeactivateWorkspaceMemberResponseSchema,
  GetWorkspaceMemberPathParamsSchema,
  GetWorkspaceMemberResponseSchema,
  ListWorkspaceMembersPathParamsSchema,
  ListWorkspaceMembersQuerySchema,
  ListWorkspaceMembersResponseSchema,
  ReactivateWorkspaceMemberPathParamsSchema,
  ReactivateWorkspaceMemberResponseSchema,
  UpdateWorkspaceMemberBodySchema,
  UpdateWorkspaceMemberPathParamsSchema,
  UpdateWorkspaceMemberResponseSchema,
} from './identity/members.schemas';
import {
  ListPendingInvitationsPathParamsSchema,
  ListPendingInvitationsResponseSchema,
  ResendInvitationPathParamsSchema,
  ResendInvitationResponseSchema,
  RevokeInvitationPathParamsSchema,
  RevokeInvitationResponseSchema,
} from './identity/invitations.schemas';
import {
  ConfirmMemberAvatarUploadPathParamsSchema,
  CreateMemberAvatarUploadBodySchema,
  CreateMemberAvatarUploadPathParamsSchema,
  GetMemberProfilePathParamsSchema,
  MemberProfileResponseSchema,
  MyMemberProfilePathParamsSchema,
  ReadMemberAvatarPathParamsSchema,
  UpdateMemberProfileBodySchema,
  UpdateOwnMemberProfileBodySchema,
} from './identity/member-profiles.schemas';
import {
  MemberPreferencePathParamsSchema,
  GetMemberPreferenceResponseSchema,
  SaveMemberPreferenceBodySchema,
  SaveMemberPreferenceResponseSchema,
} from './identity/member-preferences.schemas';
import {
  AdvanceMemberOnboardingStepBodySchema,
  AdvanceMemberOnboardingStepResponseSchema,
  CompleteMemberOnboardingBodySchema,
  CompleteMemberOnboardingResponseSchema,
  ExchangeMemberInviteSessionBodySchema,
  ExchangeMemberInviteSessionResponseSchema,
  GetMemberOnboardingResponseSchema,
  MemberOnboardingPathParamsSchema,
  RequestMemberInviteSessionCodeBodySchema,
  RequestMemberInviteSessionCodeResponseSchema,
} from './identity/member-onboarding.schemas';
import {
  CompanyOnboardingPathParamsSchema,
  GetCompanyOnboardingResponseSchema,
  SaveCompanyOnboardingDraftBodySchema,
  CompanyOnboardingMeResponseSchema,
  SubmitCompanyOnboardingBodySchema,
  SubmitCompanyOnboardingResponseSchema,
} from './identity/company-onboarding.schemas';
import {
  ConfirmMediaUploadPathParamsSchema,
  ConfirmMediaUploadResponseSchema,
  CreateMediaUploadBodySchema,
  CreateMediaUploadPathParamsSchema,
  CreateMediaUploadResponseSchema,
  DeleteMediaFilePathParamsSchema,
  DeleteMediaFileResponseSchema,
  GetMediaFileMetadataPathParamsSchema,
  GetMediaFileMetadataResponseSchema,
  ReadManyMediaFilesBodySchema,
  ReadManyMediaFilesPathParamsSchema,
  ReadManyMediaFilesResponseSchema,
  ReadMediaFilePathParamsSchema,
  ReadMediaFileResponseSchema,
} from './media/files.schemas';
import { CompanyPathParamsSchema, CompanyResponseSchema, UpdateCompanyBodySchema } from './identity/company.schemas';
import {
  UpdateWebAddressBodySchema,
  WebAddressPathParamsSchema,
  WebAddressResponseSchema,
} from './identity/web-address.schemas';
import {
  ConfirmBrandingLogoUploadPathParamsSchema,
  CreateBrandingLogoUploadBodySchema,
  CreateBrandingLogoUploadPathParamsSchema,
  ReadBrandingLogoPathParamsSchema,
  UpdateWorkspaceBrandingBodySchema,
  WorkspaceBrandingPathParamsSchema,
  WorkspaceBrandingResponseSchema,
} from './identity/workspace-branding.schemas';
import {
  CompanySignaturePathParamsSchema,
  CompanySignatureResponseSchema,
  ResolveCompanySignatureQueryParamsSchema,
  ResolvedCompanySignatureResponseSchema,
  UpdateCompanySignatureBodySchema,
} from './identity/company-signature.schemas';
import {
  CreateDocumentTemplateBodySchema,
  DocumentTemplatePathParamsSchema,
  DocumentTemplateSchema,
  DocumentTemplatesPathParamsSchema,
  ListDocumentTemplatesResponseSchema,
  SuggestDocumentTemplateFieldsBodySchema,
  SuggestDocumentTemplateFieldsResponseSchema,
  UpdateDocumentTemplateBodySchema,
} from './identity/document-templates.schemas';
import {
  CreateEmailTemplateBodySchema,
  EmailTemplatePathParamsSchema,
  EmailTemplateSchema,
  EmailTemplatesPathParamsSchema,
  ListEmailTemplatesResponseSchema,
  UpdateEmailTemplateBodySchema,
} from './identity/email-templates.schemas';
import {
  IssueApiKeyBodySchema,
  IssueApiKeyPathParamsSchema,
  IssueApiKeyResponseSchema,
  ListApiKeysPathParamsSchema,
  ListApiKeysQuerySchema,
  ListApiKeysResponseSchema,
  RevokeApiKeyPathParamsSchema,
  RevokeApiKeyResponseSchema,
  UpdateApiKeyBodySchema,
  UpdateApiKeyPathParamsSchema,
  UpdateApiKeyResponseSchema,
} from './identity/api-keys.schemas';
import {
  CompleteOauthConsentBodySchema,
  CompleteOauthConsentPathParamsSchema,
  CompleteOauthConsentResponseSchema,
  ConnectedClientListItemSchema,
  ListConnectedClientsPathParamsSchema,
  ListConnectedClientsResponseSchema,
  DeclineOauthConsentBodySchema,
  DeclineOauthConsentPathParamsSchema,
  DeclineOauthConsentResponseSchema,
  PrepareOauthConsentQuerySchema,
  PrepareOauthConsentResponseSchema,
  RevokeConnectedClientPathParamsSchema,
  UpdateConnectedClientAccessBodySchema,
  UpdateConnectedClientAccessPathParamsSchema,
} from './identity/connected-clients.schemas';
import {
  AuthorizationServerMetadataSchema,
  AuthorizeOauthQuerySchema,
  OauthProtocolErrorSchema,
  OauthTokenBodySchema,
  OauthTokenResponseSchema,
  RegisterOauthClientBodySchema,
  RegisterOauthClientResponseSchema,
  RevokeOauthTokenBodySchema,
} from './identity/oauth-server.schemas';
import {
  ExchangeImpersonationBodySchema,
  ExchangeImpersonationResponseSchema,
  StartImpersonationBodySchema,
  StartImpersonationResponseSchema,
} from './identity/impersonation.schemas';
import {
  CheckLeadDuplicatesBodySchema,
  CheckLeadDuplicatesPathParamsSchema,
  CheckLeadDuplicatesResponseSchema,
  CreateLeadBodySchema,
  CreateLeadPathParamsSchema,
  CreateLeadResponseSchema,
  GetWorkspaceLeadPathParamsSchema,
  GetWorkspaceLeadResponseSchema,
  ListWorkspaceLeadsPathParamsSchema,
  ListWorkspaceLeadsQuerySchema,
  ListWorkspaceLeadsResponseSchema,
  MarkLeadLostBodySchema,
  MarkLeadLostPathParamsSchema,
  MarkLeadLostResponseSchema,
  ArchiveLeadBodySchema,
  ArchiveLeadPathParamsSchema,
  ArchiveLeadResponseSchema,
  ReopenLeadBodySchema,
  ReopenLeadPathParamsSchema,
  ReopenLeadResponseSchema,
  QualifyLeadBodySchema,
  QualifyLeadPathParamsSchema,
  QualifyLeadResponseSchema,
  UpdateLeadOwnerBodySchema,
  UpdateLeadOwnerPathParamsSchema,
  UpdateLeadOwnerResponseSchema,
  UpdateLeadNotesBodySchema,
  UpdateLeadNotesPathParamsSchema,
  UpdateLeadNotesResponseSchema,
  UpdateLeadLegalBasisBodySchema,
  UpdateLeadLegalBasisPathParamsSchema,
  UpdateLeadLegalBasisResponseSchema,
  UpdateLeadIntentBodySchema,
  UpdateLeadIntentPathParamsSchema,
  UpdateLeadIntentResponseSchema,
  UpdateLeadPropertyAddressBodySchema,
  UpdateLeadPropertyAddressPathParamsSchema,
  UpdateLeadPropertyAddressResponseSchema,
  UpdateLeadPropertyBodySchema,
  UpdateLeadPropertyPathParamsSchema,
  UpdateLeadPropertyResponseSchema,
  RecordLeadNotePathParamsSchema,
  RecordLeadNoteBodySchema,
  RecordLeadNoteResponseSchema,
  RecordLeadCommunicationPathParamsSchema,
  RecordLeadCommunicationBodySchema,
  RecordLeadCommunicationResponseSchema,
  CompleteLeadActivityPathParamsSchema,
  CompleteLeadActivityBodySchema,
  CompleteLeadActivityResponseSchema,
  LeadActivityTransitionPathParamsSchema,
  LeadActivityTransitionResponseSchema,
  RescheduleLeadActivityBodySchema,
  CancelLeadActivityBodySchema,
  ReassignLeadActivityBodySchema,
  ListMyTasksPathParamsSchema,
  ListMyTasksQuerySchema,
  ListMyTasksResponseSchema,
  ReassignLeadStandortPathParamsSchema,
  ReassignLeadStandortBodySchema,
  ReassignLeadStandortResponseSchema,
  AssignLeadResponsibleMembersPathParamsSchema,
  AssignLeadResponsibleMembersBodySchema,
  AssignLeadResponsibleMembersResponseSchema,
  ListAssignableMembersPathParamsSchema,
  ListAssignableMembersResponseSchema,
} from './lead/leads.schemas';
import { ReferenceFeedResponseSchema } from './public-reference-feed';
import {
  CreateConversationPathParamsSchema,
  CreateConversationBodySchema,
  CreateConversationResponseSchema,
  ListConversationsPathParamsSchema,
  ListConversationsQuerySchema,
  ListConversationsResponseSchema,
  GetConversationPathParamsSchema,
  GetConversationResponseSchema,
  PostMessagePathParamsSchema,
  PostMessageBodySchema,
  PostMessageResponseSchema,
  MessageSchema,
  RecordPageReadPathParamsSchema,
  RecordPageReadBodySchema,
  ListMessagesPathParamsSchema,
  ListMessagesQuerySchema,
  ListMessagesResponseSchema,
  ListModelsPathParamsSchema,
  ListModelsResponseSchema,
  ConversationPathParamsSchema,
  RunStreamEventSchema,
} from './assistant/conversations.schemas';
import {
  CreateLeadPartnerBodySchema,
  CreateLeadPartnerPathParamsSchema,
  CreateLeadPartnerResponseSchema,
  IssueLeadPartnerKeyPathParamsSchema,
  IssueLeadPartnerKeyResponseSchema,
  LeadPartnerActionPathParamsSchema,
  LeadPartnerResponseSchema,
  ListLeadPartnersPathParamsSchema,
  ListLeadPartnersResponseSchema,
} from './lead/lead-partners.schemas';
import {
  CompleteCompanyMailboxConnectionResponseSchema,
  ConnectImapCompanyMailboxBodySchema,
  ReconnectImapCompanyMailboxBodySchema,
  CompleteMailboxConnectionBodySchema,
  CompleteMailboxConnectionPathParamsSchema,
  CompleteMailboxConnectionResponseSchema,
  DisconnectMailboxPathParamsSchema,
  DisconnectMailboxResponseSchema,
  ConnectImapMailboxBodySchema,
  ConnectImapMailboxPathParamsSchema,
  DiscoverImapServerBodySchema,
  DiscoverImapServerPathParamsSchema,
  DiscoverImapServerResponseSchema,
  GetMailboxConnectionPathParamsSchema,
  InitiateMailboxConnectionBodySchema,
  InitiateMailboxConnectionPathParamsSchema,
  InitiateMailboxConnectionResponseSchema,
  MailboxConnectionResponseSchema,
  GetEmailThreadPathParamsSchema,
  EmailThreadResponseSchema,
  GetEmailMessageBodyPathParamsSchema,
  EmailMessageBodyResponseSchema,
  MarkEmailMessageReadPathParamsSchema,
  MarkEmailMessageReadResponseSchema,
  SyncMailboxPathParamsSchema,
  SyncMailboxResponseSchema,
  DownloadEmailAttachmentPathParamsSchema,
  SendEmailPathParamsSchema,
  SendEmailBodySchema,
  SendEmailResponseSchema,
  ListMailboxMessagesPathParamsSchema,
  ListMailboxMessagesQuerySchema,
  ListMailboxMessagesResponseSchema,
  CountUnplacedEmailThreadsPathParamsSchema,
  CountUnplacedEmailThreadsResponseSchema,
  AssignEmailThreadContactPathParamsSchema,
  AssignEmailThreadContactBodySchema,
  AssignEmailThreadContactResponseSchema,
  RemoveEmailThreadContactPathParamsSchema,
  RemoveEmailThreadContactResponseSchema,
  PlaceEmailThreadBodySchema,
  PlaceEmailThreadResponseSchema,
  RemoveEmailThreadPlacementResponseSchema,
  EmailThreadPlacementSuggestionsResponseSchema,
  MailboxThreadResponseSchema,
  ListCompanyMailboxesPathParamsSchema,
  ListCompanyMailboxesResponseSchema,
  CompanyMailboxPathParamsSchema,
  CompanyMailboxThreadPathParamsSchema,
  ListCompanyMailboxContactThreadsPathParamsSchema,
  ListCompanyMailboxContactThreadsQuerySchema,
  ListCompanyMailboxContactThreadsResponseSchema,
} from './communications/mailbox.schemas';
import {
  AppointmentSchema,
  AppointmentWriteResponseSchema,
  CalendarConnectionSchema,
  CalendarEventsQuerySchema,
  CalendarEventsResponseSchema,
  CreateAppointmentBodySchema,
  CreateAppointmentPathParamsSchema,
  UpdateAppointmentPathParamsSchema,
  CancelAppointmentPathParamsSchema,
  UpdateAppointmentBodySchema,
  DealSuggestionsResponseSchema,
  UpcomingAppointmentsResponseSchema,
  UpcomingPathParamsSchema,
  GetCalendarConnectionPathParamsSchema,
  GetDealSuggestionsPathParamsSchema,
  ListCalendarEventsPathParamsSchema,
  SyncCalendarPathParamsSchema,
  SyncCalendarResponseSchema,
} from './communications/calendar.schemas';
import {
  InviteProspectToViewingBodySchema,
  ViewingInvitationPreviewSchema,
  InviteProspectToViewingPathParamsSchema,
  ViewingInvitationResponseSchema,
} from './communications/viewing-invitations.schemas';
import { StreamEventSchema, WorkspaceEventStreamPathParamsSchema } from './communications/workspace-events.schemas';
import {
  IssueTelegramLinkCodeResponseSchema,
  MyTelegramConnectionResponseSchema,
  MyTelegramPathParamsSchema,
} from './communications/telegram-link.schemas';
import {
  IssueWhatsAppLinkCodeResponseSchema,
  MyWhatsAppConnectionResponseSchema,
  MyWhatsAppPathParamsSchema,
} from './communications/whatsapp-link.schemas';
import {
  AdminPortalConnectionResponseSchema,
  AdminPortalConnectionsResponseSchema,
  CompletePortalConnectionBodySchema,
  ListPortalConnectionsResponseSchema,
  PortalAccessPathParamsSchema,
  PropertyOpenImmoListingPathParamsSchema,
  PortalConnectionPathParamsSchema,
  PortalConnectionResponseSchema,
  PortalReadinessResponseSchema,
  TransmitOpenImmoListingBodySchema,
  TransmitPortalListingsBodySchema,
  PropertyPortalListingsPathParamsSchema,
  PropertyPortalListingsResponseSchema,
  SavePortalAccessBodySchema,
  StartPortalConnectionResponseSchema,
} from './property/portal.schemas';
import {
  BrowserJobPathParamsSchema,
  BrowserRunPathParamsSchema,
  BrowserRunQueueResponseSchema,
  ClaimBrowserRunBodySchema,
  ClaimBrowserRunResponseSchema,
  ConfirmBrowserRunBodySchema,
  DecideBrowserRunBodySchema,
  BrowserGoLiveJobResponseSchema,
  GoLiveJobQuerySchema,
  StartEndListingRunBodySchema,
  StartGoLiveRunBodySchema,
  StartGoLiveRunResponseSchema,
  BrowserRunResponseSchema,
  FinishBrowserRunBodySchema,
  ReportBrowserRunProgressBodySchema,
  StartBrowserRunBodySchema,
  StartBrowserRunResponseSchema,
  BrowserJobQuerySchema,
  BrowserJobResponseSchema,
  BrowserListingsPathParamsSchema,
  BrowserListingsResponseSchema,
  LinkBrowserListingBodySchema,
  BrowserImportOptionsResponseSchema,
  BrowserImportPathParamsSchema,
  BrowserImportPictureUploadsBodySchema,
  BrowserImportPictureUploadsResponseSchema,
  BrowserImportListingBodySchema,
  BrowserImportListingResponseSchema,
  RecordBrowserOutcomeBodySchema,
  RecordBrowserOutcomeResponseSchema,
} from './property/browser-publish.schemas';
import { PublicWebExposePageSchema, PublicWebExposePathParamsSchema } from './property/public-web-expose.schemas';
import { PublishWebExposeBodySchema, WebExposeReadinessResponseSchema } from './property/web-expose.schemas';
import {
  AdvanceDealPhaseBodySchema,
  AdvanceDealPhasePathParamsSchema,
  AdvanceDealPhaseResponseSchema,
  RevertDealPhaseBodySchema,
  RevertDealPhasePathParamsSchema,
  RevertDealPhaseResponseSchema,
  AddDealContactBodySchema,
  AddDealContactPathParamsSchema,
  AddDealContactResponseSchema,
  CreateDealBodySchema,
  CreateDealPathParamsSchema,
  CreateDealResponseSchema,
  GetDealPathParamsSchema,
  GetWorkspaceDealPathParamsSchema,
  GetDealResponseSchema,
  GetDealPhaseHistoryPathParamsSchema,
  GetDealPhaseHistoryResponseSchema,
  ListDealsPathParamsSchema,
  ListDealsQuerySchema,
  ListDealsResponseSchema,
  ListWorkspaceDealsPathParamsSchema,
  ListWorkspaceDealsQuerySchema,
  ListWorkspaceDealsResponseSchema,
  RemoveDealContactPathParamsSchema,
  RemoveDealContactResponseSchema,
  UpdateDealMandateTermsBodySchema,
  UpdateDealMandateTermsPathParamsSchema,
  UpdateDealMandateTermsResponseSchema,
  UpdateDealNotesBodySchema,
  UpdateDealNotesPathParamsSchema,
  UpdateDealNotesResponseSchema,
  DeferDealBodySchema,
  DeferDealPathParamsSchema,
  DeferDealResponseSchema,
  ResumeDealPathParamsSchema,
  ResumeDealResponseSchema,
  MarkDealLostBodySchema,
  MarkDealLostPathParamsSchema,
  MarkDealLostResponseSchema,
  ReopenDealPathParamsSchema,
  ReopenDealResponseSchema,
  DismissBuyerCommissionDifferenceBodySchema,
  DismissBuyerCommissionDifferencePathParamsSchema,
  DismissBuyerCommissionDifferenceResponseSchema,
  DismissDealPropertyCleanupPathParamsSchema,
  DismissDealPropertyCleanupResponseSchema,
  DeleteDealPathParamsSchema,
  DeleteDealResponseSchema,
  ReassignDealStandortPathParamsSchema,
  ReassignDealStandortBodySchema,
  ReassignDealStandortResponseSchema,
  AssignDealResponsibleMembersPathParamsSchema,
  AssignDealResponsibleMembersBodySchema,
  AssignDealResponsibleMembersResponseSchema,
  ListDealAssignableMembersPathParamsSchema,
  ListDealAssignableMembersResponseSchema,
} from './deal/deals.schemas';
import {
  AdvanceProspectPhaseBodySchema,
  AdvanceProspectPhasePathParamsSchema,
  AdvanceProspectPhaseResponseSchema,
  CreateProspectBodySchema,
  CreateProspectPathParamsSchema,
  CreateProspectResponseSchema,
  GetVisibleProspectPathParamsSchema,
  GetVisibleProspectResponseSchema,
  ListProspectsPathParamsSchema,
  ListProspectsQuerySchema,
  ListProspectsResponseSchema,
  ListProspectNextActivitiesPathParamsSchema,
  ListProspectNextActivitiesQuerySchema,
  ListProspectNextActivitiesResponseSchema,
  MarkProspectLostBodySchema,
  MarkProspectLostPathParamsSchema,
  MarkProspectLostResponseSchema,
  ChangeProspectOfferStatusBodySchema,
  ChangeProspectOfferStatusPathParamsSchema,
  ChangeProspectOfferStatusResponseSchema,
  RecordProspectOfferBodySchema,
  RecordProspectOfferPathParamsSchema,
  RecordProspectOfferResponseSchema,
  ReopenProspectBodySchema,
  ReopenProspectPathParamsSchema,
  ReopenProspectResponseSchema,
  RevertProspectPhaseBodySchema,
  RevertProspectPhasePathParamsSchema,
  RevertProspectPhaseResponseSchema,
  SendBrokerageNoticeResponseSchema,
  MarkBrokerageAcceptanceReplyResponseSchema,
  UpdateProspectNotaryAppointmentDateBodySchema,
  UpdateProspectNotaryAppointmentDatePathParamsSchema,
  UpdateProspectNotaryAppointmentDateResponseSchema,
} from './deal/prospects.schemas';
import {
  AssessProspectViewingBodySchema,
  AssessProspectViewingPathParamsSchema,
  AssessProspectViewingResponseSchema,
  ListProspectViewingsPathParamsSchema,
  ListProspectViewingsResponseSchema,
} from './deal/prospect-viewings.schemas';
import {
  BuyerDataRoomSchema,
  DataRoomCardSchema,
  ListDataRoomReleasesPathParamsSchema,
  ListDataRoomReleasesQuerySchema,
  ListDataRoomReleasesResponseSchema,
  SaveDataRoomReleasesBodySchema,
  SaveDataRoomReleasesPathParamsSchema,
  SaveDataRoomReleasesResponseSchema,
  DataRoomWriteBodySchema,
  ReleaseDataRoomBodySchema,
  DataRoomWritePathParamsSchema,
  GetBuyerDataRoomBodySchema,
  GetDataRoomDocumentUrlBodySchema,
  GetDataRoomPathParamsSchema,
} from './deal/data-room.schemas';
import {
  BrokerageNoticeReadinessPathParamsSchema,
  BrokerageNoticeReadinessQuerySchema,
  BrokerageNoticeReadinessResponseSchema,
  GetBrokerageAgreementPathParamsSchema,
  GetBrokerageAgreementResponseSchema,
  SendBrokerageNoticeBodySchema,
  SendBrokerageNoticePathParamsSchema,
  MarkBrokerageAcceptanceReplyBodySchema,
  MarkBrokerageAcceptanceReplyPathParamsSchema,
} from './deal/brokerage-agreements.schemas';
import {
  CreateBuyerContractTemplateBodySchema,
  UpdateBuyerContractTemplateBodySchema,
  BuyerContractTemplatePathParamsSchema,
  BuyerContractTemplateSchema,
  BuyerContractTemplatesPathParamsSchema,
  ListBuyerContractTemplatesResponseSchema,
} from './deal/buyer-contract-templates.schemas';
import {
  SignBodySchema,
  SigningRevocationBodySchema,
  SigningDocumentBodySchema,
  SigningDocumentSchema,
  SigningPageSchema,
  SigningTokenBodySchema,
} from './deal/signing-page.schemas';
import {
  BrokerageAgreementPageSchema,
  ConcludeBrokerageAgreementBodySchema,
  ServeBrokerageAgreementPageBodySchema,
  GetBrokerageExposeDownloadBodySchema,
  BrokerageExposeDownloadSchema,
  WithdrawBrokerageAgreementBodySchema,
} from './deal/brokerage-agreement-page.schemas';
import { GetBrokerageProofResponseSchema } from './deal/brokerage-proof.schemas';
import {
  DealDocumentDraftQuerySchema,
  DealDocumentDraftResponseSchema,
  DealDocumentFileQuerySchema,
  DealDocumentPathParamsSchema,
  SigningRequestPathParamsSchema,
  SigningSignerPathParamsSchema,
  DeleteDealDocumentResponseSchema,
  GenerateDealDocumentBodySchema,
  GenerateDealDocumentResponseSchema,
  GetDealDocumentFileResponseSchema,
  GetDealDocumentResponseSchema,
  ListDealDocumentsResponseSchema,
  RetryDealDocumentResponseSchema,
  SetOriginalReceivedBodySchema,
  SetOriginalReceivedResponseSchema,
} from './deal/deal-documents.schemas';
import {
  MarkSigningAcceptanceReplyBodySchema,
  MarkSigningAcceptanceReplyResponseSchema,
  RemindSignersBodySchema,
  RemindSignersResponseSchema,
  SendForSignatureBodySchema,
  SendForSignatureResponseSchema,
  SigningDraftSchema,
  WithdrawSigningRequestBodySchema,
  WithdrawSigningRequestResponseSchema,
} from './deal/deal-signing-requests.schemas';
import { GetSigningProofResponseSchema } from './deal/signing-proof.schemas';
import { ListPropertyInquiriesResponseSchema, SubmitWebsiteInquiryBodySchema } from './deal/inquiries.schemas';
import { SubmitWebExposeInquiryBodySchema } from './deal/web-expose-inquiries.schemas';
import {
  ListDealNextActivitiesPathParamsSchema,
  ListDealNextActivitiesQuerySchema,
  ListDealNextActivitiesResponseSchema,
  ListDealOverviewPathParamsSchema,
  ListDealOverviewQuerySchema,
  ListDealOverviewResponseSchema,
} from './deal/deal-overview.schemas';
import {
  DashboardFunnelQuerySchema,
  DashboardPathParamsSchema,
  DashboardQuerySchema,
  GetDashboardFunnelResponseSchema,
  GetDashboardAttentionResponseSchema,
  GetDashboardNewRecordsResponseSchema,
  GetDashboardSetupResponseSchema,
  GetDashboardTeamResponseSchema,
} from './deal/dashboard.schemas';
import {
  ListMandatesPathParamsSchema,
  ListMandatesQuerySchema,
  ListMandatesResponseSchema,
} from './deal/mandates.schemas';
import {
  ACTIVITY_PAGE_DEFAULT_LIMIT,
  ListActivitiesPathParamsSchema,
  ListActivitiesQuerySchema,
  ListActivitiesResponseSchema,
  ListMemberActivitiesPathParamsSchema,
  ListMemberActivitiesQuerySchema,
  ListAutoResumeNoticesPathParamsSchema,
  ListAutoResumeNoticesQuerySchema,
  ListAutoResumeNoticesResponseSchema,
  MarkActivitySeenPathParamsSchema,
  MarkActivitySeenResponseSchema,
} from './activity/activities.schemas';
import {
  ActivityRecordResponseSchema,
  ActivityTransitionPathParamsSchema,
  CancelActivityRecordBodySchema,
  CompleteActivityRecordBodySchema,
  CompleteActivityRecordResponseSchema,
  DeleteActivityRecordResponseSchema,
  ListMemberActivityTasksPathParamsSchema,
  ListMemberActivityTasksQuerySchema,
  ListMemberActivityTasksResponseSchema,
  ReassignActivityRecordBodySchema,
  RescheduleActivityRecordBodySchema,
  RecordActivityRecordBodySchema,
  RecordActivityRecordPathParamsSchema,
} from './activity/activity-records.schemas';
import {
  ActivityFeedResponseSchema,
  ActivityOwnOpenCountResponseSchema,
  ListScopedActivitiesPathParamsSchema,
  ListScopedActivitiesQuerySchema,
  ListScopedLeadActivitiesQuerySchema,
} from './activity/activity-feed.schemas';
import {
  ListMemberViewingsPathParamsSchema,
  ListMemberViewingsResponseSchema,
} from './activity/member-viewings.schemas';
import { adminDealContracts } from './deal/admin-deal.contracts';
import { adminCompanyContracts } from './relationship/admin-company.contracts';
import { adminValuationContracts, valuationAgentContracts, valuationContracts } from './valuation/valuation.contracts';
import { IdsFilterQuerySchema } from './common/ids-filter.schema';
import { WireErrorSchema } from './errors/wire-error.schema';
import type { Permission } from './permissions';
import { z as zod } from 'zod';

/**
 * Cross-app contract tree. Plain const objects — no adapter library. Each
 * leaf carries `{ method, path, query?, body?, pathParams?, response }`,
 * where `response` is a status-keyed map of success schemas (the success
 * status is named in the contract — usually `200`; 4xx / 5xx envelopes
 * always use `WireErrorSchema` per ADR 0013 and don't need to be
 * enumerated per leaf).
 *
 * Walkers (one for backend Fastify routes, one for the typed SPA client)
 * pick this tree apart and produce their respective surfaces; both sides
 * read the same `path` / `method` and validate against the same `query` /
 * `body` / `response` schemas. See `README.md` for the walker contracts
 * and the recipe for adding a new endpoint.
 *
 * Every path is prefixed with `/v1` — single versioned API surface for
 * web-app, admin-web-app, and any future mobile client.
 */

/**
 * Fields shared by every endpoint regardless of payload shape. The
 * `EndpointDefinition` union discriminates on `kind` so the contract is the
 * single source of truth for every endpoint — `'json'` (today's
 * request/response zod, the implicit default) and `'stream'` (SSE: no JSON
 * response) today, extensible to `'form-data'` etc. later. The membership /
 * permission gate (`permission`) lives here, so it is reused identically for
 * all kinds.
 */
type EndpointBase = {
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly path: string;
  readonly pathParams?: z.ZodTypeAny;
  readonly description?: string;
  /**
   * Authorisation gate for this endpoint. The `'PUBLIC'` sentinel marks
   * routes that accept unauthenticated callers (sign-in, callback,
   * refresh, sign-out, health). Every other endpoint names the
   * `Permission` literal required to invoke it. The literal type means
   * a missing field is a compile error — default-deny at the type
   * system; the route-builder's tier middleware enforces the runtime
   * check (a non-`PUBLIC` route must be registered inside a role
   * middleware closure, otherwise registration throws).
   */
  readonly permission: Permission;
  /**
   * Documentation tier for this endpoint (IMM-203 / ADR 0026). Absent ⇒
   * never documented. `'PUBLIC'` ⇒ included in the public, partner-facing
   * docs site.
   *
   * Deliberately distinct from `permission: 'PUBLIC'`, which controls
   * *authentication* (unauthenticated callers), not *documentation* — the
   * two share the literal `'PUBLIC'` but live on different fields and mean
   * different things. Modelled as a string enum, not a boolean, so a later
   * tier (e.g. an `'INTERNAL'` docs site for our own reference) is an added
   * member, not a schema change. The public docs generator includes a leaf
   * iff `docs === 'PUBLIC'`.
   */
  readonly docs?: 'PUBLIC';
};

/**
 * The default endpoint kind: a request/response endpoint validated by zod on
 * both sides. `kind` is OPTIONAL here so every existing entry (which carries
 * no `kind` field) is a `JsonEndpoint` — full back-compat.
 */
export type JsonEndpoint = EndpointBase & {
  readonly kind?: 'json';
  readonly query?: z.ZodTypeAny;
  readonly body?: z.ZodTypeAny;
  /**
   * Status-keyed map of response body schemas. The contract names the
   * success status (usually `200`, but a contract may declare `201` /
   * `204` etc.); `routeSpec` forwards this directly into Fastify's
   * schema.response. The typed client picks the schema by the actual
   * response status.
   */
  readonly response: Readonly<Record<number, z.ZodTypeAny>>;
};

/**
 * A Server-Sent Events endpoint: no JSON response body (the handler hijacks
 * the reply and writes a `text/event-stream`). `kind: 'stream'` is the
 * REQUIRED discriminant. `event` optionally names the SSE event payload
 * schema, for docs/clients.
 */
export type StreamEndpoint = EndpointBase & {
  readonly kind: 'stream';
  readonly event?: z.ZodTypeAny;
};

export type EndpointDefinition = JsonEndpoint | StreamEndpoint;

const EmptyObjectSchema = zod.object({}).strict();

/**
 * Operator surface over the API-keys resource tree. Extracted from the
 * `contracts` literal into a named const (IMM-556.4) because the inferred type
 * of that literal had grown past the limit `tsc` will serialize into a `.d.ts`
 * — adding one route to any group failed the build with TS7056. A named group
 * is emitted as a `typeof` reference instead of being inlined, which is the
 * same shape `valuationContracts` already uses. Typing is unchanged: the
 * `as const satisfies` below keeps every leaf's literal method/path/permission.
 */
const adminApiKeyContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/api-keys',
    query: ListAdminApiKeysQuerySchema,
    response: { 200: ListAdminApiKeysResponseSchema },
    permission: 'backoffice:identity:api-key-read',
    description:
      'List API keys across every workspace (operator view), newest-first, unpaginated. Optionally filter by `workspaceId` and/or `status`. Keys of workspaces marked as testing environments are hidden unless `includeTestWorkspaces=true`; a service key bound to no workspace is always listed. The secret hash is never returned.',
  },
  get: {
    method: 'GET',
    path: '/v1/admin/api-keys/:id',
    pathParams: GetAdminApiKeyPathParamsSchema,
    response: { 200: GetAdminApiKeyResponseSchema },
    permission: 'backoffice:identity:api-key-read',
    description:
      'Return one API key by id with its workspace name, member email, granted permissions, and revocation metadata. 404 with identity.api-key.not-found when absent.',
  },
  resolveOwner: {
    method: 'GET',
    path: '/v1/admin/workspaces/:workspaceId/api-key-owner',
    pathParams: ResolveAdminApiKeyOwnerPathParamsSchema,
    response: { 200: ResolveAdminApiKeyOwnerResponseSchema },
    permission: 'backoffice:identity:api-key-create',
    description:
      'Resolve the owner member an admin-issued key would act as for the given workspace (IMM-435): the stored owner when active and holding workspace-wide access, else any active member holding it. Returns { owner: { memberId, email, name } } or { owner: null } when the workspace has no eligible owner. Read-only companion to the issue endpoint, called on workspace-select to show "Handelt als".',
  },
  issue: {
    method: 'POST',
    path: '/v1/admin/api-keys',
    body: IssueAdminApiKeyBodySchema,
    response: { 200: IssueAdminApiKeyResponseSchema },
    permission: 'backoffice:identity:api-key-create',
    description:
      "Issue a workspace API key on an operator's behalf (IMM-435). Binds the key to the target workspace's resolved owner and records the acting operator as issuer. The granted permissions must be members of the admin grantable catalogue (all workspace/org permissions minus api-key management — 400 otherwise) and held by the resolved owner (422 identity.api-key.permission-not-held). 422 identity.api-key.no-eligible-owner when the workspace has no active member with workspace-wide access. The plaintext secret is returned exactly once and never again.",
  },
  issueService: {
    method: 'POST',
    path: '/v1/admin/api-keys/admin',
    body: IssueAdminKeyBodySchema,
    response: { 200: IssueAdminKeyResponseSchema },
    permission: 'backoffice:identity:api-key-create',
    description:
      "Issue an admin API key (IMM-556.1) — the credential a machine consumer of the valuation API authenticates with. Unlike a workspace key it binds to no member and no single workspace: it is bound to the ACTING operator and holds whatever that operator holds at call time, so it carries no granted permissions of its own and there is no permission picker. `scope` is required and explicit — either { kind: 'ALL' } or { kind: 'SELECTED', workspaceIds: [...] } with at least one id (400 otherwise); a scope is never defaulted. 422 identity.api-key.workspace-unknown when a named workspace does not exist, and 422 identity.api-key.name-taken when another ACTIVE admin API key already carries the name (a revoked key never blocks a name, since re-issuing after revocation is the documented recovery for a lost secret). The plaintext `svc_…` secret is returned exactly once and never again. The key authenticates nothing until IMM-556.2.",
  },
  update: {
    method: 'PATCH',
    path: '/v1/admin/api-keys/:id',
    pathParams: UpdateAdminApiKeyPathParamsSchema,
    body: UpdateAdminApiKeyBodySchema,
    response: { 200: UpdateAdminApiKeyResponseSchema },
    permission: 'backoffice:identity:api-key-update',
    description:
      "Rewrite an issued key's granted permissions and rate limit in place (IMM-493.1), without revoking it or re-minting its secret — a partner integration holding the key keeps working across the change. A full replace: both fields are required. The requested permissions must be members of the admin grantable catalogue (400 otherwise), and any permission being ADDED must be held by the key's bound member (422 identity.api-key.permission-not-held); a permission the key already carries survives the edit even if the member has since lost it, because the verifier re-intersects against the member's live set on every request. 422 identity.api-key.cannot-edit-revoked when the key is revoked. An edit whose values already match is a 200 no-op that writes nothing and emits no event. 404 identity.api-key.not-found when the id is unknown. 422 identity.api-key.cannot-edit-service-key when the id names an admin API key: an admin API key is fixed for its whole life (IMM-556.1 BR 9) — revoke it and issue a new one.",
  },
  revoke: {
    method: 'POST',
    path: '/v1/admin/api-keys/:id/revoke',
    pathParams: RevokeAdminApiKeyPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: RevokeAdminApiKeyResponseSchema },
    permission: 'backoffice:identity:api-key-revoke',
    description:
      'Revoke a workspace API key from the admin tier (IMM-435.2) — flips status to revoked, stamps revokedAt, and records the acting operator in revokedByAdmin. The key fails authentication from the next request. Idempotent: revoking an already-revoked key is a 200 no-op that returns the original revocation metadata unchanged and emits no second event. 404 identity.api-key.not-found when the id is unknown.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). The
 * inferred type of that literal had grown past the limit `tsc` will serialize
 * into a `.d.ts`, so adding a single route to ANY group failed the build with
 * TS7056. A named group is emitted as a `typeof` reference instead of being
 * inlined whole — the same shape `valuationContracts` already uses.
 *
 * Typing is unchanged: `as const satisfies Record<string, EndpointDefinition>`
 * keeps every leaf's literal method / path / permission and its exact schemas,
 * which the route-builder's request inference depends on.
 */
const propertiesContracts = {
  create: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties',
    pathParams: CreatePropertyPathParamsSchema,
    body: CreatePropertyBodySchema,
    response: { 200: CreatePropertyResponseSchema },
    permission: 'organization:property:property-create',
    description:
      'Create a property under a workspace organisation the caller is a member of. The property lands in status DRAFT assigned to the calling member.',
  },
  list: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties',
    pathParams: ListWorkspaceOrganizationPropertiesPathParamsSchema,
    query: IdsFilterQuerySchema,
    response: { 200: ListWorkspaceOrganizationPropertiesResponseSchema },
    permission: 'organization:property:property-read',
    docs: 'PUBLIC',
    description:
      'List properties under one location of a workspace the caller is a member of, newest-first. Pass `?ids=` to restrict to a known set (batch hydration) instead of the full list.\n\nThis is also the published surface\'s ENTRY POINT (IMM-585). Every workspace-addressed route takes a `workspaceId`, and an integrator is often handed a location id instead — this read takes that location id and every row it answers with carries the `workspaceId` its property belongs to, so one call turns the id you were given into the one every other route wants. A location belongs to exactly one workspace, so any row answers it. A location with no properties answers `{"data": []}` and cannot tell you: ask whoever issued the key.\n\nWorth knowing before you reach for it: a wrong `workspaceId` on a workspace-addressed route is answered with a 401 carrying `"code": "identity.api-key.workspace-mismatch"`, while an invalid or revoked key is answered with a 401 carrying no `code` at all (both share the same flat `"Unauthorized"` message). Branch on the code: see the mismatch code and fix the id with this read; see no code and go looking for a credential problem. On THIS route the `organizationId` behaves differently: every location id your key does not reach — one that does not exist, one that exists in ANOTHER workspace, and one inside your own workspace that the key\'s member holds no role in — is answered with the same 404 carrying `"code": "organization.not-found"`. Deliberately one answer, so the API never confirms which location ids are real. Read that 404 as "not a location this key reaches, and I am told no more than that"; a 403 on this route means the key reaches the location but was issued without the grant this read needs.',
  },
  workspaceList: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/properties',
    pathParams: ListVisiblePropertiesPathParamsSchema,
    query: ListVisiblePropertiesQuerySchema,
    response: { 200: ListVisiblePropertiesResponseSchema },
    permission: 'workspace:property:property-read',
    docs: 'PUBLIC',
    description:
      'List properties across every location the caller can see (their affiliated organisations + descendants), plus any property assigned to them, newest-first. Pass `?organizationId=` to narrow to one visible location and `?ids=` to restrict to a known set (batch hydration). `?q=` applies the free-text match of the ⌘K palette (internal title + linked address, diacritic-insensitive); blank means absent.',
  },
  reassignStandort: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/properties/:id/standort',
    pathParams: ReassignPropertyStandortPathParamsSchema,
    body: ReassignPropertyStandortBodySchema,
    response: { 200: ReassignPropertyStandortResponseSchema },
    permission: 'workspace:property:property-reassign',
    description:
      "Move a property across Standorte (IMM-20.4 AC22). Allowed when the caller is Owner/Workspace-Admin, or the property's creator, or both the source and target Standort are within the caller's visible set; otherwise 403. The target must be an organisation in the same workspace.",
  },
  get: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id',
    pathParams: GetWorkspaceOrganizationPropertyPathParamsSchema,
    response: { 200: GetWorkspaceOrganizationPropertyResponseSchema },
    permission: 'organization:property:property-read',
    description:
      'Return one property under a workspace organisation the caller is a member of. 404 if the property does not exist in that workspace + organisation.',
  },
  getWorkspace: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/properties/:id',
    pathParams: GetWorkspacePropertyPathParamsSchema,
    response: { 200: GetVisiblePropertyResponseSchema },
    permission: 'workspace:property:property-read',
    docs: 'PUBLIC',
    description:
      "Fetch a single property by id within a workspace the caller is a member of. Unlike the organisation-addressed read, this resolves a property in any location the caller may see — their visible location set, plus any property assigned to them — because the board's hierarchical visibility (IMM-20.4) can surface a property whose home location is not the caller's selected one. A property outside that set answers 404, exactly as the board hides it — unless a deal or lead in a location the caller sees links it, in which case it opens and `accessOrganizationId` names that location: address the property's organisation-scoped routes through it. 404 when the property does not exist in the workspace (tenant-isolated).\n\n`links` carries this record's own URL as `self` — the workspace-addressed path you just called, not a canonical alternative — plus the URL of everything filed under the property: its activity timeline (`organization:activity:activity-read`), its documents (`organization:property:document-read`), its gallery (`organization:property:media-read`), its facts dossier (`organization:property:property-read`) and its delivered valuation (`organization:valuation:valuation-request-read`). All five are always present — an empty gallery or an unvalued property still has a resource to read — and every one of them is addressed under the property's own location, so this workspace-addressed read still hands you links you can follow. Follow them verbatim rather than assembling paths.\n\nA link is a routing convenience, never a statement about your key: its presence says the resource exists, and whether you may read it is answered by the request you make. Several of the targets below need a grant this read does not imply, so a key can legitimately be refused on a link it can see — each one names the grant it wants.",
  },
  updateBuilding: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/building',
    pathParams: UpdatePropertyBuildingPathParamsSchema,
    body: UpdatePropertyBuildingBodySchema,
    response: { 200: UpdatePropertyBuildingResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Replace the whole `building` section of one property (all-or-nothing — the body carries every column). 404 if the property does not exist in that workspace + organisation.',
  },
  updateDescriptions: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/descriptions',
    pathParams: UpdatePropertyDescriptionsPathParamsSchema,
    body: UpdatePropertyDescriptionsBodySchema,
    response: { 200: UpdatePropertyDescriptionsResponseSchema },
    permission: 'organization:property:property-update',
    description:
      "Replace the whole `descriptions` section of one property for the German locale — the four free texts (`description`, `equipmentDescription`, `locationDescription`, `miscellaneous`). All four are sent every time and any of them may be `null`; an empty or whitespace-only value is stored as `null`, which is a perfectly ordinary state and blocks nothing. No text is ever shortened here: a value over a portal's own limit is stored as written, and the portal shortens it at its own end. `expectedVersion` is the `version` carried by the bundle you read; send `null` if the property carried no descriptions bundle at all. 409 `property.property-descriptions.stale-edit` when it no longer matches — a colleague saved in between, and nothing is written. 422 `property.property-descriptions.too-long` when any text exceeds 20 000 characters, naming the field in `details.field`. `exposeTitle` (one line, ≤ 120) and `highlights` (ordered, each ≤ 80, blank entries dropped) are optional: leave one out to keep what is stored, `null` / `[]` clears it. 422 `property.property-descriptions.expose-title-too-long` / `property.property-descriptions.highlight-too-long` (`details.index`) when over the cap. 404 if the property does not exist in that workspace + organisation.",
  },
  updateAddress: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/address',
    pathParams: UpdatePropertyAddressPathParamsSchema,
    body: UpdatePropertyAddressBodySchema,
    response: { 200: UpdatePropertyAddressResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Replace the whole address of one property (every column on the `addresses` row, including the auto-filled latitude/longitude). The address row keeps its id; only its columns change. 404 if the property does not exist in that workspace + organisation.',
  },
  updateIdentity: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/identity',
    pathParams: UpdatePropertyIdentityPathParamsSchema,
    body: UpdatePropertyIdentityBodySchema,
    response: { 200: UpdatePropertyIdentityResponseSchema },
    permission: 'organization:property:property-update',
    description:
      "Edit the identity-level fields of one property after creation: the marketing type (SALE ↔ RENTAL — flips which pricing section the detail page shows; any attached deal still references the property unaffected) and the reference number. The reference is workspace-unique across non-deleted properties; a duplicate fails 422 `property.property.reference-number-taken` with the offending value in `details.referenceNumber`. Pass `referenceNumber: null` to clear it; re-saving the property's own current reference is allowed. 404 if the property does not exist in that workspace + organisation.",
  },
  updateEnergy: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/energy',
    pathParams: UpdatePropertyEnergyPathParamsSchema,
    body: UpdatePropertyEnergyBodySchema,
    response: { 200: UpdatePropertyEnergyResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Replace the whole `energy` section of one property — the scalar fields on `property_energy` plus the `property_energy_heating_types` and `property_energy_firing_types` child collections (all-or-nothing per the DB CHECK and the contract).',
  },
  updateInvestmentYield: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/investment-yield',
    pathParams: UpdatePropertyInvestmentYieldPathParamsSchema,
    body: UpdatePropertyInvestmentYieldBodySchema,
    response: { 200: UpdatePropertyInvestmentYieldResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Replace the whole `investment-yield` section of one property (all-or-nothing — the body carries every column on `property_investment_yield`). 404 if the property does not exist in that workspace + organisation.',
  },
  updatePlotDetails: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/plot-details',
    pathParams: UpdatePropertyPlotDetailsPathParamsSchema,
    body: UpdatePropertyPlotDetailsBodySchema,
    response: { 200: UpdatePropertyPlotDetailsResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Replace the whole `plot-details` section of one property (all-or-nothing — the body carries every BauNVO ratio, the three Grundstück flags and both Erschliessung/Bebauung enums). 404 if the property does not exist in that workspace + organisation.',
  },
  updateAmenities: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/amenities',
    pathParams: UpdatePropertyAmenitiesPathParamsSchema,
    body: UpdatePropertyAmenitiesBodySchema,
    response: { 200: UpdatePropertyAmenitiesResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Replace the whole `property_amenities` collection for one property. The body is the complete desired set: the repo DELETEs every existing row for the property and INSERTs the payload in a TX. An empty array `[]` is valid and clears the section.',
  },
  updatePricingSale: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/pricing-sale',
    pathParams: UpdatePropertyPricingSalePathParamsSchema,
    body: UpdatePropertyPricingSaleBodySchema,
    response: { 200: UpdatePropertyPricingSaleResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Replace the whole `pricing-sale` section of one property — the row on `property_pricing_sale`, replaced wholesale (single-row upsert). The buyer commission is `commissionBasis` with `commissionRateNet` or `commissionAmountNet`, net of VAT; `commission` and `commissionIncludesVat` are read-only legacy fields, ignored on write and kept as stored. 422 with `property.property.pricing-not-applicable` if the property is not SALE, `property.property.commission-rate-out-of-range` if the rate is outside 0–100, `property.property.commission-amount-not-positive` if the amount is 0 or less.',
  },
  updatePricingRental: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/pricing-rental',
    pathParams: UpdatePropertyPricingRentalPathParamsSchema,
    body: UpdatePropertyPricingRentalBodySchema,
    response: { 200: UpdatePropertyPricingRentalResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Replace the whole `pricing-rental` section of one property. `totalRent` is stored exactly as sent — the frontend suggests the sum, the backend never computes it. The legacy free-text `deposit` is read-only and is not accepted here; it is preserved across the replace from the stored value. Refused with 400 when `heatingCostsIncluded` is set alongside a `heatingCosts` figure, or when both `depositAmount` and `depositMonths` are sent. 422 with `property.property.pricing-not-applicable` if the property is not RENTAL.',
  },
  updateRentalTerms: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/rental-terms',
    pathParams: UpdatePropertyRentalTermsPathParamsSchema,
    body: UpdatePropertyRentalTermsBodySchema,
    response: { 200: UpdatePropertyRentalTermsResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Replace the whole Mietkonditionen section of one letting — furnishing, lease term (with its end date), minimum term, rent type and subletting — on the `property_pricing_rental` row, without touching the rental price columns. Refused with 400 when `leaseTerm` is FIXED_TERM without a `fixedTermUntil` of today or later, or INDEFINITE with one. 422 with `property.property.pricing-not-applicable` if the property is not RENTAL.',
  },
  updateAdditionalCosts: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/additional-costs',
    pathParams: UpdatePropertyAdditionalCostsPathParamsSchema,
    body: UpdatePropertyAdditionalCostsBodySchema,
    response: { 200: UpdatePropertyAdditionalCostsResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Replace the whole `property_additional_costs` collection for one property — DELETE every existing row and INSERT the payload entries in a single TX. An empty array `[]` is valid and clears the section. Available on any marketing_type.',
  },
  updateAreas: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/areas',
    pathParams: UpdatePropertyAreasPathParamsSchema,
    body: UpdatePropertyAreasBodySchema,
    response: { 200: UpdatePropertyAreasResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Replace the whole `areas` section of one property — DELETE every `property_areas` row for the property and INSERT the entries from the body, in one TX. An empty body deletes everything. 404 if the property does not exist in that workspace + organisation.',
  },
  activate: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/activate',
    pathParams: PropertyLifecyclePathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: ActivatePropertyResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Move a property to ACTIVE. From DRAFT this enforces the publication-ready gate — street, house number, postal code, city; the marketing-type price (purchase price / base rent); the object-type primary area (living / usable / total / plot area, or parking-space type for garages) — and fails 422 `property.property.cannot-activate` with the missing field keys in `details.missingFields`. From INACTIVE it re-activates without the gate. Any other source status fails 422 `property.property.invalid-status-transition`. A concurrent transition that moved the status first fails 422 `property.property.status-conflict`. 404 if the property does not exist in that workspace + organisation.',
  },
  deactivate: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/deactivate',
    pathParams: PropertyLifecyclePathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: DeactivatePropertyResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Move an ACTIVE property to INACTIVE — it leaves the default active list and reappears under the inactive filter; data and any attached deal are unaffected. Any other source status fails 422 `property.property.invalid-status-transition`. A concurrent transition that moved the status first fails 422 `property.property.status-conflict`. 404 if the property does not exist in that workspace + organisation.',
  },
  archive: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/archive',
    pathParams: PropertyLifecyclePathParamsSchema,
    body: ArchivePropertyRequestBodySchema,
    response: { 200: ArchivePropertyResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Archive a property from any non-ARCHIVED status with an Archivierungsgrund (`reason`) and optional `note` (≤ 280 chars) — it leaves the default list and reappears only under the archived filter, regardless of attached deal state. `OTHER` requires a non-empty note; a missing reason or an empty `OTHER` note is rejected 400 at the request boundary. `SOLD` requires a SALE marketing type and `RENTED` a RENTAL one, else 422 `property.property.archive-reason-marketing-type-mismatch`. Archiving an already-ARCHIVED property fails 422 `property.property.invalid-status-transition`. A concurrent transition that moved the status first fails 422 `property.property.status-conflict`. When the property still has open deals, `lossReason` is required and every open deal is marked lost with it in the same step; it fails 422 `property.property.archive-loss-reason-required` without one, and 403 `property.property.archive-open-deal-not-losable` when the caller may not mark one of them as lost. `lossReason` / `lossNote` are ignored when there is no open deal. 404 if the property does not exist in that workspace + organisation.',
  },
  archivePreview: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/archive-preview',
    pathParams: PropertyLifecyclePathParamsSchema,
    response: { 200: PropertyArchivePreviewResponseSchema },
    permission: 'organization:property:property-update',
    description:
      "What archiving this property would also do: how many open deals (DRAFT / ACTIVE / DEFERRED, not deleted) it carries and whether the caller may mark every one of them as lost (`organization:deal:deal-update` in each deal's organisation). `mayLoseOpenDeals` is true when there is none. 404 if the property does not exist in that workspace + organisation.",
  },
  changeArchiveReason: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/archive-reason',
    pathParams: PropertyLifecyclePathParamsSchema,
    body: ArchivePropertyBodySchema,
    response: { 200: ChangeArchiveReasonResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Change the Archivierungsgrund (`reason`) and optional `note` of an already-ARCHIVED property in place, without changing its status — the edit is recorded in the status-change history. Same validation as archive (`SOLD`⇒SALE, `RENTED`⇒RENTAL, `OTHER`⇒note). Changing the reason of a non-ARCHIVED property fails 422 `property.property.invalid-status-transition`. 404 if the property does not exist in that workspace + organisation.',
  },
  updateReferenceSettings: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/reference-settings',
    pathParams: PropertyLifecyclePathParamsSchema,
    body: UpdatePropertyReferenceSettingsBodySchema,
    response: { 200: UpdatePropertyReferenceSettingsResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Set the reference flags on a sold Objekt (`ARCHIVED` with `archiveReason = SOLD`). The body carries the full desired pair `{ isReference, publishOnHomepage }`, applied atomically. `publishOnHomepage: true` requires `isReference: true` — the pair is rejected 400 at the boundary and 422 `property.property.homepage-requires-reference` in the domain. Setting the flags on a property that is not sold fails 422 `property.property.reference-requires-sold`. Turning `isReference` off cascades `publishOnHomepage` to false in the same write. 404 if the property does not exist in that workspace + organisation.',
  },
  updateHandReservation: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/hand-reservation',
    pathParams: PropertyLifecyclePathParamsSchema,
    body: UpdatePropertyHandReservationBodySchema,
    response: { 200: UpdatePropertyHandReservationResponseSchema },
    permission: 'organization:property:property-update',
    description:
      "Mark an ACTIVE property reserved by hand (`reservedByHand: true`) or lift the mark (`false`). The body carries the desired state: a request for the state the property already has changes nothing and answers 200. The hand mark is independent of the Verkaufs-Pipeline — the response's `reservation` stays non-null while a Kaufinteressent reserves the property. Marking a property that is not ACTIVE fails 422 `property.property.reservation-requires-active`; lifting never fails on status, because only an ACTIVE property can carry the mark. The mark is lifted automatically when the property leaves ACTIVE and does not come back when it returns. 404 if the property does not exist in that workspace + organisation.",
  },
  unarchive: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/unarchive',
    pathParams: PropertyLifecyclePathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: UnarchivePropertyResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Un-archive an ARCHIVED property. When the archive came with a deal loss (`archivedWithDealLoss` is set), the property returns to the status it had before (`restoreStatus`) and every deal lost in that step is reopened in the phase it was lost in, in the same step. Otherwise it lands on INACTIVE, never directly on ACTIVE. Un-archiving a non-ARCHIVED property fails 422 `property.property.invalid-status-transition`. A concurrent transition that moved the status first fails 422 `property.property.status-conflict`. 404 if the property does not exist in that workspace + organisation.',
  },
  stageInAcquisition: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/stage-in-acquisition',
    pathParams: PropertyLifecyclePathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: StageInAcquisitionResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Move a DRAFT property to IN_ACQUISITION — the manual counterpart to the Qualify-driven staging, for a property built by hand. Gate-free: no publication-ready check applies to a pre-ACTIVE state. Any source status other than DRAFT fails 422 `property.property.invalid-status-transition`. A concurrent transition that moved the status first fails 422 `property.property.status-conflict`. 404 if the property does not exist in that workspace + organisation.',
  },
  revertToDraft: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/revert-to-draft',
    pathParams: PropertyLifecyclePathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: RevertToDraftResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Move an IN_ACQUISITION property back to DRAFT — the two pre-ACTIVE staging states are freely interchangeable by hand. Gate-free. Any source status other than IN_ACQUISITION fails 422 `property.property.invalid-status-transition`. A concurrent transition that moved the status first fails 422 `property.property.status-conflict`. 404 if the property does not exist in that workspace + organisation.',
  },
  reassign: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/reassign',
    pathParams: ReassignPropertyPathParamsSchema,
    body: ReassignPropertyBodySchema,
    response: { 200: ReassignPropertyResponseSchema },
    permission: 'organization:property:property-reassign',
    description:
      "Repoint the property's primary assignment to another member of the same workspace; the assignee changes immediately. 404 if the target member is unknown, deactivated, or belongs to another workspace, or if the property does not exist in that workspace + organisation.",
  },
  softDelete: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/properties/:id',
    pathParams: SoftDeletePropertyPathParamsSchema,
    query: SoftDeletePropertyQuerySchema,
    response: { 200: SoftDeletePropertyResponseSchema },
    permission: 'organization:property:property-delete',
    description:
      'Soft-delete a property — sets `deleted_at` so it leaves every list (including the archived view) and subsequent reads 404. Reversible only via engineering / support tooling; there is no in-product undo. An optional `expectedStatus` query (IMM-171.3) refuses the delete with `property.property.delete-status-conflict` (422) when the property is no longer in that status at commit — the post-Verloren cleanup passes `IN_ACQUISITION`; omitted → unconditional delete. 404 if the property does not exist in that workspace + organisation. Returns the deleted id.',
  },
  listContacts: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/contacts',
    pathParams: ListPropertyContactsPathParamsSchema,
    query: ListPropertyContactsQuerySchema,
    response: { 200: ListPropertyContactsResponseSchema },
    permission: 'organization:property:property-read',
    description:
      'List contacts linked to one property with their per-link role, optionally filtered to a single `?role=` (e.g. OWNER). Joins `property_contacts` with the contact list-item projection so each row carries the contact id, display name, type, primary email / phone, city, and the role. 404 if the property does not exist in that workspace + organisation.',
  },
  listByContact: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/contacts/:id/properties',
    pathParams: ListContactPropertiesPathParamsSchema,
    response: { 200: ListContactPropertiesResponseSchema },
    permission: 'organization:property:property-read',
    description:
      'List properties linked to one contact with their per-link role. The inverse of `listContacts` — owned by the property module because `property_contacts` lives in the property schema. 404 if the contact does not exist in that workspace + organisation.',
  },
  attachContact: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/contacts',
    pathParams: AttachPropertyContactPathParamsSchema,
    body: AttachPropertyContactBodySchema,
    response: { 200: AttachPropertyContactResponseSchema },
    permission: 'organization:property:property-contact-write',
    description:
      'Attach an existing workspace contact to a property under a given role, with an optional free-text note (max 500 chars). 404 if either the property or the contact is missing from that workspace + organisation. 422 `property.property-contact.already-linked` if that (contact, property, role) triple already exists; 422 `property.property-contact.role-marketing-mismatch` if a prospect role does not match the property marketing type (Kaufinteressent only on Verkauf, Mietinteressent only on Vermietung). Returns the resulting `PropertyContact` row so the FE can splice it into its rendered list without a re-fetch.',
  },
  updateContact: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/contacts/:contactId/:contactRole',
    pathParams: UpdatePropertyContactPathParamsSchema,
    body: UpdatePropertyContactBodySchema,
    response: { 200: UpdatePropertyContactResponseSchema },
    permission: 'organization:property:property-contact-write',
    description:
      'Edit one property↔contact link — its role and/or note. The link is addressed by its current triple in the path (a contact may hold several roles on the same property); the body carries the new role and note. 404 `property.property-contact.not-linked` if no link exists for that triple (checked before the role guards). 422 `property.property-contact.already-linked` if the role change collides with an existing link; 422 `property.property-contact.role-marketing-mismatch` if the new prospect role does not match the property marketing type. Returns the updated `PropertyContact` row.',
  },
  detachContact: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/properties/:id/contacts/:contactId/:contactRole',
    pathParams: DetachPropertyContactPathParamsSchema,
    response: { 200: DetachPropertyContactResponseSchema },
    permission: 'organization:property:property-contact-write',
    description:
      'Remove one property↔contact link, addressed by its full triple in the path. The contact and property entities are untouched. 404 `property.property-contact.not-linked` if no link exists for that triple, so a double "Verknüpfung lösen" is safe.',
  },
  createDocumentUpload: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/documents/uploads',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: CreatePropertyDocumentUploadBodySchema,
    response: { 200: CreatePropertyDocumentUploadResponseSchema },
    permission: 'organization:property:document-write',
    description:
      'Begin a direct-to-S3 upload for a property document (IMM-218.1). Applies the fixed V1 policy — PDF, DOCX, JPEG, PNG; ≤ 20 MB — and returns a short-lived presigned POST plus a pending media file id. No document row is created yet; that happens on confirm. 404 if the property does not exist in that workspace + organisation.',
  },
  confirmDocumentUpload: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/documents',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: ConfirmPropertyDocumentUploadBodySchema,
    response: { 200: ConfirmPropertyDocumentUploadResponseSchema },
    permission: 'organization:property:document-write',
    description:
      'Confirm a pending document upload: verifies the stored bytes (type + size) via the media module and creates the document row linked to the property, stamped with the uploader and now. `category` defaults to `UNCATEGORIZED` and `displayName` to the original filename. Returns the stored `PropertyDocument`. 404 if the property is foreign; 422 if the bytes fail validation.',
  },
  listDocuments: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/documents',
    pathParams: PropertyDocumentsPathParamsSchema,
    query: ListPropertyDocumentsQuerySchema,
    response: { 200: ListPropertyDocumentsResponseSchema },
    permission: 'organization:property:document-read',
    docs: 'PUBLIC',
    description:
      "List the files filed under one property, newest first. Each row carries its display name, its `category` and the `categoryGroup` derived from it, how the category was decided (`categorySource`: `MANUAL` a person chose it, `AUTO` the classifier did, `NONE` nobody has), where the automatic classification stands (`classificationStatus`), the content type, the size in bytes, the uploader and the upload date. `uploadedByName` is `null` when the uploader is no longer an active member — the id beside it still resolves them.\n\nPass `?group=` to narrow to one category group, or `?group=UNCATEGORIZED` for the files still awaiting a category — the sentinel is both a filter value and the category such a file carries.\n\nThe two counts beside the rows are the **whole property's**, deliberately unaffected by `?group=`, so a filtered list beside an unfiltered count is correct rather than a bug: `uncategorizedCount` is how many of this property's files still have no category, and `analysisBacklogCount` how many of them a bulk content analysis would pick up — settled classification, and no usable analysis result yet.\n\n`canManage` on a row says whether the **member the key is bound to** could rename, re-categorise or delete that file **inside immoOS**. It never describes what the key may do: no file write is published here, so a key holding this grant reads and nothing more. Filing a document (`organization:property:document-write`) and requesting one (`organization:property:document-request`) are grantable, and a connected client reaches both through the immoOS MCP tools.\n\n`redacted` marks a redacted copy and `redactedFromDocumentId` points at its original; it is `null` on an original and on a copy whose original was deleted. Both counts leave out copies that still have an original. `redactionAvailable` says whether redaction can be offered at all.\n\n`uploadedByValuationAgent` marks a file the valuation agent filed (its `uploadedByName` is `null`), and `supersededAt` is set once a newer result of the same kind replaced it.\n\n404 if the property is not one this key may see, identical to the answer for a property that does not exist.",
  },
  createDocumentRequest: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/document-requests',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: CreateDocumentRequestBodySchema,
    response: { 200: DocumentRequestResponseSchema },
    permission: 'organization:property:document-request',
    description:
      "Raise a Dokumentenanforderung (IMM-383.2) — request the named missing Kategorien from the Objekt's Eigentümer. Snapshots each Kategorie's Pflichtstufe server-side, persists the record, and publishes `property.document-request-created` for IMM-412 (which composes/brands/sends the e-mail — not done here). Re-sending writes a fresh record. Gated non-Viewer (`document-request`). 422 `property.document-request.no-owner-email` when the Objekt has no Eigentümer contact with an e-mail; 404 if the property is foreign.",
  },
  readiness: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/readiness',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: PropertyReadinessResponseSchema },
    permission: 'organization:property:document-read',
    description:
      "The property's data-room readiness (Verkaufsreife) — IMM-383.1. Computes, for the Objekt's readiness profile (from object type + residential Untertyp), the required-document checklist: each row's Pflicht/Optional obligation and its Vorhanden/Fehlt/Läuft ab/Abgelaufen status (expiry from the extracted Energieausweis validity date), plus the overall SALE_READY/ALMOST_COMPLETE/INCOMPLETE signal (INCOMPLETE when a mandatory row is missing or expired, ALMOST_COMPLETE when one is expiring, a missing optional row never lowers it — IMM-1470), the mandatory present/required counter, and the present/total counter over every row. Advisory only; open to any member who may view the Objekt. 404 if the property does not exist in that workspace + organisation.",
  },
  dossier: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/dossier',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: PropertyDossierSchema },
    permission: 'organization:property:property-read',
    docs: 'PUBLIC',
    description:
      "Every fact immoOS holds about this property, grouped by taxonomy key, beside the value a person confirmed on the property record itself.\n\nOne entry per key. `authoritative` is the confirmed value together with the property field it was read from, or `null` when that field is unset or the key maps to no field at all; `advisory` is every live fact for that key, newest extraction first. A key appears when it has at least one live fact, or when it maps to a property field that carries a value — a key with neither is absent, so the list is not a fixed catalogue and its length differs from property to property. Look entries up by `factKey` rather than by position.\n\n**A key whose live facts disagree comes back as `conflict: true` with every candidate still in `advisory`, and no winner.** Choosing between readings is a human act inside immoOS, where a member confirms one of them onto the property record; until that happens, the disagreement is the answer. `advisory` is ordered by extraction time, which is an ordering and not a ranking — taking `advisory[0]` on a conflicted key silently picks a side this API deliberately refuses to pick. Readings are compared after normalising for the key's type, so `120` and `\"120\"` on a numeric key are one value rather than a conflict.\n\n**Re-reading a document supersedes what its previous run reported, and a superseded fact is invisible here.** Disagreement between two different documents is never superseded that way — it survives, which is what makes `conflict` worth reporting.\n\nEach fact carries where it came from and how sure the reader was. `sourceId` is the id of the document it was extracted from — the same id the property's document list returns — `sourceName` is that document's display name at the moment it was read (a human-readable companion to the id), and `sourceCategory` is that document's category then, which may since have changed. `sourceName` is `null` when the source document has since been deleted — the fact still returns — and it is always `null` under the `EXPOSE` projection, which withholds a customer's internal filename; on this `INTERNAL` operation it carries the name. Two fields are narrower in practice than the schema allows, and it is worth writing your parser against what is actually produced: `unit` is `null` on every fact today (the document reader records no separate unit), and `confidence`, recorded as a decimal between 0 and 1, takes `0.9`, `0.6` or `0.3` for a document reading; another writer may record any value between 0 and 1. Treat it as a band, not a probability. `modality` says whether the value was read from a document (`DOCUMENT`), a photo (`IMAGE`), or not read from a medium at all (`NONE`: an owner's statement, an external register, an on-site capture or a member's entry). Branch on `provenance.origin` rather than on `modality`.\n\n`occurrence` names which occurrence of a field a fact describes, for a field that occurs more than once (`Einheit 2`, `Gebäude 1`); it is null for a field that occurs once. `provenance` says where the value came from (`origin`), how well it is backed (`status`: `PROVEN`, `ASSUMED`, `CONTRADICTED`, `UNKNOWN`, `NOT_APPLICABLE`), how far a person checked it (`reviewLevel`: `AI_DETECTED`, or `MEMBER_CONFIRMED` with the member and time in `confirmation`), what proves it (`evidence`: a document and page, or an external source and its date; more kinds may be added), the date the value refers to (`timeReference`, `YYYY-MM` or `YYYY-MM-DD`) and, for a value that is unknown or does not apply, why (`reason`, with `value` null). `provenance` is null on facts recorded before 2026-11, which carry none. `conflict` compares values within one occurrence; two units with different rents do not conflict.\n\n`factKey` is the entry's catalog ID — the „Objektaufnahmebogen“ `GRUPPE.NAME`, or `GRUPPE.NAME.unterfeld` for a subfield (`FLA.WOHNFLAECHE`, `LAG.ADRESSE.plz`) — and is the stable identifier; branch on it. The 34 keys immoOS used before 2026-11 were renamed to catalog IDs once. `label` is display copy immoOS maintains for its own screens — German today, and renamable at any time. `scope` says whether the key describes the property itself (`PROPERTY_INTRINSIC`) or the terms it is being marketed on (`DEAL_CONTEXT`). `publicSafe` says whether immoOS has cleared that fact type for public display; every key ships `false`, and the flag does not affect what you receive here — you get every entry either way.\n\n`consumer` echoes the projection this response was composed for, and this operation always answers `INTERNAL`: the full internal view, deal-context facts included, with nothing withheld from a key that its bound member may already see. **The projection is set server-side from the route and is never read from the request** — no query parameter, header or body field selects one, so a caller cannot widen its own view. The narrower `EXPOSE` token in the same set is reserved for a future keyless public tier; no operation published here returns it.\n\n`brief` is a cached natural-language summary of these same entries, or `null` when none has been generated yet — a normal `200` rather than an error, and the usual answer for a property whose facts have only just changed. It is refreshed in the background: a read that finds it out of date queues the refresh and still returns the stored text, so the brief can lag the entries by a read or two. `stale` reports exactly that — `true` when the cached prose no longer matches the entries beside it (the same condition that queued the refresh), so you can flag lagging prose rather than presenting it as current; the text is still returned either way. It lives on the `brief` object, so a `null` brief carries no staleness claim at all. `model` names the model that wrote it; it is an internal identifier, not a token to branch on.\n\n`memory` is everything else immoOS has read about the property — from its documents and from e-mails, calls and notes recorded on its deals, and statements an immoOS operator added by hand (`source.type` `OPERATOR`, whose `source.id` is the property's id; `prompt` and `model` are null on those, and only on those) — one group per statement, newest reading first, disagreement kept as `conflict: true` with no winner, exactly as for `entries`. You receive only the entries whose source this key may read: a document's with `organization:property:document-read`, an e-mail's, call's or note's with `organization:activity:activity-read` in this location; a statement an operator added by hand reaches every key that may read this dossier. `effectiveValue` is the value to use: a member's correction when there is one. `source.id` is a document id, an activity id, or on `OPERATOR` the property's id, according to `source.type`. The brief does not summarise `memory`.\n\n404 if the property is not one this key may see, identical to the answer for a property that does not exist. The suggestion flow this data drives inside immoOS — the values a member is offered for confirmation, and the write that applies them — is not part of this REST API; that write's permission, `organization:property:property-update`, is grantable, and a connected client reaches it through the immoOS MCP tools.",
  },
  completeness: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/completeness',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: PropertyCompletenessResponseSchema },
    permission: 'organization:property:property-read',
    description:
      "The Objekt's per-product data completeness (IMM-1386) for Akquisebriefing, Bewertung 1a and Bewertung 1b, against the fact-key catalog's needs for its object type: per product COMPLETE/INCOMPLETE, `required` and `missingRecommended`. `required` holds the missing Pflicht fields (`missing: true`) plus the present ones that carry a marker (`missing: false`), so count the gaps with `missingRequiredCount`, not `required.length`; `status` is COMPLETE iff `missingRequiredCount` is 0. `markers` CONTRADICTED/ASSUMED mark a present field, UNKNOWN a missing one. A field that does not apply counts as met and is not listed. Fields are ordered by catalog group, then label. Computed on every read, never cached. Advisory only. 404 like the dossier when the Objekt does not exist in that workspace + organisation or is out of reach.",
  },
  getCapture: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/capture',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: GetPropertyCaptureResponseSchema },
    permission: 'organization:property:property-read',
    description:
      "The Objekt's on-site capture (IMM-1397), or `capture: null` when none was started. One capture per Objekt.",
  },
  startCapture: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/capture',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: StartPropertyCaptureBodySchema,
    response: { 200: PropertyCaptureSchema },
    permission: 'organization:property:property-update',
    description:
      'Start the on-site capture with the kind of viewing; stores it as a finding on AUF.BESICHTIGUNG.ART (origin ON_SITE, MEMBER_CONFIRMED). 422 property.property-capture.already-started when the Objekt has one; 422 property.property-capture.object-type-not-supported for a category other than HOUSE, APARTMENT, LAND.',
  },
  changeCaptureViewingKind: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/capture/viewing-kind',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: ChangePropertyCaptureViewingKindBodySchema,
    response: { 200: PropertyCaptureSchema },
    permission: 'organization:property:property-update',
    description:
      "Change the capture's kind of viewing; replaces the capture's own AUF.BESICHTIGUNG.ART finding. 404 property.property-capture.not-found without a capture.",
  },
  completeCapture: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/capture/complete',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: PropertyCaptureSchema },
    permission: 'organization:property:property-update',
    description:
      'Complete the on-site capture, or complete it again after it was continued: sets completedAt to now and completedByMemberId to the caller. Never requires completeness; the open required fields and photos are returned. 404 property.property-capture.not-found without a capture.',
  },
  getCaptureFields: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/capture/fields',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: PropertyCaptureFieldsResponseSchema },
    permission: 'organization:property:property-read',
    description:
      "The fields the on-site capture asks: every catalog field with a Bewertung 1b need (Pflicht or Empfohlen) for the Objekt's type, by group, split into missing and already known, with this capture's answers. 404 property.property-capture.not-found without a capture.",
  },
  recordCaptureFinding: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/capture/findings',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: RecordCaptureFindingBodySchema,
    response: { 200: PropertyCaptureFieldsResponseSchema },
    permission: 'organization:property:property-update',
    description:
      "Store one capture answer as a finding (origin ON_SITE, MEMBER_CONFIRMED): a value, UNKNOWN with reason, NOT_APPLICABLE with reason, or CLEARED to withdraw the capture's own finding. Never writes the curated Objekt field. Returns the re-read field list. 422 property.property-capture.field-not-asked for a field the capture does not list.",
  },
  getCapturePhotos: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/capture/photos',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: PropertyCapturePhotosResponseSchema },
    permission: 'organization:property:property-read',
    description:
      "The capture's photo list: the photo protocol's motifs for the Objekt's type (Pflicht first, then Empfohlen, each in protocol order) with their minimum and taken count, and every Aufnahmefoto of the capture, oldest first, each with a short-lived inline URL. 404 property.property-capture.not-found without a capture. Aufnahmefotos are internal to the valuation and never appear in the gallery, an Exposé, a portal listing or a Datenraum.",
  },
  createCapturePhotoUpload: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/capture/photos/uploads',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: CreateCapturePhotoUploadBodySchema,
    response: { 200: CreateMediaUploadResponseSchema },
    permission: 'organization:property:property-update',
    description:
      "Begin a direct-to-S3 upload of one Aufnahmefoto for a protocol motif. 404 without a capture. 422 property.property-capture.motif-not-applicable for a motif that does not apply to the Objekt's type; 422 property.property-media.type-not-allowed / property.property-media.image-too-large (JPEG, PNG or WebP up to 20 MB); 422 property.property-capture.photo-limit-reached at 250 photos. Aufnahmefotos are internal to the valuation and never appear in the gallery, an Exposé, a portal listing or a Datenraum.",
  },
  confirmCapturePhotoUpload: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/capture/photos',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: ConfirmCapturePhotoUploadBodySchema,
    response: { 200: PropertyCapturePhotosResponseSchema },
    permission: 'organization:property:property-update',
    description:
      "Confirm an uploaded Aufnahmefoto under its motif and return the re-read photo list. Confirming the same file again changes nothing. 404 without a capture. 422 property.property-capture.motif-not-applicable; 422 property.property-capture.photo-limit-reached; 422 property.property-capture.not-a-capture-upload for a file that was not uploaded through the capture's presign. Aufnahmefotos are internal to the valuation and never appear in the gallery, an Exposé, a portal listing or a Datenraum.",
  },
  factSuggestions: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/fact-suggestions',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: PropertyFactSuggestionsSchema },
    permission: 'organization:property:property-read',
    description:
      'The property-level promotion suggestions (IMM-500.4) — one entry per Objekt field the facts layer can fill, each carrying its suggested value, every supporting source with confidence, and the current Objekt value it would overwrite. Candidates come from the document facts and, since IMM-1050.6, from e-mails, calls and notes that state the field — the latter only when the caller holds `organization:activity:activity-read` on this organisation and the record belongs to it; `sourceType` says whether `sourceId` is a document or an activity. A key whose live facts disagree is returned with `conflict: true` and `suggested: null`, the candidates listed for the member to resolve — the layer never picks a winner. A key none of whose facts clear the confidence floor is returned with `suggested: null` too; an entry whose Objekt field already equals the suggestion is omitted (nothing to confirm). This org-gated route is INTERNAL. 404 if the property does not exist in that workspace + organisation, leaking nothing.',
  },
  applyFactSuggestions: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/fact-suggestions/apply',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: ApplyFactSuggestionsBodySchema,
    response: { 200: ApplyFactSuggestionsResponseSchema },
    permission: 'organization:property:property-update',
    description:
      'Write the promotion suggestions a member confirmed onto the Objekt (IMM-500.4) — the property-level counterpart to the per-document apply. The body carries only the fact keys the member confirmed; the suggestions are re-derived server-side and only a value the layer would offer right now is written, so a key that has since become conflicting, dropped below the confidence floor, or is unmapped is refused and reported in `skippedFields` rather than trusted from the request. Writes delegate to the same section applier the per-document flow uses, in one transaction; a value that cannot be coerced is skipped as `invalid-value`. Gated by `property-update` (distinct from the `property-read` the list uses), after the workspace-scope check. Partial success is normal and not an error. 404 if the property is unknown or foreign, leaking nothing.',
  },
  factReview: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/fact-review',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: PropertyFactReviewSchema },
    permission: 'organization:property:property-read',
    description:
      "The machine-read values of one Objekt that a member has not checked yet (IMM-1404): every live fact whose review level is `AI_DETECTED`, with its catalog label and group, value, occurrence, origin, status, evidence, time reference and the Objekt field it would fill. `objektField.replaces` is true when confirming would change a value already entered on the Objekt. `markers` says, per Objekt field shown on the property, whether the value has a machine finding behind it and how far it was checked; an empty field carries the findings' value with `fieldEmpty: true`. Read under linked access like the dossier. 404 if the property does not exist in that workspace + organisation.",
  },
  confirmFacts: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/fact-review/confirm',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: ConfirmFactsBodySchema,
    response: { 200: ConfirmFactsResponseSchema },
    permission: 'organization:property:property-update',
    description:
      "Confirm machine-read values as „Makler bestätigt“ (IMM-1404), recording the member and time. Findings of the same field and occurrence that state the same value are confirmed with them. A value whose catalog entry maps to an Objekt field is written there in the same transaction; a field that already holds a different value is only written when it is listed in `replaceFieldKeys`, otherwise nothing changes (422 `property.fact.replace-not-agreed`). A field the Objekt cannot take is reported in `skippedFields` and the value is still confirmed. Only a member's own session confirms: not available to an API key or under impersonation (403). 404 `property.fact.not-found` when any fact is not a live fact of this Objekt; 422 `property.fact.conflicting-values` when two facts would write different values into one field.",
  },
  withdrawFactConfirmations: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/fact-review/withdraw-confirmation',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: WithdrawFactConfirmationsBodySchema,
    response: { 200: WithdrawFactConfirmationsResponseSchema },
    permission: 'organization:property:property-update',
    description:
      "Take back a member's confirmation of machine-read values (IMM-1404): each returns to `AI_DETECTED` and loses its confirmer and time, together with the confirmed findings of the same field and occurrence that state the same value. The Objekt field keeps its value. A value a member entered themselves cannot be taken back (422 `property.fact.entered-by-member`). Not available to an API key or under impersonation (403). 404 `property.fact.not-found` when any fact is not a live fact of this Objekt.",
  },
  loadImmoosIqData: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/immoos-iq/load',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: LoadImmoosIqDataResponseSchema },
    permission: 'organization:property:property-update',
    description:
      "Load the official cadastre, planning-law and Bodenrichtwert-zone data for the Objekt's address from immoOS IQ and store each value as a fact (IMM-1389): origin EXTERNAL, review level AI_DETECTED, evidence naming the original source and its date, never immoOS IQ. A value that differs from the Objekt's own field is stored as CONTRADICTED; the Objekt's fields are never written. A reload supersedes the earlier immoOS IQ fact per catalog ID and occurrence and touches no other source. NO_DATA (200) when immoOS IQ has nothing for the address; nothing is stored then. 422 property.immoos-iq.address-incomplete without street, house number, postcode and town; 502 property.immoos-iq.unreachable when immoOS IQ does not answer. 404 if the property is unknown, foreign or only linked.",
  },
  immoosIqStatus: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/immoos-iq',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: ImmoosIqLoadStatusResponseSchema },
    permission: 'organization:property:property-read',
    description:
      'When immoOS IQ data was last loaded onto this property (IMM-1389): the recording time of its newest live immoOS IQ fact, or null when none is live. A load that found no data changes nothing here. 404 if the property is unknown or foreign.',
  },
  createMediaUpload: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/media/uploads',
    pathParams: PropertyMediaPathParamsSchema,
    body: CreatePropertyMediaUploadBodySchema,
    response: { 200: CreatePropertyMediaUploadResponseSchema },
    permission: 'organization:property:media-write',
    description:
      'Begin a direct-to-S3 upload for a property media file (IMM-219.1). Branches on the declared content type: images (JPEG, PNG, WebP; ≤ 20 MB) vs videos (MP4; ≤ 200 MB); any other type is refused. Enforces the 100-item gallery cap for the batch and returns a short-lived presigned POST plus a pending media file id. No media row is created yet; that happens on confirm. Workspace-scoped: 404 if the property does not exist in that workspace + organisation.',
  },
  confirmMediaUpload: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/media',
    pathParams: PropertyMediaPathParamsSchema,
    body: ConfirmPropertyMediaUploadBodySchema,
    response: { 200: ConfirmPropertyMediaUploadResponseSchema },
    permission: 'organization:property:media-write',
    description:
      'Confirm a pending media upload: verifies the stored bytes via the media module, classifies the type (IMAGE / VIDEO) and area, appends the item at the next position and creates the media row linked to the property, stamped with the uploader and now. Re-checks the 100-item gallery cap defensively. Returns the stored `PropertyMediaItem`, including its presigned inline URL. Workspace-scoped: 404 if the property or the pending file is foreign; 422 if the bytes fail validation.',
  },
  addMediaVideoLink: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/media/video-links',
    pathParams: PropertyMediaPathParamsSchema,
    body: AddPropertyMediaVideoLinkBodySchema,
    response: { 200: ListPropertyMediaResponseSchema },
    permission: 'organization:property:media-write',
    description:
      "Add a YouTube or Vimeo video to the property's gallery by its link (IMM-1165). The link must be a video page on YouTube (`watch?v=`, `youtu.be/`, `shorts/`, `embed/`) or Vimeo (`vimeo.com/{id}`, `player.vimeo.com/video/{id}`), at most 2,000 characters; other query parameters are dropped. immoOS asks the provider for the video's title and preview image once, keeps its own copy of the image, and never downloads the video. The item is appended to `videos`, not published, and counts toward the 100-item gallery cap. Returns the regrouped `ListPropertyMediaResponse`. 422 `property.property-media.video-link-unsupported` for any other link; 422 `property.property-media.video-link-not-public` when the provider reports the video private or missing; 422 `property.property-media.video-link-duplicate` when the property already holds this video; 422 `property.property-media.gallery-full` at the cap; 502 `property.property-media.video-provider-unreachable` when the provider does not answer. 404 if the property is foreign or absent.",
  },
  listMedia: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/media',
    pathParams: PropertyMediaPathParamsSchema,
    response: { 200: ListPropertyMediaResponseSchema },
    permission: 'organization:property:media-read',
    docs: 'PUBLIC',
    description:
      "List everything in a property's gallery, grouped into `photos`, `floorPlans` and `videos`. Each group is ordered by `position`, which is the item's ordinal **within its own group** and is numbered from 0 for each group independently. An empty gallery is three empty arrays on a 200, never a 404. The gallery is capped at 100 items, so the response is bounded and complete — there is no paging.\n\nOnly an image carries a `category`, and it is the category that decides the group: `PHOTO` puts it in `photos`, `FLOOR_PLAN` in `floorPlans`. A video's `category` is always `null` — not a value nobody has filled in, but a field that does not apply to it.\n\n`isCoverImage` marks the one picture the property leads with. At most one item per property carries it, only a photo is eligible, and it is always the first entry of `photos`. A property may have none.\n\n`isPublished` says whether the item is in the property's **marketing set** — the subset that may be rendered into a listing brochure or transmitted to a portal. An item outside the set is still returned here in its group and in its position: the gallery is the whole record, and this flag is the decision made about each item. The cover image is always in the set.\n\n`videoLink` is set on a video that lives at YouTube or Vimeo and was added by its link; it is `null` on every upload. Such an item sits in `videos` and shares their order, the marketing set and the 100-item cap, but immoOS holds no video bytes for it: its `url` and `thumbnailUrl` both point at immoOS's copy of the provider's preview image, and `contentType`, `sizeBytes` and `originalFilename` describe that image. Open `videoLink.url` to watch it.\n\nEvery item carries a short-lived URL for its original bytes in `url`, minted per call and rendered inline by a browser, with that URL's own lifetime in `urlExpiresInSeconds`. `thumbnailUrl` is the same for the derivative the server generates from it — a thumbnail for an image, a poster frame for a video — with its own `thumbnailExpiresInSeconds`.\n\n**An empty `thumbnailUrl` is a normal 200, not an error.** Derivatives are produced asynchronously after the upload, so `thumbnailUrl` and `thumbnailExpiresInSeconds` are both `null` while one is still being generated, and stay `null` if the generation failed. The response does not distinguish the two states, and the answer is the same either way: use `url`, and re-read the gallery later if you want the derivative. No URL here is stored on our side or usable as an identifier — keep the item `id`, and re-mint from the lifetime beside each URL rather than waiting for a fetch to fail.\n\n`uploadedByName` is `null` when the uploader is no longer an active member — the id beside it still resolves them.\n\n`canManage` on an item says whether the **member the key is bound to** could delete it, or move it back into the property's files, **inside immoOS**; curation — renaming, re-categorising, reordering, choosing the cover image — is shared across the workspace and does not follow this flag. It never describes what the key may do: no gallery write is published here, and `organization:property:media-write` is grantable only for the immoOS MCP tools that add a video by its link or a 360° tour: every gallery write route refuses an API key, so a key holding this grant reads and nothing more.\n\n404 if the property is not one this key may see, identical to the answer for a property that does not exist.",
  },
  readMedia: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/media/:mediaId/read',
    pathParams: PropertyMediaItemPathParamsSchema,
    query: ReadPropertyMediaQuerySchema,
    response: { 200: ReadPropertyMediaResponseSchema },
    permission: 'organization:property:media-read',
    docs: 'PUBLIC',
    description:
      "Mint a short-lived URL for one item in a property's gallery and return it with `expiresInSeconds`, the lifetime of that URL. Pass `?mode=preview` for a URL a browser renders inline, or `?mode=download` — the default — for one that saves the file under its original filename. Either way the bytes are the **original** upload, never the generated thumbnail or poster; those are reached through `thumbnailUrl` on the gallery read and have no download of their own. For a video added by link (`videoLink` set on the gallery read), the original is immoOS's copy of the provider's preview image; the video itself is only at the provider.\n\nThe URL is minted per call and never stored on our side, so it is not an identifier: keep the media item id, re-mint when you need the bytes, and use `expiresInSeconds` to decide when rather than waiting for a fetch to fail.\n\n404 covers three situations and answers them identically on purpose — the property is not one this key may see, the media item does not exist, or it belongs to a different property. Nothing about an item you may not see is disclosed.",
  },
  renameMedia: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/properties/:id/media/:mediaId/name',
    pathParams: PropertyMediaItemPathParamsSchema,
    body: RenamePropertyMediaBodySchema,
    response: { 200: ListPropertyMediaResponseSchema },
    permission: 'organization:property:media-write',
    description:
      "Rename a property media item's display name (1–255 chars) — IMM-219.2. Only the display name changes; the original filename is preserved for the download. Workspace-shared curation, gated by `media-write` only (NOT restricted to the uploader). Returns the full regrouped `ListPropertyMediaResponse`. 404 if the property or the media item is foreign or absent, leaking nothing.",
  },
  recategorizeMedia: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/properties/:id/media/:mediaId/category',
    pathParams: PropertyMediaItemPathParamsSchema,
    body: RecategorizePropertyMediaBodySchema,
    response: { 200: ListPropertyMediaResponseSchema },
    permission: 'organization:property:media-write',
    description:
      'Move an image between the photos (`PHOTO`) and floor plans (`FLOOR_PLAN`) areas — IMM-219.2. The item is removed from its source area (which closes up) and appended to the end of the target area; if the current cover image is re-categorised to a floor plan its flag is cleared. Workspace-shared curation, gated by `media-write` only. Returns the full regrouped `ListPropertyMediaResponse`. 422 `property.property-media.not-an-image` if the target is a video (videos carry no category). 404 if the property or the media item is foreign or absent.',
  },
  setMediaCoverImage: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/properties/:id/media/:mediaId/cover-image',
    pathParams: PropertyMediaItemPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: ListPropertyMediaResponseSchema },
    permission: 'organization:property:media-write',
    description:
      "Mark a photo as the property's cover image — IMM-219.2. Clears any prior cover image, sets this one and moves it to position 0 of the photos area (leading it), all atomically. Only an image in the photos area is eligible. Workspace-shared curation, gated by `media-write` only. Returns the full regrouped `ListPropertyMediaResponse`. 422 `property.property-media.cover-image-not-a-photo` if the item is a video or a floor plan. 404 if the property or the media item is foreign or absent.",
  },
  setMediaPublished: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/media/:mediaId/published',
    pathParams: PropertyMediaItemPathParamsSchema,
    body: SetPropertyMediaPublishedBodySchema,
    response: { 200: ListPropertyMediaResponseSchema },
    permission: 'organization:property:media-write',
    description:
      "Put a property media item into the marketing set, or take it out (IMM-794). The set is what decides whether a picture may leave the workspace: a listing brochure or a portal listing reads it and may only ever render a subset of it, never add to it. Workspace-shared curation gated by `media-write` only — not restricted to the uploader. Returns the full regrouped `ListPropertyMediaResponse` so the caller replaces its gallery in one step. 422 `property.property-media.cover-image-always-published` when the target is the property's cover image and `isPublished` is false: the cover image is always in the set, so a different cover image must be chosen first. 404 if the property or the media item is foreign or absent, leaking nothing.",
  },
  publishMediaArea: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/properties/:id/media/publish-area',
    pathParams: PropertyMediaPathParamsSchema,
    body: PublishPropertyMediaAreaBodySchema,
    response: { 200: ListPropertyMediaResponseSchema },
    permission: 'organization:property:media-write',
    description:
      "Put every item of one gallery area (`PHOTOS`, `FLOOR_PLANS` or `VIDEOS`) into the marketing set in one step (IMM-1208). All of the area's items change together in one transaction, or none does; items already in the set and the other areas are left as they are. Workspace-shared curation gated by `media-write` only — not restricted to the uploader. Publishing only: there is no area-wide un-publish. Returns the full regrouped `ListPropertyMediaResponse` so the caller replaces its gallery in one step. An area that is empty or already fully published is a 200 that changes nothing. 404 if the property is foreign or absent, leaking nothing.",
  },
  reorderMedia: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/properties/:id/media/reorder',
    pathParams: PropertyMediaPathParamsSchema,
    body: ReorderPropertyMediaBodySchema,
    response: { 200: ListPropertyMediaResponseSchema },
    permission: 'organization:property:media-write',
    description:
      "Re-sequence a media area (`PHOTOS`, `FLOOR_PLANS` or `VIDEOS`) from the client's `orderedIds` — IMM-219.2. The whole area is re-numbered 0..n-1 in the given order; on the photos area an existing cover image is re-asserted to index 0 regardless of the sent order. Workspace-shared curation, gated by `media-write` only. Returns the full regrouped `ListPropertyMediaResponse`. 422 `property.property-media.reorder-mismatch` if `orderedIds` is not exactly the set of ids currently in that area. 404 if the property is foreign or absent.",
  },
  deleteMedia: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/properties/:id/media/:mediaId',
    pathParams: PropertyMediaItemPathParamsSchema,
    response: { 200: ListPropertyMediaResponseSchema },
    permission: 'organization:property:media-write',
    description:
      "Permanently delete a media item (IMM-219.3): removes its metadata row, its original bytes AND its generated thumbnail/poster derivative via the media module (idempotent at storage); the source area's positions close up 0..n-1. Gated to the uploader or an Owner / Admin (`media-manage-any`); anyone else is refused with the Forbidden shape after the workspace-scope check. Deleting the cover image leaves the Objekt with none (no auto-promotion). Returns the regrouped `ListPropertyMediaResponse`. 404 if the property or the media is foreign.",
  },
  demoteMediaToDocument: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/media/:mediaId/demote-to-document',
    pathParams: PropertyMediaItemPathParamsSchema,
    response: { 200: PropertyDocumentSchema },
    permission: 'organization:property:document-write',
    description:
      "Move a gallery image back into the Objekt's documents (IMM-451.2): inserts an `UNCATEGORIZED` `property_documents` row (settled, never re-classified) reusing the same stored bytes and deletes the media row, in one transaction (the bytes are never re-uploaded or reaped). Gated to the media uploader or an Owner / Admin (`media-manage-any`) after the workspace-scope check. Returns the created `PropertyDocument`. 422 `property.property-media.not-an-image` if the media item is a video. 404 if the property or the media is foreign.",
  },
  listTours: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/tours',
    pathParams: PropertyToursPathParamsSchema,
    response: { 200: ListPropertyToursResponseSchema },
    permission: 'organization:property:media-read',
    description:
      "List an Objekt's 360° tours (IMM-1164), ordered by `position` (0-based). A tour is a link to the provider's page, never a file: `link` is the https URL to open, `provider` the provider recognised from its host (null for any other host), `isPublished` whether it is in the marketing set. Tours are not gallery media: `listMedia` never returns them and they do not count toward the 100-item gallery cap. At most 3. 404 if the property is not one the caller may see.",
  },
  addTour: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/tours',
    pathParams: PropertyToursPathParamsSchema,
    body: SavePropertyTourBodySchema,
    response: { 200: ListPropertyToursResponseSchema },
    permission: 'organization:property:media-write',
    description:
      'Add a 360° tour by link (IMM-1164). `link` must be an https URL with a host, at most 2,000 characters; `title` 1–80 characters. The tour is appended at the end, not published. Returns the regrouped tour list. 422 `property.property-tour.invalid-link`; 422 `property.property-tour.limit-reached` when the Objekt already has 3. 404 if the property is foreign or absent.',
  },
  updateTour: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/properties/:id/tours/:tourId',
    pathParams: PropertyTourPathParamsSchema,
    body: SavePropertyTourBodySchema,
    response: { 200: ListPropertyToursResponseSchema },
    permission: 'organization:property:media-write',
    description:
      "Change a tour's link and title (IMM-1164); the same checks as `addTour`. Position and publication are unchanged. Workspace-shared curation gated by `media-write` only. 422 `property.property-tour.invalid-link`; 404 `property.property-tour.not-found`.",
  },
  reorderTours: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/tours/order',
    pathParams: PropertyToursPathParamsSchema,
    body: ReorderPropertyToursBodySchema,
    response: { 200: ListPropertyToursResponseSchema },
    permission: 'organization:property:media-write',
    description:
      "Re-sequence the Objekt's tours from `orderedIds`, re-numbered 0..n-1 (IMM-1164). Workspace-shared curation gated by `media-write` only. 422 `property.property-tour.reorder-mismatch` if `orderedIds` is not exactly the set of the Objekt's tours. 404 if the property is foreign or absent.",
  },
  setTourPublished: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/tours/:tourId/published',
    pathParams: PropertyTourPathParamsSchema,
    body: SetPropertyTourPublishedBodySchema,
    response: { 200: ListPropertyToursResponseSchema },
    permission: 'organization:property:media-write',
    description:
      'Put a 360° tour into the marketing set, or take it out (IMM-1164). A published tour may be shown on the web exposé. It is never transmitted to a portal. Workspace-shared curation gated by `media-write` only, not restricted to the member who added it. Setting the value it already has changes nothing. Returns the regrouped tour list. 404 `property.property-tour.not-found` if the tour is foreign or gone; 404 if the property is foreign or absent.',
  },
  deleteTour: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/properties/:id/tours/:tourId',
    pathParams: PropertyTourPathParamsSchema,
    response: { 200: ListPropertyToursResponseSchema },
    permission: 'organization:property:media-write',
    description:
      'Permanently remove a tour (IMM-1164); the remaining tours close up 0..n-1. Gated to the member who added it or an Owner / Admin (`media-manage-any`); anyone else is refused with 403 `property.property-media.manage-denied` after the workspace-scope check. 404 `property.property-tour.not-found`.',
  },
  readDocument: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/documents/:documentId/read',
    pathParams: ReadPropertyDocumentPathParamsSchema,
    query: ReadPropertyDocumentQuerySchema,
    response: { 200: ReadPropertyDocumentResponseSchema },
    permission: 'organization:property:document-read',
    docs: 'PUBLIC',
    description:
      "Mint a short-lived URL for one of a property's files and return it with `expiresInSeconds`, the lifetime of that URL. Pass `?mode=preview` for a URL a browser renders inline, or `?mode=download` — the default — for one that saves the file under its original filename.\n\nThe URL is minted per call and never stored on our side, so it is not an identifier: keep the document id, re-mint when you need the bytes, and use `expiresInSeconds` to decide when rather than waiting for a fetch to fail.\n\n404 covers three situations and answers them identically on purpose — the property is not one this key may see, the document does not exist, or it is filed under a different property. Nothing about a file you may not see is disclosed.",
  },
  listExposes: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/exposes',
    pathParams: PropertyExposesPathParamsSchema,
    response: { 200: ListPropertyExposesResponseSchema },
    permission: 'organization:property:document-read',
    description:
      "List the Objekt's two print exposés, `FULL` (Vollversion) then `SHORT` (Kurzexposé), always both. `file` is null for an empty version. Exposés are not documents: `listDocuments` never returns them. 404 if the property is not one the caller may see.",
  },
  readExpose: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/exposes/:version/read',
    pathParams: PropertyExposeVersionPathParamsSchema,
    response: { 200: ReadPropertyExposeResponseSchema },
    permission: 'organization:property:document-read',
    description:
      'Mint a short-lived download URL for one exposé version. The browser saves the PDF under its stored file name. 404 `property.property-expose.not-found` when the version is empty; 404 if the property is not one the caller may see.',
  },
  createExposeUpload: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/exposes/uploads',
    pathParams: PropertyExposesPathParamsSchema,
    body: CreatePropertyExposeUploadBodySchema,
    response: { 200: CreatePropertyExposeUploadResponseSchema },
    permission: 'organization:property:document-write',
    description:
      'Begin a direct-to-storage upload for an exposé PDF. Policy: `application/pdf` only, ≤ 20 MB. 422 `property.property-expose.not-a-pdf` / `property.property-expose.too-large`. Returns a short-lived presigned POST and a pending file id. Nothing is filed until `fileExpose`. 404 if the property is not one the caller may see.',
  },
  fileExpose: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/exposes/:version',
    pathParams: PropertyExposeVersionPathParamsSchema,
    body: FilePropertyExposeBodySchema,
    response: { 200: FilePropertyExposeResponseSchema },
    permission: 'organization:property:document-write',
    description:
      'Make an uploaded PDF the exposé for `:version` (`FULL` Vollversion, `SHORT` Kurzexposé). This replaces any previous exposé of that version outright, and the previous file is deleted. Filing the same upload to the same version again changes nothing. 422 `property.property-expose.not-an-expose-upload` for a file not uploaded through `createExposeUpload`; 422 `property.property-expose.upload-already-used` for a file already backing another version; 422 `media.file.invalid-content` when the bytes are not a valid PDF. 404 if the property is not one the caller may see.',
  },
  removeExpose: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/properties/:id/exposes/:version',
    pathParams: PropertyExposeVersionPathParamsSchema,
    response: { 200: RemovePropertyExposeResponseSchema },
    permission: 'organization:property:document-write',
    description:
      'Empty one exposé version: deletes its file for good and answers the now-empty version. Removing an empty version is a no-op that answers the same. Not available to an API key, whatever grants it holds, and no MCP tool offers it. 404 if the property is not one the caller may see.',
  },
  renameDocument: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/properties/:id/documents/:documentId/name',
    pathParams: PropertyDocumentPathParamsSchema,
    body: RenamePropertyDocumentBodySchema,
    response: { 200: RenamePropertyDocumentResponseSchema },
    permission: 'organization:property:document-write',
    description:
      "Rename a property document's display name (1–255 chars) — IMM-218.3. Only the display name changes; original filename, uploader and upload date are preserved. Gated to the uploader or an Owner / Admin (`document-manage-any`); a member who is neither is refused with the Forbidden shape after the workspace-scope check. Returns the updated `PropertyDocument`. 404 if the property or the document is foreign, leaking nothing.",
  },
  recategorizeDocument: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/properties/:id/documents/:documentId/category',
    pathParams: PropertyDocumentPathParamsSchema,
    body: RecategorizePropertyDocumentBodySchema,
    response: { 200: RecategorizePropertyDocumentResponseSchema },
    permission: 'organization:property:document-write',
    description:
      'Re-categorise a property document (IMM-218.3): sets `category` to the chosen taxonomy value; the `categoryGroup` is re-derived server-side. Uploader and upload date are preserved. Gated to the uploader or an Owner / Admin (`document-manage-any`); anyone else is refused with the Forbidden shape after the workspace-scope check. Returns the updated `PropertyDocument`. 404 if the property or the document is foreign.',
  },
  startDocumentRedaction: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/documents/:documentId/redactions',
    pathParams: PropertyDocumentPathParamsSchema,
    body: StartDocumentRedactionBodySchema,
    response: { 202: DocumentRedactionSchema },
    permission: 'organization:property:document-write',
    description:
      'Start redacting a PDF, JPEG or PNG (IMM-249.2): burns the given boxes into a copy that is checked before it is stored next to the original, named `copyName` plus the original extension. Answers 202 with a `RUNNING` redaction; poll `readDocumentRedaction` for the outcome. Not available to an API key. 400 for no box, a blank name or a box outside the document; 403 without `document-write`; 404 if the property or the document is foreign; 422 `property.document-redaction.unsupported-type` for any other type; 502 when the redaction engine cannot be reached or is not set up.',
  },
  readDocumentRedaction: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/document-redactions/:redactionId',
    pathParams: DocumentRedactionPathParamsSchema,
    response: { 200: DocumentRedactionSchema },
    permission: 'organization:property:document-write',
    description:
      'Read where a redaction stands (IMM-249.2): `RUNNING`, `SUCCEEDED` with the copy document id, or `FAILED` with `ENGINE_FAILED` or `CHECK_FAILED`. 403 without `document-write`; 404 if the property is foreign or the redaction is not on it.',
  },
  setDocumentForBuyers: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/documents/:documentId/for-buyers',
    pathParams: PropertyDocumentPathParamsSchema,
    body: SetPropertyDocumentForBuyersBodySchema,
    response: { 200: SetPropertyDocumentForBuyersResponseSchema },
    permission: 'organization:property:document-write',
    description:
      'Mark a property document „Für Käufer“ (forBuyers true) or take the mark off (false) — IMM-1176. Marked documents are listed on the personal page of every Kaufinteressent with an active Datenraum release. Documents in the group Person & Legitimation (422 `cannot-share-personal-document`) and documents of type Wertgutachten, Bewertung or Akquisebriefing (422 `cannot-share-valuation-document`) are refused when marking. Gated to the uploader or an Owner / Admin (`document-manage-any`). Setting the current value changes nothing. Returns the updated `PropertyDocument`. 404 if the property or the document is foreign.',
  },
  setDocumentsForBuyers: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/properties/:id/documents/for-buyers',
    pathParams: PropertyDocumentsPathParamsSchema,
    body: SetPropertyDocumentsForBuyersBodySchema,
    response: { 200: SetPropertyDocumentsForBuyersResponseSchema },
    permission: 'organization:property:document-write',
    description:
      'Mark several documents of one property „Für Käufer“ (forBuyers true) or take the mark off (false) — IMM-1329. Each document is changed exactly as by setDocumentForBuyers, on its own: one result per requested id, in request order — CHANGED, UNCHANGED (it already had the value) or FAILED with reason NOT_FOUND (not on this property), PERSONAL_DOCUMENT (marking a Person & Legitimation document), VALUATION_DOCUMENT (marking a Wertgutachten, Bewertung or Akquisebriefing document) or FORBIDDEN (neither the uploader nor `document-manage-any`). A failure never undoes another document. 1–500 unique ids. 404 if the property is foreign.',
  },
  deleteDocument: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/properties/:id/documents/:documentId',
    pathParams: PropertyDocumentPathParamsSchema,
    response: { 200: DeletePropertyDocumentResponseSchema },
    permission: 'organization:property:document-write',
    description:
      'Permanently delete a property document (IMM-218.3): removes the metadata row AND its stored bytes via the media module (idempotent at storage). Gated to the uploader or an Owner / Admin (`document-manage-any`); anyone else is refused with the Forbidden shape after the workspace-scope check. Returns the removed document id. 404 if the property or the document is foreign.',
  },
  requeueDocumentClassification: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/documents/classification-runs',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: RequeueDocumentClassificationResponseSchema },
    permission: 'organization:property:document-write',
    description:
      "Re-run auto-classification over a property's uncategorised documents (IMM-483.1): moves every document that is `UNCATEGORIZED`, not `MANUAL`-sourced and settled (`DONE`/`FAILED`) back to `PENDING`, so the existing classification poller picks it up on its normal schedule. Idempotent — a document already `PENDING`/`CLASSIFYING` is counted but not re-queued, so a double-click cannot double-queue. Adds no classification code and makes no vision call in the request path. Returns `{ queued, uncategorized }` — the rows re-queued and the property's total uncategorised count, which differ when documents are excluded or already in flight. Gated by `document-write` after the workspace-scope check; 404 if the property is foreign, leaking nothing.",
  },
  queueDocumentAnalysis: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/documents/analysis-runs',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: QueueDocumentAnalysisResponseSchema },
    permission: 'organization:property:document-write',
    description:
      "Queue an analysis for every one of a property's documents without a usable result (IMM-550.1): inserts one `PENDING` `Dokumentanalyse` per document whose classification has settled (`DONE`/`FAILED`) and whose latest analysis is absent, `FAILED` or `NO_DATA`, so the existing analysis poller claims them on its normal schedule. A `COMPLETED` analysis is never touched (it can carry manual corrections); a document already `PENDING`/`ANALYZING` is counted in `inFlight` but never re-queued, so a double-press cannot double-queue; a document still being classified is left to the categorise → analyse chain. Adds no analysis code and makes no vision call in the request path. The `queued` set is computed from the same SQL predicate as the list response's `analysisBacklogCount`, so the banner's number and the run cannot drift. Gated by `document-write` after the workspace-scope check; 404 if the property is foreign, leaking nothing.",
  },
  promoteDocumentToGallery: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/documents/:documentId/promote-to-gallery',
    pathParams: PropertyDocumentPathParamsSchema,
    response: { 200: PropertyMediaItemSchema },
    permission: 'organization:property:media-write',
    description:
      "Move a document into the Objekt's Bilder-Galerie as a photo (IMM-451.2): inserts a `PropertyMedia` row reusing the same stored bytes and deletes the document row, in one transaction (the bytes are never re-uploaded or reaped). Gated to the document uploader or an Owner / Admin (`document-manage-any`) after the workspace-scope check. Returns the created `PropertyMediaItem`. 422 `property.property-media.gallery-full` when the 100-item cap is reached. 404 if the property or the document is foreign.",
  },
  triggerDocumentAnalysis: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/documents/:documentId/analysis',
    pathParams: DocumentAnalysisPathParamsSchema,
    response: { 202: DocumentAnalysisResponseSchema },
    permission: 'organization:property:document-write',
    description:
      'Trigger a fresh document analysis (IMM-366.2): creates a new `PENDING` `Dokumentanalyse` linked to the document + workspace + Objekt, which an async job picks up and runs against a vision LLM. Re-triggering supersedes any prior result with a new PENDING analysis (the manual-correction warning is IMM-366.4). Gated by `document-write` and non-Viewer, after the workspace-scope check. Returns the created analysis (status `PENDING`). 404 if the property or the document is foreign, leaking nothing.',
  },
  getDocumentAnalysis: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/documents/:documentId/analysis',
    pathParams: DocumentAnalysisPathParamsSchema,
    response: { 200: DocumentAnalysisResponseSchema },
    permission: 'organization:property:document-read',
    description:
      'Read the latest analysis for a property document (IMM-366.2): its status plus, when `COMPLETED`, the per-field-confidence `result`, or the `failureReason` for `NO_DATA` / `FAILED`. Open to anyone who may view the Objekt (`document-read`). 404 if the property or the document is foreign, or if the document has no analysis yet, leaking nothing.',
  },
  correctDocumentAnalysisField: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/properties/:id/documents/:documentId/analysis',
    pathParams: DocumentAnalysisPathParamsSchema,
    body: CorrectDocumentAnalysisFieldBodySchema,
    response: { 200: DocumentAnalysisResponseSchema },
    permission: 'organization:property:document-write',
    description:
      "Manually correct one field of a document's latest analysis (IMM-366.4): replaces the extracted value with the member's, marks it `corrected`, keeps the model's reading in `originalValue`, and records who + when. Only a `COMPLETED` analysis with that field key can be corrected; a re-run supersedes the correction (with a client-side warning). Gated by `document-write` (non-Viewer) after the workspace-scope check; returns the updated analysis. 404 if the property, document, analysis or field key is unknown, leaking nothing.",
  },
  applyDocumentAnalysis: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/documents/:documentId/analysis/apply',
    pathParams: DocumentAnalysisPathParamsSchema,
    body: ApplyDocumentAnalysisBodySchema,
    response: { 200: ApplyDocumentAnalysisResponseSchema },
    permission: 'organization:property:property-update',
    description:
      "Write a document analysis' extracted values onto the Objekt itself (IMM-382). The body carries only the field keys the member explicitly checked; each is coerced to its real property type (a manual correction is stored as text) and merged onto the section's current values, so untouched fields in a replace-whole-section write are preserved. A checked key that conflicts with an existing Objekt value overwrites it — the confirm modal surfaces conflicts and the member's selection is the consent. Gated by `property-update` (distinct from the `document-write` the analysis itself uses), after the workspace-scope check. Fields that cannot be written are reported in `skippedFields` rather than failing the call; partial success is normal. 404 if the property, the document or the analysis is unknown or foreign, leaking nothing.",
  },
  listDocumentAnalyses: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/document-analyses',
    pathParams: PropertyDocumentsPathParamsSchema,
    response: { 200: DocumentAnalysisListResponseSchema },
    permission: 'organization:property:document-read',
    description:
      "Read the latest analysis status per document for a property (IMM-366.3), so the Dokumente list can show each document's analyse state at a glance in one request. Returns one summary per analysed document (`{ documentId, status, completedAt }`); documents with no analysis are absent. Open to anyone who may view the Objekt (`document-read`). 404 if the property is foreign, leaking nothing.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). The
 * inferred type of that literal had grown past the limit `tsc` will serialize
 * into a `.d.ts`, so adding a single route to ANY group failed the build with
 * TS7056. A named group is emitted as a `typeof` reference instead of being
 * inlined whole — the same shape `valuationContracts` already uses.
 *
 * Typing is unchanged: `as const satisfies Record<string, EndpointDefinition>`
 * keeps every leaf's literal method / path / permission and its exact schemas,
 * which the route-builder's request inference depends on.
 */
const leadsContracts = {
  create: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads',
    pathParams: CreateLeadPathParamsSchema,
    body: CreateLeadBodySchema,
    response: { 200: CreateLeadResponseSchema },
    permission: 'organization:lead:lead-create',
    docs: 'PUBLIC',
    description:
      'Capture an Owner-Lead in the workspace. Created in status NEW with receivedAt defaulting to now and legalBasis defaulting to PRE_CONTRACTUAL_MEASURES. Dedup is a warning surfaced via checkDuplicates, never a block here.',
  },
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/leads',
    pathParams: ListWorkspaceLeadsPathParamsSchema,
    query: ListWorkspaceLeadsQuerySchema,
    response: { 200: ListWorkspaceLeadsResponseSchema },
    permission: 'workspace:lead:lead-read',
    docs: 'PUBLIC',
    description:
      'List the Owner-Lead inbox of a workspace the caller is a member of. With no query params it returns the default inbox — status NEW or CONTACTED, newest-first by createdAt. Optional organizationId narrows to one location; status/source/leadPartnerId filters AND-combine, search matches name/phone/address case-insensitively, sortField/sortDirection reorder, and includeArchived surfaces ARCHIVED leads. `?q=` is the ⌘K palette filter — a different, diacritic-folding match over name/e-mail/phone/source that also lifts the default status narrowing; blank means absent.',
  },
  tasks: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/leads/tasks',
    pathParams: ListMyTasksPathParamsSchema,
    query: ListMyTasksQuerySchema,
    response: { 200: ListMyTasksResponseSchema },
    permission: 'workspace:lead:lead-read',
    docs: 'PUBLIC',
    description:
      "My tasks (IMM-418.5): the acting member's open (planned) lead activities across every lead they may see, ordered by due date ascending so overdue items sort first. Only activities on NEW / CONTACTED leads are returned — those on CONVERTED / LOST / ARCHIVED leads are excluded as unactionable (Decision D5). Keyset-paginated (limit <= 100, opaque cursor). A pure read; open activities ARE the tasks, so there is no task entity and no write path.",
  },
  activityFeed: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/lead-activities/feed',
    pathParams: ListScopedActivitiesPathParamsSchema,
    query: ListScopedLeadActivitiesQuerySchema,
    response: { 200: ActivityFeedResponseSchema },
    // Unchanged — oversight WIDENS what the read returns, it never gates the
    // route (IMM-776.2, mirroring IMM-776.1).
    permission: 'workspace:lead:lead-read',
    description:
      "The Aktivitäten tab's lead-side read (IMM-776.2): the same ActivityFeedItem rows IMM-776.1 defined, out of immo_lead.lead_notes ∪ lead_communications — the caller's own lead activities plus every lead activity in the Standorte where they hold organization:activity:activity-oversight, across UPCOMING (state OPEN, non-terminal lead, due_at ASC) or PAST (state DONE/CANCELLED, every lead status, ordered on COALESCE(occurred_at, cancelled_at, created_at) DESC). Every row carries origin: 'LEAD_ACTIVITY', subjectType: 'LEAD', appointmentType: null. canModify is the lead module's own canEditLead rule, not the store's. No subjectTypes param — every row here is a LEAD. Internal — not published API surface.",
  },
  activityOwnOpenCount: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/lead-activities/own-open-count',
    pathParams: ListScopedActivitiesPathParamsSchema,
    response: { 200: ActivityOwnOpenCountResponseSchema },
    permission: 'workspace:lead:lead-read',
    description:
      "The Aktivitäten nav counter's lead half (IMM-1360.4): the caller's own OPEN lead notes and communications on non-terminal leads across their visible Standorte — the number the lead feed reports as `ownOpenCount`. Internal — not published API surface.",
  },
  reassignStandort: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/standort',
    pathParams: ReassignLeadStandortPathParamsSchema,
    body: ReassignLeadStandortBodySchema,
    response: { 200: ReassignLeadStandortResponseSchema },
    permission: 'workspace:lead:lead-reassign',
    description:
      "Move a lead across Standorte (IMM-20.4 AC22). Allowed when the caller is Owner/Workspace-Admin, or the lead's creator, or both the source and target Standort are within the caller's visible set; otherwise 403. The target must be an organisation in the same workspace.",
  },
  assign: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/assign',
    pathParams: AssignLeadResponsibleMembersPathParamsSchema,
    body: AssignLeadResponsibleMembersBodySchema,
    response: { 200: AssignLeadResponsibleMembersResponseSchema },
    permission: 'organization:lead:lead-assign',
    description:
      "Replace a lead's responsible-member set (IMM-344.3). At least one member is required (400 when empty). Only Owner/Workspace-Admin (any member) and Standortleitung (members of their own Standort subtree) may assign — regular members lack the permission (403). A Standortleitung targeting a member outside their subtree is refused (403). Each newly-added member is e-mailed; re-confirming the same set is a no-op.",
  },
  listAssignableMembers: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/leads/assignable-members',
    pathParams: ListAssignableMembersPathParamsSchema,
    response: { 200: ListAssignableMembersResponseSchema },
    permission: 'organization:lead:lead-assign',
    description:
      "List the members the caller may assign a lead to (IMM-344.3): every workspace member for Owner/Workspace-Admin, only the caller's Standort subtree for a Standortleitung. Scoped off the caller's visible organisations.",
  },
  listActivityAssignees: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/leads/activity-assignees',
    pathParams: ListAssignableMembersPathParamsSchema,
    response: { 200: ListAssignableMembersResponseSchema },
    permission: 'organization:activity:activity-write',
    description:
      'List the members the caller may make responsible for a lead activity (IMM-754.1) — the „Zuständig" select in the activity composer. Same roster and same visible-organisation scoping as `assignable-members`, behind a different gate: choosing who a task falls to is not the power to reassign the lead, so it is gated on the permission that governs recording an activity rather than on `lead-assign`, which a regular member does not hold. The write path already permits it — `resolveActivityAssignee` validates a chosen member against this very set without demanding any assign permission.',
  },
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/leads/:id',
    pathParams: GetWorkspaceLeadPathParamsSchema,
    response: { 200: GetWorkspaceLeadResponseSchema },
    permission: 'organization:lead:lead-read',
    docs: 'PUBLIC',
    description:
      "Fetch a single Owner-Lead by id within a workspace the caller is a member of. 404 when the lead does not exist in the workspace (tenant-isolated).\n\n`links.self` is the URL of the endpoint that produced the response — this path; a lead has exactly one detail route. `links.activities` is the lead's activity timeline (`organization:activity:activity-read`), present for every lead that is filed against a location: a timeline is addressed under one, and a lead captured without a location carries `self` alone.\n\nOnce the lead has been qualified, `links` also carries what it converted into — `convertedContact`, `convertedProperty` (`organization:property:property-read`) and `convertedDeal` (`organization:deal:deal-read`). While the lead is unqualified those keys are ABSENT rather than null, because it has converted into nothing. The contact read behind `convertedContact` is not documented here and its grant, `organization:relationship:contact-read`, is not one the key-issuance dialog offers a workspace member.",
  },
  checkDuplicates: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/check-duplicates',
    pathParams: CheckLeadDuplicatesPathParamsSchema,
    body: CheckLeadDuplicatesBodySchema,
    response: { 200: CheckLeadDuplicatesResponseSchema },
    permission: 'organization:lead:lead-read',
    docs: 'PUBLIC',
    description:
      'Return existing workspace leads that match the candidate on email, street + postcode, or cadastral identifier — the dedup warning the capture form shows before saving. Empty array means no match.',
  },
  markLost: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/mark-lost',
    pathParams: MarkLeadLostPathParamsSchema,
    body: MarkLeadLostBodySchema,
    response: { 200: MarkLeadLostResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      'Mark a NEW or CONTACTED Owner-Lead as Lost with a required loss reason and an optional loss note (≤500 chars). Sets status=LOST and lostAt=now. Refused with 422 when the lead is CONVERTED (terminal-read-only). 404 when the lead is not in the workspace.',
  },
  archive: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/archive',
    pathParams: ArchiveLeadPathParamsSchema,
    body: ArchiveLeadBodySchema,
    response: { 200: ArchiveLeadResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      'Archive a NEW, CONTACTED or LOST Owner-Lead. Sets status=ARCHIVED and archivedAt=now; the lead leaves the default inbox. Refused with 422 when the lead is CONVERTED (terminal-read-only). 404 when the lead is not in the workspace.',
  },
  reopen: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/reopen',
    pathParams: ReopenLeadPathParamsSchema,
    body: ReopenLeadBodySchema,
    response: { 200: ReopenLeadResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      'Reopen a LOST or ARCHIVED Owner-Lead back to CONTACTED. The prior loss reason/note are retained as history (no longer determinative). Refused with 422 when the lead is CONVERTED or already active. 404 when the lead is not in the workspace.',
  },
  qualify: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/qualify',
    pathParams: QualifyLeadPathParamsSchema,
    body: QualifyLeadBodySchema,
    response: { 200: QualifyLeadResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      "Qualify a NEW or CONTACTED Owner-Lead in one all-or-nothing transaction: create an OWNER Contact, an ACTIVE Property (no purchasePrice; address + area come from the lead), the property↔contact owner link and an ACTIVE Deal, all derived from the lead, then flip the lead to CONVERTED (terminal-read-only) and record the created contact/property/deal ids. The Property and the Deal are created with the same marketing type, resolved as marketingType ?? the lead's recorded intent; marketingType overrides an existing intent when supplied. The Contact's first name is resolved as ownerFirstName ?? the lead owner's recorded first name; ownerFirstName overrides it when supplied. Any failure rolls the whole transaction back and leaves the lead untouched. Refused with 422 when the lead is not NEW/CONTACTED, the lead records no intent and no marketingType is supplied (lead.lead.marketing-type-required), the lead's owner records no first name and no ownerFirstName is supplied (lead.lead.owner-first-name-required), or the property still lacks publish-gate fields (the response lists them). 404 when the lead is not in the workspace.",
  },
  updateOwner: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/leads/:id/owner',
    pathParams: UpdateLeadOwnerPathParamsSchema,
    body: UpdateLeadOwnerBodySchema,
    response: { 200: UpdateLeadOwnerResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      "Replace an Owner-Lead's owner section (identity + contact channels). The body is the full owner shape; it overwrites the existing values wholesale. 404 when the lead is not in the workspace.",
  },
  updateNotes: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/leads/:id/notes',
    pathParams: UpdateLeadNotesPathParamsSchema,
    body: UpdateLeadNotesBodySchema,
    response: { 200: UpdateLeadNotesResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      "Replace an Owner-Lead's free-text notes. A blank or absent value clears the notes to null. 404 when the lead is not in the workspace.",
  },
  updateLegalBasis: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/leads/:id/legal-basis',
    pathParams: UpdateLeadLegalBasisPathParamsSchema,
    body: UpdateLeadLegalBasisBodySchema,
    response: { 200: UpdateLeadLegalBasisResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      "Update an Owner-Lead's GDPR legal basis (the core `leads.legal_basis` column). 400 on an unknown enum value. 404 when the lead is not in the workspace.",
  },
  updateIntent: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/leads/:id/intent',
    pathParams: UpdateLeadIntentPathParamsSchema,
    body: UpdateLeadIntentBodySchema,
    response: { 200: UpdateLeadIntentResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      "Record or correct an Owner-Lead's intent (SALE or RENTAL) — what the owner wants to do with the property. `null` clears it back to not-yet-asked. Refused once the lead is qualified. 400 on an unknown value. 404 when the lead is not in the workspace.",
  },
  updatePropertyAddress: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/leads/:id/property-address',
    pathParams: UpdateLeadPropertyAddressPathParamsSchema,
    body: UpdateLeadPropertyAddressBodySchema,
    response: { 200: UpdateLeadPropertyAddressResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      "Update an Owner-Lead's address section (property location + cadastral identifiers). Only the location columns of the shared property sub-record change; the object-characteristic columns are preserved. The sub-record is created if absent. 404 when the lead is not in the workspace.",
  },
  updateProperty: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/leads/:id/property',
    pathParams: UpdateLeadPropertyPathParamsSchema,
    body: UpdateLeadPropertyBodySchema,
    response: { 200: UpdateLeadPropertyResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      "Replace an Owner-Lead's property object characteristics (type, construction year, living/plot area, estimated value). The lead_property row is shared with the Address section: this endpoint preserves the address columns and creates the row when absent. 404 when the lead is not in the workspace.",
  },
  recordNote: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/activities/notes',
    pathParams: RecordLeadNotePathParamsSchema,
    body: RecordLeadNoteBodySchema,
    response: { 200: RecordLeadNoteResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      "Record a note against a lead and publish lead.lead-note-recorded, which the activity module projects onto the lead timeline. occurredAt defaults to now; refuses empty / over-long content (≤ 10,000 chars) and a future occurredAt (1-minute skew). 404 when the lead is not in that workspace. Distinct from updateNotes, which replaces the lead's single free-text notes field.",
  },
  recordCommunication: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/activities/communications',
    pathParams: RecordLeadCommunicationPathParamsSchema,
    body: RecordLeadCommunicationBodySchema,
    response: { 200: RecordLeadCommunicationResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      'Record a communication (call / SMS / WhatsApp / fax / letter) against a lead and publish lead.lead-communication-recorded, which the activity module projects onto the lead timeline with a direction-aware title. Direction is required; subject applies to fax/letter and duration to call (ignored otherwise). occurredAt defaults to now; refuses empty / over-long content and a future occurredAt (1-minute skew). A LOG (DONE) communication may carry an Ergebnis (outcome); it is rejected on a PLAN. 404 when the lead is not in that workspace.',
  },
  completeActivity: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/activities/:activityId/completion',
    pathParams: CompleteLeadActivityPathParamsSchema,
    body: CompleteLeadActivityBodySchema,
    response: { 200: CompleteLeadActivityResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      'Complete an OPEN (planned) note / communication on a lead (IMM-418.2): transition it OPEN → DONE, capturing „Stattgefunden am" (occurredAt, ≤ now + 60s skew, default now), an optional completion note appended to the content, and — for a communication — an Ergebnis. Completing a member communication on a NEW lead advances it to CONTACTED (the IMM-417 positive half). Refused with 422 when the activity is already DONE/CANCELLED, when an outcome is supplied for a Notiz, or when the lead is CONVERTED. 404 when the lead or activity is not in that workspace.',
  },
  rescheduleActivity: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/activities/:activityId/reschedule',
    pathParams: LeadActivityTransitionPathParamsSchema,
    body: RescheduleLeadActivityBodySchema,
    response: { 200: LeadActivityTransitionResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      'Reschedule an OPEN (planned) note / communication on a lead (IMM-418.4): replace „Fällig am" with a new future date. The activity stays OPEN (no completion, no Ergebnis, no status advance); the prior due date is retained in the audited domain event. Refused with 422 when the activity is DONE/CANCELLED, when dueAt is in the past, or when the lead is CONVERTED. 404 when the lead or activity is not in that workspace.',
  },
  cancelActivity: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/activities/:activityId/cancellation',
    pathParams: LeadActivityTransitionPathParamsSchema,
    body: CancelLeadActivityBodySchema,
    response: { 200: LeadActivityTransitionResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      'Cancel („Absagen") an OPEN (planned) note / communication on a lead (IMM-418.4): close it as CANCELLED without a completion — no „Stattgefunden am", no Ergebnis, no IMM-417 status advance and no auto-Wiedervorlage. A cancelled activity leaves „Anstehend" and never appears in „Verlauf". Refused with 422 when the activity is already DONE/CANCELLED or the lead is CONVERTED. 404 when the lead or activity is not in that workspace.',
  },
  reassignActivity: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/leads/:id/activities/:activityId/assignee',
    pathParams: LeadActivityTransitionPathParamsSchema,
    body: ReassignLeadActivityBodySchema,
    response: { 200: LeadActivityTransitionResponseSchema },
    permission: 'organization:lead:lead-update',
    description:
      "Reassign the Zuständiger of an OPEN (planned) note / communication on a lead (IMM-418.4): validate the target against the workspace's assignable members and move the activity to their open list; the change is audited. Refused with 422 when the activity is DONE/CANCELLED, the member is not assignable, or the lead is CONVERTED. 404 when the lead or activity is not in that workspace.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). The
 * inferred type of that literal had grown past the limit `tsc` will serialize
 * into a `.d.ts`, so adding a single route to ANY group failed the build with
 * TS7056. A named group is emitted as a `typeof` reference instead of being
 * inlined whole — the same shape `valuationContracts` already uses.
 *
 * Typing is unchanged: `as const satisfies Record<string, EndpointDefinition>`
 * keeps every leaf's literal method / path / permission and its exact schemas,
 * which the route-builder's request inference depends on.
 */
const dashboardContracts = {
  funnel: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/dashboard/funnel',
    pathParams: DashboardPathParamsSchema,
    query: DashboardFunnelQuerySchema,
    response: { 200: GetDashboardFunnelResponseSchema },
    permission: 'workspace:deal:deal-read',
    description:
      "The dashboard's funnel band for one Standort (`standortId`) or, without one, the caller's own records. A Standortleitung (deal-oversight in the Standort) sees the whole Standort; everyone else only the records they are responsible for; with no Standort selected a leader gets `CHOOSE_STANDORT` and no figures. Every figure carries the list filter it was counted with. `period` selects the intake window (default LAST_30_DAYS); stock figures ignore it.",
  },
  newRecords: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/dashboard/new-records',
    pathParams: DashboardPathParamsSchema,
    query: DashboardQuerySchema,
    response: { 200: GetDashboardNewRecordsResponseSchema },
    permission: 'workspace:deal:deal-read',
    description:
      "The dashboard's „Neue Leads“ and „Neue Kundenanfragen“: the five newest open owner leads and active Interessenten in scope, each with its last completed activity.",
  },
  attention: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/dashboard/attention',
    pathParams: DashboardPathParamsSchema,
    query: DashboardQuerySchema,
    response: { 200: GetDashboardAttentionResponseSchema },
    permission: 'workspace:deal:deal-read',
    description:
      "The dashboard's „Braucht Aufmerksamkeit“: overdue tasks, owner leads still new after 24 hours, active deals needing action and data gaps, each with the list filter it was counted with, plus the next six upcoming tasks, each with its assignee.",
  },
  setup: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/dashboard/setup',
    pathParams: DashboardPathParamsSchema,
    query: DashboardQuerySchema,
    response: { 200: GetDashboardSetupResponseSchema },
    permission: 'workspace:deal:deal-read',
    description:
      "The dashboard's „Was noch zu tun ist“: per set-up item (member onboarding, Postfach) the members done and in total, and the open members by name with their state. A Standortleitung sees the Standort's active members; everyone else only themselves. Only items with an open member are returned; `setup` is null in CHOOSE_STANDORT.",
  },
  team: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/dashboard/team',
    pathParams: DashboardPathParamsSchema,
    query: DashboardQuerySchema,
    response: { 200: GetDashboardTeamResponseSchema },
    permission: 'workspace:deal:deal-read',
    description:
      "The dashboard's Team table for a Standortleitung: one row per active member of the Standort, alphabetical (not a ranking), with their active deals, Pipeline-Courtage, Courtage aus Aufträgen & Online, overdue tasks and owner leads without a response, plus a last „Ohne Zuständigkeit“ row for active deals with no responsible member (omitted when there is none). A deal with several responsible members counts in full for each of them. Every figure carries the list filter it was counted with. `team` is null unless the caller leads the selected Standort.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const dealsContracts = {
  // The Deal-Überblick (IMM-430.7). Deliberately NOT cursor-paginated: the
  // attention flag is derived, so it can be neither a `WHERE` nor an `ORDER BY`,
  // and paging before deriving would make „deals needing attention first" true
  // only within a page while a flag filter returned near-empty pages. The
  // handler derives over the whole scoped set and bounds it, surfacing
  // `truncated` rather than silently cutting.
  overview: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/deals/overview',
    pathParams: ListDealOverviewPathParamsSchema,
    query: ListDealOverviewQuerySchema,
    response: { 200: ListDealOverviewResponseSchema },
    // The workspace-tier read, like the sibling `workspaceList` — this route is
    // workspace-addressed because the surface spans Standorte.
    // `organization:deal:deal-oversight` WIDENS the scope from own-deals to every
    // deal in view; it is not a gate, because the AC asks for a narrowed fallback
    // view rather than a locked door.
    permission: 'workspace:deal:deal-read',
    description:
      "The Standortleitung's cross-deal overview: every open deal in scope (non-DRAFT, non-LOST; lost deals only when `statuses` includes `LOST`) with its phase, active days in phase, responsible members, next open activity and derived attention flag, ordered so deals needing attention come first. Filterable by Standort, Zuständig, phase and flag. Not paginated — bounded, with `truncated` set when the scope exceeds the bound.",
  },
  nextActivities: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/deals/next-activities',
    pathParams: ListDealNextActivitiesPathParamsSchema,
    query: ListDealNextActivitiesQuerySchema,
    response: { 200: ListDealNextActivitiesResponseSchema },
    // The same tier and grant as `overview`, which already serves this exact field. Not `docs: 'PUBLIC'`:
    // the board's hydration read (IMM-1313).
    permission: 'workspace:deal:deal-read',
    description:
      "The Deal board's batch read of each listed deal's next open activity (the Deal-Überblick's „Nächste Aktivität“): its kind, title, due date and whether it is overdue, or null when the deal has nothing open. Only deals the caller may list are answered; any other id is absent, indistinguishable from an unknown one. 1–100 ids per call.",
  },
  mandates: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/deals/mandates',
    pathParams: ListMandatesPathParamsSchema,
    query: ListMandatesQuerySchema,
    response: { 200: ListMandatesResponseSchema },
    // Same tier and same widening as `overview`: oversight widens the scope, it never gates.
    permission: 'workspace:deal:deal-read',
    description:
      "The Aufträge list: every non-DRAFT deal in scope whose phase ever reached MANDATE_RECEIVED — won, lost and deferred ones included — with its current phase and status, its Objekt's marketing state (propertyState) and its responsible members, newest Auftrag first. Without organization:deal:deal-oversight only the deals the caller is responsible for. Optional ?organizationId= narrows to one visible location. Not paginated — bounded, with `truncated` set when the scope exceeds the bound.",
  },
  list: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/deals',
    pathParams: ListDealsPathParamsSchema,
    query: ListDealsQuerySchema,
    response: { 200: ListDealsResponseSchema },
    permission: 'organization:deal:deal-read',
    docs: 'PUBLIC',
    description:
      'List deals under a workspace organisation the caller is a member of, longest-in-phase first. Optional ?status=, ?phase=, ?type= and ?mine=true filters. Slim projection (deal fields + ids + derived expected commission) — the FE hydrates contact / property names.',
  },
  workspaceList: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/deals',
    pathParams: ListWorkspaceDealsPathParamsSchema,
    query: ListWorkspaceDealsQuerySchema,
    response: { 200: ListWorkspaceDealsResponseSchema },
    permission: 'workspace:deal:deal-read',
    docs: 'PUBLIC',
    description:
      'List deals across every location the caller may see (their affiliated organisations and their descendants, IMM-20.4), plus any deal assigned to the caller, longest-in-phase first. Same slim row shape and ?status=/?phase=/?type=/?mine=true filters as the org-addressed list; an optional ?organizationId= narrows the result to one location within that visible set. `?q=` keeps only deals on the properties the ⌘K palette matched; blank means absent. An optional ?propertyId= narrows the result to the deals on that property, within the same visible set.',
  },
  reassignStandort: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/deals/:id/standort',
    pathParams: ReassignDealStandortPathParamsSchema,
    body: ReassignDealStandortBodySchema,
    response: { 200: ReassignDealStandortResponseSchema },
    permission: 'workspace:deal:deal-reassign',
    description:
      "Move a deal across Standorte (IMM-20.4 AC22). Allowed when the caller is Owner/Workspace-Admin, or the deal's creator, or both the source and target Standort are within the caller's visible set; otherwise 403. The target must be an organisation in the same workspace.",
  },
  assign: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/deals/:id/assign',
    pathParams: AssignDealResponsibleMembersPathParamsSchema,
    body: AssignDealResponsibleMembersBodySchema,
    response: { 200: AssignDealResponsibleMembersResponseSchema },
    permission: 'organization:deal:deal-assign',
    description:
      "Replace a deal's responsible-member set (IMM-402.1). At least one member is required (400 when empty). Only Owner/Workspace-Admin (any member) and Standortleitung (members of their own Standort subtree) may assign — regular members lack the permission (403). A Standortleitung targeting a member outside their subtree is refused (403). Re-confirming the same set is a no-op. Closed deals stay assignable; a soft-deleted or non-existent deal is 404.",
  },
  listAssignableMembers: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/deals/assignable-members',
    pathParams: ListDealAssignableMembersPathParamsSchema,
    response: { 200: ListDealAssignableMembersResponseSchema },
    permission: 'organization:deal:deal-assign',
    description:
      "List the members the caller may assign a deal to (IMM-402.1): every workspace member for Owner/Workspace-Admin, only the caller's Standort subtree for a Standortleitung. Scoped off the caller's visible organisations.",
  },
  listActivityAssignees: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/deals/activity-assignees',
    pathParams: ListDealAssignableMembersPathParamsSchema,
    response: { 200: ListDealAssignableMembersResponseSchema },
    permission: 'organization:activity:activity-write',
    description:
      'List the members the caller may make responsible for a deal activity (IMM-754.1) — the „Zuständig" select in the activity composer. Same roster and same visible-organisation scoping as `assignable-members`, behind a different gate: choosing who a task falls to is not the power to reassign the deal, so it is gated on the permission that governs recording an activity rather than on `deal-assign`, which a regular member does not hold.',
  },
  create: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals',
    pathParams: CreateDealPathParamsSchema,
    body: CreateDealBodySchema,
    response: { 200: CreateDealResponseSchema },
    permission: 'organization:deal:deal-create',
    description:
      'Create a deal under a workspace organisation the caller is a member of. Validates the property and owner contacts, refuses if an active deal of the same type already exists on the property or a contact already holds a non-owner link, then creates the deal (phase Im Austausch / status Aktiv) and ensures an owner property-contact link per contact. Refused 422 `deal.deal.cannot-create-property-archived` when the Objekt is archived — lift the archive first. The buyer commission is not stored on the deal: `buyerCommissionPercent` is deprecated and ignored — set it on the Objekt with PUT /v1/organizations/{organizationId}/properties/{id}/pricing-sale.',
  },
  get: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/deals/:id',
    pathParams: GetDealPathParamsSchema,
    response: { 200: GetDealResponseSchema },
    permission: 'organization:deal:deal-read',
    docs: 'PUBLIC',
    description:
      "Return one deal under a workspace organisation the caller is a member of. 404 if the deal does not exist in that workspace + organisation.\n\n`links.self` is the URL of the endpoint that produced the response — this organisation-addressed path. The same deal fetched through the workspace-addressed detail read answers with THAT path instead, so two responses whose `self` differs may still be the same deal: compare `id`, never `self`. `detailUrl` is deprecated in favour of `links.self` and still populated — it is always the organisation-addressed URL whichever route you called, which is exactly why it is superseded rather than removed.\n\n`links` also carries the deal's property at the organisation-addressed property read (`organization:property:property-read`; the property read documented here is the workspace-addressed one), its activity timeline (`organization:activity:activity-read`), its phase history and its valuation request (`organization:valuation:valuation-request-read`). All four are always present.",
  },
  getWorkspace: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/deals/:id',
    pathParams: GetWorkspaceDealPathParamsSchema,
    response: { 200: GetDealResponseSchema },
    permission: 'organization:deal:deal-read',
    docs: 'PUBLIC',
    description:
      "Fetch a single deal by id within a workspace the caller is a member of. Unlike the organisation-addressed read, this resolves a deal in any location the caller may see — their visible location set, plus deals they created or are responsible for — because the board's hierarchical visibility (IMM-20.4) can surface a deal whose home location is not the caller's selected one. A deal outside that set answers 404, exactly as the board hides it. 404 when the deal does not exist in the workspace (tenant-isolated).\n\n`links.self` is the URL of the endpoint that produced the response — this workspace-addressed path. The same deal fetched through the organisation-addressed detail read answers with THAT path instead, so two responses whose `self` differs may still be the same deal: compare `id`, never `self`. `detailUrl` is deprecated in favour of `links.self` and still populated — but it is always the ORGANISATION-addressed URL, even here, which is exactly why it is superseded.\n\n`links` also carries the deal's property at the organisation-addressed property read (`organization:property:property-read`; the property read documented here is the workspace-addressed one), its activity timeline (`organization:activity:activity-read`), its phase history and its valuation request (`organization:valuation:valuation-request-read`). All four are always present.",
  },
  advancePhase: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/phase-transitions',
    pathParams: AdvanceDealPhasePathParamsSchema,
    body: AdvanceDealPhaseBodySchema,
    response: { 200: AdvanceDealPhaseResponseSchema },
    permission: 'organization:deal:deal-phase-advance',
    description:
      'Advance a deal one step forward through the phase pipeline. The target phase must be exactly the next phase; skipping is refused. Reaching the final phase auto-wins the deal (status WON) and emits deal.won; reaching the contract-received phase emits deal.contract-received.',
  },
  phaseHistory: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/deals/:id/phase-history',
    pathParams: GetDealPhaseHistoryPathParamsSchema,
    response: { 200: GetDealPhaseHistoryResponseSchema },
    permission: 'organization:deal:deal-read',
    docs: 'PUBLIC',
    description:
      "Return the deal's phase-history reasons keyed by phase-history row id (the deal-timeline activity's contentId). The frontend merges these into the timeline so a forward note and the backward reason render under their transition. The reason is not carried on the audited deal.phase-changed event and never enters the activity store — it is read from the deal's own history table here.",
  },
  revertPhase: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/phase-reversions',
    pathParams: RevertDealPhasePathParamsSchema,
    body: RevertDealPhaseBodySchema,
    response: { 200: RevertDealPhaseResponseSchema },
    permission: 'organization:deal:deal-phase-move-back',
    description:
      'Move a deal back to any earlier phase in the pipeline with a mandatory reason. Restricted (via the role→permission config) to the Owner, an Admin, or the deal’s creator. Skipping multiple phases backward in one move is allowed; an empty reason is refused. Refused on a won deal and only allowed from an active workflow. The transition is recorded with direction BACKWARD and the reason in the phase history.',
  },
  addContact: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/contacts',
    pathParams: AddDealContactPathParamsSchema,
    body: AddDealContactBodySchema,
    response: { 200: AddDealContactResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      'Add an Eigentümer-Kontakt to a deal. Validates the contact exists in scope, holds no non-owner property link, and is not in another active deal on the property; ensures the IMM-6 owner link and records a kontakt-added activities entry. Refused on a won deal. Returns the updated deal.',
  },
  removeContact: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/deals/:id/contacts/:contactId',
    pathParams: RemoveDealContactPathParamsSchema,
    response: { 200: RemoveDealContactResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      'Remove an Eigentümer-Kontakt from a deal and record a kontakt-removed activities entry. Refused if it would leave the deal with zero contacts or on a won deal. The IMM-6 property↔contact link is not removed. Returns the updated deal.',
  },
  updateNotes: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/deals/:id/notes',
    pathParams: UpdateDealNotesPathParamsSchema,
    body: UpdateDealNotesBodySchema,
    response: { 200: UpdateDealNotesResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      'Append to the deal’s Notizen (Hindernisse / Nächste Schritte) timelines; each provided field becomes the new latest value, omitted fields are left unchanged. Refused on a won deal. Returns the updated deal.',
  },
  updateMandateTerms: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/deals/:id/mandate-terms',
    pathParams: UpdateDealMandateTermsPathParamsSchema,
    body: UpdateDealMandateTermsBodySchema,
    response: { 200: UpdateDealMandateTermsResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      "Set the deal's Auftrag terms (IMM-1150): mandateType, mandateStartDate, mandateDurationMonths, sellerCommissionPercent (0–100, at most two decimals, net of VAT) and expectedSalePrice. The buyer commission is not a deal term: it belongs to the Objekt, so `buyerCommissionPercent` is deprecated and ignored — set it with PUT /v1/organizations/{organizationId}/properties/{id}/pricing-sale. An omitted field is left unchanged; null clears it. The end date `mandateEndsOn` is derived, never sent. Sale deals only: refused 422 deal.deal.cannot-update-mandate-terms-not-sale on a rental deal and 422 deal.deal.cannot-update-mandate-terms-won-immutable on a won deal. 404 when the deal is not in that Standort. Returns the updated deal.",
  },
  defer: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/defer',
    pathParams: DeferDealPathParamsSchema,
    body: DeferDealBodySchema,
    response: { 200: DeferDealResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      'Put a deal on hold (`Zurückgestellt`) with a follow-up date, or re-target the date when it is already deferred. The date must be today or later (workspace-local). Refused on a won or lost deal. Returns the updated deal.',
  },
  resume: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/resume',
    pathParams: ResumeDealPathParamsSchema,
    response: { 200: ResumeDealResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      'Take a deferred deal off hold (`Wieder aufnehmen`): status returns to `Aktiv`, the phase is unchanged, the follow-up date clears, and a `wieder-aufgenommen` status entry is recorded. Refused unless the deal is deferred. Returns the updated deal.',
  },
  markLost: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/mark-lost',
    pathParams: MarkDealLostPathParamsSchema,
    body: MarkDealLostBodySchema,
    response: { 200: MarkDealLostResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      'Mark a deal `Verloren` (`Als verloren markieren`) with a required Verlustgrund and an optional Verlust-Notiz: status → `Verloren`, `Verloren am` is set to now, and a `verloren` status entry records the reason / note. Only allowed from an interactive status; refused on a won deal. When no other open deal remains on the Objekt, the Objekt is archived in the same step (Archivierungsgrund derived from the Verlustgrund). Returns the updated deal.',
  },
  reopen: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/reopen',
    pathParams: ReopenDealPathParamsSchema,
    response: { 200: ReopenDealResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      'Reopen a `Verloren` deal (`Wieder öffnen`): status returns to `Aktiv`, the phase is restored to the value held before the loss, the prior Verlustgrund / Verlust-Notiz stay in history but are no longer determinative, and a `wieder-geöffnet` status entry is recorded. Refused unless the deal is lost. An archived Objekt returns to the status it had before it was archived, in the same step. Returns the updated deal.',
  },
  dismissPropertyCleanup: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/dismiss-property-cleanup',
    pathParams: DismissDealPropertyCleanupPathParamsSchema,
    response: { 200: DismissDealPropertyCleanupResponseSchema },
    permission: 'organization:deal:deal-phase-advance',
    description:
      'Dismiss the post-Verloren cleanup banner (`[Behalten]`, IMM-171.3): stamps `cleanup_dismissed_at` = now so the banner stops nagging for this Verloren cycle. A no-op-safe setter — no status guard; a reopen → re-lost bumps `lostAt` past the dismissal and re-offers the cleanup. Gated on the same deal-management permission as the phase advance (`canEditDeal`). Returns the updated deal.',
  },
  dismissBuyerCommissionDifference: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/buyer-commission-difference/dismiss',
    pathParams: DismissBuyerCommissionDifferencePathParamsSchema,
    body: DismissBuyerCommissionDifferenceBodySchema,
    response: { 200: DismissBuyerCommissionDifferenceResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      "Record the member's pick on a Deal whose Auftrag carried its own Käuferprovision before IMM-1380 and differs from its Objekt's (`buyerCommissionDifference`). It clears the stored Auftrag value so the Deal is no longer listed, for every member. The Objekt's commission is not changed. Idempotent: a Deal with no stored value returns unchanged. Allowed on a Deal of any status, won included. Returns the updated deal.",
  },
  delete: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/deals/:id',
    pathParams: DeleteDealPathParamsSchema,
    response: { 200: DeleteDealResponseSchema },
    permission: 'organization:deal:deal-delete',
    description:
      'Soft-delete a deal (`Löschen`). Restricted (via the role→permission config) to the Owner or an Admin. Refused on a won deal regardless of role — won deals are immutable. The deal is hidden from every list / get / filter response and no longer blocks a new active deal on the same property + type; there is no hard delete in V1 (restoration is tooling-only). Returns the deleted deal.',
  },
} as const satisfies Record<string, EndpointDefinition>;

const dealDocumentsContracts = {
  draft: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/deals/:id/documents/draft',
    pathParams: GetDealPathParamsSchema,
    query: DealDocumentDraftQuerySchema,
    response: { 200: DealDocumentDraftResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      "Resolve a document template (Vorlage) against an active sale deal for the document dialog (IMM-268): every data field the template uses with its typed value or why it has none, the source inputs (owner, Objekt, deal, Standort, Makler, company) still empty and whether each is required, the current value of every input, and the workspace-local date the document would carry. Requires the deal-update grant, because the draft exists only to generate. 404 deal.deal.not-found; 404 deal.generated-document.template-not-found when the template does not exist in this workspace; 422 deal.generated-document.cannot-generate-rental-deal / cannot-generate-not-active. `locationOption` (Maklervertrag Makler block): LOCATION names the Deal's Standort and its Standortleitungen, HEADQUARTERS the Hauptstandort and its own Standortleitungen. Absent → the default (HEADQUARTERS when the Deal sits on the Hauptstandort, else LOCATION). `location.option` and `location.defaultOption` echo both.",
  },
  generate: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/documents',
    pathParams: GetDealPathParamsSchema,
    body: GenerateDealDocumentBodySchema,
    response: { 202: GenerateDealDocumentResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      "Generate a document (Maklervertrag or Vollmacht) on an active sale deal from a document template (Vorlage), checked against what the preview showed (IMM-268). Stores an entry with status GENERATING and answers 202; the PDF is produced in the background and filed in the Objekt's Dokumente. 404 deal.deal.not-found; 404 deal.generated-document.template-not-found; 422 deal.generated-document.cannot-generate-rental-deal / cannot-generate-not-active / cannot-generate-missing-fields (details.missing lists the required inputs) / cannot-generate-preview-outdated (the template or the data changed since the preview). `documentOnlyValues`: Standort or Makler values that apply to this document only and are never saved at a source; a value for another Standort or a former Makler answers cannot-generate-preview-outdated. `locationOption`: as on the draft; null → the default.",
  },
  list: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/deals/:id/documents',
    pathParams: GetDealPathParamsSchema,
    response: { 200: ListDealDocumentsResponseSchema },
    permission: 'organization:deal:deal-read',
    description:
      'List the documents generated on a deal, newest first. A failed attempt from the in-app dialog is not listed; a failed attempt from a connected client or a retry is. 404 deal.deal.not-found.',
  },
  get: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/deals/:id/documents/:documentId',
    pathParams: DealDocumentPathParamsSchema,
    response: { 200: GetDealDocumentResponseSchema },
    permission: 'organization:deal:deal-read',
    description:
      'Fetch one generated document of a deal, including a failed attempt from the in-app dialog. 404 deal.deal.not-found; 404 deal.generated-document.not-found when the document does not belong to this deal.',
  },
  file: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/deals/:id/documents/:documentId/file',
    pathParams: DealDocumentPathParamsSchema,
    query: DealDocumentFileQuerySchema,
    response: { 200: GetDealDocumentFileResponseSchema },
    permission: 'organization:deal:deal-read',
    description:
      "Mint a short-lived URL for a generated document's PDF. ?disposition=attachment (default) downloads it under its file name, inline opens it in the browser. 404 deal.deal.not-found; 404 deal.generated-document.not-found; 422 deal.generated-document.cannot-download-not-ready while it is GENERATING or FAILED.",
  },
  remove: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/deals/:id/documents/:documentId',
    pathParams: DealDocumentPathParamsSchema,
    response: { 200: DeleteDealDocumentResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      "Delete a generated document. Allowed for the creator or a holder of organization:deal:document-manage-any. It removes the entry, its PDF and its copy in the Objekt's Dokumente; a copy already deleted there is skipped. It is not available to an API key. 404 deal.deal.not-found / deal.generated-document.not-found; 403 deal.generated-document.manage-denied; 403 identity.api-key.route-not-available; 422 deal.generated-document.cannot-delete-generating / cannot-delete-signing-open / cannot-delete-signed.",
  },
  retry: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/documents/:documentId/retries',
    pathParams: DealDocumentPathParamsSchema,
    response: { 202: RetryDealDocumentResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      "Start a failed document again as a new entry credited to the caller, from the failed entry's content with today's data. The failed entry is removed. Answers 202 with the new GENERATING entry. 404 deal.deal.not-found / deal.generated-document.not-found; 422 deal.generated-document.cannot-retry-not-failed / cannot-generate-rental-deal / cannot-generate-not-active / cannot-generate-missing-fields.",
  },
  signingDraft: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/deals/:id/documents/:documentId/signing-draft',
    pathParams: DealDocumentPathParamsSchema,
    response: { 200: SigningDraftSchema },
    permission: 'organization:deal:deal-update',
    description:
      "Read what the send-for-signature dialog shows (IMM-1171.2): the Eigentümer of the deal in owner order with their primary e-mail address or null, the signing member's name, the agency name and whether the Unternehmensdaten block sending. 404 deal.deal.not-found; 404 deal.generated-document.not-found. API keys are refused.",
  },
  sendForSignature: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/documents/:documentId/signing-requests',
    pathParams: DealDocumentPathParamsSchema,
    body: SendForSignatureBodySchema,
    response: { 201: SendForSignatureResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      "Send a finished Maklervertrag or Vollmacht to every Eigentümer for an online signature (IMM-1171.2): records a signing request against the exact PDF with the agent's typed signature, e-mails each owner the PDF and a personal link, and walks a Maklervertrag's deal forward to Vertrag verschickt. `requestKey` makes a retry return the first request. Saving a missing owner address needs organization:relationship:contact-update. 422 deal.signing-request.cannot-send-* for the refusals. API keys are refused.",
  },
  signedFile: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/deals/:id/signing-requests/:requestId/signed-file',
    pathParams: SigningRequestPathParamsSchema,
    query: DealDocumentFileQuerySchema,
    response: { 200: GetDealDocumentFileResponseSchema },
    permission: 'organization:deal:deal-read',
    description:
      "Mint a short-lived URL for the fully signed PDF of a signing request (IMM-1171.5): the document, the Unterschriften-Seite with every signature and the Nachweis. ?disposition=attachment (default) downloads it under its file name, inline opens it in the browser. 404 deal.deal.not-found; 404 deal.signing-request.not-found when the request is not this deal's or its signed PDF is not ready yet.",
  },
  remindSigners: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/signing-requests/:requestId/reminders',
    pathParams: SigningRequestPathParamsSchema,
    body: RemindSignersBodySchema,
    response: { 200: RemindSignersResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      "Remind every Eigentümer who has not signed yet (IMM-1171.6): each gets the reminder e-mail with the sent PDF and a fresh personal link, and lastReminderAt moves to now. 404 deal.deal.not-found; 404 deal.signing-request.not-found when the request is not this deal's; 422 deal.signing-request.cannot-remind-closed when it is fully signed or withdrawn; 422 deal.signing-request.cannot-remind-not-active when the deal is not active. API keys are refused.",
  },
  withdrawSigningRequest: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/signing-requests/:requestId/withdrawal',
    pathParams: SigningRequestPathParamsSchema,
    body: WithdrawSigningRequestBodySchema,
    response: { 200: WithdrawSigningRequestResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      'Withdraw an open signing request (IMM-1171.6): every personal link shows the inactive page, signatures already given stay in the proof, nobody is e-mailed and the deal phase is unchanged. The document can be sent again or deleted. 404 deal.deal.not-found / deal.signing-request.not-found; 422 deal.signing-request.cannot-withdraw-closed / cannot-withdraw-not-active. API keys are refused.',
  },
  markAcceptanceReply: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/deals/:id/signing-requests/:requestId/signers/:signerId/acceptance-reply',
    pathParams: SigningSignerPathParamsSchema,
    body: MarkSigningAcceptanceReplyBodySchema,
    response: { 200: MarkSigningAcceptanceReplyResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      "Record that an Eigentümer's acceptance reply by e-mail to a signed Maklervertrag arrived (IMM-1171.9): sets the signer's acceptanceReplyMarkedAt to the server time and records the member in the Nachweis. Idempotent — a second call returns the first mark unchanged. Nothing else changes: status, signed PDF, filing and phase do not wait for the reply. Allowed on a withdrawn request and a closed deal. 404 deal.deal.not-found / deal.signing-request.not-found (also for a signer of another request); 422 deal.signing-request.cannot-mark-acceptance-reply-not-signed when the document is not a Maklervertrag or the owner has not signed. API keys are refused.",
  },
  setOriginalReceived: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/deals/:id/documents/:documentId/original-receipt',
    pathParams: DealDocumentPathParamsSchema,
    body: SetOriginalReceivedBodySchema,
    response: { 200: SetOriginalReceivedResponseSchema },
    permission: 'organization:deal:deal-update',
    description:
      "Record or clear that the paper original of a Vollmacht arrived (IMM-1171.11): received true sets the document's originalReceivedAt to the server time, false clears it. Idempotent — repeating the current state changes nothing. Independent of any signing request and of the deal's status. Answers the document as the list shows it. 404 deal.deal.not-found / deal.generated-document.not-found; 422 deal.generated-document.cannot-mark-original-not-power-of-attorney when the document is not a Vollmacht. API keys are refused.",
  },
  signingProof: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/deals/:id/signing-requests/:requestId/proof',
    pathParams: SigningRequestPathParamsSchema,
    response: { 200: GetSigningProofResponseSchema },
    permission: 'organization:deal:deal-read',
    description:
      "Read the Nachweis of a signing request (IMM-1171.7): per Eigentümer the page as served at the click, every declaration word for word with its box state, the typed name, the time to the second, IP address, browser and the page fingerprint, and every e-mail sent to them with its sent time and delivery status. 404 deal.deal.not-found; 404 deal.signing-request.not-found when the request is not this deal's. Not available to API keys.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const prospectsContracts = {
  workspaceList: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/prospects',
    pathParams: ListProspectsPathParamsSchema,
    query: ListProspectsQuerySchema,
    response: { 200: ListProspectsResponseSchema },
    permission: 'workspace:deal:prospect-read',
    description:
      "List Interessenten (one Kontakt × one Kauf-Objekt) across every location the caller may see, longest-in-phase first. ?status= (default ACTIVE, or ALL), ?propertyId= narrows to one Objekt, ?organizationIds= (comma-separated) or ?organizationId= narrows to those Standorte within the caller's visible set and never widens it, ?assignedToMe=true keeps the caller's own. Every row is the full Prospect shape, including the Objekt's active count and reservation and every offer and counter-offer (offers). offeredPrice is the current offer (accepted, otherwise newest open offer of the buyer).",
  },
  nextActivities: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/prospects/next-activities',
    pathParams: ListProspectNextActivitiesPathParamsSchema,
    query: ListProspectNextActivitiesQuerySchema,
    response: { 200: ListProspectNextActivitiesResponseSchema },
    // The ticket's grant: the one the tab's own list read needs. Not `docs: 'PUBLIC'`, and refused to API keys
    // at the route: the Objekt tab's hydration read (IMM-1363).
    permission: 'workspace:deal:prospect-read',
    description:
      "The Objekt tab's batch read of each listed Interessent's next open activity: its kind, Termin-Art or communication type and direction, title, due date and whether it is overdue; null when an active Interessent has nothing open and for every lost or won one. Only Interessenten the caller may read are answered; any other id is absent, indistinguishable from an unknown one. 1–100 ids per call. Not available to API keys.",
  },
  workspaceGet: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/prospects/:id',
    pathParams: GetVisibleProspectPathParamsSchema,
    response: { 200: GetVisibleProspectResponseSchema },
    permission: 'workspace:deal:prospect-read',
    description:
      "Fetch one Interessent by id across every location the caller may see. 404 when it does not exist in the workspace, sits in a location outside the caller's visible set, or its Kontakt or Objekt was deleted — the three are indistinguishable. Returns the full Prospect shape, including the Objekt's active count and reservation and every offer and counter-offer (offers). offeredPrice is the current offer (accepted, otherwise newest open offer of the buyer).",
  },
  listViewings: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/prospects/:id/viewings',
    pathParams: ListProspectViewingsPathParamsSchema,
    response: { 200: ListProspectViewingsResponseSchema },
    permission: 'workspace:deal:prospect-read',
    description:
      'List the Besichtigungen of an Interessent — VIEWING appointments linked to it that are not private, in any status — oldest first, each with its assessment (attendance, impression, comment, who recorded or last changed it, when, edited) or null. 404 like the detail read.',
  },
  assessViewing: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/prospects/:id/viewings/:appointmentId/assessment',
    pathParams: AssessProspectViewingPathParamsSchema,
    body: AssessProspectViewingBodySchema,
    response: { 200: AssessProspectViewingResponseSchema },
    permission: 'organization:deal:prospect-phase-advance',
    description:
      'Record or correct the assessment of one Besichtigung of the Interessent: attendance ATTENDED or NO_SHOW, an impression (required with ATTENDED, dropped with NO_SHOW) and an optional comment. Refused before the appointment starts and when it is cancelled. ATTENDED moves an Aktiv Interessent before VIEWING_COMPLETED there once, recorded with reasonCode VIEWING_ASSESSED. Saving unchanged values changes nothing. Returns the Prospect and the Besichtigung.',
  },
  create: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects',
    pathParams: CreateProspectPathParamsSchema,
    body: CreateProspectBodySchema,
    response: { 200: CreateProspectResponseSchema },
    permission: 'organization:deal:prospect-create',
    description:
      "Create an Interessent for a Kontakt and a Kauf-Objekt of a workspace organisation the caller is a member of. Both must resolve in that organisation; a rental or archived Objekt is refused, as is a second active Interessent for the same Kontakt and Objekt. The Interessent starts in phase Interesse / status Aktiv with the caller as Verantwortlicher, and the Kontakt's Kaufinteressent link on the Objekt is ensured.",
  },
  advancePhase: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:id/phase-transitions',
    pathParams: AdvanceProspectPhasePathParamsSchema,
    body: AdvanceProspectPhaseBodySchema,
    response: { 200: AdvanceProspectPhaseResponseSchema },
    permission: 'organization:deal:prospect-phase-advance',
    description:
      "Move an Interessent forward to any later phase; phases may be skipped. Entering Kaufangebot or later without any recorded offer of the buyer needs offeredPrice > 0; an offeredPrice given on any forward move records a new offer entry (OPEN) with offerDate (default today), offerFinancingReservation and offerNote, which are read only together with offeredPrice; entering Gekauft needs purchasePrice > 0 and wins the Interessent. Entering Notartermin needs notaryAppointmentDate (a calendar day, YYYY-MM-DD, past days allowed) on the request, even when one is stored; passing it with any other target phase is refused. Entering Reserviert, Notartermin or Gekauft is refused while another Interessent reserves the Objekt (details.reservingProspectId). Prices ≤ 0 are refused with the gate's code, not a 400. Returns the moved Prospect.",
  },
  revertPhase: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:id/phase-reversions',
    pathParams: RevertProspectPhasePathParamsSchema,
    body: RevertProspectPhaseBodySchema,
    response: { 200: RevertProspectPhaseResponseSchema },
    permission: 'organization:deal:prospect-phase-move-back',
    description:
      'Move an Interessent back to any earlier phase with a mandatory reason; phases may be skipped. Offered and purchase prices are kept. Refused on a won or lost Interessent, for a blank reason, and for a phase that is not earlier. The move is recorded with direction BACKWARD and the reason. Returns the moved Prospect.',
  },
  recordOffer: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:id/offers',
    pathParams: RecordProspectOfferPathParamsSchema,
    body: RecordProspectOfferBodySchema,
    response: { 200: RecordProspectOfferResponseSchema },
    permission: 'organization:deal:prospect-update',
    description:
      'Record an offer of the buyer (kind OFFER) or a counter-offer of the Eigentümer (kind COUNTER_OFFER) on an Aktiv Interessent, in any phase. amount > 0; offeredOn (YYYY-MM-DD) not after today in Europe/Berlin, default today; financingReservation only with OFFER; note at most 500 characters. The entry starts OPEN and can never be edited or deleted. Refused on a won or lost Interessent. Returns the Prospect with every entry.',
  },
  changeOfferStatus: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:id/offers/:offerId/status-transitions',
    pathParams: ChangeProspectOfferStatusPathParamsSchema,
    body: ChangeProspectOfferStatusBodySchema,
    response: { 200: ChangeProspectOfferStatusResponseSchema },
    permission: 'organization:deal:prospect-update',
    description:
      'Change the status of one offer or counter-offer of an Aktiv Interessent: from OPEN to ACCEPTED, REJECTED or WITHDRAWN, and from any of these back to OPEN. Any other move is refused (deal.prospect-offer.cannot-change-status-not-allowed). Accepting is refused while another entry of the Interessent is ACCEPTED (details.acceptedOfferId). Accepting changes nothing else: no phase, no other entry, no other Interessent. Refused on a won or lost Interessent. 404 deal.prospect-offer.not-found when the entry does not belong to the Interessent. Returns the Prospect with every entry.',
  },
  updateNotaryAppointmentDate: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/prospects/:id/notary-appointment-date',
    pathParams: UpdateProspectNotaryAppointmentDatePathParamsSchema,
    body: UpdateProspectNotaryAppointmentDateBodySchema,
    response: { 200: UpdateProspectNotaryAppointmentDateResponseSchema },
    permission: 'organization:deal:prospect-update',
    description:
      "Set or move an active Interessent's Notartermin date (a calendar day, YYYY-MM-DD; past days allowed), in any phase. Every change is recorded in its history and timeline; saving the stored date again changes nothing. A date cannot be cleared. Refused on a won or lost Interessent. Returns the updated Prospect.",
  },
  markLost: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:id/mark-lost',
    pathParams: MarkProspectLostPathParamsSchema,
    body: MarkProspectLostBodySchema,
    response: { 200: MarkProspectLostResponseSchema },
    permission: 'organization:deal:prospect-update',
    description:
      'Mark an active Interessent `Verloren` with a required Verlustgrund and an optional Verlust-Notiz. The phase is kept, so „Wieder aufnehmen" returns to it. A reserved Objekt is freed at once. Refused on a won or already lost Interessent. Returns the updated Prospect.',
  },
  reopen: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:id/reopen',
    pathParams: ReopenProspectPathParamsSchema,
    body: ReopenProspectBodySchema,
    response: { 200: ReopenProspectResponseSchema },
    permission: 'organization:deal:prospect-update',
    description:
      'Take a `Verloren` Interessent up again: status returns to Aktiv in the phase it had, and the Verlustgrund and note stay in history only. Refused when another active Interessent exists for the same Kontakt and Objekt (details.existingProspectId), when its phase is Reserviert or Notartermin and another Interessent reserves the Objekt (details.reservingProspectId), on a won Interessent, and when it is not lost. Returns the updated Prospect.',
  },
  brokerageAgreementGet: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/prospects/:id/brokerage-agreement',
    pathParams: GetBrokerageAgreementPathParamsSchema,
    response: { 200: GetBrokerageAgreementResponseSchema },
    permission: 'workspace:deal:prospect-read',
    description:
      "Read an Interessent's Maklervertrag & Widerruf card: the stored summary, the address the Belehrung went to, and the reason sending is blocked right now (null when it may be sent). 404 like the detail read. Not available to API keys.",
  },
  brokerageAgreementProof: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/prospects/:id/brokerage-agreement/proof',
    pathParams: GetBrokerageAgreementPathParamsSchema,
    response: { 200: GetBrokerageProofResponseSchema },
    permission: 'workspace:deal:prospect-read',
    description:
      "Read the Nachweis of an Interessent's Maklervertrag: the stored page snapshot as served, the declarations, time to the second, IP address and browser, the Muster version, any Widerruf, and every e-mail sent to the Interessent with its status. Every fingerprint is recomputed on read (fingerprintMatches). contract and withdrawal are null until they exist; no agreement answers empty e-mails. 404 like the detail read. Not available to API keys.",
  },
  brokerageNoticeReadiness: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/prospects/brokerage-notice-readiness',
    pathParams: BrokerageNoticeReadinessPathParamsSchema,
    query: BrokerageNoticeReadinessQuerySchema,
    response: { 200: BrokerageNoticeReadinessResponseSchema },
    permission: 'workspace:deal:prospect-read',
    description:
      'Tell the create dialog whether a Widerrufsbelehrung could be sent for a Kontakt and a Kauf-Objekt of the location before the Interessent exists: blockedBy names the first failed precondition, or is null. Not available to API keys.',
  },
  sendBrokerageNotice: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:id/brokerage-notice',
    pathParams: SendBrokerageNoticePathParamsSchema,
    body: SendBrokerageNoticeBodySchema,
    response: { 200: SendBrokerageNoticeResponseSchema },
    permission: 'organization:deal:prospect-update',
    description:
      "Send the Widerrufsbelehrung e-mail to an Aktiv Interessent's Kontakt in the Makler firm's name, with a fresh personal link; sending again is allowed. Refused with deal.brokerage-agreement.cannot-send-notice-* when a precondition fails; a provider rejection is not an error — it returns the Prospect with brokerageAgreement.notice.state FAILED. Not available to API keys.",
  },
  markBrokerageAcceptanceReplyReceived: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:id/brokerage-agreement/acceptance-reply',
    pathParams: MarkBrokerageAcceptanceReplyPathParamsSchema,
    body: MarkBrokerageAcceptanceReplyBodySchema,
    response: { 200: MarkBrokerageAcceptanceReplyResponseSchema },
    permission: 'organization:deal:prospect-update',
    description:
      "Record that the Interessent's e-mail acceptance of the Maklervertrag arrived: sets brokerageAgreement.contract.acceptanceReplyReceivedAt to the server time. Idempotent — a second call returns the first mark unchanged. The contract keeps counting from the online conclusion. Refused with deal.brokerage-agreement.cannot-mark-acceptance-reply-not-concluded when no contract was concluded online. Not available to API keys.",
  },
  dataRoomGet: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/prospects/:id/data-room',
    pathParams: GetDataRoomPathParamsSchema,
    response: { 200: DataRoomCardSchema },
    permission: 'workspace:deal:prospect-read',
    description:
      "Read an Interessent's Datenraum card: the release (active or ended, with times, the releasing member and the end reason), how many of the Objekt's documents are marked „Für Käufer“, and why a (re)release is refused right now without an acknowledgement (`blockedBy`, null when it may run, always null while active; `overridable` says the member may release past it by acknowledging it), and per document marked „Für Käufer“ or opened by the buyer, the first and last view and the last download (`documents`). 404 like the detail read.",
  },
  releaseDataRoom: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:id/data-room/release',
    pathParams: DataRoomWritePathParamsSchema,
    body: ReleaseDataRoomBodySchema,
    response: { 200: DataRoomCardSchema },
    permission: 'organization:deal:prospect-update',
    description:
      'Release the Datenraum to an Aktiv Interessent and e-mail the buyer a fresh personal link. „Erneut freigeben“ restarts an ended release. Refused 422 deal.data-room.cannot-release-* when the gate fails. A Maklervertrag reason the card reports as `overridable` lets the release through only when `acknowledgedReason` names it; the release then records it as `overriddenReason`. Without it the request is refused with that reason. Releasing an active release changes nothing and sends nothing. A provider rejection is not an error: the e-mail is stored FAILED. Not available to API keys.',
  },
  withdrawDataRoomRelease: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:id/data-room/withdrawal',
    pathParams: DataRoomWritePathParamsSchema,
    body: DataRoomWriteBodySchema,
    response: { 200: DataRoomCardSchema },
    permission: 'organization:deal:prospect-update',
    description:
      'End an active release. A release that is not active is left alone. Refused 422 deal.prospect.cannot-change-won on a Gewonnen Interessent.',
  },
  dataRoomReleasesList: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/prospects/data-room-releases',
    pathParams: ListDataRoomReleasesPathParamsSchema,
    query: ListDataRoomReleasesQuerySchema,
    response: { 200: ListDataRoomReleasesResponseSchema },
    permission: 'workspace:deal:prospect-read',
    description:
      "List the Datenraum release of every Aktiv Interessent of one Objekt across the locations the caller may see, sorted by the Kontakt's name — the dialog „Freigaben verwalten“ (IMM-1329). Per row: the release (state, releasedAt, endedAt, endReason; null when never released), blockedBy and overridable exactly as the Interessent's Datenraum card reports them, withdrawalPeriodEndsOn, and canUpdate — whether the caller may release or withdraw at that row's location. Verloren and Gewonnen Interessenten are not listed.",
  },
  saveDataRoomReleases: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/data-room-releases',
    pathParams: SaveDataRoomReleasesPathParamsSchema,
    body: SaveDataRoomReleasesBodySchema,
    response: { 200: SaveDataRoomReleasesResponseSchema },
    permission: 'organization:deal:prospect-update',
    description:
      'Release or withdraw the Datenraum for several Interessenten of one Objekt in this location (IMM-1329). Each change runs exactly as releaseDataRoom or withdrawDataRoomRelease, on its own: each newly released Interessent gets their own e-mail, an overridable Maklervertrag reason needs acknowledgedReason on that change, and one failure never undoes another. One result per change, in request order: RELEASED, WITHDRAWN, UNCHANGED (already in that state) or FAILED with the blockedBy reason — NOT_ACTIVE also for an Interessent that is gone, Gewonnen, or not of this Objekt and location. 1–200 changes, one per prospectId. Not available to API keys.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). The
 * inferred type of that literal had grown past the limit `tsc` will serialize
 * into a `.d.ts`, so adding a single route to ANY group failed the build with
 * TS7056. A named group is emitted as a `typeof` reference instead of being
 * inlined whole — the same shape `valuationContracts` already uses.
 *
 * Typing is unchanged: `as const satisfies Record<string, EndpointDefinition>`
 * keeps every leaf's literal method / path / permission and its exact schemas,
 * which the route-builder's request inference depends on.
 */
const contactsContracts = {
  create: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/contacts',
    pathParams: CreateContactPathParamsSchema,
    body: CreateContactBodySchema,
    response: { 200: CreateContactResponseSchema },
    permission: 'organization:relationship:contact-create',
    description:
      'Create a contact (Person or Company) under a workspace organisation the caller is a member of. Rejects with contact.contact.cannot-create-* when the discriminator + profile block disagree or required fields are missing.',
  },
  list: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/contacts',
    pathParams: ListWorkspaceContactsPathParamsSchema,
    query: ListWorkspaceContactsQuerySchema,
    response: { 200: ListWorkspaceContactsResponseSchema },
    permission: 'organization:relationship:contact-read',
    description:
      'List contacts under a workspace organisation the caller is a member of, newest-first. Pass `?ids=` to restrict to a known set (batch hydration) instead of the full list, or `?q=` to search by name, e-mail, or phone (phone normalised to E.164).',
  },
  workspaceList: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/contacts',
    pathParams: ListVisibleContactsPathParamsSchema,
    query: ListVisibleContactsQuerySchema,
    response: { 200: ListVisibleContactsResponseSchema },
    permission: 'workspace:relationship:contact-read',
    description:
      'List contacts across the Standorte the caller may see (their affiliated organisations + descendants), plus any contact assigned to the caller, newest-first. Pass `?organizationId=` to narrow to a single visible Standort and `?ids=` to restrict to a known set (batch hydration). `email` narrows to Kontakte holding exactly that address (case-insensitive), within the same visible set.',
  },
  workspaceGet: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/contacts/:id',
    pathParams: GetVisibleContactPathParamsSchema,
    response: { 200: GetVisibleContactResponseSchema },
    permission: 'workspace:relationship:contact-read',
    description:
      "Fetch a single contact by id within a workspace the caller is a member of, scoped to the same visible set as the workspace-addressed list — the contact's Standort is in the caller's visible set OR it is assigned to the caller — plus any contact linked to a deal, lead or property in that visible set (`accessOrganizationId` then names the Standort to address its organisation-scoped routes through). Unlike the organisation-addressed read, this resolves a contact whose home Standort is not the caller's selected one (IMM-20.4), so a contact that appears in the list never 404s when opened. 404 when the contact is outside the caller's visible scope.",
  },
  reassignStandort: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/contacts/:id/standort',
    pathParams: ReassignContactStandortPathParamsSchema,
    body: ReassignContactStandortBodySchema,
    response: { 200: ReassignContactStandortResponseSchema },
    permission: 'workspace:relationship:contact-reassign',
    description:
      "Move a contact across Standorte (IMM-20.4 AC22). Allowed when the caller is Owner/Workspace-Admin, or the contact's creator, or both the source and target Standort are within the caller's visible set; otherwise 403. The target must be an organisation in the same workspace. Contacts carry no creator column, so the creator path never applies.",
  },
  get: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/contacts/:id',
    pathParams: GetWorkspaceContactPathParamsSchema,
    response: { 200: GetWorkspaceContactResponseSchema },
    permission: 'organization:relationship:contact-read',
    description:
      'Return one contact under a workspace organisation the caller is a member of. 404 if the contact does not exist in that organisation.',
  },
  updateProfile: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/contacts/:id/profile',
    pathParams: UpdateContactProfilePathParamsSchema,
    body: UpdateContactProfileBodySchema,
    response: { 200: UpdateContactProfileResponseSchema },
    permission: 'organization:relationship:contact-update',
    description:
      'Replace the contact profile (PERSON or COMPANY block). Switching between PERSON and COMPANY drops the previous child row and rewrites the display name. `person.birthName` and `person.birthPlace` keep their stored value when omitted; send `null` to clear them.',
  },
  updateTypes: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/contacts/:id/types',
    pathParams: UpdateContactTypesPathParamsSchema,
    body: UpdateContactTypesBodySchema,
    response: { 200: UpdateContactTypesResponseSchema },
    permission: 'organization:relationship:contact-update',
    description: 'Replace the contact types multi-set (OWNER / BUYER / TENANT / LANDLORD / PARTNER).',
  },
  updateEmails: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/contacts/:id/emails',
    pathParams: UpdateContactEmailsPathParamsSchema,
    body: UpdateContactEmailsBodySchema,
    response: { 200: UpdateContactEmailsResponseSchema },
    permission: 'organization:relationship:contact-update',
    description:
      'Replace the contact emails collection wholesale (DELETE + INSERT in a single TX). Same primary auto-promote rule as create.',
  },
  updatePhones: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/contacts/:id/phones',
    pathParams: UpdateContactPhonesPathParamsSchema,
    body: UpdateContactPhonesBodySchema,
    response: { 200: UpdateContactPhonesResponseSchema },
    permission: 'organization:relationship:contact-update',
    description:
      'Replace the contact phones collection wholesale (DELETE + INSERT in a single TX). Same primary auto-promote rule as create.',
  },
  updateAddress: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/contacts/:id/address',
    pathParams: UpdateContactAddressPathParamsSchema,
    body: UpdateContactAddressBodySchema,
    response: { 200: UpdateContactAddressResponseSchema },
    permission: 'organization:relationship:contact-update',
    description: 'Replace or clear (when body.address is null) the contact address row.',
  },
  updateWarningNote: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/contacts/:id/warning-note',
    pathParams: UpdateContactWarningNotePathParamsSchema,
    body: UpdateContactWarningNoteBodySchema,
    response: { 200: UpdateContactWarningNoteResponseSchema },
    permission: 'organization:relationship:contact-update',
    description: 'Replace the contact warning-note banner (null to clear).',
  },
  recordNote: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/contacts/:id/notes',
    pathParams: RecordContactNotePathParamsSchema,
    body: RecordContactNoteBodySchema,
    response: { 200: RecordContactNoteResponseSchema },
    permission: 'organization:relationship:contact-update',
    description:
      'Record a note (Notiz) against a contact and publish relationship.contact-note-recorded, which the activity module projects onto the contact timeline. occurredAt defaults to now; refuses empty / over-long content (≤ 10,000 chars) and a future occurredAt (1-minute skew). 404 when the contact is not in that workspace + organisation.',
  },
  recordCommunication: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/contacts/:id/communications',
    pathParams: RecordContactCommunicationPathParamsSchema,
    body: RecordContactCommunicationBodySchema,
    response: { 200: RecordContactCommunicationResponseSchema },
    permission: 'organization:relationship:contact-update',
    description:
      'Record a communication (call / SMS / WhatsApp / fax / letter) against a contact and publish relationship.contact-communication-recorded, which the activity module projects onto the contact timeline with a direction-aware title. Direction is required; subject applies to fax/letter and duration to call (ignored otherwise). occurredAt defaults to now; refuses empty / over-long content and a future occurredAt (1-minute skew). 404 when the contact is not in that workspace + organisation.',
  },
  amendNote: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/contacts/:id/notes/:noteId',
    pathParams: AmendContactNotePathParamsSchema,
    body: AmendContactNoteBodySchema,
    response: { 200: AmendContactNoteResponseSchema },
    permission: 'organization:relationship:contact-note-update',
    description:
      'Amend a recorded note and publish relationship.contact-note-updated, which the activity timeline projects as a new content version ("bearbeitet") and re-sorts by occurredAt. Re-validates content (≤ 10,000 chars) and the future-date guard. 404 when the contact or note is not in that workspace + organisation.',
  },
  amendCommunication: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/contacts/:id/communications/:communicationId',
    pathParams: AmendContactCommunicationPathParamsSchema,
    body: AmendContactCommunicationBodySchema,
    response: { 200: AmendContactCommunicationResponseSchema },
    permission: 'organization:relationship:contact-communication-update',
    description:
      'Amend a recorded communication and publish relationship.contact-communication-updated, which the activity timeline projects as a new content version ("bearbeitet"). The type is immutable; subject applies to fax/letter and duration to call. Re-validates as at record time. 404 when the contact or communication is not in that workspace + organisation.',
  },
  deleteNote: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/contacts/:id/notes/:noteId',
    pathParams: DeleteContactNotePathParamsSchema,
    response: { 200: DeleteContactNoteResponseSchema },
    permission: 'organization:relationship:contact-note-delete',
    description:
      'Hard-delete a recorded note and publish relationship.contact-note-deleted, which the activity timeline removes the projected entry. Restricted to the recorder or an Owner/Admin (enforced by the role→permission config). 404 when the contact or note is not in that workspace + organisation.',
  },
  deleteCommunication: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/contacts/:id/communications/:communicationId',
    pathParams: DeleteContactCommunicationPathParamsSchema,
    response: { 200: DeleteContactCommunicationResponseSchema },
    permission: 'organization:relationship:contact-communication-delete',
    description:
      'Hard-delete a recorded communication and publish relationship.contact-communication-deleted, which the activity timeline removes the projected entry. Restricted to the recorder or an Owner/Admin (enforced by the role→permission config). 404 when the contact or communication is not in that workspace + organisation.',
  },
  delete: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/contacts/:id',
    pathParams: DeleteContactPathParamsSchema,
    response: { 200: DeleteContactResponseSchema },
    permission: 'organization:relationship:contact-delete',
    description:
      'Hard-delete a contact. FK CASCADE removes every child row in the same TX. 404 when the contact does not exist in that workspace + organisation.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). The
 * inferred type of that literal had grown past the limit `tsc` will serialize
 * into a `.d.ts`, so adding a single route to ANY group failed the build with
 * TS7056. A named group is emitted as a `typeof` reference instead of being
 * inlined whole — the same shape `valuationContracts` already uses.
 *
 * Typing is unchanged: `as const satisfies Record<string, EndpointDefinition>`
 * keeps every leaf's literal method / path / permission and its exact schemas,
 * which the route-builder's request inference depends on.
 */
const organizationsContracts = {
  create: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/organizations',
    pathParams: CreateOrganizationPathParamsSchema,
    body: CreateOrganizationBodySchema,
    response: { 200: CreateOrganizationResponseSchema },
    permission: 'workspace:identity:organization-create',
    description:
      'Create a subsidiary organisation under a workspace the caller is a member of. Rejects with identity.workspace.membership-not-found (404) when the caller does not belong to the workspace.',
  },
  // Full org tree scoped to the calling member's access — orgs the member holds
  // a role in PLUS all their descendants (BFS). For admin/owner surfaces:
  // Standorte settings page, member invite/edit pickers. Gated by
  // workspace:identity:organizations which is NOT implicit — only workspace-owners
  // and admins carry it (checked by the navbar Standorte link).
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/organizations',
    pathParams: ListOrganizationTreePathParamsSchema,
    response: { 200: ListOrganizationTreeResponseSchema },
    permission: 'workspace:identity:organizations-read',
    // Published verbatim by IMM-751.1 — counters, hierarchy, prefix and the
    // internal references included. The coverage read is not a substitute: it
    // carries no street, house number or city, and it is not even the same SET
    // of rows (it answers for the whole workspace, this one for the bound
    // member's engaged subtree).
    docs: 'PUBLIC',
    description:
      "List organisations the caller is engaged with (holds any org role in) plus all their descendants. Sub-orgs of an engaged org are visible without requiring a direct role in each child. Each row carries the location's postal address, its parent in the hierarchy, and the counts immoOS shows beside it. Read-only.\n\nThe result is scoped to the key's bound member, so it is not necessarily the whole workspace: the Coverage read answers with every location and this one with the member's engaged subtree, which coincide only for a member holding workspace-wide access. Only this read carries a postal address.\n\nThree fields are internal references with no published read behind them: `companyId` and `internalWebsite` name records this API does not expose, and `accessible` is always `true` here (it distinguishes context rows on an unpublished surface). They are present because the response is published verbatim rather than reshaped into a second surface to keep in step.",
  },
  // Flat list of orgs the calling member is directly affiliated with (role row in
  // member_organisation_roles). No BFS expansion. Implicit for all members — used
  // by the navbar org-switch to show the member's own Standorte.
  listMine: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/me/organizations',
    pathParams: ListMyOrganizationsPathParamsSchema,
    response: { 200: ListMyOrganizationsResponseSchema },
    permission: 'workspace:identity:my-organizations-read',
    description:
      "List the calling member's own affiliated organisations (holds a role in). Does not expand to sub-org descendants.",
  },
  get: {
    method: 'GET',
    path: '/v1/organizations/:organizationId',
    pathParams: GetWorkspaceOrganizationPathParamsSchema,
    response: { 200: GetWorkspaceOrganizationResponseSchema },
    permission: 'organization:identity:organization-read',
    description:
      'Return one organisation under a workspace the caller is a member of. 404 if the org does not exist or belongs to a different workspace.',
  },
  getMyPermissions: {
    method: 'GET',
    path: '/v1/me/organizations/:organizationId/permissions',
    pathParams: GetMyOrganizationPermissionsPathParamsSchema,
    response: { 200: GetMyOrganizationPermissionsResponseSchema },
    permission: 'organization:identity:organization-read',
    description:
      "Return the calling member's effective organisation-scoped permission literals for the addressed Standort. Used by the client hide-not-disable layer after org switch.",
  },
  update: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId',
    pathParams: UpdateWorkspaceOrganizationPathParamsSchema,
    body: UpdateWorkspaceOrganizationBodySchema,
    response: { 200: UpdateWorkspaceOrganizationResponseSchema },
    permission: 'organization:identity:organization-update',
    description: 'Update an organisation name under a workspace the caller is a member of.',
  },
  updateProfile: {
    method: 'PATCH',
    path: '/v1/organizations/:organizationId/profile',
    pathParams: UpdateOrganizationProfilePathParamsSchema,
    body: UpdateOrganizationProfileBodySchema,
    response: { 200: UpdateOrganizationProfileResponseSchema },
    permission: 'organization:identity:organization-update',
    description:
      "Update an organisation's operating model and legal-identity profile (operating model, legal form, company name, managing director, commercial register, VAT id, trade-permit/supervisory authority, professional indemnity insurance, content-responsible person, external/internal website). Optional fields may be cleared with null/empty. 400 on a value that violates its field rule (invalid VAT id, non-http(s) URL, out-of-list legal form or operating model).",
  },
  triggerPartnerOnboarding: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/organizations/onboardings',
    pathParams: TriggerPartnerOnboardingPathParamsSchema,
    body: TriggerPartnerOnboardingBodySchema,
    response: { 201: TriggerPartnerOnboardingResponseSchema },
    permission: 'workspace:identity:organization-create',
    description:
      'Trigger partner onboarding for a new Standort: create the partner Standort shell (operatingModel=PARTNER_COMPANY, setupStatus=IN_SETUP) plus its own referenced Company, open an onboarding record (status=invited) and publish the audited, IDs-only invited event. The magic-link invitation is sent asynchronously by the communications deliverable, which issues the 14-day link at send time and stores only its hash — the raw token is never persisted, logged, or returned. 400 on an invalid location-manager e-mail.',
  },
  // IMM-255.4 — operator review + decision on a partner submission. Workspace-
  // scoped, gated by organization:identity:organization-update (approver authority =
  // whoever may create a Standort). The onboarding is addressed by the Standort
  // (organization) id and verified for workspace tenancy.
  openOnboardingReview: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/organizations/:organizationId/onboarding/review',
    pathParams: OnboardingReviewPathParamsSchema,
    body: EmptyObjectSchema,
    response: { 200: OnboardingReviewResponseSchema },
    permission: 'organization:identity:organization-update',
    description:
      'Open the review surface on a partner submission: move the onboarding submitted → in_review (idempotent when already in_review). 404 when no onboarding exists for the organisation or it belongs to another workspace; 422 when the record is not submitted/in_review. Applies nothing to the Company.',
  },
  approveOnboarding: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/organizations/:organizationId/onboarding/approve',
    pathParams: OnboardingReviewPathParamsSchema,
    body: EmptyObjectSchema,
    response: { 200: OnboardingReviewResponseSchema },
    permission: 'organization:identity:organization-update',
    description:
      "Approve a partner submission: move the onboarding to approved (approvedAt set), apply the partner's proposed legal/identity + contact data onto the location's referenced Company, and complete the Standort setup (setupStatus=COMPLETED). 404 when no onboarding exists for the organisation or it belongs to another workspace; 422 when the record is not submitted/in_review.",
  },
  requestOnboardingChanges: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/organizations/:organizationId/onboarding/request-changes',
    pathParams: OnboardingReviewPathParamsSchema,
    body: RequestOnboardingChangesBodySchema,
    response: { 200: OnboardingReviewResponseSchema },
    permission: 'organization:identity:organization-update',
    description:
      'Request changes on a partner submission: move the onboarding to changes_requested, store the reviewer notes, and send the partner a change-request mail re-issuing their magic link with the notes. Writes nothing to the Company. 404 when no onboarding exists for the organisation or it belongs to another workspace; 422 when the record is not submitted/in_review.',
  },
  rejectOnboarding: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/organizations/:organizationId/onboarding/reject',
    pathParams: OnboardingReviewPathParamsSchema,
    body: EmptyObjectSchema,
    response: { 200: OnboardingReviewResponseSchema },
    permission: 'organization:identity:organization-update',
    description:
      'Reject a partner submission: move the onboarding to rejected (terminal). Writes nothing to the Company. 404 when no onboarding exists for the organisation or it belongs to another workspace; 422 when the record is not submitted/in_review.',
  },
  // IMM-267 — Einzugsgebiet (coverage). Org-addressed
  // (`/v1/organizations/:organizationId/postal-codes`): the workspace is derived
  // from the organisation by the gate. Reads reuse organization-read (implicit
  // for any member); writes need the workspace-scoped coverage-write permission
  // (owner/admin only).
  listPostalCodes: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/postal-codes',
    pathParams: ListCoveragePostalCodesPathParamsSchema,
    response: { 200: ListCoveragePostalCodesResponseSchema },
    permission: 'organization:identity:organization-read',
    description:
      "List the PLZs assigned to a Standort's Einzugsgebiet, ascending. Readable by any member of the workspace.",
  },
  assignPostalCodes: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/postal-codes',
    pathParams: AssignCoveragePostalCodesPathParamsSchema,
    body: AssignCoveragePostalCodesBodySchema,
    response: { 200: AssignCoveragePostalCodesResponseSchema },
    permission: 'workspace:identity:organization-coverage-write',
    description:
      "Bulk-assign PLZs to a Standort's Einzugsgebiet (partial success). Each PLZ is partitioned into assigned (newly added), alreadyPresent (already on this Standort), or conflicts (owned by a sibling Standort in the workspace, reported with the owner name — never taken over). A PLZ is workspace-unique across Standorte.",
  },
  removePostalCode: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/postal-codes/:postalCode',
    pathParams: RemoveCoveragePostalCodePathParamsSchema,
    response: { 200: RemoveCoveragePostalCodeResponseSchema },
    permission: 'workspace:identity:organization-coverage-write',
    description:
      "Release a PLZ from a Standort's Einzugsgebiet. 422 identity.coverage-postal-code.not-assigned when the PLZ is not assigned to that Standort.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const documentTemplatesContracts = {
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/document-templates',
    pathParams: DocumentTemplatesPathParamsSchema,
    response: { 200: ListDocumentTemplatesResponseSchema },
    permission: 'workspace:identity:document-template-read',
    description:
      "List the workspace's document templates (Vorlagen, IMM-270): name, category (BROKER_CONTRACT | POWER_OF_ATTORNEY), when it was last saved, and the member who created it. Readable by every member. Content is not included; read one template with `get`.",
  },
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/document-templates/:documentTemplateId',
    pathParams: DocumentTemplatePathParamsSchema,
    response: { 200: DocumentTemplateSchema },
    permission: 'workspace:identity:document-template-read',
    description:
      'Read one document template: its summary plus its content (a structured document of headings, paragraphs, lists, tables, page breaks and `dataField` nodes, each naming a catalogue key and whether generation needs a value) and its one-line footer. Readable by every member. 404 identity.document-template.not-found when it does not exist in this workspace.',
  },
  create: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/document-templates',
    pathParams: DocumentTemplatesPathParamsSchema,
    body: CreateDocumentTemplateBodySchema,
    response: { 200: DocumentTemplateSchema },
    permission: 'workspace:identity:document-template-create',
    description:
      'Create a document template (IMM-270). Owner / Admin only. `name` is 1–80 characters and unique within the workspace and category, compared case-insensitively (409 identity.document-template.name-taken). `content` is the structured document `get` returns; every `dataField` must name a catalogue key offered for the category — the MANDATE_* keys exist for BROKER_CONTRACT only (422 identity.document-template.data-field-not-allowed). `footer` is one line of at most 200 characters, "" for none.',
  },
  update: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/document-templates/:documentTemplateId',
    pathParams: DocumentTemplatePathParamsSchema,
    body: UpdateDocumentTemplateBodySchema,
    response: { 200: DocumentTemplateSchema },
    permission: 'workspace:identity:document-template-update',
    description:
      "Replace a document template's content and footer. Owner / Admin only. The category and name do not change. Same content rules as `create`. Applies to documents generated afterwards; documents already generated are not affected. 404 identity.document-template.not-found.",
  },
  delete: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/document-templates/:documentTemplateId',
    pathParams: DocumentTemplatePathParamsSchema,
    response: { 204: zod.void() },
    permission: 'workspace:identity:document-template-delete',
    description:
      'Delete a document template. Owner / Admin only. Documents already generated from it are not affected. 404 identity.document-template.not-found when it does not exist in this workspace.',
  },
  suggestFields: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/document-templates/field-suggestions',
    pathParams: DocumentTemplatesPathParamsSchema,
    body: SuggestDocumentTemplateFieldsBodySchema,
    response: { 200: SuggestDocumentTemplateFieldsResponseSchema },
    permission: 'workspace:identity:document-template-create',
    description:
      "Suggest where a document template imported from a Word file needs data fields (IMM-270). Owner / Admin only. The caller sends the document's text as numbered blocks (paragraphs and tables); an AI model proposes, per passage that holds data or per empty table cell labelled by its row and column, a catalogue key offered for the category. The server keeps only suggestions whose passage occurs verbatim in the sent text, whose cell is empty, and whose key is in the catalogue, and returns them as offsets. Nothing is stored, and the text is sent to the model only for this answer. `outcome` is FAILED when no part of the document could be analysed; the caller then continues without suggestions.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const buyerContractTemplatesContracts = {
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/buyer-contract-templates',
    pathParams: BuyerContractTemplatesPathParamsSchema,
    response: { 200: ListBuyerContractTemplatesResponseSchema },
    permission: 'workspace:deal:buyer-contract-template-read',
    description:
      "List the workspace's Käufer-Maklervertrag Vorlagen: the immoOS standard always, the workspace's own Vorlage when it has one. Exactly one is active. Readable by every member. Blocks are not included; read one with `get`.",
  },
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/buyer-contract-templates/:buyerContractTemplateId',
    pathParams: BuyerContractTemplatePathParamsSchema,
    response: { 200: BuyerContractTemplateSchema },
    permission: 'workspace:deal:buyer-contract-template-read',
    description:
      "Read one Käufer-Maklervertrag Vorlage: its summary plus its three blocks (contract, withdrawal notice, withdrawal form). Readable by every member. 404 deal.buyer-contract-template.not-found when the id is neither the standard nor this workspace's own Vorlage.",
  },
  create: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/buyer-contract-templates',
    pathParams: BuyerContractTemplatesPathParamsSchema,
    body: CreateBuyerContractTemplateBodySchema,
    response: { 201: BuyerContractTemplateSchema },
    permission: 'workspace:deal:buyer-contract-template-create',
    description:
      "Adapt the immoOS standard into the workspace's own Käufer-Maklervertrag Vorlage (IMM-1431.2): saves version 1 and makes it active at once. Owner / Admin only. `name` is 1–80 characters. 409 deal.buyer-contract-template.already-adapted when the workspace already has its own Vorlage; 422 deal.buyer-contract-template.cannot-save-block-empty (details.blocks) when a block has no text and no data field.",
  },
  update: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/buyer-contract-templates/:buyerContractTemplateId',
    pathParams: BuyerContractTemplatePathParamsSchema,
    body: UpdateBuyerContractTemplateBodySchema,
    response: { 200: BuyerContractTemplateSchema },
    permission: 'workspace:deal:buyer-contract-template-update',
    description:
      "Save the next version of the workspace's own Käufer-Maklervertrag Vorlage (IMM-1431.2). Owner / Admin only. The name and the active Vorlage do not change. 404 deal.buyer-contract-template.not-found when the id is not the workspace's own live Vorlage (the standard included); 422 deal.buyer-contract-template.cannot-save-block-empty (details.blocks).",
  },
  activate: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/buyer-contract-templates/:buyerContractTemplateId/activate',
    pathParams: BuyerContractTemplatePathParamsSchema,
    response: { 200: ListBuyerContractTemplatesResponseSchema },
    permission: 'workspace:deal:buyer-contract-template-update',
    description:
      "Make one Käufer-Maklervertrag Vorlage the one the workspace sends (IMM-1431.5): the workspace's own Vorlage, or the immoOS standard by its fixed id. Owner / Admin only. No text changes. Activating the active one changes nothing. Returns the list. 404 deal.buyer-contract-template.not-found when the id is neither the standard nor the workspace's own live Vorlage.",
  },
  delete: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/buyer-contract-templates/:buyerContractTemplateId',
    pathParams: BuyerContractTemplatePathParamsSchema,
    response: { 204: zod.void() },
    permission: 'workspace:deal:buyer-contract-template-delete',
    description:
      "Delete the workspace's own Käufer-Maklervertrag Vorlage (IMM-1431.5). Afterwards the standard is active. Owner / Admin only. Its versions are kept, so sends and concluded contracts keep their wording. 404 deal.buyer-contract-template.not-found when the id is not the workspace's own live Vorlage (the standard included).",
  },
} as const satisfies Record<string, EndpointDefinition>;

const emailTemplatesContracts = {
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/email-templates',
    pathParams: EmailTemplatesPathParamsSchema,
    response: { 200: ListEmailTemplatesResponseSchema },
    permission: 'workspace:identity:email-template-read',
    description:
      "List the workspace's e-mail templates (E-Mail-Vorlagen, IMM-1177): name, area (BUYER | OWNER | GENERAL), subject, when it was last saved and by whom (null for a starter template no member has saved). Readable by every member. The body is not included; read one template with `get`.",
  },
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/email-templates/:emailTemplateId',
    pathParams: EmailTemplatePathParamsSchema,
    response: { 200: EmailTemplateSchema },
    permission: 'workspace:identity:email-template-read',
    description:
      'Read one e-mail template: its summary plus its body, sanitised HTML in which each placeholder is `<span data-placeholder="KEY"></span>` and each fill-in field `<span data-fill-field="label"></span>`. Readable by every member. 404 identity.email-template.not-found when it does not exist in this workspace.',
  },
  create: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/email-templates',
    pathParams: EmailTemplatesPathParamsSchema,
    body: CreateEmailTemplateBodySchema,
    response: { 200: EmailTemplateSchema },
    permission: 'workspace:identity:email-template-create',
    description:
      'Create an e-mail template (IMM-1177). Owner / Admin only. `name` is 1–80 characters and unique within the workspace, compared case-insensitively (409 identity.email-template.name-taken). `subject` and `body` are HTML; the server sanitises them (the body keeps bold, italic, underline, lists, links and line breaks; the subject keeps text only) and normalises chips to `<span data-placeholder="KEY"></span>` / `<span data-fill-field="label"></span>`. Each must hold visible text or a chip; the subject at most 200 and the body at most 20000 visible characters (400). A placeholder key outside the list is refused (422 identity.email-template.placeholder-unknown); a fill-in label must be 1–40 characters (400).',
  },
  update: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/email-templates/:emailTemplateId',
    pathParams: EmailTemplatePathParamsSchema,
    body: UpdateEmailTemplateBodySchema,
    response: { 200: EmailTemplateSchema },
    permission: 'workspace:identity:email-template-update',
    description:
      "Replace an e-mail template's name, area, subject and body. Owner / Admin only. Same rules as `create`. E-mails already written keep their text. 404 identity.email-template.not-found.",
  },
  delete: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/email-templates/:emailTemplateId',
    pathParams: EmailTemplatePathParamsSchema,
    response: { 204: zod.void() },
    permission: 'workspace:identity:email-template-delete',
    description:
      'Delete an e-mail template. Owner / Admin only. E-mails already sent are not affected. 404 identity.email-template.not-found when it does not exist in this workspace.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const authContracts = {
  signIn: {
    method: 'GET',
    path: '/v1/auth/sign-in',
    query: SignInQuerySchema,
    response: { 200: SignInResponseSchema },
    permission: 'PUBLIC',
    description: 'Build a provider-aware AuthKit authorization URL for the client to navigate to.',
  },
  callback: {
    method: 'POST',
    path: '/v1/auth/callback',
    body: CallbackBodySchema,
    response: { 200: CallbackResponseSchema },
    permission: 'PUBLIC',
    description: 'Exchange an AuthKit code for an access token; seal the refresh token into the API cookie.',
  },
  refresh: {
    method: 'POST',
    path: '/v1/auth/refresh',
    // Empty body: web clients ride the refresh cookie; mobile clients
    // would carry the refresh token in the body, modelled when the
    // mobile-app spec lands. Required `X-Immo-Client` header is the
    // CSRF belt and is documented at the handler, not on the contract
    // (headers are not part of the walked surface).
    body: EmptyObjectSchema,
    response: { 200: RefreshResponseSchema },
    permission: 'PUBLIC',
    description: 'Rotate the access token using the refresh-token cookie or body (mobile).',
  },
  signOut: {
    method: 'POST',
    path: '/v1/auth/sign-out',
    body: EmptyObjectSchema,
    response: { 200: SignOutResponseSchema },
    permission: 'PUBLIC',
    description:
      'Clear the refresh cookie and return the WorkOS logout URL. Idempotent — runs without a session so an already-signed-out client never bounces against the gate.',
  },
  me: {
    method: 'GET',
    path: '/v1/auth/me',
    response: { 200: MeResponseSchema },
    permission: 'workspace:identity:profile-read',
    description: 'Return the authenticated user + their organisation memberships (empty in IMM-27 until IMM-22 lands).',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Sandbox sign-in (IMM-1084.3). Registered only under SANDBOX_MODE; no leaf
 * carries `docs`, so none reaches the public OpenAPI document.
 */
const sandboxAuthContracts = {
  gate: {
    method: 'GET',
    path: '/v1/auth/sandbox/gate',
    response: { 200: SandboxGateResponseSchema },
    permission: 'PUBLIC',
    description:
      'Sandbox only (IMM-1084); not registered outside SANDBOX_MODE. Report whether the caller holds a valid gate cookie, which account it was granted to, and how: `via` is `google` for the immoOS Google gate, `agent` for the agent token (IMM-1309), `null` without a gate.',
  },
  gateStart: {
    method: 'GET',
    path: '/v1/auth/sandbox/gate/start',
    query: SandboxGateStartQuerySchema,
    response: { 200: SandboxGateStartResponseSchema },
    permission: 'PUBLIC',
    description:
      'Sandbox only (IMM-1084); not registered outside SANDBOX_MODE. Build the Google authorization URL for the immoOS Workspace gate and seal the OIDC round-trip cookie.',
  },
  gateCallback: {
    method: 'POST',
    path: '/v1/auth/sandbox/gate/callback',
    body: SandboxGateCallbackBodySchema,
    response: { 200: SandboxGateCallbackResponseSchema },
    permission: 'PUBLIC',
    description:
      'Sandbox only (IMM-1084); not registered outside SANDBOX_MODE. Redeem the Google code and, for a verified immoos.ai Workspace account, seal the 8-hour gate cookie. 403 identity.sandbox-gate.account-not-authorised for any other account; 401 identity.auth.sign-in-failed when the round-trip is broken.',
  },
  gateAgent: {
    method: 'POST',
    path: '/v1/auth/sandbox/gate/agent',
    body: EmptyObjectSchema,
    response: { 200: SandboxGateCallbackResponseSchema },
    permission: 'PUBLIC',
    description:
      'Sandbox only (IMM-1309); registered only under SANDBOX_MODE with SANDBOX_AGENT_TOKEN and SANDBOX_AGENT_MEMBER_EMAIL set. With `Authorization: Bearer <SANDBOX_AGENT_TOKEN>`, seal the 8-hour gate cookie for the review member (SANDBOX_AGENT_MEMBER_EMAIL); a session behind it may sign in as that member only. 401 identity.sandbox-gate.required on a missing or wrong bearer; 429 identity.sandbox-gate.rate-limited past 10 attempts a minute.',
  },
  members: {
    method: 'GET',
    path: '/v1/auth/sandbox/members',
    response: { 200: SandboxMembersResponseSchema },
    permission: 'PUBLIC',
    description:
      'Sandbox only (IMM-1084); not registered outside SANDBOX_MODE. Behind the gate cookie, list every user with at least one membership, to sign in as. 401 identity.sandbox-gate.required without a valid gate.',
  },
  session: {
    method: 'POST',
    path: '/v1/auth/sandbox/session',
    body: SandboxSessionBodySchema,
    response: { 200: SandboxSessionResponseSchema },
    permission: 'PUBLIC',
    description:
      'Sandbox only (IMM-1084); not registered outside SANDBOX_MODE. Behind the gate cookie, mint an immoOS session as the member with the given e-mail and seal the refresh cookie. Requires the X-Immo-Client header. 401 identity.sandbox-gate.required without a valid gate; 404 identity.sandbox-member.not-found when no member has the e-mail.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const organizationOnboardingContracts = {
  requestSessionCode: {
    method: 'POST',
    path: '/v1/organization-onboarding/session/code',
    body: RequestOnboardingSessionCodeBodySchema,
    response: { 200: RequestOnboardingSessionCodeResponseSchema },
    permission: 'PUBLIC',
    description:
      'Step 1 of the magic-link session exchange (ADR 0030): verify the token hash-only and ask WorkOS to e-mail a one-time Magic Auth code to the onboarding location manager. Returns { onboardingId, branding } — no access token yet. 422 on an unknown or expired token. The raw token is verified here only — never stored, logged, or returned.',
  },
  exchangeSession: {
    method: 'POST',
    path: '/v1/organization-onboarding/session',
    body: ExchangeOnboardingSessionBodySchema,
    response: { 200: ExchangeOnboardingSessionResponseSchema },
    permission: 'PUBLIC',
    description:
      'Step 2 of the magic-link session exchange (ADR 0030): verify the token hash-only and redeem the WorkOS Magic Auth code e-mailed in step 1, minting a session for the onboarding location manager. Returns { accessToken, onboardingId } in memory and seals the refresh token into the __Secure-immoos_onboarding_refresh cookie. 422 on an unknown/expired token or an invalid/expired code. The raw token is verified here only — never stored, logged, or returned.',
  },
  refreshSession: {
    method: 'POST',
    path: '/v1/organization-onboarding/session/refresh',
    // Empty body: the partner rides the __Secure-immoos_onboarding_refresh
    // cookie sealed at exchange. Required `X-Immo-Client` header is the CSRF
    // belt, enforced at the handler (headers are not part of the walked surface).
    body: EmptyObjectSchema,
    response: { 200: RefreshOnboardingSessionResponseSchema },
    permission: 'PUBLIC',
    description:
      'Rotate a partner onboarding access token using the onboarding refresh cookie (the member /v1/auth/refresh rejects partners, who hold no workspace membership until approval). Authorises by onboarding ownership: the refreshed WorkOS user must own an onboarding as its location manager. Returns { accessToken }; re-seals the rotated refresh token into the __Secure-immoos_onboarding_refresh cookie. 401 when the cookie is missing/rejected; 422 when the user owns no onboarding.',
  },
  me: {
    method: 'GET',
    path: '/v1/organization-onboarding/:onboardingId',
    pathParams: OnboardingPathParamsSchema,
    response: { 200: OnboardingMeResponseSchema },
    permission: 'organization:identity:partner-onboard',
    description:
      "Read the onboarding state (status, location manager, consents, submittedAt) for the onboarding id in the path. The bearer-authenticated user must be that onboarding's location manager. 401 without a bearer; 404 when the id is unknown; 403 when the user is not the location manager or the onboarding is not editable. Never returns the magic-link token hash.",
  },
  start: {
    method: 'PATCH',
    path: '/v1/organization-onboarding/:onboardingId/start',
    pathParams: OnboardingPathParamsSchema,
    body: EmptyObjectSchema,
    response: { 200: StartOnboardingResponseSchema },
    permission: 'organization:identity:partner-onboard',
    description:
      'Move the partner onboarding invited → in_progress (first wizard step saved); a second start while already in_progress is a benign no-op. 401 without a bearer; 404 when the id is unknown; 403 when the user is not the location manager or the onboarding is not editable.',
  },
  saveDraft: {
    method: 'PATCH',
    path: '/v1/organization-onboarding/:onboardingId/draft',
    pathParams: OnboardingPathParamsSchema,
    body: SaveDraftBodySchema,
    response: { 200: OnboardingMeResponseSchema },
    permission: 'organization:identity:partner-onboard',
    description:
      "Save one wizard step as a draft (IMM-257). Verifies the step's draft schema (vatId/website format guarded only when provided), REPLACES the parked draft company-data with the partial payload, and advances the non-regressing resume pointer (currentStep). Moves invited|changes_requested → in_progress; an in_progress record stays in_progress. Timestamps are server-set. 401 without a bearer; 404 when the id is unknown; 403 when the user is not the location manager or the onboarding is not editable; 400 on a bad vatId/website format (body validation). No partner data is applied to the Company/Standort.",
  },
  submit: {
    method: 'POST',
    path: '/v1/organization-onboarding/:onboardingId/submit',
    pathParams: OnboardingPathParamsSchema,
    body: SubmitOnboardingBodySchema,
    response: { 200: SubmitOnboardingResponseSchema },
    permission: 'organization:identity:partner-onboard',
    description:
      'Submit the partner onboarding in_progress → submitted with revision-safe consents (each stored with a server-set timestamp + the granting location manager). Sets submittedAt server-side and notifies the workspace operators. 401 without a bearer; 404 when the id is unknown; 403 when the user is not the location manager or the onboarding is not editable; 422 when the record is not in in_progress. No partner data is applied to the Company/Standort.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const membersContracts = {
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/members',
    pathParams: ListWorkspaceMembersPathParamsSchema,
    query: ListWorkspaceMembersQuerySchema,
    response: { 200: ListWorkspaceMembersResponseSchema },
    permission: 'workspace:identity:member-read',
    description:
      'List members of a workspace the caller belongs to. `?status=active` (default), `deactivated`, or `all`. Owner first, then newest-first.',
  },
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/members/:id',
    pathParams: GetWorkspaceMemberPathParamsSchema,
    response: { 200: GetWorkspaceMemberResponseSchema },
    permission: 'workspace:identity:member-read',
    description:
      'Return one member of a workspace the caller belongs to. 404 if the member id does not exist or belongs to a different workspace.',
  },
  create: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/members',
    pathParams: CreateWorkspaceMemberPathParamsSchema,
    body: CreateWorkspaceMemberBodySchema,
    response: { 200: CreateWorkspaceMemberResponseSchema },
    permission: 'workspace:identity:member-create',
    description:
      'Add a member to the workspace. Creates an internal `users` row in status=invited (linked to the IdP on first sign-in) if no user exists for the email. 409 if the email is already a member of this workspace.',
  },
  // The members of ONE Standort (IMM-751.3), addressed BY that Standort. The
  // address is the boundary: a `?standortId=` filter on the workspace roster
  // would be a convenience the caller may omit, and a boundary that can be
  // omitted is not a boundary. There are no query parameters at all.
  listInOrganisation: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/members',
    pathParams: ListStandortMembersPathParamsSchema,
    response: { 200: ListStandortMembersResponseSchema },
    permission: 'organization:identity:location-member-read',
    docs: 'PUBLIC',
    description:
      'List the active members of one location, with the details a personalised item needs. The location IS the address — there is no filter to omit — so a key reaches exactly the locations the member it is bound to holds a role in.\n\nEvery location id your key does not reach — one that does not exist, one in another workspace, and one inside your own workspace that the key\'s member holds no role in — is answered with the same 404 carrying `"code": "organization.not-found"`. Deliberately one answer, so the API never confirms which location ids are real. A 403 on this route means the opposite: the key reaches the location, and was issued without the grant this read needs. Neither is a fault to retry.\n\nDeactivated members are absent. `academicTitle`, `position` and `mobile` are always present and are `null` when nobody has recorded them — check them before you print rather than assuming a value, and expect `firstName`/`lastName` to be empty for a member who has been invited but has never signed in.',
  },
  createInOrganisation: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/members',
    pathParams: CreateOrganisationMemberPathParamsSchema,
    body: CreateOrganisationMemberBodySchema,
    response: { 200: CreateOrganisationMemberResponseSchema },
    permission: 'organization:identity:member-create',
    description:
      "Invite a member into a single organisation (org-addressed). The new member is affiliated to the path organisation as their primary Standort. Lets a Standort-owner seat members in their own location without workspace authority; the anti-escalation guard still bounds the granted role to the actor's permissions in that organisation. 409 if the email is already a member of the organisation's workspace.",
  },
  update: {
    method: 'PATCH',
    path: '/v1/workspaces/:workspaceId/members/:id',
    pathParams: UpdateWorkspaceMemberPathParamsSchema,
    body: UpdateWorkspaceMemberBodySchema,
    response: { 200: UpdateWorkspaceMemberResponseSchema },
    permission: 'workspace:identity:member-update',
    description:
      'Update a member profile (email, first name, last name). Email change propagates to the IdP when the user has signed in at least once.',
  },
  deactivate: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/members/:id/deactivate',
    pathParams: DeactivateWorkspaceMemberPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: DeactivateWorkspaceMemberResponseSchema },
    permission: 'workspace:identity:member-update',
    description:
      'Deactivate a member. The member loses workspace access on their next request. 422 with identity.member.cannot-deactivate-self when the caller tries to deactivate themselves; 422 with identity.member.already-deactivated when already inactive.',
  },
  reactivate: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/members/:id/reactivate',
    pathParams: ReactivateWorkspaceMemberPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: ReactivateWorkspaceMemberResponseSchema },
    permission: 'workspace:identity:member-update',
    description:
      'Reactivate a deactivated member. 422 with identity.member.already-active when the member is already active.',
  },
  changeOrganisationRole: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/members/:memberId/role',
    pathParams: ChangeMemberOrganisationRolePathParamsSchema,
    body: ChangeMemberOrganisationRoleBodySchema,
    response: { 200: ChangeMemberOrganisationRoleResponseSchema },
    permission: 'organization:identity:member-role-assign',
    description:
      "Set a member's organisation role in the addressed organisation (org-addressed). The new role replaces the member's existing roles in that organisation. 404 when the member does not belong to the organisation's workspace. 403 when the actor would grant a role beyond its own permissions in the organisation, or targets its own organisation role.",
  },
  setOrganisationRoles: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/members/:memberId/roles',
    pathParams: SetMemberOrganisationRolesPathParamsSchema,
    body: SetMemberOrganisationRolesBodySchema,
    response: { 200: SetMemberOrganisationRolesResponseSchema },
    permission: 'organization:identity:member-role-assign',
    description:
      "Bulk-replace all of a member's organisation roles (multi-role). Accepts 1–N role slugs; all existing roles for the member in the addressed organisation are removed and replaced. Same authority rules as changeOrganisationRole apply.",
  },
  setLeadRecipient: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/members/:memberId/lead-recipient',
    pathParams: SetLeadRecipientPathParamsSchema,
    body: SetLeadRecipientBodySchema,
    response: { 200: SetLeadRecipientResponseSchema },
    permission: 'organization:identity:member-role-assign',
    description:
      'Flag or unflag a member as an automatic recipient of incoming leads routed to the addressed Standort (\u201eErh\u00e4lt neue Leads\u201c). PUT, not PATCH: the body is a single total boolean, so the write sets the desired state and is never a toggle \u2014 replaying it lands on the same state and two concurrent writes of the same value cannot race to opposite results. Configuration only: no existing lead is reassigned and no e-mail is sent. 404 identity.member.not-found when the member holds no role in the addressed Standort. Same authority rules as setOrganisationRoles.',
  },
  removeOrganisationRoles: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/members/:memberId/roles',
    pathParams: RemoveMemberOrganisationRolesPathParamsSchema,
    response: { 200: RemoveMemberOrganisationRolesResponseSchema },
    permission: 'organization:identity:member-role-assign',
    description:
      "Remove a member from the addressed organisation: delete every role the member holds there, dropping its Standortzugehörigkeit (derived from those role rows). Clears the member's primary Standort when it pointed to this organisation. Idempotent — a no-op when the member holds no role in the organisation. Same authority rules as setOrganisationRoles; 422 identity.member.cannot-remove-self when the caller targets its own membership.",
  },
  changeWorkspaceRole: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/members/:memberId/workspace-role',
    pathParams: ChangeMemberWorkspaceRolePathParamsSchema,
    body: ChangeMemberWorkspaceRoleBodySchema,
    response: { 200: ChangeMemberWorkspaceRoleResponseSchema },
    permission: 'workspace:identity:workspace-role-assign',
    description:
      "Set a member's user workspace role (workspace-addressed). The new role replaces the user's existing workspace roles. 404 when the member does not belong to the workspace. 403 when the actor would grant a role beyond its own workspace permissions, grant global access it does not itself hold, target its own workspace role, or demote the last member holding global access (identity.member.workspace-must-retain-owner).",
  },
  removeWorkspaceRole: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/members/:memberId/workspace-role',
    pathParams: RemoveMemberWorkspaceRolePathParamsSchema,
    response: { 200: RemoveMemberWorkspaceRoleResponseSchema },
    permission: 'workspace:identity:workspace-role-assign',
    description:
      "Remove a member's workspace-tier role entirely (workspace-addressed); the member keeps its organisation roles but holds no workspace role afterwards. 404 when the member does not belong to the workspace. 403 when the actor targets its own role, the target outranks the actor, or it would leave the workspace with no member holding global access (identity.member.workspace-must-retain-owner).",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const invitationsContracts = {
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/invitations',
    pathParams: ListPendingInvitationsPathParamsSchema,
    response: { 200: ListPendingInvitationsResponseSchema },
    permission: 'workspace:identity:invitation-read',
    description:
      "List the workspace's pending invitations (invited email, assigned role, issuer, expiry) for the pending-invitations panel. Accepted, revoked, and expired invitations are excluded.",
  },
  resend: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/invitations/:id/resend',
    pathParams: ResendInvitationPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: ResendInvitationResponseSchema },
    permission: 'workspace:identity:invitation-resend',
    description:
      'Resend a pending invitation: revoke the previous link and issue a fresh one with a new 14-day expiry under a new invitation id. Old shared copies stop working. 404 when the invitation does not exist or belongs to another workspace; 422 when it is not pending.',
  },
  revoke: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/invitations/:id/revoke',
    pathParams: RevokeInvitationPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: RevokeInvitationResponseSchema },
    permission: 'workspace:identity:invitation-revoke',
    description:
      'Revoke a pending invitation without issuing a replacement; the link stops working and the invitation is recorded as revoked. 404 when the invitation does not exist or belongs to another workspace; 422 when it is not pending.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const memberProfilesContracts = {
  getMine: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/me/profile',
    pathParams: MyMemberProfilePathParamsSchema,
    response: { 200: MemberProfileResponseSchema },
    permission: 'workspace:identity:profile-read',
    description:
      "Return the calling member's own agent profile within a workspace they belong to. Optional fields are null/empty until the profile is maintained; firstName/lastName are the member's own (empty until set) and displayName is their composition, falling back to the e-mail while no name is set; e-mail is read-only (sourced from the member); location is the derived primary location (null until IMM-20 provides one).",
  },
  updateMine: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/me/profile',
    pathParams: MyMemberProfilePathParamsSchema,
    body: UpdateOwnMemberProfileBodySchema,
    response: { 200: MemberProfileResponseSchema },
    permission: 'workspace:identity:profile-update',
    description:
      "Create-or-update the calling member's own agent profile (first/last name, salutation, title, position, phone, mobile, bio, specialisations, languages, certifications, social links). The name is mandatory and is written through to the identity provider, which stays its source of truth. Optional fields may be cleared by sending null/empty. 400 on a value that violates its field rule (empty or > 80-char name, bio > 600, invalid phone, non-http(s) URL, out-of-enum specialisation/language/salutation).",
  },
  getByMember: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/members/:id/profile',
    pathParams: GetMemberProfilePathParamsSchema,
    response: { 200: MemberProfileResponseSchema },
    permission: 'workspace:identity:member-profile-read',
    docs: 'PUBLIC',
    description:
      "Return a colleague's agent profile within a workspace the caller belongs to — all fields are readable team-internally. `avatarMediaId` is the member's portrait; resolve it into a fetchable URL with the member-addressed avatar read (`GET .../members/{id}/avatar/read`) rather than treating it as a path segment of any media endpoint. 404 if the member id does not exist or belongs to a different workspace.",
  },
  updateByMember: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/members/:id/profile',
    pathParams: GetMemberProfilePathParamsSchema,
    body: UpdateMemberProfileBodySchema,
    response: { 200: MemberProfileResponseSchema },
    permission: 'workspace:identity:member-profile-update',
    description:
      "Owner/Admin create-or-update of another member's agent profile within the workspace (same fields + photo as self-edit, except the name — that is the member's own to set, and admin name edits stay on the member-update endpoint). Gated by workspace:identity:member-profile-update — a member without it gets the IMM-22 Forbidden response. 404 if the member id does not exist or belongs to a different workspace.",
  },
  createAvatarUpload: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/me/avatar/upload',
    pathParams: CreateMemberAvatarUploadPathParamsSchema,
    body: CreateMemberAvatarUploadBodySchema,
    response: { 200: CreateMediaUploadResponseSchema },
    permission: 'workspace:identity:profile-update',
    description:
      "Begin a direct-to-S3 upload for the calling member's profile photo. The server fixes the allowed content types (JPEG/PNG/WebP) and the 5 MB size cap — the caller supplies only the declared type, size, and an optional filename. Creates a pending media file scoped to the workspace and returns a short-lived presigned POST. 422 when the declared type or size violates the avatar policy. The client POSTs bytes straight to S3, then calls confirm-avatar-upload.",
  },
  confirmAvatarUpload: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/me/avatar/upload/:fileId/confirm',
    pathParams: ConfirmMemberAvatarUploadPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: ConfirmMediaUploadResponseSchema },
    permission: 'workspace:identity:profile-update',
    description:
      'Confirm a pending avatar upload: verifies the stored object format (magic bytes) matches the declared type and is within the 5 MB cap, flips the file to ready, and returns the media file. Idempotent on an already-ready file. After confirmation, pass the returned fileId to the profile-update endpoint to attach it. 422 when byte-level validation fails; 404 when the file does not exist in the workspace or no object was uploaded.',
  },
  readAvatar: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/me/avatar/:fileId/read',
    pathParams: ReadMemberAvatarPathParamsSchema,
    response: { 200: ReadMediaFileResponseSchema },
    permission: 'workspace:identity:profile-read',
    description:
      "Return a short-lived presigned GET URL for a profile photo stored in the workspace. Despite the `/me/` address the file is resolved by workspace and file id, NOT against the calling member — that is what lets the app render colleagues' portraits from one cached lookup, and correcting the wording here is part of IMM-603, whose reporter could not find this route precisely because its description denied what it does. It resolves PROFILE PHOTOS ONLY: a file that was not uploaded through the avatar upload answers 404 exactly as a missing one does (IMM-604), so this route cannot be used to reach documents, reports or gallery images that its implicit permission does not cover. The narrowing is by file kind, not by owner — any member's portrait still resolves. Prefer the member-addressed `readMemberAvatar` for anything driven by a member id. Same shape as the generic media read endpoint but gated by workspace:identity:profile-read (implicit for all members) rather than organization:media:file-read. 404 when the file does not exist in the workspace, is still pending, or is not a profile photo.",
  },
  readMemberAvatar: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/members/:id/avatar/read',
    pathParams: GetMemberProfilePathParamsSchema,
    response: { 200: ReadMediaFileResponseSchema },
    permission: 'workspace:identity:member-profile-read',
    docs: 'PUBLIC',
    description:
      "Return a short-lived presigned GET URL for a member's profile photo (IMM-603). The server resolves that member's current `avatarMediaId` itself, so a caller that can read the profile can open the portrait it names without a second round-trip — this is the reader for the `avatarMediaId` the profile endpoint publishes. Same body as the generic media read: `url`, `expiresInSeconds`, and the file's metadata. Deliberately takes NO file id: the id comes from the stored profile, so the endpoint cannot be pointed at an arbitrary media file. 404 — never 403 — when the member does not exist in that workspace, or carries no photo; the two are indistinguishable on purpose.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const memberPreferencesContracts = {
  getMine: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/me/preferences/:key',
    pathParams: MemberPreferencePathParamsSchema,
    response: { 200: GetMemberPreferenceResponseSchema },
    permission: 'workspace:identity:preferences-read',
    description:
      "Return the calling member's own stored value for a preference key, or null when they have never saved it. The member is resolved from the session — never client-supplied.",
  },
  saveMine: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/me/preferences/:key',
    pathParams: MemberPreferencePathParamsSchema,
    body: SaveMemberPreferenceBodySchema,
    response: { 200: SaveMemberPreferenceResponseSchema },
    permission: 'workspace:identity:preferences-update',
    description:
      "Create-or-replace the calling member's own stored value for a preference key so it survives across browsers. The value is stored as-is (arbitrary JSON). Sending `null` resets the key: the stored value is removed and the next read answers `null`, so the client falls back to its defaults. The member is resolved from the session — never client-supplied.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const memberInviteContracts = {
  requestSessionCode: {
    method: 'POST',
    path: '/v1/member-invite/session/code',
    body: RequestMemberInviteSessionCodeBodySchema,
    response: { 200: RequestMemberInviteSessionCodeResponseSchema },
    permission: 'PUBLIC',
    description:
      'Step 1 of the member-invite magic-link session exchange (ADR 0030): verify the invitation token hash-only and ask WorkOS to e-mail a one-time Magic Auth code to the invitee. Returns { workspaceName } — no access token yet. 422 on an unknown or expired token. The raw token is verified here only — never stored, logged, or returned.',
  },
  exchangeSession: {
    method: 'POST',
    path: '/v1/member-invite/session',
    body: ExchangeMemberInviteSessionBodySchema,
    response: { 200: ExchangeMemberInviteSessionResponseSchema },
    permission: 'PUBLIC',
    description:
      'Step 2 of the member-invite magic-link session exchange (ADR 0030): verify the token hash-only and redeem the WorkOS Magic Auth code e-mailed in step 1, minting a session for the invited member, activating their user, and accepting their pending invitation (which initiates the onboarding gate). Returns { accessToken } in memory and seals the refresh token into the __Secure-immoos_refresh cookie. 422 on an unknown/expired token or an invalid/expired code. The raw token is verified here only — never stored, logged, or returned.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const memberOnboardingContracts = {
  getMine: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/me/onboarding',
    pathParams: MemberOnboardingPathParamsSchema,
    response: { 200: GetMemberOnboardingResponseSchema },
    permission: 'workspace:identity:profile-read',
    description:
      "Return the calling member's onboarding lifecycle (status, resume step, recorded consent) within a workspace they belong to (IMM-271.2). The wizard loader reads it to resume at the stored step; the saved profile (get-my-profile) carries the prior field input.",
  },
  advanceStep: {
    method: 'PATCH',
    path: '/v1/workspaces/:workspaceId/me/onboarding',
    pathParams: MemberOnboardingPathParamsSchema,
    body: AdvanceMemberOnboardingStepBodySchema,
    response: { 200: AdvanceMemberOnboardingStepResponseSchema },
    permission: 'workspace:identity:profile-update',
    description:
      "Advance the calling member's onboarding resume marker to `currentStep` (IMM-271.2). Allowed only while the onboarding is in_progress; 422 identity.member-onboarding.invalid-transition otherwise.",
  },
  complete: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/me/onboarding/complete',
    pathParams: MemberOnboardingPathParamsSchema,
    body: CompleteMemberOnboardingBodySchema,
    response: { 200: CompleteMemberOnboardingResponseSchema },
    permission: 'workspace:identity:profile-update',
    description:
      "Complete the calling member's onboarding (IMM-271.2): the server-authoritative gate requires a title/position and a granted consent (the profile photo is optional). 422 identity.member-onboarding.incomplete-required-core listing the missing items when the gate fails; 422 identity.member-onboarding.invalid-transition when the onboarding is not in_progress. On success the consent is stamped revision-safe (granted + server timestamp + member e-mail + wording version), the onboarding is marked completed, and the member enters the app as Aktiv.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const companyOnboardingContracts = {
  getMine: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/company/onboarding',
    pathParams: CompanyOnboardingPathParamsSchema,
    response: { 200: GetCompanyOnboardingResponseSchema },
    permission: 'workspace:identity:company-read',
    description:
      'Return the workspace main-company onboarding (status, resume step, draft company data) for IMM-322. The wizard loader reads it to resume at the stored step and hydrate the form. Gated by company-read (workspace owner/admin); the entry gate itself branches off /auth/me companyOnboardingStatus, not this route. Only members with company-update may save or submit.',
  },
  saveDraft: {
    method: 'PATCH',
    path: '/v1/workspaces/:workspaceId/company/onboarding',
    pathParams: CompanyOnboardingPathParamsSchema,
    body: SaveCompanyOnboardingDraftBodySchema,
    response: { 200: CompanyOnboardingMeResponseSchema },
    permission: 'workspace:identity:company-update',
    description:
      'Save one company-onboarding wizard step as a draft (IMM-322): the partial company data REPLACES the parked draft and the resume pointer advances (never regresses). Allowed only while the onboarding is in_progress; 422 identity.company-onboarding.invalid-transition otherwise.',
  },
  submit: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/company/onboarding/submit',
    pathParams: CompanyOnboardingPathParamsSchema,
    body: SubmitCompanyOnboardingBodySchema,
    response: { 200: SubmitCompanyOnboardingResponseSchema },
    permission: 'workspace:identity:company-update',
    description:
      'Complete the workspace main-company onboarding (IMM-322): the server-authoritative gate requires the full legal + contact field set (HRB and website optional, IMM-325). 422 identity.company-onboarding.incomplete-required listing the missing items when the gate fails; 422 identity.company-onboarding.invalid-transition when not in_progress. On success the data is written directly onto the workspace main Company (legal fields + address) and the onboarding is marked completed — no review step.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const companyContracts = {
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/company',
    pathParams: CompanyPathParamsSchema,
    response: { 200: CompanyResponseSchema },
    permission: 'workspace:identity:company-read',
    description:
      "Return the workspace's single company record (identity + contact + address). Readable by every member of the workspace. Fields are null/empty until the record is maintained — consumers (the Exposé legal footer, IMM-220) degrade gracefully on missing parts.",
  },
  update: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/company',
    pathParams: CompanyPathParamsSchema,
    body: UpdateCompanyBodySchema,
    response: { 200: CompanyResponseSchema },
    permission: 'workspace:identity:company-update',
    description:
      "Create-or-update the workspace's single company record (company name, legal form, structured address linked to identity.addresses, phone, fax, email, website, social links). Owner/Admin only. Optional fields may be cleared with null/empty; the address is all-or-nothing. 400 on a value that violates its field rule (invalid email, non-http(s) URL, out-of-list legal form, incomplete address). privacyPolicyUrl must be an https URL of at most 300 characters; for privacyPolicyUrl, tradePermitAuthority and contentResponsiblePerson, unlike every other field, omitting the key keeps the stored value (null or empty clears it).",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The workspace's Web-Adresse lives behind its own pair, not on the company `PUT`: that is a full replace of a
 * company row, and the Web-Adresse is unique across every tenant (IMM-1161.1).
 */
const webAddressContracts = {
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/web-address',
    pathParams: WebAddressPathParamsSchema,
    response: { 200: WebAddressResponseSchema },
    permission: 'workspace:identity:company-read',
    description:
      "The workspace's Web-Adresse — the first path segment of every web exposé link — or null when none is chosen, plus a suggestion derived from the workspace name. Readable by every member of the workspace.",
  },
  update: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/web-address',
    pathParams: WebAddressPathParamsSchema,
    body: UpdateWebAddressBodySchema,
    response: { 200: WebAddressResponseSchema },
    permission: 'workspace:identity:company-update',
    description:
      'Set or change the Web-Adresse. Owner/Admin only. 422 identity.web-address.invalid-format for anything but 3–40 characters of a–z, 0–9 and inner single hyphens; 422 identity.web-address.taken when another workspace holds it or it is reserved. Saving the value the workspace already holds changes nothing. Changing it moves every web exposé link of the workspace to the new address at once; the old address answers as unavailable.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const brandingContracts = {
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/branding',
    pathParams: WorkspaceBrandingPathParamsSchema,
    response: { 200: WorkspaceBrandingResponseSchema },
    permission: 'workspace:identity:branding-read',
    description:
      "Return the workspace's single branding/CI record (house + document colours and role typography). Readable by every member of the workspace; fields are null/empty until maintained — consumers (the Exposé, IMM-220) degrade gracefully on missing parts. Logo variants (IMM-223.2) are not part of this payload yet.",
  },
  update: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/branding',
    pathParams: WorkspaceBrandingPathParamsSchema,
    body: UpdateWorkspaceBrandingBodySchema,
    response: { 200: WorkspaceBrandingResponseSchema },
    permission: 'workspace:identity:branding-update',
    description:
      "Create-or-update the workspace's single branding/CI record (six hex colour tokens, three role typefaces from the curated list, label casing + tracking, and the four logo/favicon media file ids). Owner/Admin only. Optional fields may be cleared with null/empty. A logo id must reference a ready media file in the workspace; replacing or clearing one deletes the previously-referenced media. 400 on a value that violates its field rule (invalid hex colour, off-list typeface, malformed tracking); 422 on a logo id that is not a ready file in the workspace.",
  },
  createLogoUpload: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/branding/logo-uploads',
    pathParams: CreateBrandingLogoUploadPathParamsSchema,
    body: CreateBrandingLogoUploadBodySchema,
    response: { 200: CreateMediaUploadResponseSchema },
    permission: 'workspace:identity:branding-update',
    description:
      'Begin a direct-to-S3 upload for a workspace logo or favicon. The server fixes the allowed content types by `kind` (logo → PNG/JPEG, favicon → PNG/ICO) and the 5 MB cap — the caller supplies only the declared type, size, and optional filename. SVG is not accepted in V1. Creates a pending media file scoped to the workspace and returns a short-lived presigned POST. Owner/Admin only. 422 when the declared type or size violates the policy. The client POSTs bytes straight to S3, then calls confirm-logo-upload and attaches the returned fileId via the branding update.',
  },
  confirmLogoUpload: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/branding/logo-uploads/:fileId/confirm',
    pathParams: ConfirmBrandingLogoUploadPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: ConfirmMediaUploadResponseSchema },
    permission: 'workspace:identity:branding-update',
    description:
      'Confirm a pending logo/favicon upload: verifies the stored object and flips the file to ready, returning the media file. Idempotent on an already-ready file. Owner/Admin only. 422 on byte-level validation failure; 404 when the file does not exist in the workspace or no object was uploaded.',
  },
  readLogo: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/branding/logos/:fileId/read',
    pathParams: ReadBrandingLogoPathParamsSchema,
    response: { 200: ReadMediaFileResponseSchema },
    permission: 'workspace:identity:branding-read',
    description:
      "Return a short-lived presigned GET URL for a workspace logo/favicon file. Gated by branding-read (implicit for all members) so every member can display the firm's logo. 404 when the file does not exist in the workspace or is still pending — the client shows the empty state (graceful degradation).",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
/**
 * Crossing into a linked application (IMM-751.2.1).
 *
 * DELIBERATELY NOT `docs: 'PUBLIC'`. This route is called by immoOS's own
 * signed-in web app, never by an integrator: it needs a member session, and the
 * thing it answers with is a URL for that member's browser. Publishing it would
 * advertise a door no key can open (parent PRD AC 28).
 */
const appHandoffsContracts = {
  // Which applications this workspace may cross into (IMM-751.4). Internal to
  // immoOS's own web app — no `docs` — and gated on the SAME literal the mint
  // route carries, which is what lets the navigation row derive its gate from
  // this leaf rather than transcribe a permission beside it.
  listLinkedApplications: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/linked-applications',
    pathParams: ListLinkedApplicationsPathParamsSchema,
    response: { 200: ListLinkedApplicationsResponseSchema },
    permission: 'organization:identity:app-handoff-create',
    description:
      'List the linked applications approved for a workspace, whether each is released for use, and whether its entry point is offered at all. The two flags are independent: `entryPointVisible` decides whether the entry point is shown, `released` decides whether its action works, so an application can be live and crossable while its entry point stays hidden. Returns no URL: the redirect target is minted server-side, so nothing the browser holds can steer a crossing. An unconfigured workspace answers with an empty list, which is what makes the entry point absent rather than dead.',
  },
  mint: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/app-handoffs',
    pathParams: MintAppHandoffPathParamsSchema,
    body: MintAppHandoffBodySchema,
    response: { 200: MintAppHandoffResponseSchema },
    permission: 'organization:identity:app-handoff-create',
    description:
      "Mint a single-use handoff token for the calling member and return the address to send their browser to. The token identifies WHO is crossing and carries no read authority whatsoever — the receiving application still needs its own workspace API key for every read. It lives five minutes, is spendable once, and only its SHA-256 digest is stored. `targetId` names an approved application from deployment configuration; 422 identity.app-handoff.target-not-approved when it is not approved for this workspace, and 429 identity.app-handoff.rate-limited past the per-member ceiling. The raw token rides the redirect URL's fragment, so it never reaches the target's server logs.",
  },
  // The receiving half, and the ONLY published one (IMM-751.2.2). No workspace
  // in the path: the caller holds a token, not a workspace id, and requiring it
  // to discover one first is friction at the one moment the crossing must be
  // seamless. Tenancy comes from the presenting key instead, which is what
  // `requireWorkspaceApiKey` exists for.
  exchange: {
    method: 'POST',
    path: '/v1/app-handoffs/exchange',
    body: ExchangeAppHandoffBodySchema,
    response: { 200: ExchangeAppHandoffResponseSchema },
    permission: 'workspace:identity:app-handoff-exchange',
    docs: 'PUBLIC',
    description:
      "Exchange a handoff token for the identity it names. immoOS redirects a signed-in member to your application with a single-use token in the URL's fragment; read it there and post it here with your own workspace API key.\n\nYou get back three ids — the member, their workspace, and the location they work from (`null` when they have none) — and the token is spent in the same statement that read it, so a second exchange of the same token fails. Nothing about the member or the location is included: read their profile and their location with the reads under **Member profiles** and **Locations**, using the same key.\n\nThe token identifies WHO is crossing and authorises NOTHING. It is not a credential: a call carrying a token but no valid workspace API key is denied, and a key that is not the one configured for the application the token was minted for is refused exactly as an unknown token is.\n\n**Every refusal is identical.** An unknown token, an expired one, one already spent, one minted for another application, and one belonging to a member who has since been deactivated all answer 422 identity.app-handoff.token-invalid with the same body. That is deliberate — a caller who could tell them apart could probe which members exist. Treat all of them the same way: send the member back to immoOS to follow the link again. Tokens live five minutes.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const companySignatureContracts = {
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/company-signature',
    pathParams: CompanySignaturePathParamsSchema,
    response: { 200: CompanySignatureResponseSchema },
    permission: 'workspace:identity:branding-read',
    description:
      'Return the workspace\'s single company e-mail signature record: the raw stored template (sanitised HTML with variable/logo markers) and the logo size. Readable by every member of the workspace. The template is "" and the logo size MEDIUM until maintained — an unset signature reads as the empty default rather than 404, so the editor always has a record to render.',
  },
  update: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/company-signature',
    pathParams: CompanySignaturePathParamsSchema,
    body: UpdateCompanySignatureBodySchema,
    response: { 200: CompanySignatureResponseSchema },
    permission: 'workspace:identity:branding-update',
    description:
      "Create-or-update the workspace's single company e-mail signature. Owner/Admin only. The template is sanitised (scripts and unsafe markup stripped, the variable/logo markers and whitelisted inline styles kept) before storage; an empty template clears the signature. 400 identity.company-signature.signature-too-long when the visible text exceeds 5.000 characters; 400 identity.company-signature.unsupported-variable when the template references a token outside the fixed variable catalogue. On rejection the previous template is retained.",
  },
  resolve: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/company-signature/resolved',
    pathParams: CompanySignaturePathParamsSchema,
    query: ResolveCompanySignatureQueryParamsSchema,
    response: { 200: ResolvedCompanySignatureResponseSchema },
    permission: 'workspace:identity:branding-read',
    description:
      'Resolve the workspace\'s company e-mail signature into mail-safe HTML for a member: Person/Kontakt variables from the member identity + profile, Firma variables (incl. logo) from the workspace company data + CI. `?memberId=<uuid>` picks the member to resolve for (the "Profil" switcher); defaults to the calling member. Missing values collapse their line (no empty `{token}`); a missing logo/photo drops its `<img>`. Returns `{ html: null }` when the workspace has no signature or its template is blank -- the composer then inserts no default signature. Readable by every member of the workspace.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const apiKeysContracts = {
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/api-keys',
    pathParams: ListApiKeysPathParamsSchema,
    query: ListApiKeysQuerySchema,
    response: { 200: ListApiKeysResponseSchema },
    permission: 'workspace:identity:api-key-read',
    description:
      'List the member-bound API keys of a workspace the caller is a member of, newest-first. Never returns the secret — only metadata (name, granted permissions, rate limit, status, issuer, last-used). Optional `mcpCapable`: `false` returns HTTP-API keys only, `true` returns MCP keys only; omitted returns both.',
  },
  issue: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/api-keys',
    pathParams: IssueApiKeyPathParamsSchema,
    body: IssueApiKeyBodySchema,
    response: { 200: IssueApiKeyResponseSchema },
    permission: 'workspace:identity:api-key-create',
    description:
      'Issue an API key bound to the calling member. The granted permissions must be a subset of the grantable allow-list and of the issuer’s own current permissions; 422 identity.api-key.permission-not-held otherwise. The plaintext secret is returned exactly once in this response and never again. `workspace:assistant:conversation-*` only as part of the browser-extension bundle; 422 identity.api-key.permission-not-grantable otherwise.',
  },
  update: {
    method: 'PATCH',
    path: '/v1/workspaces/:workspaceId/api-keys/:apiKeyId',
    pathParams: UpdateApiKeyPathParamsSchema,
    body: UpdateApiKeyBodySchema,
    response: { 200: UpdateApiKeyResponseSchema },
    permission: 'workspace:identity:api-key-update',
    description:
      "Rewrite a key's granted permissions and rate limit in place, without revoking it or re-minting its secret. Integrations holding the key keep working. A full replace: both fields are required. A permission being ADDED must be in the customer grantable catalogue (422 identity.api-key.permission-not-grantable) and held by both the key's bound member and the calling member (422 identity.api-key.permission-not-held). A permission the key already carries survives the edit. 422 identity.api-key.cannot-edit-revoked for a revoked key. An edit whose values already match is a 200 no-op that writes nothing and emits no event. 404 identity.api-key.not-found when the key is not in the workspace. Expiry, name and description are not editable.",
  },
  revoke: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/api-keys/:apiKeyId/revoke',
    pathParams: RevokeApiKeyPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: RevokeApiKeyResponseSchema },
    permission: 'workspace:identity:api-key-revoke',
    description:
      'Revoke an API key in a workspace the caller is a member of — flips status to revoked and stamps revokedAt/revokedBy. Idempotent: revoking an already-revoked key is a 200 no-op with no second audit event. 404 when the key does not exist in the workspace.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The connected-client surface (IMM-601.8.2): the OAuth grants a member's MCP
 * clients hold. Gated by its OWN `connected-client-*` triple, held by every
 * member: a grant acts as the member who approved it and is capped to what they
 * hold, so connecting an MCP client needs no authority beyond membership. The
 * api-key pair it used to share made MCP an Owner/Admin feature by accident.
 *
 * The two consent leaves are the bridge that creates a grant, and they are
 * asymmetric on purpose. `prepareConsent` is PUBLIC because the issuer redirects
 * a browser at it that may hold no session at all — it reads a request and
 * grants nothing. `consent` is workspace-authed, so the grant is always bound
 * to whoever is actually signed in.
 */
const connectedClientsContracts = {
  prepareConsent: {
    method: 'GET',
    path: '/v1/oauth/consent',
    query: PrepareOauthConsentQuerySchema,
    response: { 200: PrepareOauthConsentResponseSchema },
    // PUBLIC — the auth-layer sentinel, exactly as the `auth.*` leaves carry it.
    // Our own authorize endpoint redirects the member's browser here before we
    // know whether they hold a session; the leaf reads the request row and grants
    // nothing, and the screen sends an anonymous visitor through `/sign-in` with a
    // `returnTo`. Deliberately no `docs` marker: it is a step of an interactive
    // flow, not an HTTP operation a partner calls.
    permission: 'PUBLIC',
    description:
      "Read a pending authorization request and return what the consent screen renders: the request id the approve call echoes back, the registered client's name, the requested scopes that are grantable at all, and the requested scopes immoOS has no such permission for. Grants nothing and writes nothing. 422 identity.oauth-consent.invalid-request when the request is unknown, expired or already answered.",
  },
  consent: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/connected-clients/consent',
    pathParams: CompleteOauthConsentPathParamsSchema,
    body: CompleteOauthConsentBodySchema,
    response: { 200: CompleteOauthConsentResponseSchema },
    permission: 'workspace:identity:connected-client-create',
    description:
      "Record the calling member's consent as a connected-client grant, mint the authorization code, and return the client's registered redirect carrying that code, the state and iss. The approved scopes are capped TWICE server-side — against what the stored request asked for, then against the member's own live permissions — so a member can never consent a client into authority they do not hold, and the response states what was actually granted. Answering a request is single-use: a second approval of the same request is refused. 422 identity.oauth-consent.invalid-request when the request is unknown, expired or already answered. `access` ALL_TOOLS records an unscoped grant that reaches every key-grantable permission the member holds at each call; approvedScopes is then ignored. Omitted, it defaults to SELECTED_PERMISSIONS.",
  },
  decline: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/connected-clients/decline',
    pathParams: DeclineOauthConsentPathParamsSchema,
    body: DeclineOauthConsentBodySchema,
    response: { 200: DeclineOauthConsentResponseSchema },
    // AUTHENTICATED, on the SAME permission as approve and inside the same
    // membership middleware — deliberately not PUBLIC. A decline grants nothing
    // but it WRITES, and a public, cookie-reachable, state-mutating leaf is a
    // cross-site form POST away from letting any page on the internet cancel an
    // in-flight authorization for whoever visits it. The member is already signed
    // in by the time Cancel is reachable, so a session costs nothing.
    permission: 'workspace:identity:connected-client-create',
    description:
      "Decline a pending authorization request and return the client's registered redirect carrying error=access_denied, the state and iss. Answering is single-use, so a declined request can be neither re-declined nor approved. 422 identity.oauth-consent.invalid-request when the request is unknown, expired or already answered.",
  },
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/connected-clients',
    pathParams: ListConnectedClientsPathParamsSchema,
    response: { 200: ListConnectedClientsResponseSchema },
    permission: 'workspace:identity:connected-client-read',
    description:
      'List the ACTIVE OAuth grants held by connected MCP clients in a workspace the caller is a member of, newest-first. Revoked grants are omitted entirely. Never returns a token, a token prefix, or the issuer’s grant id — only the client name, the granted permissions, when it was connected and when it was last used. `access` says whether the grant is unscoped (`grantedPermissions` then empty) or scoped.',
  },
  revoke: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/connected-clients/:connectedClientId',
    pathParams: RevokeConnectedClientPathParamsSchema,
    // The one bodyless leaf in this contract. Every other DELETE returns the
    // resulting state because a panel re-renders from it; here the resulting
    // state is "gone from the list", so there is nothing to render.
    response: { 204: zod.void() },
    permission: 'workspace:identity:connected-client-revoke',
    description:
      'Revoke a connected client’s OAuth grant. The grant fails authentication on its very next MCP call — the local row is authoritative, so revocation does not wait for the issuer’s token to expire. Idempotent: revoking an already-revoked grant is a 204 no-op. 404 identity.connected-client.not-found when the id is unknown in this workspace.',
  },
  updateAccess: {
    method: 'PATCH',
    path: '/v1/workspaces/:workspaceId/connected-clients/:connectedClientId',
    pathParams: UpdateConnectedClientAccessPathParamsSchema,
    body: UpdateConnectedClientAccessBodySchema,
    response: { 200: ConnectedClientListItemSchema },
    permission: 'workspace:identity:connected-client-revoke',
    description:
      "Change a connected client's access in place: unscoped (`ALL_TOOLS`, follows the catalogue) or a fixed set of permissions (`SELECTED_PERMISSIONS`, at least one). Takes effect on the client's next MCP call with no reconnect and no new token. A permission being ADDED must be held by the calling member; one already on the connection may stay. 422 identity.connected-client.permission-not-held, 422 identity.connected-client.no-permissions. Idempotent: a body naming the current state is a 200 no-op that writes nothing and emits no event. 404 identity.connected-client.not-found when the id is unknown, revoked, or another person's.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const impersonationContracts = {
  start: {
    method: 'POST',
    path: '/v1/admin/impersonation-sessions',
    body: StartImpersonationBodySchema,
    response: { 200: StartImpersonationResponseSchema },
    permission: 'backoffice:identity:member-impersonate',
    description:
      'Start an admin impersonation session against a workspace member (IMM-346). Requires a fresh TOTP step-up. Creates a 30-minute, single-active-per-admin session and returns a single-use handoff code — never the operating credential. The admin browser hands the code off to the customer app, which exchanges it. 403 when the admin lacks the impersonation capability; rejected when the target is not a workspace member or is platform staff/an admin.',
  },
  exchange: {
    method: 'POST',
    path: '/v1/impersonation/exchange',
    body: ExchangeImpersonationBodySchema,
    response: { 200: ExchangeImpersonationResponseSchema },
    permission: 'PUBLIC',
    description:
      'Exchange a single-use impersonation handoff code (issued by the admin start endpoint) for the opaque operating credential. PUBLIC because the code itself is the proof of possession — like the sign-in callback. The code is burned on first exchange; a reused, expired, or unknown code is rejected.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const mediaFilesContracts = {
  createUpload: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/media/uploads',
    pathParams: CreateMediaUploadPathParamsSchema,
    body: CreateMediaUploadBodySchema,
    response: { 200: CreateMediaUploadResponseSchema },
    permission: 'organization:media:file-write',
    description:
      "Begin a direct-to-S3 upload. The caller supplies the constraints (allowedContentTypes, maxSizeBytes) and a free-form kind label — the media module holds no per-use-case policy. Creates a `pending` file scoped to the workspace and returns a short-lived presigned POST whose policy pins the key, content type, and a content-length range. 422 (media.file.upload-not-allowed) when the declared type/size violates the caller's allow-list/cap. The client POSTs the bytes straight to S3, then calls confirm.",
  },
  confirm: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/media/files/:id/confirm',
    pathParams: ConfirmMediaUploadPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: ConfirmMediaUploadResponseSchema },
    permission: 'organization:media:file-write',
    description:
      "Confirm an uploaded file: the module verifies the stored object's real format (magic bytes) matches the declared content type and its size is within the declared size, flips it to `ready`, and returns the file. Idempotent on an already-`ready` file. 422 (media.file.invalid-content) when the bytes fail validation; 404 when the file does not exist in the workspace or no object was uploaded.",
  },
  read: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/media/files/:id/read',
    pathParams: ReadMediaFilePathParamsSchema,
    response: { 200: ReadMediaFileResponseSchema },
    permission: 'organization:media:file-read',
    description:
      "Return a short-lived presigned GET URL plus the metadata needed to render or download a `ready` file in the caller's workspace. The download is served with the original filename (content-disposition) and stored content type. 404 when the file does not exist in the workspace or is still `pending` — `pending` files are never exposed.",
  },
  getMetadata: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/media/files/:id',
    pathParams: GetMediaFileMetadataPathParamsSchema,
    response: { 200: GetMediaFileMetadataResponseSchema },
    permission: 'organization:media:file-read',
    description:
      "Return the metadata for a `ready` file in the caller's workspace without minting a read URL. 404 when the file does not exist in the workspace or is still `pending`.",
  },
  readMany: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/media/files/read-many',
    pathParams: ReadManyMediaFilesPathParamsSchema,
    body: ReadManyMediaFilesBodySchema,
    response: { 200: ReadManyMediaFilesResponseSchema },
    permission: 'organization:media:file-read',
    description:
      "Batch-resolve up to 100 file ids to a read URL + metadata each. Ids outside the caller's workspace or not yet `ready` are silently omitted, so the result may be shorter than the request. POST (not GET) so the id list travels in the body.",
  },
  delete: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/media/files/:id',
    pathParams: DeleteMediaFilePathParamsSchema,
    response: { 200: DeleteMediaFileResponseSchema },
    permission: 'organization:media:file-delete',
    description:
      'Permanently remove a file: deletes the stored object and the metadata row, returning the id. Idempotent — deleting a file already absent from the workspace is a success, not a 404.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const coverageContracts = {
  readWorkspaceCoverage: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/coverage',
    pathParams: WorkspaceCoveragePathParamsSchema,
    response: { 200: WorkspaceCoverageResponseSchema },
    permission: 'workspace:identity:organization-coverage-read',
    docs: 'PUBLIC',
    description:
      "Read the workspace's coverage: every location of the key's workspace with id, name, type (main/sub), status (active/inactive), parentId, and its assigned postal codes (5-digit, ascending), grouped per location. The main location comes first, then the remaining locations by name. A location covering nothing has an empty postalCodes list (empty means covers nothing, never all postal codes); an inactive location is still returned with status inactive and keeps its postal codes. Postal codes are non-overlapping — each appears under exactly one location, never aggregated onto a parent. The response carries a content-derived ETag and a short cache window; repeat with If-None-Match to get 304 Not Modified when coverage is unchanged. generatedAt is the snapshot instant.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const leadPartnersContracts = {
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/lead-partners',
    pathParams: ListLeadPartnersPathParamsSchema,
    response: { 200: ListLeadPartnersResponseSchema },
    permission: 'organization:lead:lead-partner-read',
    description:
      'List the workspace-wide Lead Partners, name-ascending. Includes deactivated partners (active=false) so existing leads still resolve their attribution; the capture form filters to active ones.',
  },
  create: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/lead-partners',
    pathParams: CreateLeadPartnerPathParamsSchema,
    body: CreateLeadPartnerBodySchema,
    response: { 200: CreateLeadPartnerResponseSchema },
    permission: 'organization:lead:lead-partner-manage',
    description:
      'Create a workspace Lead Partner (Active=true). Name must be unique within the workspace (422 lead.lead-partner.name-taken otherwise); the slug is derived from the name. There is no hard-delete — deactivation is the only retirement path.',
  },
  deactivate: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/lead-partners/:id/deactivate',
    pathParams: LeadPartnerActionPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: LeadPartnerResponseSchema },
    permission: 'organization:lead:lead-partner-manage',
    description:
      'Deactivate a Lead Partner — it stays attached to existing leads but is no longer selectable on new ones. Idempotent. 404 when it does not exist in the workspace.',
  },
  reactivate: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/lead-partners/:id/reactivate',
    pathParams: LeadPartnerActionPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: LeadPartnerResponseSchema },
    permission: 'organization:lead:lead-partner-manage',
    description: 'Reactivate a deactivated Lead Partner — selectable again on new leads. Idempotent.',
  },
  issueKey: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/lead-partners/:id/issue-key',
    pathParams: IssueLeadPartnerKeyPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: IssueLeadPartnerKeyResponseSchema },
    permission: 'organization:lead:lead-partner-manage',
    description:
      'Issue a lead-scoped API key for a Lead Partner and link it (1:1). The acting member owns the key; it is granted only organization:lead:lead-create. The plaintext secret is returned exactly once. 422 (lead.lead-partner.key-already-issued) when the partner already has a linked key — revoke it first.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const activitiesContracts = {
  feed: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/activities/feed',
    pathParams: ListScopedActivitiesPathParamsSchema,
    query: ListScopedActivitiesQuerySchema,
    response: { 200: ActivityFeedResponseSchema },
    // Unchanged — oversight WIDENS what the read returns, it never gates the
    // route (IMM-776.1).
    permission: 'organization:activity:activity-read',
    description:
      "The Aktivitäten tab's cross-Standort read (IMM-776.1): the caller's own activities plus every activity in the Standorte where they hold organization:activity:activity-oversight, across UPCOMING (state OPEN, due_at ASC) or PAST (state DONE/CANCELLED, ordered on COALESCE(occurred_at, cancelled_at, created_at) DESC) — never both. `scope` reports which of OWN/OVERSIGHT/WORKSPACE produced the answer; `ownOpenCount` is the caller's own open work, counted independently of every filter and of the page bound, and is null on a PAST read. `organizationIds` narrows within the caller's visible set and never widens it; an empty intersection answers an empty page. `assignees` accepts uuids and/or the literal UNASSIGNED. Capped at ACTIVITY_FEED_MAX_LIMIT (200, the default); `truncated` says when the cap bit.",
  },
  ownOpenCount: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/activities/own-open-count',
    pathParams: ListScopedActivitiesPathParamsSchema,
    response: { 200: ActivityOwnOpenCountResponseSchema },
    permission: 'organization:activity:activity-read',
    description:
      "The Aktivitäten nav counter's store half (IMM-1360.4): the caller's own OPEN activities across their visible Standorte (and Standort-less rows), filter-independent — the number the feed reports as `ownOpenCount`. Internal — not published API surface.",
  },
  tasks: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/activities/tasks',
    pathParams: ListMemberActivityTasksPathParamsSchema,
    query: ListMemberActivityTasksQuerySchema,
    response: { 200: ListMemberActivityTasksResponseSchema },
    permission: 'organization:activity:activity-read',
    description:
      "Meine Aufgaben (IMM-430.4): the acting member's OPEN activities from the shared store, across every Standort they may see, ordered by due date ascending so overdue items sort first. Always the caller's own — there is no assignee parameter. Serves DEAL subjects until IMM-512 migrates leads and contacts onto the store; the shape is already subject-generic, so that migration needs no contract change. Capped rather than paginated (limit <= 200, default 200): the page merges this with the lead tasks read client-side, and two independent cursors cannot be merged coherently — `truncated` says when the cap bit.",
  },
  viewings: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/activities/viewings',
    pathParams: ListMemberViewingsPathParamsSchema,
    // The calendar's own window validation: to > from, at most MAX_CALENDAR_WINDOW_DAYS (62) wide.
    query: CalendarEventsQuerySchema,
    response: { 200: ListMemberViewingsResponseSchema },
    permission: 'organization:activity:activity-read',
    description:
      "The calendar's composer Besichtigungen (IMM-1372.3): the acting member's own VIEWING_ON_SITE / VIEWING_VIRTUAL activities on an Interessent, in the Standorte they may see and hold organization:activity:activity-read in — OPEN with dueAt in [from, to), or DONE with occurredAt in [from, to); never CANCELLED. Always the caller's own — there is no assignee parameter. Each lasts one hour. Ordered by startsAt, then activityId. Refused to API keys (identity.api-key.route-not-available).",
  },
  list: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/activities',
    pathParams: ListActivitiesPathParamsSchema,
    query: ListActivitiesQuerySchema,
    response: { 200: ListActivitiesResponseSchema },
    permission: 'organization:activity:activity-read',
    docs: 'PUBLIC',
    description: `Return the activity timeline for a subject (deal / lead / property / contact / valuation request) within a workspace organisation, newest first. Each entry carries its content type, actor, occurredAt, and a renderable body whose keys vary by content type. Keyset-paginated: ?limit (defaults to ${ACTIVITY_PAGE_DEFAULT_LIMIT}) and an opaque ?cursor taken from the previous page’s nextCursor.`,
  },
  autoResumeNotices: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/activities/auto-resume-notices',
    pathParams: ListAutoResumeNoticesPathParamsSchema,
    query: ListAutoResumeNoticesQuerySchema,
    response: { 200: ListAutoResumeNoticesResponseSchema },
    permission: 'organization:activity:activity-read',
    docs: 'PUBLIC',
    description:
      'Return the unseen auto-resume notices for ?dealId — the system (scheduled) auto-resume entries on that deal which the caller deferred and has not yet acknowledged. Drives the one-time auto-resume notice shown on next visit (IMM-194); the user-facing copy lives in packages/i18n. Empty once every notice on the deal is marked seen.',
  },
  markSeen: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/activities/:activityId/seen',
    pathParams: MarkActivitySeenPathParamsSchema,
    response: { 200: MarkActivitySeenResponseSchema },
    permission: 'organization:activity:activity-update',
    description:
      'Mark an activity seen by the caller — records a per-member `activity_seen_by` row so a once-per-member notice (the auto-resume notice) does not reappear. Idempotent: a second call is a no-op. 404 when the activity is not visible in that workspace organisation.',
  },
  record: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/activities',
    pathParams: RecordActivityRecordPathParamsSchema,
    body: RecordActivityRecordBodySchema,
    response: { 200: ActivityRecordResponseSchema },
    permission: 'organization:activity:activity-write',
    description:
      "Record a plannable activity against any subject (IMM-430.1) — a note, a communication or an appointment — into the shared activity store, and publish activity.activity-recorded, which this module projects onto the subject's timeline. The subject itself is always linked; `links` adds the further records the entry should surface on (a deal activity links its contact, so both timelines render it). `mode: LOG` sets state DONE with an occurredAt (defaults to now, refused beyond a 1-minute future skew); `mode: PLAN` and the APPOINTMENT kind arrive with IMM-430.2 and are refused here. Refuses empty / over-long content (≤ 10,000 chars). 404 when the subject is not in that workspace organisation. A `CONTACT` link to a contact the caller cannot see — deleted, or in a Standort outside their visible set — is dropped, never refused; the entry is still recorded on the subject.",
  },
  complete: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/activities/:activityId/completion',
    pathParams: ActivityTransitionPathParamsSchema,
    body: CompleteActivityRecordBodySchema,
    response: { 200: CompleteActivityRecordResponseSchema },
    // Re-gated from `activity-write` to `activity-update` (IMM-601.4): the three
    // transitions edit an EXISTING activity, which is what `activity-update`
    // (mark-seen's literal) already means. No role loses reach — agent and
    // Owner/Admin hold both literals; assistant/viewer hold neither.
    permission: 'organization:activity:activity-update',
    description:
      'Complete an open activity (IMM-430.3): sets state DONE with an occurredAt (defaults now, refused beyond a 1-minute future skew) and captures the optional Ergebnis. A non-successful Ergebnis auto-creates exactly one open follow-up in the SAME transaction — a Wiedervorlage for a communication, a replacement appointment for a missed Termin — inheriting the kind, communication type and appointment type, due 24h later unless ?followUpDueAt overrides it. Deliberately not idempotent: completing an activity that is not open is refused, which is what stops a double-submit creating two follow-ups. Supplying an outcome from the wrong kind\u2019s vocabulary, or any outcome on a note, is refused. 404 when the activity is not in that workspace organisation.',
  },
  reschedule: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/activities/:activityId/reschedule',
    pathParams: ActivityTransitionPathParamsSchema,
    body: RescheduleActivityRecordBodySchema,
    response: { 200: ActivityRecordResponseSchema },
    // Re-gated to `activity-update` (IMM-601.4) — see `complete`.
    permission: 'organization:activity:activity-update',
    description:
      'Move an open activity to a new „Fällig am" (IMM-430.3). It stays open; the new date must be in the future, and rescheduling an activity that is not open is refused. 404 when the activity is not in that workspace organisation.',
  },
  cancel: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/activities/:activityId/cancellation',
    pathParams: ActivityTransitionPathParamsSchema,
    body: CancelActivityRecordBodySchema,
    response: { 200: ActivityRecordResponseSchema },
    // Re-gated to `activity-update` (IMM-601.4) — see `complete`.
    permission: 'organization:activity:activity-update',
    description:
      'Cancel („Absagen") an open activity (IMM-430.3): state becomes CANCELLED and it leaves Anstehend for Verlauf. occurredAt stays null — a cancelled activity never claims to have happened. Cancelling an activity that is not open is refused. 404 when the activity is not in that workspace organisation.',
  },
  reassign: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/activities/:activityId/assignee',
    pathParams: ActivityTransitionPathParamsSchema,
    body: ReassignActivityRecordBodySchema,
    response: { 200: ActivityRecordResponseSchema },
    permission: 'organization:activity:activity-write',
    description:
      'Hand an open activity to another workspace member (IMM-430.3). The new assignee is validated as an active member of the workspace, and the change is projected onto the subject\u2019s timeline as an audited entry. 404 when the activity is not in that workspace organisation, or when the chosen member is not in it.',
  },
  delete: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/activities/:activityId',
    pathParams: ActivityTransitionPathParamsSchema,
    response: { 200: DeleteActivityRecordResponseSchema },
    // The floor: every author of a store activity holds it. The author-or-Owner rule
    // (responsible member OR activity-delete) is the presenter's second check.
    permission: 'organization:activity:activity-update',
    description:
      'Remove one synced e-mail activity from a timeline (IMM-866.8). The e-mail stays in the Postfach; only the activity row is deleted, and the message is remembered as removed for the record it was removed from, so the Konversation reconcile does not record it there again. Later replies on the Konversation are still recorded, and a Konversation that moves to another record is recorded there. Allowed for the Postfach owner or a holder of organization:activity:activity-delete; anyone else is refused with 403 activity.activity-record.delete-forbidden. An unknown id, or one in another Standort, answers 404 activity.activity-record.not-found; an activity that is not a synced e-mail answers 422 activity.activity-record.delete-not-supported. Closed to API-key principals. Internal — not published API surface.',
  },
  listByMember: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/members/:memberId/activities',
    pathParams: ListMemberActivitiesPathParamsSchema,
    query: ListMemberActivitiesQuerySchema,
    response: { 200: ListActivitiesResponseSchema },
    permission: 'organization:activity:activity-read',
    docs: 'PUBLIC',
    description: `Return all activities where the given member is the actor, across all organisations in the workspace, newest first. Keyset-paginated with ?limit (defaults to ${ACTIVITY_PAGE_DEFAULT_LIMIT}) & an opaque ?cursor. Workspace-addressed but gated on an organisation-scoped permission, so an API key’s grant is resolved against the workspace’s main location.`,
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const searchContracts = {
  query: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/search',
    pathParams: SearchPathParamsSchema,
    query: SearchQuerySchema,
    response: { 200: SearchResultsResponseSchema },
    // Inert by design — every member holds it. What the caller actually sees
    // is decided per entity inside each search source, against that entity's
    // own read literal, so this route can never grant reach the entity's own
    // list endpoint would refuse.
    permission: 'workspace:search:search-read',
    description:
      "Search the caller's active workspace across every entity type at once (the ⌘K command palette, IMM-448). Fans out in parallel over the registered sources and returns their hits grouped by entity type in a fixed order, each group capped at 5 rows plus the true match count. A group the caller may not read is ABSENT; a group that was searched and matched nothing is present with no rows. Every source scopes through its own module's visible-Standort read path, so no record appears that the caller could not open directly. Queries shorter than the two-character minimum are rejected.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const companiesContracts = {
  create: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/companies',
    pathParams: CreateCrmCompanyPathParamsSchema,
    body: CreateCrmCompanyBodySchema,
    response: { 200: CreateCrmCompanyResponseSchema },
    permission: 'organization:relationship:company-create',
    description:
      'Create a CRM company (Firma) under a workspace organisation the caller is a member of. Only the name is required; the type must be one of the ten fixed values or empty. Distinct from the organisation-owned legal entity under the identity namespace.',
  },
  get: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/companies/:id',
    pathParams: GetWorkspaceCrmCompanyPathParamsSchema,
    response: { 200: GetWorkspaceCrmCompanyResponseSchema },
    permission: 'organization:relationship:company-read',
    description:
      'Return one company under a workspace organisation the caller is a member of. 404 if the company does not exist in that organisation.',
  },
  update: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/companies/:id',
    pathParams: UpdateCrmCompanyPathParamsSchema,
    body: UpdateCrmCompanyBodySchema,
    response: { 200: UpdateCrmCompanyResponseSchema },
    permission: 'organization:relationship:company-update',
    description:
      'Replace every editable field of a company in one call (the entity is flat — no child collections). Only the name is required. 404 if the company does not exist in that organisation.',
  },
  workspaceList: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/companies',
    pathParams: ListCrmCompaniesPathParamsSchema,
    query: ListCrmCompaniesQuerySchema,
    response: { 200: ListCrmCompaniesResponseSchema },
    permission: 'workspace:relationship:company-read',
    description:
      'List companies across the Standorte the caller may see (their affiliated organisations + descendants), sorted by name ascending by default. Filter by `?type=`, search by `?q=` (name / city / e-mail), and pass `?ids=` to restrict to a known set. `contactCount` is 0 until the contact–company join lands (IMM-4.3).',
  },
  workspaceGet: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/companies/:id',
    pathParams: GetVisibleCrmCompanyPathParamsSchema,
    response: { 200: GetVisibleCrmCompanyResponseSchema },
    permission: 'workspace:relationship:company-read',
    description:
      "Fetch a single company by id within a workspace the caller is a member of, scoped to the same visible-Standort set as the workspace-addressed list, so a company listed there never 404s when opened. 404 when the company is outside the caller's visible scope.",
  },
  archive: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/companies/:id/archive',
    pathParams: CrmCompanyLifecyclePathParamsSchema,
    response: { 200: ArchiveCrmCompanyResponseSchema },
    permission: 'organization:relationship:company-archive',
    description:
      'Archive an Aktiv company (Archivieren). It leaves the default list and reappears only under `?includeArchived=true`. Rejects when the company is already archived. Linked contacts are unaffected.',
  },
  unarchive: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/companies/:id/unarchive',
    pathParams: CrmCompanyLifecyclePathParamsSchema,
    response: { 200: UnarchiveCrmCompanyResponseSchema },
    permission: 'organization:relationship:company-unarchive',
    description:
      'Return an archived company to Aktiv (Wieder aktivieren). Restricted to Owner / Admin (via the role→permission config). Rejects when the company is not archived.',
  },
  delete: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/companies/:id',
    pathParams: CrmCompanyLifecyclePathParamsSchema,
    response: { 200: DeleteCrmCompanyResponseSchema },
    permission: 'organization:relationship:company-delete',
    description:
      'Soft-delete a company (endgültig löschen). Restricted to Owner / Admin. The company disappears from every list including the archived view; there is no hard delete in V1. Cascades to remove all contact–company links (the contacts themselves are untouched).',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const companyContactsContracts = {
  link: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/companies/:companyId/contacts',
    pathParams: LinkContactToCompanyPathParamsSchema,
    body: LinkContactToCompanyBodySchema,
    response: { 200: CompanyContactLinkResponseSchema },
    permission: 'organization:relationship:company-contact-write',
    description:
      'Link a contact to a company with a role (max 200 chars). MODE A passes an existing `contactId`; MODE B inline-creates a contact (Vorname + Nachname required, E-Mail/Telefon optional). Rejects a duplicate (contact, company) link. Setting `isPrimaryEmployer` demotes any prior primary for that contact.',
  },
  updateLink: {
    method: 'PUT',
    path: '/v1/organizations/:organizationId/companies/:companyId/contacts/:contactId',
    pathParams: CompanyLinkPathParamsSchema,
    body: UpdateCompanyLinkBodySchema,
    response: { 200: CompanyContactLinkResponseSchema },
    permission: 'organization:relationship:company-contact-write',
    description:
      "Edit a link's role and/or its primär-Arbeitgeber flag. Setting the flag demotes any prior primary for that contact. 404 when the link does not exist.",
  },
  unlink: {
    method: 'DELETE',
    path: '/v1/organizations/:organizationId/companies/:companyId/contacts/:contactId',
    pathParams: CompanyLinkPathParamsSchema,
    response: { 200: UnlinkContactFromCompanyResponseSchema },
    permission: 'organization:relationship:company-contact-write',
    description:
      'Remove a contact–company link (Verknüpfung lösen). Both the contact and the company remain; only the join is deleted. 404 when the link does not exist.',
  },
  listByCompany: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/companies/:companyId/contacts',
    pathParams: ListCompanyContactsPathParamsSchema,
    response: { 200: ListCompanyContactsResponseSchema },
    permission: 'organization:relationship:company-read',
    description:
      "The company's contact roster — every linked contact with its role string and the primary-employer flag. Used by the company detail page.",
  },
  listByContact: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/contacts/:contactId/companies',
    pathParams: ListContactCompaniesPathParamsSchema,
    response: { 200: ListContactCompaniesResponseSchema },
    permission: 'organization:relationship:contact-read',
    description:
      "A contact's linked companies (the 'Firmen' section) — each company with its role, the primary flag, and its status (so the frontend can show the '(archiviert)' suffix).",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminPropertiesContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/properties',
    query: ListAdminPropertiesQuerySchema,
    response: { 200: ListAdminPropertiesResponseSchema },
    permission: 'backoffice:property:property-read',
    description:
      'List every property across every workspace + organisation, newest-first. Properties of workspaces marked as testing environments are hidden unless `includeTestWorkspaces=true`.',
  },
  get: {
    method: 'GET',
    path: '/v1/admin/properties/:id',
    pathParams: GetAdminPropertyPathParamsSchema,
    response: { 200: GetAdminPropertyResponseSchema },
    permission: 'backoffice:property:property-read',
    description:
      'Return a single property by id with its workspace + organisation names. Soft-deleted properties are returned too, with `deletedAt` and `deletedBy` set; 404 only when no property has the id.',
  },
  memory: {
    method: 'GET',
    path: '/v1/admin/properties/:id/memory',
    pathParams: GetAdminPropertyPathParamsSchema,
    response: { 200: OperatorPropertyMemorySchema },
    permission: 'backoffice:property:property-read',
    description:
      "Everything immoOS has read about the Property — its Objekt memory (IMM-1050), newest first: one entry per statement with the value as read, the member's correction if any, the Objekt field it fits, its source, the passage, the prompt and version, the model, the confidence and when it was read. Operator session only. `operator` names the operator behind an operator-side change (IMM-1157); an entry an operator deleted is not listed unless it is an orphan. `operator.edited` names the operator who re-filed the entry, with the as-read value of each changed field. Orphans — operator changes the source's latest reading no longer matches — are listed with `operator.orphanedAt`; they are not part of the dossier. It also lists statements an immoOS operator added by hand (`source.type` `OPERATOR`, whose `source.id` is the property's id; `prompt` and `model` are null on those, and only on those), with `operator.addedBy`. 404 when the Property is unknown.",
  },
  inquiries: {
    method: 'GET',
    path: '/v1/admin/properties/:id/inquiries',
    pathParams: GetAdminPropertyPathParamsSchema,
    response: { 200: ListPropertyInquiriesResponseSchema },
    permission: 'backoffice:property:property-read',
    description:
      "Every inquiry the Property received, from any source, newest first: when immoOS received it, its source, the sender's name, e-mail and phone, the message, and the outcome (Interessent created, attached to an active Interessent, or kept without one because the Property is archived or let). Operator session only. An unknown Property answers an empty list.",
  },

  // The Property reads a valuation consumer needs (IMM-587.3). Entity-addressed
  // like every other admin leaf: the tenant is resolved from the Property and
  // checked against the caller's workspace scope. These three join
  // `ADMIN_KEY_CONSUMER_LEAVES`; `list`, `get`, `memory` and `inquiries` above, and
  // `readDocument` below, stay on plain `requireAdmin` and remain unreachable by
  // a key.
  dossier: {
    method: 'GET',
    path: '/v1/admin/properties/:id/dossier',
    pathParams: GetAdminPropertyPathParamsSchema,
    response: { 200: PropertyDossierSchema },
    permission: 'backoffice:property:property-read',
    description:
      "The Property's facts layer, as a machine (IMM-587.3) — every fact read out of its documents beside the value a person confirmed, plus the cached natural-language brief, plus the Objekt memory, every entry (IMM-1050). The memory includes statements an immoOS operator added by hand (`source.type` `OPERATOR`, whose `source.id` is the property's id; `prompt` and `model` are null on those, and only on those). Always the INTERNAL projection: set server-side from the route and never read from the request, so a caller cannot widen its own view. 404 when the Property is unknown OR outside the key's workspace scope — deliberately the same answer.",
  },
  listMedia: {
    method: 'GET',
    path: '/v1/admin/properties/:id/media',
    pathParams: GetAdminPropertyPathParamsSchema,
    response: { 200: ListPropertyMediaResponseSchema },
    permission: 'backoffice:property:property-read',
    description:
      "The Property's Medien grouped into the three areas, as a machine (IMM-587.3), with presigned URLs. `canManage` is false on every item: the management verbs are not on the machine surface. 404 when the Property is unknown or outside scope.",
  },
  listDocuments: {
    method: 'GET',
    path: '/v1/admin/properties/:id/documents',
    pathParams: GetAdminPropertyPathParamsSchema,
    query: ListPropertyDocumentsQuerySchema,
    response: { 200: ListPropertyDocumentsResponseSchema },
    permission: 'backoffice:property:property-read',
    description:
      "The Property's Dokumente newest-first, as a machine (IMM-587.3), optionally narrowed by derived group. `canManage` is false on every item. 404 when the Property is unknown or outside scope.",
  },
  readDocument: {
    method: 'GET',
    path: '/v1/admin/properties/:id/documents/:documentId/read',
    pathParams: ReadAdminPropertyDocumentPathParamsSchema,
    query: ReadPropertyDocumentQuerySchema,
    response: { 200: ReadPropertyDocumentResponseSchema },
    permission: 'backoffice:property:property-read',
    description:
      "Mint a short-lived URL for one of the Property's Dokumente, for an operator checking a memory entry against its source (IMM-1050.3). `?mode=preview` renders inline, `?mode=download` (default) saves under the original filename. Operator session only. 404 when the Property is unknown, the document does not exist, or it is filed under another Property — answered identically.",
  },

  // Filing a Dokument on the Property (IMM-587.4) — the last gap in the
  // one-credential lifecycle: a machine that produced a Wertgutachten can now
  // put it where the agency looks for it. The only genuinely new cross-tenant
  // WRITE in this feature, which is why it takes its own permission.
  createDocumentUpload: {
    method: 'POST',
    path: '/v1/admin/properties/:id/documents/uploads',
    pathParams: GetAdminPropertyPathParamsSchema,
    body: CreatePropertyDocumentUploadBodySchema,
    response: { 200: CreatePropertyDocumentUploadResponseSchema },
    permission: 'backoffice:property:document-write',
    description:
      "Begin a direct-to-S3 upload for a Property document, as a machine (IMM-587.4). The content-type allow-list and the size cap come from the contract, never from the request, so a machine cannot widen its own policy. 404 when the Property is unknown OR outside the key's workspace scope — deliberately the same answer.",
  },
  confirmDocumentUpload: {
    method: 'POST',
    path: '/v1/admin/properties/:id/documents',
    pathParams: GetAdminPropertyPathParamsSchema,
    body: ConfirmPropertyDocumentUploadBodySchema,
    response: { 200: ConfirmPropertyDocumentUploadResponseSchema },
    permission: 'backoffice:property:document-write',
    description:
      "Confirm a Property document upload, as a machine (IMM-587.4). Re-verifies the stored bytes for type and size, then files the document under the category named — `VALUATION_REPORT` for a Wertgutachten, which lands in its derived group and counts toward the **Property**'s Verkaufsreife like any other. The uploader is recorded as the bound operator for the audit trail; the data room displays »immoOS«. A machine-filed document is never auto-analysed and never promoted into the gallery. 404 when the Property is unknown or outside scope.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The valuation agent's read of an Objekt's facts (IMM-1385.4). Called by our own agent with a `vag_`
 * token, so no leaf is public docs.
 */
const valuationAgentPropertyFactsContracts = {
  get: {
    method: 'GET',
    path: '/v1/valuation-agent/properties/:propertyId/facts',
    pathParams: zod.object({ propertyId: zod.uuid() }),
    response: { 200: PropertyFactsSchema },
    permission: 'backoffice:valuation:agent-property-read',
    description:
      "An Objekt's facts for the valuation agent: per catalog field the confirmed Objekt value and every live fact, each with occurrence and provenance (origin, status, reviewLevel, confirmation, evidence, timeReference, reason). The same result the workspace MCP's property_facts returns. 404 property.property.not-found for an unknown Objekt.",
  },
  write: {
    method: 'POST',
    path: '/v1/valuation-agent/properties/:propertyId/findings',
    pathParams: zod.object({ propertyId: zod.uuid() }),
    body: WriteValuationAgentFindingsBodySchema,
    response: { 200: WriteValuationAgentFindingsResponseSchema },
    permission: 'backoffice:valuation:agent-finding-write',
    description:
      "The valuation agent's findings about an Objekt, judged one by one: each result is STORED with its factId, or REFUSED with a code and details. Every finding is stored as „KI erkannt“ (AI_DETECTED) under the agent's own source. A newer finding for the same factKey and occurrence supersedes the agent's earlier one and never another source's. The same (factKey, occurrence) twice in one body is a 400. 404 property.property.not-found for an unknown Objekt.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The valuation agent's read of an Objekt's data completeness (IMM-1396.2). Called by our own agent with a
 * `vag_` token, so no leaf is public docs.
 */
const valuationAgentPropertyCompletenessContracts = {
  get: {
    method: 'GET',
    path: '/v1/valuation-agent/properties/:propertyId/completeness',
    pathParams: zod.object({ propertyId: zod.uuid() }),
    response: { 200: PropertyCompletenessResponseSchema },
    permission: 'backoffice:valuation:agent-property-read',
    description:
      "An Objekt's data completeness for the valuation agent: per product (Akquisebriefing, Bewertung 1a, Bewertung 1b) whether every required field for its object type is present, the missing required fields, the present ones marked ASSUMED or CONTRADICTED, and the missing recommended ones. The same result the Makler's „Datenstand“ card and the workspace MCP's property_completeness return. A valuation started with required fields missing lists each of them, with the value assumed, under „Annahmen“. 404 property.property.not-found for an unknown Objekt.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The valuation agent's read of an Objekt's on-site capture and its Aufnahmefotos (IMM-1397.5). Called by
 * our own agent with a `vag_` token, so no leaf is public docs.
 */
const valuationAgentPropertyCaptureContracts = {
  get: {
    method: 'GET',
    path: '/v1/valuation-agent/properties/:propertyId/capture',
    pathParams: zod.object({ propertyId: zod.uuid() }),
    response: { 200: ValuationAgentPropertyCaptureResponseSchema },
    permission: 'backoffice:valuation:agent-capture-photo-read',
    description:
      "An Objekt's on-site capture (IMM-1397) for the valuation agent: the kind of viewing, whether and when it was completed, and every Aufnahmefoto with its photo-protocol motif, the time it was taken and a short-lived URL to the original. `capture: null` when the Objekt has none. The capture's values are facts with origin ON_SITE, read through /v1/valuation-agent/properties/:propertyId/facts. 404 property.property.not-found for an unknown Objekt.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminAuthContracts = {
  signIn: {
    method: 'GET',
    path: '/v1/admin/auth/sign-in',
    query: AdminSignInQuerySchema,
    response: { 200: AdminSignInResponseSchema },
    permission: 'PUBLIC',
    description:
      "Build the operator Google sign-in URL. Operators sign in with Google only (IMM-1422); `provider` is accepted for compatibility and ignored. With the operator Google app configured and GOOGLE_OAUTH=direct, the URL is Google's and the callback completes it (IMM-1422). With X-Immo-Client: admin-mobile, codeChallenge is required and the response sets no cookie.",
  },
  callback: {
    method: 'POST',
    path: '/v1/admin/auth/callback',
    body: AdminCallbackBodySchema,
    response: { 200: AdminCallbackResponseSchema },
    permission: 'PUBLIC',
    description:
      "Exchange the WorkOS code for an admin session. Rejects with admin.auth.user-not-an-admin when the resolved email is outside the platform-staff allow-list. With the operator Google app configured and GOOGLE_OAUTH=direct, the URL is Google's and the callback completes it (IMM-1422). With X-Immo-Client: admin-mobile, codeVerifier is required and the response carries refreshToken in the body instead of a cookie.",
  },
  refresh: {
    method: 'POST',
    path: '/v1/admin/auth/refresh',
    body: AdminRefreshBodySchema,
    response: { 200: AdminRefreshResponseSchema },
    permission: 'PUBLIC',
    description:
      'Rotate the admin access token using the admin refresh-token cookie, for a WorkOS-issued admin session or an immoOS-issued operator session (IMM-1422). With X-Immo-Client: admin-mobile, the credential is body.refreshToken and the rotated token comes back in the body; no cookie is read or set.',
  },
  mobileCallback: {
    method: 'GET',
    path: '/v1/admin/auth/mobile-callback',
    query: AdminMobileCallbackQuerySchema,
    response: { 302: zod.void() },
    permission: 'PUBLIC',
    description:
      "The operator Google app's redirect for the admin iPhone app (IMM-1422). Copies code, state and error onto immoos-admin://auth/callback, where ASWebAuthenticationSession picks them up. Redirects nowhere else.",
  },
  signOut: {
    method: 'POST',
    path: '/v1/admin/auth/sign-out',
    body: AdminSignOutBodySchema,
    response: { 200: AdminSignOutResponseSchema },
    permission: 'PUBLIC',
    description:
      "Clear the admin refresh cookie and return the URL to send the browser to: the WorkOS logout URL for a WorkOS-issued admin session, or the admin app's /sign-in for an immoOS-issued operator session (IMM-1422), whose session is revoked. Idempotent — the post-logout returnTo is sourced from server-side ADMIN_APP_ORIGIN so the browser always lands on the same env's /sign-in. With X-Immo-Client: admin-mobile, a WorkOS-issued session is also revoked server-side, because the app never opens the hosted logout page; a failed revoke still answers 200. A `pushToken` in the body unregisters that device from pushes (IMM-1411.3), with or without a bearer, before the session ends.",
  },
  me: {
    method: 'GET',
    path: '/v1/admin/auth/me',
    response: { 200: AdminMeResponseSchema },
    permission: 'backoffice:identity:own-profile-read',
    description:
      'Return the authenticated admin user, for a WorkOS-issued admin session or an immoOS-issued operator session (IMM-1422). Workspace-tier surfaces use /v1/auth/me instead.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminWorkspacesContracts = {
  create: {
    method: 'POST',
    path: '/v1/admin/workspaces',
    body: CreateAdminWorkspaceBodySchema,
    response: { 200: CreateAdminWorkspaceResponseSchema },
    permission: 'backoffice:identity:workspace-create',
    description: 'Create a new workspace. Platform-admin only — gated by the WorkOS allow-list at the boundary.',
  },
  list: {
    method: 'GET',
    path: '/v1/admin/workspaces',
    query: ListAdminWorkspacesQuerySchema,
    response: { 200: ListAdminWorkspacesResponseSchema },
    permission: 'backoffice:identity:workspace-read',
    description:
      'List every workspace ordered by creation date (newest first). Workspaces marked as testing environments are hidden unless `includeTestWorkspaces=true`. Wrapped in { data } for future pagination.',
  },
  get: {
    method: 'GET',
    path: '/v1/admin/workspaces/:id',
    pathParams: GetAdminWorkspacePathParamsSchema,
    response: { 200: GetAdminWorkspaceResponseSchema },
    permission: 'backoffice:identity:workspace-read',
    description: 'Return a single workspace by id. 404 with identity.workspace.not-found when no row matches.',
  },
  update: {
    method: 'PATCH',
    path: '/v1/admin/workspaces/:id',
    pathParams: UpdateAdminWorkspacePathParamsSchema,
    body: UpdateAdminWorkspaceBodySchema,
    response: { 200: UpdateAdminWorkspaceResponseSchema },
    permission: 'backoffice:identity:workspace-update',
    description: 'Update a workspace name. Owner edits arrive in a later slice.',
  },
  createInvitation: {
    method: 'POST',
    path: '/v1/admin/workspaces/:workspaceId/invitations',
    pathParams: CreateAdminWorkspaceInvitationPathParamsSchema,
    body: CreateAdminWorkspaceInvitationBodySchema,
    response: { 200: CreateAdminWorkspaceInvitationResponseSchema },
    permission: 'backoffice:identity:member-create',
    description:
      'Platform-admin invite of a new workspace member: seats the member, issues a system-issued pending invitation (null member-issuer), and sends the backend magic-link + OTP email (IMM-271). 422 when the email already has a member or a live pending invite in the workspace.',
  },
  listPendingInvitations: {
    method: 'GET',
    path: '/v1/admin/workspaces/:workspaceId/pending-invitations',
    pathParams: ListPendingInvitationsPathParamsSchema,
    response: { 200: ListPendingInvitationsResponseSchema },
    permission: 'backoffice:identity:workspace-read',
    description:
      "Platform-admin view of a workspace's live (pending, non-expired) invitations — including the system-issued owner invite (null issuer). Backs the admin pending-members panel + resend.",
  },
  resendInvitation: {
    method: 'POST',
    path: '/v1/admin/workspaces/:workspaceId/invitations/:id/resend',
    pathParams: ResendInvitationPathParamsSchema,
    body: EmptyObjectSchema,
    response: { 200: ResendInvitationResponseSchema },
    permission: 'backoffice:identity:workspace-update',
    description:
      'Platform-admin resend of a pending invitation: revokes the old record, issues a fresh one, and re-sends the backend magic-link email (IMM-271). 404 for a missing/cross-workspace id; 422 when not pending.',
  },
  revokeInvitation: {
    method: 'POST',
    path: '/v1/admin/workspaces/:workspaceId/invitations/:id/revoke',
    pathParams: RevokeInvitationPathParamsSchema,
    body: EmptyObjectSchema,
    response: { 200: RevokeInvitationResponseSchema },
    permission: 'backoffice:identity:workspace-update',
    description:
      'Platform-admin revoke of a pending invitation: marks it revoked (terminal). 404 for a missing/cross-workspace id; 422 when not pending.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminWorkspaceRolesContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/workspaces/:workspaceId/roles',
    pathParams: ListAdminWorkspaceRolesPathParamsSchema,
    response: { 200: ListAdminWorkspaceRolesResponseSchema },
    permission: 'backoffice:identity:role-read',
    description:
      'List the roles defined for a workspace, oldest first. 404 with identity.workspace.not-found when the workspace does not exist.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminOrganizationsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/organizations',
    query: ListAdminOrganizationsQuerySchema,
    response: { 200: ListAdminOrganizationsResponseSchema },
    permission: 'backoffice:identity:organization-read',
    description:
      "List every organization across every workspace, newest first. Optionally filter to a single workspace via `workspaceId`. Standorte of workspaces marked as testing environments are hidden unless `includeTestWorkspaces=true`. Each row carries its Standort's `setupStatus` (`IN_SETUP` or `COMPLETED`).",
  },
  get: {
    method: 'GET',
    path: '/v1/admin/organizations/:id',
    pathParams: GetAdminOrganizationPathParamsSchema,
    response: { 200: GetAdminOrganizationResponseSchema },
    permission: 'backoffice:identity:organization-read',
    description: 'Return a single organization by id with its workspace name and full address.',
  },
  listMembers: {
    method: 'GET',
    path: '/v1/admin/organizations/:id/members',
    pathParams: GetAdminOrganizationPathParamsSchema,
    response: { 200: ListAdminOrganizationMembersResponseSchema },
    permission: 'backoffice:identity:member-read',
    description:
      'List the members affiliated with this organization (Standort) — everyone holding a role in it — with their workspace and IdP-resolved identity, newest first.',
  },
  updateAddress: {
    method: 'PATCH',
    path: '/v1/admin/organizations/:id/address',
    pathParams: UpdateAdminOrganizationAddressPathParamsSchema,
    body: UpdateAdminOrganizationAddressBodySchema,
    response: { 200: UpdateAdminOrganizationAddressResponseSchema },
    permission: 'backoffice:identity:organization-update',
    description: 'Replace the address row backing an organization.',
  },
  updateName: {
    method: 'PATCH',
    path: '/v1/admin/organizations/:id/name',
    pathParams: UpdateAdminOrganizationNamePathParamsSchema,
    body: UpdateAdminOrganizationNameBodySchema,
    response: { 200: UpdateAdminOrganizationNameResponseSchema },
    permission: 'backoffice:identity:organization-update',
    description: 'Rename an organization.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminApiCallsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/api-calls',
    query: ListApiCallsQuerySchema,
    response: { 200: ListApiCallsResponseSchema },
    permission: 'backoffice:identity:api-call-read',
    description:
      'Browse the inbound-call log newest-first across every workspace and key, keyset-paginated — the only request log since IMM-494.3 folded the per-key trail into it. Filter by workspaceId, apiKeyId, credentialClass, outcome, method, endpoint and time range: all optional and AND-combined, and omitting every filter returns everything, including calls whose workspace or key never resolved. `workspaceUnresolved` / `apiKeyUnresolved` select exactly those NULL rows and are mutually exclusive with their id filter. An unknown id yields an empty page, never a 404. Calls resolved to a workspace marked as a testing environment are hidden unless `includeTestWorkspaces=true`; unresolved calls are returned in both states. List items omit both request and response bodies. Operator-only; not part of the public API-docs surface.',
  },
  get: {
    method: 'GET',
    path: '/v1/admin/api-calls/:callId',
    pathParams: GetApiCallPathParamsSchema,
    response: { 200: GetApiCallResponseSchema },
    permission: 'backoffice:identity:api-call-read',
    description:
      'Return one call-log entry by its own id with all metadata, the stored request and response bodies (null when suppressed or absent — read `bodiesStored` to tell them apart), and the replay verdict. No key scoping: a keyless or workspace-less entry loads exactly like any other. 404 identity.api-call-log.not-found when the id is unknown. Operator-only; not part of the public API-docs surface.',
  },
  replay: {
    method: 'POST',
    path: '/v1/admin/api-calls/:callId/replay',
    pathParams: ReplayApiCallPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: ReplayApiKeyRequestResponseSchema },
    permission: 'backoffice:identity:api-key-request-replay',
    description:
      "Re-submit a rejected call from the log (IMM-458, generalised onto the call log by IMM-494.3), recovering a push we wrongly refused without asking the partner to resend it. Runs as the original API key so partner attribution is preserved. Only entries whose `replayable` is true may be replayed — 422 identity.api-key-request-log.not-replayable otherwise, carrying the blocking reason. Eligibility is a policy allow-list holding the lead push alone; every other endpoint reports ENDPOINT_NOT_REPLAYABLE. Replays are idempotent where the endpoint is: a lead push replayed twice returns the lead created the first time rather than a duplicate. Returns the replayed call's status and body; a payload that was genuinely invalid fails again and reports `succeeded: false`.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The operator's e-mail log (IMM-1076.2). Named const for the same reason as the
 * other admin groups; no `docs` key because the route is operator-only.
 */
const adminEmailLogContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/email-log',
    query: ListEmailLogQuerySchema,
    response: { 200: ListEmailLogResponseSchema },
    permission: 'backoffice:identity:audit-event-read',
    description:
      'Browse the log of e-mails immoOS handed to its e-mail provider, newest first across every workspace, keyset-paginated. Filter by recipient (`q`, a case-insensitive substring; `recipient`, an exact match ignoring case), workspaceId, kind and status: all optional and AND-combined. `recipient` matches the address exactly, ignoring case. An unknown workspaceId yields an empty page, never a 404. Entries older than 90 days are never returned. Carries no e-mail body and no link. Operator-only; not part of the public API-docs surface.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminMembersContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/members',
    query: ListAdminMembersQuerySchema,
    response: { 200: ListAdminMembersResponseSchema },
    permission: 'backoffice:identity:member-read',
    description:
      "List every member across every workspace, newest first. Members of workspaces marked as testing environments are hidden unless `includeTestWorkspaces=true`. Each row carries the member's status, every role they hold (each once), the name of their Primärer Standort and their last activity (`null` when never signed in). `organizationIds` lists every Standort the member belongs to — the Primärer Standort and every other — each once, ascending; `[]` for a member of none. `onboardingOpenCount` is the number of the member's mandatory onboarding items not done (0 = fully onboarded), derived by the same rules as `GET /v1/admin/members/:id/onboarding`.",
  },
  get: {
    method: 'GET',
    path: '/v1/admin/members/:id',
    pathParams: GetAdminMemberPathParamsSchema,
    response: { 200: GetAdminMemberResponseSchema },
    permission: 'backoffice:identity:member-read',
    description:
      "Return a single member by id with its workspace, IdP-resolved identity and status, plus their last activity (`null` when never signed in), a short-lived profile photo URL (`null` when none), the profile's position, phone and mobile, their workspace roles, and one entry per organisation the member belongs to, with its name, the roles held there and whether it is the member's primary organisation.",
  },
  onboarding: {
    method: 'GET',
    path: '/v1/admin/members/:id/onboarding',
    pathParams: GetAdminMemberPathParamsSchema,
    response: { 200: GetAdminMemberOnboardingResponseSchema },
    permission: 'backoffice:identity:member-read',
    description:
      'The onboarding checklist of a member: the 10 items in display order, each with its group, whether it is mandatory, its state and the evidence behind it, plus how many of the mandatory items are done. Derived on every read from what immoOS already holds; nothing is stored. An expired connection is open; a connection that lapses within 48 hours is done with state EXPIRING. Operator-only; not part of the public API-docs surface.',
  },
  listSignIns: {
    method: 'GET',
    path: '/v1/admin/members/:id/sign-ins',
    pathParams: GetAdminMemberPathParamsSchema,
    response: { 200: ListAdminMemberSignInsResponseSchema },
    permission: 'backoffice:identity:audit-event-read',
    description:
      'The 20 most recent sign-in events (sign-in, failed sign-in, sign-out) of the person behind a member, newest first, from the last 90 days of the audit trail. Sign-ins belong to the person: every membership of one user returns the same list.',
  },
  listActivity: {
    method: 'GET',
    path: '/v1/admin/members/:id/activity',
    pathParams: GetAdminMemberPathParamsSchema,
    query: ListAdminMemberActivityQuerySchema,
    response: { 200: ListAdminMemberActivityResponseSchema },
    permission: 'backoffice:identity:audit-event-read',
    description:
      "The member's 50 most recent activity events of the last 90 days, newest first, optionally narrowed to one group, plus the number of Europe/Berlin calendar days among today and the 29 before it on which the member was active themself. Events are a fixed set of named kinds derived from the audit trail and identity's own records; the raw audit trail is never returned. Operator edits of the member (name and e-mail, profile, roles and Standorte) are listed as EDITED_BY_OPERATOR in the ADMIN_ACTIONS group. Sign-ins belong to the person, so every membership of one user lists the same sign-ins. 404 identity.member.not-found when the id is unknown. Operator-only; not part of the public API-docs surface.",
  },
  update: {
    method: 'PATCH',
    path: '/v1/admin/members/:id',
    pathParams: UpdateAdminMemberPathParamsSchema,
    body: UpdateAdminMemberBodySchema,
    response: { 200: UpdateAdminMemberResponseSchema },
    permission: 'backoffice:identity:member-update',
    description:
      "Update a member's e-mail and name, pushed through to the IdP, and optionally their Position, Telefon and Mobil, and optionally the profile photo (avatarMediaId: a confirmed upload's file id, or null to remove it). An omitted profile field is left unchanged; null or an empty string clears it. A save that changes nothing writes nothing.",
  },
  createAvatarUpload: {
    method: 'POST',
    path: '/v1/admin/members/:id/avatar/upload',
    pathParams: GetAdminMemberPathParamsSchema,
    body: CreateMemberAvatarUploadBodySchema,
    response: { 200: CreateMediaUploadResponseSchema },
    permission: 'backoffice:identity:member-update',
    description:
      "Operator: begin a direct-to-storage upload of a member's profile photo, in the member's workspace, under the fixed avatar policy (JPEG/PNG/WebP, 5 MB). 422 when the declared type or size violates it; 404 identity.member.not-found. The client then posts the bytes and calls confirm. Operator-only; not part of the public API-docs surface.",
  },
  confirmAvatarUpload: {
    method: 'POST',
    path: '/v1/admin/members/:id/avatar/upload/:fileId/confirm',
    pathParams: AdminMemberAvatarUploadPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: ConfirmMediaUploadResponseSchema },
    permission: 'backoffice:identity:member-update',
    description:
      "Operator: confirm a pending profile-photo upload in the member's workspace (bytes verified, file ready). Pass the fileId to PATCH /v1/admin/members/:id as avatarMediaId to attach it. 422 when byte-level validation fails; 404 when the member or the file is unknown in that workspace. Operator-only; not part of the public API-docs surface.",
  },
  updateAccess: {
    method: 'PUT',
    path: '/v1/admin/members/:id/access',
    pathParams: GetAdminMemberPathParamsSchema,
    body: UpdateAdminMemberAccessBodySchema,
    response: { 200: UpdateAdminMemberAccessResponseSchema },
    permission: 'backoffice:identity:member-update',
    description:
      "Operator replace of a member's access: their workspace role (null for none), their roles at each Standort (a Standort left out is left; each listed Standort needs at least one role) and their Primärer Standort. The customer app's guards apply: 403 identity.member.workspace-must-retain-owner, 422 identity.member.affiliation-required / primary-not-in-affiliation / affiliation-requires-role, 404 identity.organization.not-found for a Standort that is not an active one of the member's workspace, 404 identity.member.not-found. All or nothing. Recorded against the operator. Operator-only; not part of the public API-docs surface.",
  },
  deactivate: {
    method: 'POST',
    path: '/v1/admin/workspaces/:workspaceId/members/:id/deactivate',
    pathParams: DeactivateWorkspaceMemberPathParamsSchema,
    body: EmptyObjectSchema,
    response: { 200: DeactivateWorkspaceMemberResponseSchema },
    permission: 'backoffice:identity:member-update',
    description:
      "Operator deactivation of a workspace member — the same deactivation as the workspace's own, recorded against the operator. 403 with identity.member.cannot-deactivate-last-owner when no other active member would keep workspace-wide access; 422 with identity.member.already-deactivated when already inactive; 404 with identity.member.not-found when the member is not in that workspace.",
  },
  reactivate: {
    method: 'POST',
    path: '/v1/admin/workspaces/:workspaceId/members/:id/reactivate',
    pathParams: ReactivateWorkspaceMemberPathParamsSchema,
    body: EmptyObjectSchema,
    response: { 200: ReactivateWorkspaceMemberResponseSchema },
    permission: 'backoffice:identity:member-update',
    description:
      'Operator reactivation of a deactivated workspace member, recorded against the operator. 422 with identity.member.already-active when already active; 404 with identity.member.not-found when the member is not in that workspace.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminDocumentAnalysisSkillsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/skills/document-analyses',
    response: { 200: ListDocumentAnalysisSkillsResponseSchema },
    permission: 'backoffice:product-skill:read',
    description:
      'List every analysable document type as a managed-prompt skill, grouped by taxonomy, with status + last-edited attribution.',
  },
  get: {
    method: 'GET',
    path: '/v1/admin/skills/document-analyses/:category',
    pathParams: DocumentAnalysisSkillPathParamsSchema,
    response: { 200: DocumentAnalysisSkillViewSchema },
    permission: 'backoffice:product-skill:read',
    description: "A single skill's editor view: prompt, status, effective default, version and attribution.",
  },
  save: {
    method: 'PUT',
    path: '/v1/admin/skills/document-analyses/:category',
    pathParams: DocumentAnalysisSkillPathParamsSchema,
    body: SaveDocumentAnalysisSkillPromptBodySchema,
    response: { 200: DocumentAnalysisSkillViewSchema },
    permission: 'backoffice:product-skill:manage',
    description:
      'Upsert a managed prompt — expectedVersion null creates + activates a new skill, a number edits an existing one (optimistic-concurrency guarded).',
  },
  activate: {
    method: 'POST',
    path: '/v1/admin/skills/document-analyses/:category/activate',
    pathParams: DocumentAnalysisSkillPathParamsSchema,
    body: SetDocumentAnalysisSkillStatusBodySchema,
    response: { 200: DocumentAnalysisSkillViewSchema },
    permission: 'backoffice:product-skill:manage',
    description: 'Re-activate an inactive skill so its stored prompt drives subsequent analyses again.',
  },
  deactivate: {
    method: 'POST',
    path: '/v1/admin/skills/document-analyses/:category/deactivate',
    pathParams: DocumentAnalysisSkillPathParamsSchema,
    body: SetDocumentAnalysisSkillStatusBodySchema,
    response: { 200: DocumentAnalysisSkillViewSchema },
    permission: 'backoffice:product-skill:manage',
    description: 'Deactivate a skill so subsequent analyses of its type revert to the code default.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The codification advisory (IMM-436.9). Named rather than inline for the reason
 * given in the note above `contracts`.
 */
const adminCodificationAdvisoryContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/skills/codification-candidates',
    response: { 200: ListCodificationCandidatesResponseSchema },
    permission: 'backoffice:product-skill:read',
    description:
      'Skills whose recent runs have settled into one repeated tool sequence — candidates for deterministic, LLM-free execution.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminToolCatalogContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/skills/tool-catalog',
    query: ListToolCatalogQuerySchema,
    response: { 200: ListToolCatalogResponseSchema },
    permission: 'backoffice:product-skill:read',
    description:
      "Every tool an agent skill's allowlist may name, with its kind, side-effect class and required permission. Pass includeDeprecated to also resolve tools an existing allowlist still references.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminAgentSkillsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/skills/agent-skills',
    response: { 200: ListAgentSkillDefinitionsResponseSchema },
    permission: 'backoffice:product-skill:read',
    description:
      'List every agent-skill definition, newest first, with its status, supported triggers and last-edited attribution.',
  },
  create: {
    method: 'POST',
    path: '/v1/admin/skills/agent-skills',
    body: CreateAgentSkillDefinitionBodySchema,
    response: { 201: AgentSkillDefinitionViewSchema },
    permission: 'backoffice:product-skill:manage',
    description:
      'Author a new agent-skill definition. It lands inactive — activating it is a separate, audited step. The key is immutable thereafter.',
  },
  get: {
    method: 'GET',
    path: '/v1/admin/skills/agent-skills/:key',
    pathParams: AgentSkillDefinitionPathParamsSchema,
    response: { 200: AgentSkillDefinitionViewSchema },
    permission: 'backoffice:product-skill:read',
    description: "A single definition's editor view: instructions, triggers, allowlist, approval window, version.",
  },
  update: {
    method: 'PUT',
    path: '/v1/admin/skills/agent-skills/:key',
    pathParams: AgentSkillDefinitionPathParamsSchema,
    body: UpdateAgentSkillDefinitionBodySchema,
    response: { 200: AgentSkillDefinitionViewSchema },
    permission: 'backoffice:product-skill:manage',
    description:
      "Edit a definition's fields (the key is immutable), optimistic-concurrency guarded by expectedVersion. Does not change its status.",
  },
  activate: {
    method: 'POST',
    path: '/v1/admin/skills/agent-skills/:key/activate',
    pathParams: AgentSkillDefinitionPathParamsSchema,
    body: SetAgentSkillDefinitionStatusBodySchema,
    response: { 200: AgentSkillDefinitionViewSchema },
    permission: 'backoffice:product-skill:manage',
    description: 'Activate an inactive definition, resuming every instance the operator previously paused.',
  },
  deactivate: {
    method: 'POST',
    path: '/v1/admin/skills/agent-skills/:key/deactivate',
    pathParams: AgentSkillDefinitionPathParamsSchema,
    body: SetAgentSkillDefinitionStatusBodySchema,
    response: { 200: AgentSkillDefinitionViewSchema },
    permission: 'backoffice:product-skill:manage',
    description:
      'Deactivate an active definition. Every instance of it pauses; grants and run history stay intact and re-activating resumes them unchanged.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminSystemSkillsContracts = {
  get: {
    method: 'GET',
    path: '/v1/admin/skills/system/document-analysis-fallback',
    response: { 200: SystemSkillViewSchema },
    permission: 'backoffice:product-skill:read',
    description:
      "The system document-analysis fallback skill's editor view: prompt, version and attribution. Applied to every document without a matching active category skill.",
  },
  save: {
    method: 'PUT',
    path: '/v1/admin/skills/system/document-analysis-fallback',
    body: SaveSystemSkillPromptBodySchema,
    response: { 200: SystemSkillViewSchema },
    permission: 'backoffice:product-skill:manage',
    description:
      'Edit the system document-analysis fallback prompt (optimistic-concurrency guarded). The fallback is the analysis floor — it cannot be created or deactivated.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The two machine-facing fleet routes (IMM-910.1, ADR 0047). `permission:
 * 'PUBLIC'` means "no bearer token required" — both leaves authenticate the
 * caller themselves, in a machine-signature preHandler that runs outside the
 * tier-middleware stack. Neither carries `docs`: the caller is the
 * `fleet-agent` host component (IMM-910.3), never a browser or a partner
 * integration.
 */
const fleetMachinesContracts = {
  enrol: {
    method: 'POST',
    path: '/v1/fleet/machines/enrol',
    body: EnrolFleetMachineBodySchema,
    response: { 200: EnrolFleetMachineResponseSchema },
    permission: 'PUBLIC',
    description:
      'A machine joins the fleet queue. Authenticated by the shared X-Immo-Fleet-Key plus a BIP-340 signature over the request — the key authenticates, it does not authorise. Re-enrolling an existing public key updates its descriptive fields without touching its approval.',
  },
  self: {
    method: 'GET',
    path: '/v1/fleet/machines/me',
    response: { 200: FleetMachineSelfResponseSchema },
    permission: 'PUBLIC',
    description:
      "The machine's own poll, authenticated by a BIP-340 signature. The first poll after approval mints and returns the machine token exactly once; every later poll returns machineToken: null.",
  },
  /**
   * Every machine of the fleet and its lanes, read by a MACHINE (IMM-939).
   *
   * What turns a NAME into the id every command route addresses — an operator
   * at a terminal knows `mini-pc`, never a uuid — and what `fleet-agent agent fleet
   * machines` prints. Fleet-wide for the same reason the live-jobs read is:
   * the host worth commanding is rarely the host being typed at.
   */
  peers: {
    method: 'GET',
    path: '/v1/fleet/machines',
    response: { 200: ListFleetPeersResponseSchema },
    permission: 'PUBLIC',
    description:
      "Every machine in the fleet with its lanes' current states, `mine` marking the caller's own. Readable by any approved machine; carries no operator's name.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The three machine-facing job routes (IMM-914.1, ADR 0047). Same tier as
 * `fleetMachinesContracts`: `permission: 'PUBLIC'` means no bearer token, and
 * each leaf authenticates its caller in a machine-signature preHandler over the
 * `job` purpose. No `docs` — the caller is the host component, never a browser
 * or a partner integration.
 */
/**
 * A lane worker reports its lane when it starts (OPS-69). Signed with the `job` purpose. The backend keeps it for the
 * operator's board only — the flow stays in the project's config.
 */
const fleetLanesContracts = {
  report: {
    method: 'POST',
    path: '/v1/fleet/lanes',
    body: ReportFleetLaneBodySchema,
    response: { 200: ReportFleetLaneResponseSchema },
    permission: 'PUBLIC',
    description:
      "A lane worker says which lane it serves and how the lane picks: project, lane, its place in the project's lane order, its `jobs/next` queues, running state, worker cap and hold labels. Kept per project and lane for the operator's board; the worker counts as serving the lane for ten minutes after each report.",
  },
  /** A worker's blocker watch puts each blocker above what it blocks in the lane's queue (OPS-86). */
  blockersFirst: {
    method: 'POST',
    path: '/v1/fleet/lanes/blockers-first',
    body: OrderFleetLaneBlockersBodySchema,
    response: { 200: OrderFleetLaneBlockersResponseSchema },
    permission: 'PUBLIC',
    description:
      "A worker sends a lane's blocker pairs (`blocker` must be built before `blocked`). Wherever the lane's queue, in the order `jobs/next` hands it out, shows a blocked ticket above its blocker, the blocker is moved directly above it, as the operator's drag would. Answers the moves made; a pair not in the queue, or a lane not reported, moves nothing.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const fleetJobsContracts = {
  claim: {
    method: 'POST',
    path: '/v1/fleet/jobs/claim',
    body: ClaimFleetJobBodySchema,
    response: { 200: ClaimFleetJobResponseSchema },
    permission: 'PUBLIC',
    description:
      'A worker claims a work item. Exactly one live job may hold a key, and one may hold a ticket across every lane, each enforced by a partial unique index — the loser is refused rather than queued.',
  },
  /**
   * The backend's pick and claim in one step (fleet-agent ADR 0011, rollout
   * step 3): the lane sends its queues, the backend picks from its tracker
   * snapshot, claims, and moves the ticket to the lane's running state.
   */
  next: {
    method: 'POST',
    path: '/v1/fleet/jobs/next',
    body: ClaimNextFleetJobBodySchema,
    response: { 200: ClaimNextFleetJobResponseSchema },
    permission: 'PUBLIC',
    description:
      "A worker asks for its next job: the backend picks the first takeable ticket of the lane's queues from the tracker snapshot (at most 30s old), claims it as `{ticket}-{lane}`, and moves it to the lane's running state. `job: null` when nothing is takeable; refused with `worker-busy` while the worker holds a live job.",
  },
  start: {
    method: 'POST',
    path: '/v1/fleet/jobs/:jobId/start',
    pathParams: FleetJobPathParamsSchema,
    body: StartFleetJobBodySchema,
    response: { 200: StartFleetJobResponseSchema },
    permission: 'PUBLIC',
    description: 'The holder records that the work began. Idempotent — a second call keeps the first startedAt.',
  },
  /**
   * Every live job in the fleet, read by a machine (IMM-938).
   *
   * A host that lost a `jobId` — a tick killed between claim and state-write,
   * a round that predates the end-call — has no other way to name the job
   * wedging its lane, and the `worker-busy` refusal only fires when there is a
   * ticket to claim. Fleet-wide rather than caller-scoped because the wedged
   * host is often not the one with a terminal in front of it.
   */
  live: {
    method: 'GET',
    path: '/v1/fleet/jobs/live',
    response: { 200: ListLiveFleetJobsResponseSchema },
    permission: 'PUBLIC',
    description:
      "Every live job the fleet holds, oldest claim first, with `mine` marking the calling machine's own. Readable by any approved machine.",
  },
  /**
   * Every key still bound to the worker that last held it. Ended jobs, so they
   * are NOT in `live`: an old lane reading `live` must never mistake one for a
   * hold.
   */
  bound: {
    method: 'GET',
    path: '/v1/fleet/jobs/bound',
    response: { 200: ListBoundFleetJobsResponseSchema },
    permission: 'PUBLIC',
    description:
      'Every key whose latest round ended and stays bound to its worker until a manual release, newest binding first. Readable by any approved machine.',
  },
  /** How often each named work item has run, blocked and resumed — the counts `fleet-agent agent preview` draws. */
  history: {
    method: 'POST',
    path: '/v1/fleet/jobs/history',
    body: ListFleetJobHistoryBodySchema,
    response: { 200: ListFleetJobHistoryResponseSchema },
    permission: 'PUBLIC',
    description:
      'Per work-item key: how many rounds were claimed, how many ended parked, and how many of those a later round picked back up. Keys that never ran are absent. Readable by any approved machine.',
  },
  /**
   * A machine hands a job back — its own, or a dead holder's. Separate from
   * `end`, whose epoch and ownership guards are what keep a woken-up zombie
   * from closing work that has since been re-claimed.
   */
  release: {
    method: 'POST',
    path: '/v1/fleet/jobs/:jobId/release',
    pathParams: FleetJobPathParamsSchema,
    body: ReleaseHeldFleetJobBodySchema,
    response: { 200: ReleaseHeldFleetJobResponseSchema },
    permission: 'PUBLIC',
    description:
      "Any approved machine ends a live job as `abandoned` — what became of the holder's work is what this call cannot know — or, for a job that already ended but is still bound to its worker, drops that binding. Either way the key is free for any worker to claim.",
  },
  /**
   * "It's fine, leave it" on a suspect job (fleet-agent ADR 0012). Any approved
   * machine may say it, like `release` — the host with a terminal in front of it
   * is often not the holder.
   */
  clearSuspect: {
    method: 'POST',
    path: '/v1/fleet/jobs/:jobId/clear-suspect',
    pathParams: FleetJobPathParamsSchema,
    response: { 200: ClearFleetJobSuspectResponseSchema },
    permission: 'PUBLIC',
    description:
      "Any approved machine drops a live job's suspect flag. The holder keeps sending its flag until the round ends; one whose `since` is not after this clear is ignored. 404 fleet.job.not-found for a job that is not live.",
  },
  /**
   * Release by worker, for a host that knows it is stuck and cannot name the
   * job holding it. A worker name binds a machine to an agent, and only one
   * live job may hold a worker — the same rule that produces `worker-busy`.
   */
  releaseByWorker: {
    method: 'POST',
    path: '/v1/fleet/jobs/release-by-worker',
    body: ReleaseFleetJobsByWorkerBodySchema,
    response: { 200: ReleaseFleetJobsByWorkerResponseSchema },
    permission: 'PUBLIC',
    description:
      'Any approved machine ends the live job held by a named worker (kept bound to it unless `unbind` is set, which also drops every binding the worker holds). Returns null when that worker was holding none, which is a success: the caller wanted it free, and it is.',
  },
  /** Extra runs for one work item past the cap (OPS-33); an append-only grant, never a reset. */
  topUp: {
    method: 'POST',
    path: '/v1/fleet/jobs/top-up',
    body: TopUpFleetJobBodySchema,
    response: { 200: TopUpFleetJobResponseSchema },
    permission: 'PUBLIC',
    description:
      "Any approved machine grants a work item extra runs past the per-ticket cap. Grants add up and are never removed; returns the key's new limit.",
  },
  resumable: {
    method: 'POST',
    path: '/v1/fleet/jobs/resumable',
    body: ListResumableFleetJobsBodySchema,
    response: { 200: ListResumableFleetJobsResponseSchema },
    permission: 'PUBLIC',
    description:
      'A worker lists the jobs bound to it whose every question is answered, oldest binding first, each with the answers it must put on the ticket before it claims. Empty for an unapproved machine.',
  },
  end: {
    method: 'POST',
    path: '/v1/fleet/jobs/:jobId/end',
    pathParams: FleetJobPathParamsSchema,
    body: EndFleetJobBodySchema,
    response: { 200: EndFleetJobResponseSchema },
    permission: 'PUBLIC',
    description:
      "The holder ends the job, freeing its key. A worker may not write the operator's `released` reason. A `finished` end may carry `move` and a `parked` end `park`: the backend writes the ticket (moved only from a state in `from`) and says what became of it in `ticket`; a failed tracker write still ends the job.",
  },
  askQuestions: {
    method: 'POST',
    path: '/v1/fleet/jobs/:jobId/questions',
    pathParams: FleetJobPathParamsSchema,
    body: AskFleetJobQuestionsBodySchema,
    response: { 200: FleetRunQuestionsResponseSchema },
    permission: 'PUBLIC',
    description:
      'The holder records what it cannot decide against the job it still holds, and waits for the answer instead of parking.',
  },
  readQuestions: {
    method: 'POST',
    path: '/v1/fleet/jobs/:jobId/questions/read',
    pathParams: FleetJobPathParamsSchema,
    body: ReadFleetJobQuestionsBodySchema,
    response: { 200: FleetRunQuestionsResponseSchema },
    permission: 'PUBLIC',
    description:
      'The holder reads every question on its job, answered or not. Refused once the job has ended, which is how a waiting run learns it was released.',
  },
  // OPS-54: the round narrates the job it holds — the replacement for its Buzz thread.
  appendEvent: {
    method: 'POST',
    path: '/v1/fleet/jobs/:jobId/events',
    pathParams: FleetJobPathParamsSchema,
    body: AppendFleetJobEventBodySchema,
    response: { 200: AppendFleetJobEventResponseSchema },
    permission: 'PUBLIC',
    description:
      "The machine that holds (or held) a job at this epoch appends one event to its timeline — claimed, progress, phase, done, dead-letter or note. Accepted after the job ended, so a round's terminal line still lands; refused once another epoch or machine owns the key's row.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The tracker snapshot a machine reads its queue from (fleet-agent ADR 0011,
 * rollout step 1). Signed over the `job` purpose like the job routes beside
 * it. The backend reads the tracker at most once per team per
 * `FLEET_QUEUE_FLOOR_MS`, however many machines ask, and holds no lane
 * config: the reader filters the open issues by its own `pick-from`.
 */
const fleetQueueContracts = {
  read: {
    method: 'POST',
    path: '/v1/fleet/queue',
    body: ReadFleetQueueBodySchema,
    response: { 200: ReadFleetQueueResponseSchema },
    permission: 'PUBLIC',
    description:
      "Any approved machine reads a tracker team's open issues and its workflow states in board order. Served from a snapshot at most 30s old; a stale one is re-read once across every backend replica. When the tracker is down, the last snapshot with its old fetchedAt; with none, 502.",
  },
  comments: {
    method: 'POST',
    path: '/v1/fleet/queue/comments',
    body: ReadFleetQueueCommentsBodySchema,
    response: { 200: ReadFleetQueueCommentsResponseSchema },
    permission: 'PUBLIC',
    description:
      'Any approved machine reads the last ten comments of up to 50 tickets, newest first, each cached 30s. A ticket the tracker does not know is absent.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * A machine COMMANDING a peer (IMM-939) — the terminal's half of what the
 * Fleet page does with a mouse.
 *
 * The same two leaves the operator has (`issue`, `get`), against the same
 * handlers and the same `FleetCommandBodySchema`, differing only in who is
 * authenticated: a BIP-340 signature over the `command` purpose rather than an
 * admin session. There is no machine-facing `list` — a CLI reads back the one
 * command it just issued, and a whole machine's history is the operator page's
 * job.
 *
 * Any approved machine may command any other. The fleet is one owner's set of
 * hosts, and the wedged host is rarely the one with a terminal in front of it.
 */
const fleetCommandsContracts = {
  issue: {
    method: 'POST',
    path: '/v1/fleet/machines/:machineId/commands',
    pathParams: FleetCommandsPathParamsSchema,
    body: FleetCommandBodySchema,
    response: { 202: FleetCommandAcceptedResponseSchema },
    permission: 'PUBLIC',
    description:
      'An approved machine issues a command against another approved machine. Accepted and recorded immediately; the addressed host collects it on its next tick.',
  },
  get: {
    method: 'GET',
    path: '/v1/fleet/machines/:machineId/commands/:commandId',
    pathParams: FleetCommandPathParamsSchema,
    response: { 200: FleetCommandViewSchema },
    permission: 'PUBLIC',
    description:
      "A single command's outcome, read by an approved machine — what the issuing 202's commandId opens, and what the CLI polls until the verb settles.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Where a worker posts a command's outcome (IMM-914.4) — the route that
 * replaces the relay's outcome event. Same machine-signed tier as the tick.
 */
const fleetCommandOutcomeContracts = {
  record: {
    method: 'POST',
    path: '/v1/fleet/commands/:commandId/outcome',
    pathParams: FleetCommandOutcomePathParamsSchema,
    body: RecordFleetCommandOutcomeBodySchema,
    response: { 200: RecordFleetCommandOutcomeResponseSchema },
    permission: 'PUBLIC',
    description:
      'A worker reports what a verb did. Re-delivering an outcome for a command that has already settled is accepted and changes nothing.',
  },
  // OPS-57: a host's step on a command it holds, posted as it happens.
  progress: {
    method: 'POST',
    path: '/v1/fleet/commands/:commandId/progress',
    pathParams: FleetCommandOutcomePathParamsSchema,
    body: RecordFleetCommandProgressBodySchema,
    response: { 200: RecordFleetCommandProgressResponseSchema },
    permission: 'PUBLIC',
    description:
      'A worker reports a step on a command it holds — received, then running. The first time recorded per step stands; a step for a settled command changes nothing.',
  },
  // OPS-57: what a doorbell ring fetches — the tick's commands without the report.
  take: {
    method: 'POST',
    path: '/v1/fleet/commands/take',
    response: { 200: TakeFleetCommandsResponseSchema },
    permission: 'PUBLIC',
    description:
      'A worker takes the commands waiting for it, marked sent in the same statement — what a tick hands out, without the report.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The host's tick (IMM-914.3, ADR 0047): one authenticated call carrying its
 * profile status and its collected log lines, answering with whether it still
 * holds its job and what it must run. Same tier as the other machine-facing
 * leaves — PUBLIC auth, a machine-signature preHandler over the `tick` purpose,
 * no docs.
 */
const fleetTickContracts = {
  tick: {
    method: 'POST',
    path: '/v1/fleet/tick',
    body: FleetTickBodySchema,
    response: { 200: FleetTickResponseSchema },
    permission: 'PUBLIC',
    description:
      "A worker's regular call: status and logs up, commands and its claim answer down. This is the only liveness signal the fleet acts on.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The host's doorbell (OPS-26): a machine-signed Server-Sent Events stream that
 * tells a host to tick NOW rather than at its next interval. It carries the kind
 * of change only — the tick stays the one path commands travel down — so a
 * dropped stream costs latency, never a command.
 */
const fleetStreamContracts = {
  stream: {
    kind: 'stream',
    method: 'GET',
    path: '/v1/fleet/stream',
    permission: 'PUBLIC',
    event: FleetStreamEventSchema,
    description:
      'Server-Sent Events stream an approved machine holds open; each event names a kind of change waiting for it, and the machine answers by ticking. Best-effort: no replay, the tick stays authoritative.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * A host's terminal reads stored log lines (ADR 0057), machine-signed like the
 * job routes — the machine identity carries no actor, so `/v1/admin/*` is
 * closed to `fleet-agent agent logs`. Any approved machine may read any machine's
 * lines: the fleet is one owner's hosts.
 */
const fleetLogsContracts = {
  read: {
    method: 'GET',
    path: '/v1/fleet/logs',
    query: ReadFleetMachineLogsQuerySchema,
    response: { 200: ReadFleetMachineLogsResponseSchema },
    permission: 'PUBLIC',
    description:
      'The newest stored lines on one stream for a machine (by name or hostname), grouped per worker, oldest first. Reads rows the fleet agent already pushed; it never reaches the machine. Readable by any approved machine.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The run ledger's totals (IMM-911.2): the same answer for the same query on
 * the machine route (a host's own CLI) and the operator route.
 */
const fleetMetricsContracts = {
  totals: {
    method: 'GET',
    path: '/v1/fleet/metrics/totals',
    query: FleetRunTotalsQuerySchema,
    response: { 200: FleetRunTotalsSchema },
    permission: 'PUBLIC',
    description:
      'Run and token totals over any combination of profile, worker, ticket, day, week and skill. Readable by any enrolled machine, in any approval state.',
  },
} as const satisfies Record<string, EndpointDefinition>;

const adminFleetMetricsContracts = {
  totals: {
    method: 'GET',
    path: '/v1/admin/fleet/metrics/totals',
    query: FleetRunTotalsQuerySchema,
    response: { 200: FleetRunTotalsSchema },
    permission: 'backoffice:fleet:read',
    description:
      'Run and token totals over any combination of profile, worker, ticket, day, week and skill, computed on read from the run ledger.',
  },
  series: {
    method: 'GET',
    path: '/v1/admin/fleet/metrics/series',
    query: FleetMetricsSeriesQuerySchema,
    response: { 200: FleetMetricsSeriesSchema },
    permission: 'backoffice:fleet:read',
    description:
      'Runs, runtime and the four token classes per time bucket (Europe/Berlin), grouped by up to two of machine, agent and model, with the previous window and an optional weekday × hour heatmap. Computed on read from the run ledger.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The operator routes over the fleet's held work (IMM-914.2, ADR 0047),
 * behind `requireAdmin`. Reading the fleet and commanding it are separate
 * grants: releasing has a consequence on another machine, so it sits with
 * `command` rather than with `read`.
 */
/**
 * The operator's board by fleet lane (OPS-69): the lanes workers reported on start, each with its rounds, its
 * stranded tickets, its queue in `jobs/next` order and what it does not take. Built on the same tracker snapshot the
 * machines read, under the same 30s floor. A queue's manual order (OPS-77) is the one write; a ticket's runs are the
 * card's details panel.
 */
const adminFleetBoardContracts = {
  get: {
    method: 'GET',
    path: '/v1/admin/fleet/board',
    query: FleetBoardQuerySchema,
    response: { 200: GetFleetBoardResponseSchema },
    permission: 'backoffice:fleet:read',
    description:
      "The fleet's lanes as a board, for every project (`view=all`, same-named lanes merged) or one. Each lane: its live rounds, tickets stranded in its running state, its queue in the order `jobs/next` hands it out, and the tickets it holds back (bound, parked, out of runs, assisted, waiting on a blocker). Lanes come from what workers reported on start; tickets from the tracker snapshot, re-read at most once per 30s.",
  },
  rank: {
    method: 'PUT',
    path: '/v1/admin/fleet/board/rank',
    body: RankFleetQueueTicketBodySchema,
    response: { 200: RankFleetQueueTicketResponseSchema },
    permission: 'backoffice:fleet:command',
    description:
      "Place a ticket of one project's lane queue right before or after a neighbour in that queue. The manual order sorts after the bound/answered preference and before Linear priority, on the board and in `jobs/next` alike; unranked tickets follow the ranked ones in Linear's order. Ranks of tickets that left the lane are dropped. 409 fleet.queue.not-in-queue when either ticket is not in the queue the board shows now.",
  },
  ticket: {
    method: 'GET',
    path: '/v1/admin/fleet/board/tickets/:ticket',
    pathParams: FleetBoardTicketPathParamsSchema,
    response: { 200: GetFleetBoardTicketResponseSchema },
    permission: 'backoffice:fleet:read',
    description:
      "One ticket's runs across every lane, newest claim first (at most 50): holder, claim, heartbeat, how it ended, the run ledger's steps with their tokens, and the questions it asked with their answers. An unknown ticket answers no runs.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const adminFleetJobsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/fleet/jobs',
    query: ListFleetJobsQuerySchema,
    response: { 200: ListFleetJobsResponseSchema },
    permission: 'backoffice:fleet:read',
    description:
      "Every live job, longest-silent first. `silent` is derived from the holding machine's last-seen moment, never stored.",
  },
  release: {
    method: 'POST',
    path: '/v1/admin/fleet/jobs/:jobId/release',
    pathParams: FleetJobPathParamsSchema,
    body: ReleaseFleetJobBodySchema,
    response: { 200: FleetJobViewSchema },
    permission: 'backoffice:fleet:command',
    description:
      'Hand a piece of work back. The previous holder is refused from its next call onward — releasing a still-reporting holder is deliberate, and is the only remedy for a round that is wedged but alive.',
  },
  /** The operator's "it's fine" on a suspect job (fleet-agent ADR 0012) — the same act as the machine route. */
  clearSuspect: {
    method: 'POST',
    path: '/v1/admin/fleet/jobs/:jobId/clear-suspect',
    pathParams: FleetJobPathParamsSchema,
    response: { 200: ClearFleetJobSuspectResponseSchema },
    permission: 'backoffice:fleet:command',
    description:
      "Drop a live job's suspect flag. A flag the holder sends again with a `since` not after this clear is ignored; a later, newer one is stored. 404 fleet.job.not-found for a job that is not live.",
  },
  // OPS-54: the job's timeline, oldest first.
  events: {
    method: 'GET',
    path: '/v1/admin/fleet/jobs/:jobId/events',
    pathParams: FleetJobPathParamsSchema,
    response: { 200: ListFleetJobEventsResponseSchema },
    permission: 'backoffice:fleet:read',
    description:
      "A job's timeline, oldest first — what its round narrated: the claim, progress, phases and how it ended. At most the most recent 500 events.",
  },
  listMessages: {
    method: 'GET',
    path: '/v1/admin/fleet/jobs/:jobId/messages',
    pathParams: FleetJobPathParamsSchema,
    response: { 200: ListFleetJobMessagesResponseSchema },
    permission: 'backoffice:fleet:read',
    description:
      'Every message sent to a job (OPS-55), oldest first, each with where its delivery stands. 404 fleet.job.not-found for an unknown job.',
  },
  sendMessage: {
    method: 'POST',
    path: '/v1/admin/fleet/jobs/:jobId/messages',
    pathParams: FleetJobPathParamsSchema,
    body: SendFleetJobMessageBodySchema,
    response: { 201: FleetJobMessageViewSchema },
    permission: 'backoffice:fleet:command',
    description:
      "Steer a running job (OPS-55): a bare `stop` ends the run at its next safe point, any other text is an instruction. Delivered to the holding host as a `steer` command, which it writes into the worker's steer inbox. 409 fleet.job.not-held for a job that has ended; 409 fleet.machine.not-approved when its host is not approved.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The operator's two WhatsApp lists (IMM-928.4): a workspace's failed
 * dispatches and the platform-wide inbound messages that matched no record.
 * Internal reads — deliberately no `docs` key.
 */
const adminWhatsAppContracts = {
  listFailedDispatches: {
    method: 'GET',
    path: '/v1/admin/workspaces/:workspaceId/whatsapp/failed-dispatches',
    pathParams: ListWhatsAppFailedDispatchesPathParamsSchema,
    query: WhatsAppOperatorListQuerySchema,
    response: { 200: ListWhatsAppFailedDispatchesResponseSchema },
    permission: 'backoffice:communications:whatsapp-dispatch-read',
    description:
      "A workspace's failed outbound WhatsApp messages, newest first, capped with a `truncated` flag. An unknown workspace answers an empty page.",
  },
  listUnmatchedMessages: {
    method: 'GET',
    path: '/v1/admin/whatsapp/unmatched-messages',
    query: WhatsAppOperatorListQuerySchema,
    response: { 200: ListWhatsAppUnmatchedMessagesResponseSchema },
    permission: 'backoffice:communications:whatsapp-dispatch-read',
    description:
      'Inbound WhatsApp messages immoOS could not route to any record, across every workspace, newest first, capped with a `truncated` flag.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The operator's view of a workspace's portal connections (IMM-1057.10,
 * IMM-246.9). `list` serves the page; `get` (ImmoScout24 only) stays for
 * operator tabs on an older bundle. Internal read on the admin tier —
 * deliberately no `docs` key, and not an `ADMIN_KEY_CONSUMER_LEAVES` member.
 */
const adminPortalConnectionsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/workspaces/:workspaceId/portal-connections',
    pathParams: PortalConnectionPathParamsSchema,
    response: { 200: AdminPortalConnectionsResponseSchema },
    permission: 'backoffice:property:property-read',
    description:
      "A workspace's portal connections for operators, one entry per portal (ImmoScout24, Kleinanzeigen, Immowelt): state, the IS24 account name, who connected or entered the access (name and e-mail) and when, when it expired or became invalid, and the portal's last error verbatim with its time. Never carries credentials, the FTP access data or a pending attempt.",
  },
  get: {
    method: 'GET',
    path: '/v1/admin/workspaces/:workspaceId/portal-connections/immoscout24',
    pathParams: PortalConnectionPathParamsSchema,
    response: { 200: AdminPortalConnectionResponseSchema },
    permission: 'backoffice:property:property-read',
    description:
      "A workspace's ImmoScout24 connection for operators: its state, the IS24 account name, who connected it (name and e-mail) and when, when it expired, and ImmoScout24's last error verbatim with its time. Never carries credentials, the account reference or a pending attempt. A workspace that never started a connection — or an unknown one — reads as NOT_CONNECTED (IMM-1057.10).",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The operator's read of a workspace's e-mail templates (IMM-1177.6): the
 * same rows and object a member of that workspace reads, through the member
 * handlers. Read-only — there is no admin write leaf. Internal read on the
 * admin tier — deliberately no `docs` key.
 */
const adminEmailTemplatesContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/workspaces/:workspaceId/email-templates',
    pathParams: EmailTemplatesPathParamsSchema,
    response: { 200: ListEmailTemplatesResponseSchema },
    permission: 'backoffice:identity:workspace-read',
    description:
      "A workspace's e-mail templates for operators (IMM-1177): the same rows, in the same order, that a member of that workspace lists. Read-only; there is no operator write. An unknown workspace lists nothing.",
  },
  get: {
    method: 'GET',
    path: '/v1/admin/workspaces/:workspaceId/email-templates/:emailTemplateId',
    pathParams: EmailTemplatePathParamsSchema,
    response: { 200: EmailTemplateSchema },
    permission: 'backoffice:identity:workspace-read',
    description:
      'One e-mail template of a workspace for operators: the same object a member of that workspace reads. 404 identity.email-template.not-found when it does not exist in that workspace.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The operator's read of a workspace's Käufer-Maklervertrag Vorlagen (IMM-1431.7):
 * the same rows a member of that workspace lists, through the member handler.
 * Read-only. Internal read on the admin tier — deliberately no `docs` key.
 */
const adminBuyerContractTemplatesContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/workspaces/:workspaceId/buyer-contract-templates',
    pathParams: BuyerContractTemplatesPathParamsSchema,
    response: { 200: ListBuyerContractTemplatesResponseSchema },
    permission: 'backoffice:identity:workspace-read',
    description:
      "A workspace's Käufer-Maklervertrag Vorlagen for operators (IMM-1431): the same rows, in the same order, that a member of that workspace lists — the immoOS standard always, the own Vorlage when there is one, exactly one active. Read-only; there is no operator write. An unknown workspace lists the standard alone.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The operator's messenger chat list and detail (IMM-1127.1): every member's
 * chat across all workspaces — one row per `messenger_member_links` row, ended
 * links included — and a chat's transcript. Internal reads — deliberately no
 * `docs` key.
 */
const adminMessengerChatsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/messenger/chats',
    query: WhatsAppOperatorListQuerySchema,
    response: { 200: ListMessengerChatsResponseSchema },
    permission: 'backoffice:communications:whatsapp-dispatch-read',
    description:
      "Every member's messenger chat (WhatsApp, Telegram) with immoOS across all workspaces — one per account binding, ended ones included — newest activity first, capped with a `truncated` flag (IMM-1127.1).",
  },
  get: {
    method: 'GET',
    path: '/v1/admin/messenger/chats/:chatId',
    pathParams: GetMessengerChatPathParamsSchema,
    response: { 200: GetMessengerChatResponseSchema },
    permission: 'backoffice:communications:whatsapp-dispatch-read',
    description:
      "One member messenger chat: its newest 5 000 messages oldest first, with the member's name, e-mail and workspace. 404 communications.messenger-chat.not-found for an unknown chat id (IMM-1127.1).",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * A parked job's question, answered on the fleet page (IMM-981). Reading the
 * open questions is `read`; answering has a consequence — it unparks a ticket
 * once relayed — so it sits with `command`, like a release.
 */
const adminFleetQuestionsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/fleet/questions',
    response: { 200: ListFleetQuestionsResponseSchema },
    permission: 'backoffice:fleet:read',
    description:
      'Every question not yet on its ticket, oldest first: unanswered ones, and answered ones a worker has not recorded yet (`answer` set). A question drops off once relayed, or once a later round claims its key.',
  },
  answer: {
    method: 'POST',
    path: '/v1/admin/fleet/questions/:questionId/answer',
    pathParams: FleetQuestionPathParamsSchema,
    body: AnswerFleetQuestionBodySchema,
    response: { 200: AnswerFleetQuestionResponseSchema },
    permission: 'backoffice:fleet:command',
    description:
      'Answer a parked question, word for word. A worker relays it to the ticket as an attended decision and drops `needs-human`; a question can be answered once.',
  },
  askAgain: {
    method: 'POST',
    path: '/v1/admin/fleet/questions/:questionId/ask-again',
    pathParams: FleetQuestionPathParamsSchema,
    body: AskFleetQuestionAgainBodySchema,
    response: { 200: AskFleetQuestionAgainResponseSchema },
    permission: 'backoffice:fleet:command',
    description:
      'Put an open question at the front of the operator\'s messenger queue (OPS-79): it goes out next, prefixed "still open since". 409 `fleet.question.already-answered`, `fleet.question.not-open` (moot), or `fleet.question.messenger-not-configured`.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Where a worker collects answered questions to carry to Linear, and reports
 * one carried (IMM-981). Machine-signed over the `job` purpose, like the job
 * routes: the backend has no Linear client, so a worker is the relay.
 */
const fleetQuestionsContracts = {
  take: {
    method: 'POST',
    path: '/v1/fleet/questions/take',
    body: TakeFleetAnswersBodySchema,
    response: { 200: TakeFleetAnswersResponseSchema },
    permission: 'PUBLIC',
    description:
      'Lease every answered, unrelayed question to the calling machine for two minutes. A lease that lapses is offered again, so a worker that dies mid-relay strands nothing.',
  },
  relayed: {
    method: 'POST',
    path: '/v1/fleet/questions/:questionId/relayed',
    pathParams: FleetQuestionPathParamsSchema,
    body: MarkFleetAnswerRelayedBodySchema,
    response: { 200: MarkFleetAnswerRelayedResponseSchema },
    permission: 'PUBLIC',
    description: 'The answer is on the ticket. Idempotent: a second report keeps the first moment.',
  },
  open: {
    method: 'POST',
    path: '/v1/fleet/questions/open',
    body: ListOpenFleetQuestionsBodySchema,
    response: { 200: ListFleetQuestionsResponseSchema },
    permission: 'PUBLIC',
    description:
      "A ticket's questions still waiting for an answer, oldest first — the fleet page's view, minus answered and moot ones. 409 `fleet.machine.not-approved` for a revoked machine.",
  },
  answer: {
    method: 'POST',
    path: '/v1/fleet/questions/:questionId/answer',
    pathParams: FleetQuestionPathParamsSchema,
    body: AnswerFleetQuestionFromMachineBodySchema,
    response: { 200: AnswerFleetQuestionResponseSchema },
    permission: 'PUBLIC',
    description:
      "Answer a parked question from a machine, in one of two bodies. `{ answer, answeredBy }` (`agent unblock`) answers exactly as the fleet page does, attributed to `answeredBy`: 404 `fleet.question.not-found`; 409 `fleet.question.already-answered` or `fleet.question.not-open`. `{ answer, assistantTurnId }` is the fleet assistant recording an answer the operator gave in the chat, attributed to the turn's fleet person and channel, and only for the question the turn is about (any question when it has none); it closes the turn with the confirmation: 404 `fleet.assistant-turn.not-found` or `fleet.question.not-found`; 409 `fleet.assistant-turn.not-held`, `fleet.question.already-answered` (the turn is closed with the notice before the 409), `fleet.question.not-open` or `fleet.machine.not-approved`; 422 `fleet.assistant-turn.out-of-context`. The relay carries the answer to the ticket.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The report inbox, read and written by a person on the fleet page (OPS-63).
 * Reading is `read`; submitting and replying start or restart an intake run,
 * so they sit with `command`, like answering a question.
 */
const adminFleetReportsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/fleet/reports',
    response: { 200: ListFleetReportsResponseSchema },
    permission: 'backoffice:fleet:read',
    description: 'The newest 100 reports, newest first, each with its state in the claim ledger.',
  },
  get: {
    method: 'GET',
    path: '/v1/admin/fleet/reports/:reportId',
    pathParams: FleetReportPathParamsSchema,
    response: { 200: FleetReportDetailSchema },
    permission: 'backoffice:fleet:read',
    description: 'One report with its whole conversation, oldest message first.',
  },
  submit: {
    method: 'POST',
    path: '/v1/admin/fleet/reports',
    body: SubmitFleetReportBodySchema,
    response: { 201: FleetReportDetailSchema },
    permission: 'backoffice:fleet:command',
    description: 'Report something in free text. The report is open: the next intake run claims it.',
  },
  reply: {
    method: 'POST',
    path: '/v1/admin/fleet/reports/:reportId/reply',
    pathParams: FleetReportPathParamsSchema,
    body: ReplyToFleetReportBodySchema,
    response: { 200: FleetReportDetailSchema },
    permission: 'backoffice:fleet:command',
    description:
      "Reply in a report's conversation. A reply to a parked, released or unplaceable report reopens it for the next intake run; a reply to a finished report is kept and changes nothing.",
  },
  releases: {
    method: 'GET',
    path: '/v1/admin/fleet/releases',
    response: { 200: ListFleetReleasesResponseSchema },
    permission: 'backoffice:fleet:read',
    description: 'The newest 50 release announcements, newest first.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The fleet page's chat with the assistant (IMM-1420): the caller's own conversation, the one their Telegram chat
 * writes to. Writing a message is a command, like answering a question, so both routes need `command`.
 */
const adminFleetAssistantContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/fleet/assistant/messages',
    query: ListFleetAssistantMessagesQuerySchema,
    response: { 200: ListFleetAssistantMessagesResponseSchema },
    permission: 'backoffice:fleet:command',
    description:
      "The caller's own conversation with the fleet assistant, Telegram and fleet page together: the newest 100 messages, oldest first. `pending` is true while a message of it waits for, or is being answered by, the assistant. With `questionId`, only the messages whose context is that question (an unknown id gives an empty list), and `pending` is about those. 400 for validation, 403 without the permission.",
  },
  post: {
    method: 'POST',
    path: '/v1/admin/fleet/assistant/messages',
    body: PostFleetAssistantMessageBodySchema,
    response: { 201: FleetAssistantMessageViewSchema },
    permission: 'backoffice:fleet:command',
    description:
      "Write to the fleet assistant in the caller's own conversation. The message becomes a turn like a Telegram one, and its answer stays on the page. With `questionId`, the message has that question as its context, as if it quoted it. 404 `fleet.question.not-found` for an unknown question. 400 for validation (empty or over 4 000 characters), 403 without the permission.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The fleet assistant's side (IMM-1420): a machine takes the next message waiting for the assistant and answers it.
 * Machine-signed over the `job` purpose. The caller is the fleet-agent assistant (IMM-1421, another repository).
 */
const fleetAssistantContracts = {
  take: {
    method: 'POST',
    path: '/v1/fleet/assistant/turns/take',
    body: TakeFleetAssistantTurnBodySchema,
    response: { 200: TakeFleetAssistantTurnResponseSchema },
    permission: 'PUBLIC',
    description:
      'Called by the fleet-agent assistant (IMM-1421). Take the oldest message waiting for the assistant in a conversation with no turn in flight, leased to the calling machine for 10 minutes; `turn` is null when nothing waits. 409 `fleet.machine.not-approved` for a machine that is not approved.',
  },
  reply: {
    method: 'POST',
    path: '/v1/fleet/assistant/turns/:messageId/reply',
    pathParams: FleetAssistantTurnPathParamsSchema,
    body: ReplyToFleetAssistantTurnBodySchema,
    response: { 200: ReplyToFleetAssistantTurnResponseSchema },
    permission: 'PUBLIC',
    description:
      'Called by the fleet-agent assistant (IMM-1421). Answer a turn this machine holds; the answer closes it. A retry of a reply this machine already made returns the stored one. 404 `fleet.assistant-turn.not-found` for an unknown message, 404 `fleet.question.not-found` for an unknown `questionId`, 409 `fleet.assistant-turn.not-held` when another machine holds the turn or it is closed, 409 `fleet.machine.not-approved`.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/** The fleet page's board, read by an approved machine (IMM-1420): what the assistant answers general questions from. */
const fleetBoardContracts = {
  read: {
    method: 'GET',
    path: '/v1/fleet/board',
    query: FleetBoardQuerySchema,
    response: { 200: GetFleetBoardResponseSchema },
    permission: 'PUBLIC',
    description:
      "Called by the fleet-agent assistant (IMM-1421). Exactly what `GET /v1/admin/fleet/board` answers, signed by an approved machine. 409 `fleet.machine.not-approved`, plus the board's own tracker errors.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The intake run's side of the report inbox (OPS-63): the claim ledger's
 * transitions and the conversation. Machine-signed over the `job` purpose,
 * like the job and question routes. Every transition is idempotent for the
 * worker that made it, so a run retrying after a dropped response never fails.
 */
const fleetReportsContracts = {
  open: {
    method: 'GET',
    path: '/v1/fleet/reports/open',
    response: { 200: ListFleetReportsResponseSchema },
    permission: 'PUBLIC',
    description:
      'Every open report, oldest first — what the intake dispatches a run for. Empty for an unapproved machine.',
  },
  read: {
    method: 'GET',
    path: '/v1/fleet/reports/:reportId',
    pathParams: FleetReportPathParamsSchema,
    response: { 200: FleetReportDetailSchema },
    permission: 'PUBLIC',
    description: 'One report with its whole conversation, oldest message first.',
  },
  claim: {
    method: 'POST',
    path: '/v1/fleet/reports/:reportId/claim',
    pathParams: FleetReportPathParamsSchema,
    body: ClaimFleetReportBodySchema,
    response: { 200: ClaimFleetReportResponseSchema },
    permission: 'PUBLIC',
    description:
      'Claim an open report for a worker. `held` when this worker holds it (again, on a retry); `stood-down` when another worker holds it or it is no longer open.',
  },
  say: {
    method: 'POST',
    path: '/v1/fleet/reports/:reportId/say',
    pathParams: FleetReportPathParamsSchema,
    body: SayOnFleetReportBodySchema,
    response: { 200: FleetReportMessageResponseSchema },
    permission: 'PUBLIC',
    description: "The holding worker says something in the report's conversation.",
  },
  park: {
    method: 'POST',
    path: '/v1/fleet/reports/:reportId/park',
    pathParams: FleetReportPathParamsSchema,
    body: ParkFleetReportBodySchema,
    response: { 200: FleetReportTransitionResponseSchema },
    permission: 'PUBLIC',
    description:
      'The holding worker asks the reporter one question and stops. The reporter has 24 hours: a reply reopens the report, silence releases it.',
  },
  release: {
    method: 'POST',
    path: '/v1/fleet/reports/:reportId/release',
    pathParams: FleetReportPathParamsSchema,
    body: ReleaseFleetReportBodySchema,
    response: { 200: FleetReportTransitionResponseSchema },
    permission: 'PUBLIC',
    description: 'The holding worker hands the report back with nothing filed. A reply from the reporter reopens it.',
  },
  finish: {
    method: 'POST',
    path: '/v1/fleet/reports/:reportId/finish',
    pathParams: FleetReportPathParamsSchema,
    body: FinishFleetReportBodySchema,
    response: { 200: FleetReportTransitionResponseSchema },
    permission: 'PUBLIC',
    description: 'The holding worker records how the report ended, with the ticket it filed if any. Final.',
  },
  unplaceable: {
    method: 'POST',
    path: '/v1/fleet/reports/:reportId/unplaceable',
    pathParams: FleetReportPathParamsSchema,
    body: MarkFleetReportUnplaceableBodySchema,
    response: { 200: FleetReportTransitionResponseSchema },
    permission: 'PUBLIC',
    description:
      'The worker could not tell what the report is. Allowed on an open report or one this worker holds; a reply from the reporter reopens it.',
  },
  announceRelease: {
    method: 'POST',
    path: '/v1/fleet/releases',
    body: AnnounceFleetReleaseBodySchema,
    response: { 200: AnnounceFleetReleaseResponseSchema },
    permission: 'PUBLIC',
    description:
      "Announce a production release on the fleet page. Idempotent per product and version: a second announcement returns the first unchanged. `product` defaults to `immoos`. `tickets` (the IDs the release moved to Deployed) drives the operators' „Deployments“ push on the first announcement only.",
  },
  blockRelease: {
    method: 'POST',
    path: '/v1/fleet/releases/blocked',
    body: BlockFleetReleaseBodySchema,
    response: { 200: BlockFleetReleaseResponseSchema },
    permission: 'PUBLIC',
    description:
      'The deployer could not ship a release: tell the tech person over the messenger, one-way (asks nothing, waits for nothing). `noticed: false` when no messenger is wired. Not deduplicated per version.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Reading a worker's stored log lines (IMM-918).
 *
 * `backoffice:fleet:read` rather than `:command`: this reads rows the fleet
 * already pushed and reaches no machine, which is the whole point of it not
 * being a `logs` command.
 */
const adminFleetLogsContracts = {
  read: {
    method: 'GET',
    path: '/v1/admin/fleet/machines/:machineId/logs',
    pathParams: FleetMachinePathParamsSchema,
    query: ReadFleetLogQuerySchema,
    response: { 200: ReadFleetLogResponseSchema },
    permission: 'backoffice:fleet:read',
    description:
      'The newest lines a worker pushed on that stream, oldest first. Reads stored rows — it never reaches the machine, so it answers for a host that is asleep, unreachable or stopped.',
  },
  clean: {
    method: 'POST',
    path: '/v1/admin/fleet/machines/:machineId/logs/clean',
    pathParams: FleetMachinePathParamsSchema,
    body: CleanFleetLogBodySchema,
    response: { 200: CleanFleetLogResponseSchema },
    permission: 'backoffice:fleet:command',
    description:
      "Drop a worker's stored lines older than 24 hours, both streams. `backoffice:fleet:command` rather than `:read` — it destroys rows, even though it reaches no machine.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The five operator routes over the fleet machine registry (IMM-910.1, ADR
 * 0047), behind `requireAdmin`.
 */
const adminFleetMachinesContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/fleet/machines',
    response: { 200: ListFleetMachinesResponseSchema },
    permission: 'backoffice:fleet:read',
    description: 'List every fleet machine, pending first then by lastSeenAt descending.',
  },
  get: {
    method: 'GET',
    path: '/v1/admin/fleet/machines/:machineId',
    pathParams: FleetMachinePathParamsSchema,
    response: { 200: FleetMachineViewSchema },
    permission: 'backoffice:fleet:read',
    description: "A single machine's enrolment state and attribution.",
  },
  health: {
    method: 'GET',
    path: '/v1/admin/fleet/machines/:machineId/health',
    pathParams: FleetMachinePathParamsSchema,
    query: FleetHostHealthSeriesQuerySchema,
    response: { 200: FleetHostHealthSeriesResponseSchema },
    permission: 'backoffice:fleet:read',
    description:
      "A machine's health readings over [from, to), the maximum per bucket (the bucket chosen from the span, at most 400 points), and the newest reading in the range (IMM-1450). 404 fleet.machine.not-found.",
  },
  approve: {
    method: 'POST',
    path: '/v1/admin/fleet/machines/:machineId/approve',
    pathParams: FleetMachinePathParamsSchema,
    response: { 200: FleetMachineViewSchema },
    permission: 'backoffice:fleet:command',
    description:
      'Approve a pending (or previously rejected/revoked) machine. Mints no token — the machine collects it lazily on its own next poll.',
  },
  reject: {
    method: 'POST',
    path: '/v1/admin/fleet/machines/:machineId/reject',
    pathParams: FleetMachinePathParamsSchema,
    response: { 200: FleetMachineViewSchema },
    permission: 'backoffice:fleet:command',
    description: 'Reject a machine. Its join requests and commands stop being honoured; the record is retained.',
  },
  revoke: {
    method: 'POST',
    path: '/v1/admin/fleet/machines/:machineId/revoke',
    pathParams: FleetMachinePathParamsSchema,
    response: { 200: FleetMachineViewSchema },
    permission: 'backoffice:fleet:command',
    description: 'Revoke an approved machine. Its token stops being accepted immediately.',
  },
  setWeeklyLimit: {
    method: 'PUT',
    path: '/v1/admin/fleet/machines/:machineId/weekly-limit',
    pathParams: FleetMachinePathParamsSchema,
    body: SetFleetMachineWeeklyLimitBodySchema,
    response: { 200: FleetMachineViewSchema },
    permission: 'backoffice:fleet:command',
    description:
      "Set or clear the machine's stored weekly limit (IMM-1107). Retired since IMM-1450: the value is stored and returned on the machine, but no claim is refused on it. The weekly cap is set per account.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The fleet's account registry (fleet-agent ADR 0027): every engine account a worker holds, from the tick's
 * `accounts`, with the operator's settings for it. Logins and tokens stay on the worker; nothing here reaches them.
 */
const adminFleetAccountsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/fleet/accounts',
    response: { 200: ListFleetRegisteredAccountsResponseSchema },
    permission: 'backoffice:fleet:read',
    description:
      'Every registered account (engine + e-mail + machine) by machine name, engine and e-mail, with the last reading and auth state its worker reported and its settings.',
  },
  update: {
    method: 'PATCH',
    path: '/v1/admin/fleet/accounts/:accountId',
    pathParams: FleetRegisteredAccountPathParamsSchema,
    body: UpdateFleetRegisteredAccountBodySchema,
    response: { 200: FleetRegisteredAccountViewSchema },
    permission: 'backoffice:fleet:command',
    description:
      "Turn the account on or off or set its weekly cap (0–1). The worker reads them from its next tick's `accountSettings`. 404 fleet.account.not-found.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The fleet-wide operator read (IMM-910.2, ADR 0047): every machine with its
 * profiles' current status and a derived `stale` flag, the single call
 * IMM-910.7's page makes. `GET /v1/admin/fleet/machines` above keeps its
 * IMM-910.1 shape unchanged.
 */
const adminFleetContracts = {
  overview: {
    method: 'GET',
    path: '/v1/admin/fleet',
    response: { 200: FleetOverviewResponseSchema },
    permission: 'backoffice:fleet:read',
    description: "The whole fleet: every machine's profiles, ordered by profile name, with a derived stale flag.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The command channel over one machine (IMM-910.5, ADR 0047 amendment).
 * `issue` is the first `backoffice:fleet:command` route that is not an
 * approval verb: it accepts a `{verb, profile}` body, records it, and
 * publishes it to the machine's own daemon over the relay. `get` exists
 * alongside `list` because the `202` hands out a `commandId` and the surface
 * that hands out an identifier owes the read that opens it (BL-040).
 *
 * IMM-910.6 adds the three log verbs (`logs`, `logs-download`, `logs-reset`)
 * with no new route: `get` also mints a fresh presigned GET for a settled
 * `logs-download`, and refuses with `409 fleet.log.stream-unavailable` when
 * the requested stream never existed.
 */
const adminFleetCommandsContracts = {
  issue: {
    method: 'POST',
    path: '/v1/admin/fleet/machines/:machineId/commands',
    pathParams: FleetCommandsPathParamsSchema,
    body: FleetCommandBodySchema,
    response: { 202: FleetCommandAcceptedResponseSchema },
    permission: 'backoffice:fleet:command',
    description:
      'Issue one of the seven commands against an approved machine. Accepted and recorded immediately; delivery to the host happens off the request path.',
  },
  list: {
    method: 'GET',
    path: '/v1/admin/fleet/machines/:machineId/commands',
    pathParams: FleetCommandsPathParamsSchema,
    response: { 200: ListFleetCommandsResponseSchema },
    permission: 'backoffice:fleet:read',
    description: "One machine's commands, newest first — including a log verb's no-pane/log-absent outcome.",
  },
  get: {
    method: 'GET',
    path: '/v1/admin/fleet/machines/:machineId/commands/:commandId',
    pathParams: FleetCommandPathParamsSchema,
    response: { 200: FleetCommandViewSchema },
    permission: 'backoffice:fleet:read',
    description:
      "A single command's outcome — the read the issuing 202's commandId opens. For a settled logs-download, mints a fresh presigned GET.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminFactKeysContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/skills/fact-keys',
    response: { 200: ListFactKeysResponseSchema },
    permission: 'backoffice:product-skill:read',
    description:
      'List every catalog entry with its group, need per product and object type, origin and status, in catalog order (group, then ID). Unpaginated — a few hundred platform rows; the read-only details are on the single-entry read.',
  },
  get: {
    method: 'GET',
    path: '/v1/admin/skills/fact-keys/:key',
    pathParams: FactKeyPathParamsSchema,
    response: { 200: FactKeyDetailViewSchema },
    permission: 'backoffice:product-skill:read',
    description:
      'Return one catalog entry with its read-only Objektaufnahmebogen details, number format and unit. usage.filledByDocumentAnalysis is true when a live fact from a document exists under the key in any workspace. 404 property.fact-key.not-found.',
  },
  create: {
    method: 'POST',
    path: '/v1/admin/skills/fact-keys',
    body: CreateFactKeyBodySchema,
    response: { 201: FactKeyViewSchema },
    permission: 'backoffice:product-skill:manage',
    description:
      'Add an operator entry to the catalog. It is active at once, origin IMMOOS_ADDITION, not public, with no Objekt field and no parent; scope follows the group. 400 property.fact-key.incomplete, 400 property.fact-key.invalid-id, 409 property.fact-key.id-taken (retired IDs included).',
  },
  update: {
    method: 'PATCH',
    path: '/v1/admin/skills/fact-keys/:key',
    pathParams: FactKeyPathParamsSchema,
    body: UpdateFactKeyBodySchema,
    response: { 200: FactKeyViewSchema },
    permission: 'backoffice:product-skill:manage',
    description:
      "Edit one fact key's operator-controlled attributes (label, scope, public-safety flag, Objekt-field mapping). key and valueType are immutable and rejected at the boundary. Group, object types and the need matrix (replaced whole) are editable; a blank label or no object type is refused 400 property.fact-key.incomplete. 422 property.fact-key.retired-has-no-need when needs are sent for a retired entry.",
  },
  retire: {
    method: 'POST',
    path: '/v1/admin/skills/fact-keys/:key/retire',
    pathParams: FactKeyPathParamsSchema,
    response: { 200: FactKeyViewSchema },
    permission: 'backoffice:product-skill:manage',
    description:
      'Retire a catalog entry. Final: there is no reactivation and the ID stays taken. Stored facts stay readable; the deriver records no new value under the key; its need reads [] for every product. 404 property.fact-key.not-found, 422 property.fact-key.already-retired.',
  },
} as const satisfies Record<string, EndpointDefinition>;

const factKeyCatalogAgentContracts = {
  overview: {
    method: 'GET',
    path: '/v1/valuation-agent/fact-key-catalog',
    response: { 200: FactKeyCatalogOverviewSchema },
    permission: 'backoffice:valuation:agent-catalog-read',
    description:
      "The fact-key catalog overview for the valuation agent: every entry as one row of strings under `columns`, retired entries marked, with `catalogVersion`. The same result the workspace MCP's fact_key_catalog returns.",
  },
  group: {
    method: 'GET',
    path: '/v1/valuation-agent/fact-key-catalog/groups/:group',
    pathParams: FactKeyCatalogGroupPathParamsSchema,
    query: FactKeyCatalogGroupQuerySchema,
    response: { 200: FactKeyCatalogGroupSchema },
    permission: 'backoffice:valuation:agent-catalog-read',
    description: `One catalog group with its full read-only details, at most ${FACT_KEY_GROUP_PAGE_SIZE} entries per page, with \`catalogVersion\`. The same result the workspace MCP's fact_key_group returns. 404 property.fact-key-group.not-found for an unknown group.`,
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminAdminsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/admins',
    response: { 200: ListAdminsResponseSchema },
    permission: 'backoffice:admin:read',
    description: 'List every admin (active + inactive), newest first.',
  },
  get: {
    method: 'GET',
    path: '/v1/admin/admins/:id',
    pathParams: GetAdminPathParamsSchema,
    response: { 200: GetAdminResponseSchema },
    permission: 'backoffice:admin:read',
    description:
      "Return a single admin by id with creator info and the operator's active admin API keys, which deactivating them would immediately stop.",
  },
  create: {
    method: 'POST',
    path: '/v1/admin/admins',
    body: CreateAdminBodySchema,
    response: { 200: CreateAdminResponseSchema },
    permission: 'backoffice:admin:create',
    description:
      'Add a new admin and trigger an invitation email — recipients new to WorkOS get a signup link, existing ones get a sign-in link.',
  },
  deactivate: {
    method: 'POST',
    path: '/v1/admin/admins/:id/deactivate',
    pathParams: DeactivateAdminPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: DeactivateAdminResponseSchema },
    permission: 'backoffice:admin:deactivate',
    description:
      'Soft-delete an admin by flipping status to inactive. Rejects self-deactivation and the last remaining active admin.',
  },
} as const satisfies Record<string, EndpointDefinition>;

const adminLandiqDataRunsContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/landiq/data-runs',
    response: { 200: ListLandIqDataRunsResponseSchema },
    permission: 'backoffice:admin:landiq-data-run-manage',
    description:
      "Every LandIQ staging data-run ticket as a board card, in manifest order: its column (PENDING, UPLOAD, BUILD, BACKFILL, IMPORTED), the column's state, the one column a move is accepted for now (next), what it waits for, the entry a click opens, and its entries. Read live from LandIQ; immoOS stores and decides nothing.",
  },
  get: {
    method: 'GET',
    path: '/v1/admin/landiq/data-runs/:entryId',
    pathParams: LandIqDataRunPathParamsSchema,
    query: GetLandIqDataRunQuerySchema,
    response: { 200: LandIqDataRunDetailSchema },
    permission: 'backoffice:admin:landiq-data-run-manage',
    description:
      "One LandIQ data-run entry with its latest run, that run's progress, and its log: the last 200 lines without logAfter, the lines since that cursor with it (pass the previous nextCursor). Read live from LandIQ. ticket is the board ticket the entry belongs to — its column, state and the next column a move is accepted for. canRetryFailedChunks is LandIQ's word on whether the run's failed chunks can be retried now. canResume is LandIQ's word on whether the failed upload can resume where it stopped.",
  },
  start: {
    method: 'POST',
    path: '/v1/admin/landiq/data-runs/:entryId/start',
    pathParams: LandIqDataRunPathParamsSchema,
    response: { 200: StartLandIqDataRunResponseSchema },
    permission: 'backoffice:admin:landiq-data-run-manage',
    description:
      "Ask LandIQ to start this staging data-run entry, once. 200 either way: STARTED with the run, or REFUSED with LandIQ's reason (BLOCKED with blockedBy, ALREADY_RUNNING with runId, ALREADY_DONE, ENVIRONMENT_NOT_ALLOWED, RUNNER_UNAVAILABLE, UNKNOWN_ENTRY) — nothing started. An accepted start is audited.",
  },
  retryFailedChunks: {
    method: 'POST',
    path: '/v1/admin/landiq/data-runs/:entryId/retry-failed-chunks',
    pathParams: LandIqDataRunPathParamsSchema,
    response: { 200: RetryLandIqDataRunFailedChunksResponseSchema },
    permission: 'backoffice:admin:landiq-data-run-manage',
    description:
      "Ask LandIQ to put this staging entry's failed import chunks back into the queue, once. No new run starts: LandIQ sets the same run running again. 200 either way: RETRIED with the run and movedChunks (0 on a repeat), or REFUSED with LandIQ's reason (UNKNOWN_ENTRY, ENVIRONMENT_NOT_ALLOWED, NOT_AN_IMPORT, NO_FAILED_CHUNKS, STORAGE_UNAVAILABLE). An accepted retry is audited.",
  },
  resume: {
    method: 'POST',
    path: '/v1/admin/landiq/data-runs/:entryId/resume',
    pathParams: LandIqDataRunPathParamsSchema,
    response: { 200: ResumeLandIqDataRunResponseSchema },
    permission: 'backoffice:admin:landiq-data-run-manage',
    description:
      "Ask LandIQ to start this staging entry as a resume, once: a new run that skips every chunk already uploaded. 200 either way: STARTED with the run, or REFUSED with LandIQ's reason (the start's reasons, plus RESUME_NOT_POSSIBLE when LandIQ no longer allows the resume). Audited as a start.",
  },
  move: {
    method: 'POST',
    path: '/v1/admin/landiq/data-runs/board/:ticket/move',
    pathParams: LandIqBoardTicketPathParamsSchema,
    body: MoveLandIqDataRunTicketBodySchema,
    response: { 200: MoveLandIqDataRunTicketResponseSchema },
    permission: 'backoffice:admin:landiq-data-run-manage',
    description:
      "Ask LandIQ to move a staging board ticket to a column, which starts every entry of that stage at once — no confirmation. 200 either way: MOVED with the ticket after the move and the runs started, or REFUSED with LandIQ's reason (UNKNOWN_TICKET, IMPORTED_NOT_A_TARGET, ENVIRONMENT_NOT_ALLOWED, NOT_NEXT_STAGE with next, STAGE_NOT_DONE with column and columnState, BLOCKED with blockedBy and waitsFor, RUNNER_UNAVAILABLE with the runs started before Render refused the rest). Every started run is audited.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const adminSandboxContracts = {
  getReset: {
    method: 'GET',
    path: '/v1/admin/sandbox/reset',
    response: { 200: GetSandboxResetResponseSchema },
    permission: 'backoffice:fleet:command',
    description:
      "The hosted sandbox's latest `sandbox data reset` run, read live from GitHub Actions: scheduled and page-started runs alike, the scheduled cron the Amsterdam gate turned into a no-op skipped. null when none of the 10 newest runs is a reset. busy: a reset or deploy run is queued or running. defaultDataKind: the stack's `sandbox:debugData` in `apps/infra/Pulumi.sandbox.yaml` on main (DEBUG when 'true', otherwise ANONYMIZED). 502 admin.sandbox-reset.github-unreachable when GitHub does not answer; 404 admin.sandbox-reset.not-configured on a backend without the GitHub token (every non-production environment, the sandbox included). Operator-only; not part of the public API-docs surface.",
  },
  startReset: {
    method: 'POST',
    path: '/v1/admin/sandbox/reset',
    body: StartSandboxResetBodySchema,
    response: { 200: StartSandboxResetResponseSchema },
    permission: 'backoffice:fleet:command',
    description:
      "Start the hosted sandbox's `sandbox data reset` workflow on main, once, with the chosen copy (ANONYMIZED or DEBUG; the Monday reset keeps following the stack setting). 200 either way: STARTED with the queued run, or REFUSED with RUN_IN_PROGRESS when a reset or deploy run is queued or running — nothing started. A started reset is audited (admin.sandbox-reset-started: operator, data kind, run). 502 admin.sandbox-reset.github-unreachable when GitHub does not answer the busy read or the dispatch — nothing recorded; 404 admin.sandbox-reset.not-configured on a backend without the GitHub token. Operator-only; not part of the public API-docs surface.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const adminPreferencesContracts = {
  getMine: {
    method: 'GET',
    path: '/v1/admin/me/preferences/:key',
    pathParams: AdminPreferencePathParamsSchema,
    response: { 200: GetAdminPreferenceResponseSchema },
    permission: 'backoffice:admin:preferences-read',
    description:
      "Return the calling operator's own stored value for a preference key, or null when they have never saved it. The operator is resolved from the session — never client-supplied.",
  },
  saveMine: {
    method: 'PUT',
    path: '/v1/admin/me/preferences/:key',
    pathParams: AdminPreferencePathParamsSchema,
    body: SaveAdminPreferenceBodySchema,
    response: { 200: SaveAdminPreferenceResponseSchema },
    permission: 'backoffice:admin:preferences-update',
    description:
      "Create-or-replace the calling operator's own stored value for a preference key so it survives across browsers. The value is stored as-is (arbitrary JSON). Sending `null` resets the key: the stored value is removed and the next read answers `null`, so the client falls back to its defaults. The operator is resolved from the session — never client-supplied.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const adminPushDevicesContracts = {
  register: {
    method: 'PUT',
    path: '/v1/admin/me/push-devices',
    body: RegisterAdminPushDeviceBodySchema,
    response: { 200: RegisterAdminPushDeviceResponseSchema },
    permission: 'backoffice:admin:push-device-manage',
    description:
      "Register the calling operator's iPhone for pushes, or refresh its last-seen time (IMM-1411.3). Upsert on the Expo token: a token another operator registered moves to the caller, so one phone never receives two operators' pushes. The operator is resolved from the session — never client-supplied — and the token is never echoed back.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const adminPushEventsContracts = {
  listMine: {
    method: 'GET',
    path: '/v1/admin/me/push-events',
    response: { 200: ListAdminPushEventsResponseSchema },
    permission: 'backoffice:admin:preferences-read',
    description:
      'List the push events offered to the calling operator (IMM-1411.4) — live (its screen PRD has shipped it) and in an area they hold the permission for — each with their own on/off choice, in catalogue order. No stored choice is off. The choice is per operator, so it is the same on every device. The operator is resolved from the session — never client-supplied.',
  },
  setMine: {
    method: 'PUT',
    path: '/v1/admin/me/push-events/:event',
    pathParams: AdminPushEventPathParamsSchema,
    body: SetAdminPushEventBodySchema,
    response: { 200: SetAdminPushEventResponseSchema },
    permission: 'backoffice:admin:preferences-update',
    description:
      'Switch one push event on or off for the calling operator, on every device at once (IMM-1411.4). `enabled: true` needs the event offered to the caller, else 422 admin.push-event.not-offered and nothing is stored; `enabled: false` always succeeds and clears the stored choice. Echoes `{ event, enabled }`. The operator is resolved from the session — never client-supplied.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const adminMcpAccessContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/mcp-access',
    response: { 200: ListMcpAccessResponseSchema },
    permission: 'backoffice:identity:mcp-access-read',
    description:
      "List the CALLING operator's own MCP backoffice credentials (IMM-522.1), newest-first, unpaginated. Scoped to the caller in the query itself — another operator's credentials never enter the response. No secret is ever projected: the item type carries no secret field at all. `status` is derived on read (revoked → expired → active), so an expired credential can never read as active.",
  },
  issue: {
    method: 'POST',
    path: '/v1/admin/mcp-access',
    body: IssueMcpAccessBodySchema,
    response: { 200: IssueMcpAccessResponseSchema },
    permission: 'backoffice:identity:mcp-access-create',
    description:
      "Mint a cross-tenant MCP backoffice credential, read-only unless an acting grant is named in `optionalGrants`, for the calling operator (IMM-522.1). The credential's granted permissions are the intersection of the permissions this surface offers with the permissions the operator holds at mint time, and are re-intersected against the operator's live set on every verification, so a credential never outlives its issuer's authority. Validity is 7, 30 or 90 days. 422 identity.mcp-access.name-already-used when the name matches one of the caller's own active credentials; 422 identity.mcp-access.credential-limit-reached at five active credentials. The plaintext secret and the ready-to-paste `claude mcp add` command are returned exactly once and are recoverable from no other endpoint. `optionalGrants` names the opt-in grants to confer (see `MCP_ACCESS_OPTIONAL_GRANTS`); an opt-in grant not named is never conferred, and every grant is still intersected with the operator's permissions.",
  },
  revoke: {
    method: 'POST',
    path: '/v1/admin/mcp-access/:id/revoke',
    pathParams: RevokeMcpAccessPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: RevokeMcpAccessResponseSchema },
    permission: 'backoffice:identity:mcp-access-revoke',
    description:
      "Revoke one of the calling operator's own MCP backoffice credentials (IMM-522.1). The credential stops authenticating on the next request. 404 identity.mcp-access.not-found when the id is unknown OR belongs to another operator — the two are deliberately indistinguishable so credential ids cannot be enumerated. 422 identity.mcp-access.already-revoked on a second revoke.",
  },
  listAll: {
    method: 'GET',
    path: '/v1/admin/mcp-access/all',
    response: { 200: ListAllMcpAccessResponseSchema },
    permission: 'backoffice:identity:mcp-access-manage',
    description:
      'Incident response (IMM-522.4): every MCP backoffice credential on the platform, each carrying its issuing operator. Behind the manage grant — an operator without it gets a 403, so the platform-wide view is absent from the payload, not merely hidden.',
  },
  revokeAny: {
    method: 'POST',
    path: '/v1/admin/mcp-access/:id/revoke-any',
    pathParams: RevokeMcpAccessPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: RevokeAnyMcpAccessResponseSchema },
    permission: 'backoffice:identity:mcp-access-manage',
    description:
      "Incident response (IMM-522.4): revoke ANY operator's credential — no ownership check, that is the point of the manage grant. Records who revoked it distinct from who issued it. 404 identity.mcp-access.not-found only when the credential genuinely does not exist; 422 identity.mcp-access.already-revoked on a second revoke.",
  },
  getSwitch: {
    method: 'GET',
    path: '/v1/admin/mcp-access/switch',
    response: { 200: McpAccessSwitchResponseSchema },
    permission: 'backoffice:identity:mcp-access-read',
    description:
      'The platform-wide MCP surface switch (IMM-522.4). Readable by every operator holding the ordinary read grant, so a disabled surface is diagnosable without the incident-response grant. changedAt/changedByAdminId are null when the switch has never been flipped (the default is enabled).',
  },
  setSwitch: {
    method: 'POST',
    path: '/v1/admin/mcp-access/switch',
    body: SetMcpAccessSwitchBodySchema,
    response: { 200: McpAccessSwitchResponseSchema },
    permission: 'backoffice:identity:mcp-access-manage',
    description:
      "Disable or re-enable the whole MCP surface in one action (IMM-522.4). Disabling refuses every credential and blocks minting WITHOUT changing any credential's stored status, so re-enabling restores the previous state exactly. Idempotent; every flip is recorded against the operator who made it.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The valuation agent's token (IMM-1402), under `/v1/admin/valuation-agent/tokens`. Operator-only: these are not
 * admin-key consumer leaves, so a `svc_` key cannot reach them.
 */
const adminValuationAgentTokenContracts = {
  get: {
    method: 'GET',
    path: '/v1/admin/valuation-agent/tokens',
    response: { 200: ValuationAgentTokensResponseSchema },
    permission: 'backoffice:identity:valuation-agent-token-read',
    description:
      "The valuation agent's live tokens (IMM-1402): the active one and, during its 24 hours, the replaced one. Never a secret and never a hash. A revoked token is absent, so no live token reads as `{ active: null, replaced: null }`.",
  },
  issue: {
    method: 'POST',
    path: '/v1/admin/valuation-agent/tokens',
    body: IssueValuationAgentTokenBodySchema,
    response: { 200: IssuedValuationAgentTokenResponseSchema },
    permission: 'backoffice:identity:valuation-agent-token-manage',
    description:
      "Issue the valuation agent's one token (IMM-1402), carrying the chosen subset of its six use-case permissions and valid for every workspace. The `vag_` secret is returned exactly once and is recoverable from no other endpoint. 422 identity.valuation-agent-token.no-permissions on an empty set; 422 identity.valuation-agent-token.already-issued while a token is active, including when two operators issue at once.",
  },
  replace: {
    method: 'POST',
    path: '/v1/admin/valuation-agent/tokens/:id/replace',
    pathParams: zod.object({ id: zod.uuid() }),
    body: IssueValuationAgentTokenBodySchema,
    response: { 200: IssuedValuationAgentTokenResponseSchema },
    permission: 'backoffice:identity:valuation-agent-token-manage',
    description:
      "Replace the valuation agent's active token (IMM-1402). A new `vag_` token carrying the chosen permissions is valid at once, and its secret is returned exactly once. The replaced token stays valid for 24 more hours, so the agent can switch over. At most one replaced token is valid at a time: replacing again within 24 hours ends the earlier overlap at once. 422 identity.valuation-agent-token.not-active unless `:id` is the currently active token (a stale page or a second click). 422 identity.valuation-agent-token.no-permissions on an empty set.",
  },
  revoke: {
    method: 'POST',
    path: '/v1/admin/valuation-agent/tokens/revoke',
    body: zod.object({}).strict(),
    response: { 200: ValuationAgentTokensResponseSchema },
    permission: 'backoffice:identity:valuation-agent-token-manage',
    description:
      'Revoke every live valuation-agent token at once (IMM-1402): the active one and a replaced one still within its overlap. The next call with either is refused. Idempotent: with nothing live it writes nothing and emits no event. Always answers `{ active: null, replaced: null }`.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Operator containment for the CUSTOMER MCP surface (IMM-601.6), under
 * `/v1/admin/mcp-surface`. Two leaves, mirroring
 * `adminMcpAccess.getSwitch/setSwitch` — the same single-row switch shape
 * applied to the member-facing `/v1/mcp` endpoint instead of the operator's own
 * `adm_` surface.
 *
 * Disabling refuses every customer MCP credential WITHOUT changing any key's
 * stored status, and leaves the plain `immo_` HTTP API untouched: the check
 * lives in the MCP route, never in the shared key verifier.
 */
const adminMcpSurfaceContracts = {
  getSwitch: {
    method: 'GET',
    path: '/v1/admin/mcp-surface/switch',
    response: { 200: WorkspaceMcpSwitchResponseSchema },
    permission: 'backoffice:identity:mcp-surface-read',
    description:
      'The platform-wide switch over the CUSTOMER MCP surface (IMM-601.6). Readable by every operator holding the read grant, so a disabled surface is diagnosable without the containment grant. changedAt/changedByAdminId are null when the switch has never been flipped (the default is enabled).',
  },
  setSwitch: {
    method: 'POST',
    path: '/v1/admin/mcp-surface/switch',
    body: SetWorkspaceMcpSwitchBodySchema,
    response: { 200: WorkspaceMcpSwitchResponseSchema },
    permission: 'backoffice:identity:mcp-surface-manage',
    description:
      "Disable or re-enable the member-facing MCP surface platform-wide in one action (IMM-601.6). Disabling refuses every customer MCP credential at `/v1/mcp` with the same flat 401 an invalid credential gets, WITHOUT changing any key's stored status and without touching plain `immo_` HTTP traffic, so re-enabling restores service with no customer action. Repeatable — setting the state it already holds is a no-op on the surface — and every call is recorded against the operator who made it, including a re-flip to the same state.",
  },
  listTools: {
    method: 'GET',
    path: '/v1/admin/mcp-surface/tools',
    query: ListMcpToolExposureQuerySchema,
    response: { 200: ListMcpToolExposureResponseSchema },
    permission: 'backoffice:identity:mcp-surface-read',
    description:
      "Every tool the workspace MCP catalogue composes, joined with its exposure row (IMM-625.2). Driven by the CATALOGUE, not the table: a tool with no row is listed as unexposed rather than omitted, because that is precisely the tool an operator has come to rule on. Ordered by toolId so the list is stable across reloads. changedAt/changedByAdminId are null for a tool nobody has ruled on. `filter` (IMM-625.3) narrows the list: `unreviewed` returns exactly the tools that have entered the catalogue without a decision, NEWEST ARRIVAL FIRST rather than by toolId, and `stale` returns only the exposure rows whose tool has left the catalogue — those carry status 'stale' with every describing field null, and are absent from every other filter except `all`. Narrowed further and paged for a catalogue that has outgrown a screen (IMM-769.7): `q` matches toolId or name case-insensitively (a stale entry on its identifier alone, which is all the catalogue left it), `module` selects the identifier's first segment, and `limit`/`offset` page the result. `total` counts what the narrowing matches before paging; `modules` carries every module under the current filter and `q` — computed BEFORE `module` narrows, so choosing one never hides the one you would switch to. `filters` carries what each chip would return under the current `q` alone, so the triage queue's depth is readable without selecting it. Paging is an offset rather than a cursor because the catalogue is composed in process and totally ordered before it is sliced, so there is no moving keyset for a cursor to pin.",
  },
  setToolExposureBatch: {
    method: 'PUT',
    path: '/v1/admin/mcp-surface/tools',
    body: SetMcpToolExposureBatchBodySchema,
    response: { 200: SetMcpToolExposureBatchResponseSchema },
    permission: 'backoffice:identity:mcp-surface-manage',
    description:
      'Expose or withdraw MANY tools in one attributed write (IMM-769.7) — the same grant and the same total-write semantics as the per-tool leaf, applied to a selection. ALL-OR-NOTHING: every identifier is resolved against the catalogue before anything is written, and an unknown one refuses the whole batch 404 identity.mcp-tool-exposure.batch-unknown-tool naming the offenders, because a half-landed batch leaves an operator who cannot tell what is exposed. A repeated identifier is the same ruling twice and is deduplicated rather than refused. Every tool in the batch is marked reviewed, exactly as a single ruling does, and each one emits its OWN change event — the audit answers "what happened to this tool", so the batching is a property of how the operator worked rather than of the tool. Bounded at 200, matching the list\'s page ceiling.',
  },
  setToolExposure: {
    method: 'PUT',
    path: '/v1/admin/mcp-surface/tools/:toolId',
    pathParams: SetMcpToolExposureParamsSchema,
    body: SetMcpToolExposureBodySchema,
    response: { 200: McpToolExposureItemSchema },
    permission: 'backoffice:identity:mcp-surface-manage',
    description:
      'Expose or withdraw one tool from the workspace MCP surface platform-wide, effective on the next request (IMM-625.2). PUT with a total boolean rather than a toggle: replaying it lands on the same state, and two concurrent writes of the same value cannot race to opposite results. Refused 404 when the catalogue does not contain the tool. Every write is attributed to the acting operator and marks the tool reviewed.',
  },
  removeToolExposure: {
    method: 'DELETE',
    path: '/v1/admin/mcp-surface/tools/:toolId',
    pathParams: SetMcpToolExposureParamsSchema,
    // Bodyless, like `workspaceConnectedClients.revoke`: the resulting state is
    // "gone from the list", so there is nothing for the row to re-render from.
    response: { 204: zod.void() },
    permission: 'backoffice:identity:mcp-surface-manage',
    description:
      "Clear a STALE exposure entry — a row naming a toolId the catalogue no longer contains (IMM-625.3). Refused 409 identity.mcp-tool-exposure.not-stale when the tool is still catalogued, because that removal would be a silent withdrawal without the confirmation the withdraw action requires; use PUT with exposed:false instead. Refused 404 identity.mcp-tool-exposure.not-found when no row exists. Removing a stale entry changes no client's effective set — the tool it names is already unreachable.",
  },
  toolReachability: {
    method: 'GET',
    path: '/v1/admin/mcp-surface/tools/:toolId/reachability',
    pathParams: DiagnoseMcpToolReachabilityParamsSchema,
    query: DiagnoseMcpToolReachabilityQuerySchema,
    response: { 200: McpToolReachabilityResponseSchema },
    // The READ grant, not manage: diagnosing is not changing.
    permission: 'backoffice:identity:mcp-surface-read',
    description:
      "Whether one named credential can reach one named tool right now, and which layer excludes it when it cannot (IMM-625.4). Evaluated in the SAME order the live surface refuses — platform kill switch, then exposure, then the credential's granted permissions, then the bound member's live permissions — short-circuiting at the first failure, so the answer names the layer the client actually hits. The two credential-independent layers are checked BEFORE the credential is read, so a disabled surface answers even for a credential that has since been revoked. `excludedBy` is null exactly when `reachable` is true. Refused 404 identity.mcp-tool-exposure.not-found when the catalogue does not contain the tool, 404 identity.mcp-surface.credential-not-found when the credential is unknown (one code for both kinds, so the endpoint cannot be used to probe which kind an id belongs to), and 422 identity.mcp-surface.seat-required — carrying the candidate seats in details.seats — when a multi-seat OAuth grant is named without a seat, because a credential-scoped answer for a person whose permissions differ per Standort would be relayed to a member as a fact about all of them.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The operator-tier read over customers' OAuth grants (IMM-625.4) — the
 * admin-tier twin of `connectedClientsContracts.list`, and the read that makes
 * an OAuth grant nameable in the reachability diagnosis at all.
 *
 * Its own permission rather than `mcp-surface-read`: this is a read of customer
 * CREDENTIALS, not of the MCP surface's configuration, and it will outlive the
 * panel that needed it.
 */
const adminConnectedClientContracts = {
  list: {
    method: 'GET',
    path: '/v1/admin/connected-clients',
    query: ListAdminConnectedClientsQuerySchema,
    response: { 200: ListAdminConnectedClientsResponseSchema },
    permission: 'backoffice:identity:connected-client-read',
    description:
      "Every OAuth grant a customer's MCP client holds, across all workspaces, ordered by last use (nulls last) because support is looking for the grant that was just used. A deliberate widening of the member's own list at `GET /v1/workspaces/:workspaceId/connected-clients`, which serves only the caller's own ACTIVE grants newest-connection first: `status` here selects revoked grants too, since a revoked grant is exactly what a support question is often about. Every privacy narrowing of that list is kept — no token, no token prefix, no issuer grant id. `workspaceId` narrows to grants whose bound USER holds a seat in that workspace, never to the grant's stored workspace_id, which records only where consent was given. `seats` is resolved from the bound user's active member rows, the same source the live MCP path reads, so it can never offer a seat the surface would not honour. `access` says whether the grant is unscoped (`ALL_TOOLS`, follows the catalogue; `grantedPermissions` is then empty) or scoped. Read-only: revoking a grant stays the member's own screen.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The single MCP method-dispatch endpoint (IMM-522.2). One leaf on purpose:
 * JSON-RPC methods multiplex inside the body, and the per-method validation
 * lives in the identity presenter's request validator. Platform-internal — no
 * `docs` marker, so the public docs site never sees it.
 */
const adminMcpContracts = {
  invoke: {
    method: 'POST',
    path: '/v1/admin/mcp',
    body: McpJsonRpcRequestSchema,
    response: { 200: McpJsonRpcResponseSchema },
    permission: 'backoffice:identity:mcp-invoke',
    description:
      'MCP method dispatch for adm_ backoffice credentials — dual-era: the 2026-07-28 revision (server/discover, tools/list, tools/call, ping) and the legacy initialize-handshake revisions 2025-03-26 … 2025-11-25 (initialize, ping, tools/list, tools/call). Single JSON response, never SSE; protocol failures are JSON-RPC errors, tool failures are isError results.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The workspace MCP method-dispatch endpoint (IMM-601.2) — the member-facing
 * sibling of `adminMcpContracts.invoke`, sharing its JSON-RPC envelope schemas.
 * One leaf on purpose: JSON-RPC methods multiplex inside the body, and the
 * per-method validation lives in the identity presenter's request validator.
 * Only an MCP-capable `immo_` key can hold the implicit
 * `workspace:identity:mcp-invoke` permission (the verifier injects it, no role
 * grants it), so every other credential is refused before the handler runs. No
 * `docs` marker — the MCP surface is documented by an authored page (IMM-601.7),
 * never by the generated HTTP operation list.
 */
const mcpContracts = {
  invoke: {
    method: 'POST',
    path: '/v1/mcp',
    body: McpJsonRpcRequestSchema,
    response: { 200: McpJsonRpcResponseSchema },
    permission: 'workspace:identity:mcp-invoke',
    description:
      'MCP method dispatch for MCP-capable immo_ workspace keys — dual-era: the 2026-07-28 revision (server/discover, tools/list, tools/call, ping) and the legacy initialize-handshake revisions 2025-03-26 … 2025-11-25 (initialize, ping, tools/list, tools/call). Single JSON response, never SSE; protocol failures are JSON-RPC errors, tool failures are isError results.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * RFC 9728 protected-resource metadata for the workspace MCP endpoint
 * (IMM-601.8.1). The one leaf outside the `/v1` surface on purpose: the path is
 * fixed by the RFC and is what the `WWW-Authenticate` challenge on every MCP
 * `401` points at, so a client that has never authenticated can discover where
 * to get a token.
 *
 * `permission: 'PUBLIC'` — the auth-layer sentinel, exactly as the `auth.*`
 * leaves carry it: the route registers with NO tier middleware and an
 * anonymous caller sails through the auth plugin. Deliberately NO `docs`
 * marker: the public docs site publishes HTTP operations a partner calls, and
 * this is a protocol document a client library reads.
 */
const oauthProtectedResourceContracts = {
  metadata: {
    method: 'GET',
    path: '/.well-known/oauth-protected-resource',
    response: { 200: ProtectedResourceMetadataSchema },
    permission: 'PUBLIC',
    description:
      'RFC 9728 protected-resource metadata for the workspace MCP endpoint: the canonical resource URI, the authorization server(s) that may mint tokens for it, the minimal supported scope set, and the bearer methods accepted. Unauthenticated and cacheable.',
  },
  // RFC 9728 §3: the metadata URL for a resource with a path inserts that path
  // AFTER the well-known segment (IMM-696). A static literal rather than a
  // wildcard — the resource set is closed (one OAuth-protected MCP endpoint),
  // and the contract tree is walked by tests against registered routes, which a
  // wildcard would defeat.
  metadataForMcp: {
    method: 'GET',
    path: '/.well-known/oauth-protected-resource/v1/mcp',
    response: { 200: ProtectedResourceMetadataSchema },
    permission: 'PUBLIC',
    description:
      'RFC 9728 §3 path-derived protected-resource metadata for the workspace MCP endpoint. Byte-identical to the root document; the root remains as the fallback clients discovered before this shipped. Unauthenticated and cacheable.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * immoOS as the OAuth 2.1 authorization server for `/v1/mcp` (IMM-601.8.3).
 *
 * All four leaves are `permission: 'PUBLIC'` — the auth-layer sentinel — and
 * none carries a `docs` marker: they are steps of an interactive protocol a
 * client library performs, not HTTP operations a partner calls, so the public
 * docs site's exact-operation-set assertion is untouched.
 *
 * Every response here is an RFC wire format rather than the immoOS envelope, and
 * every request schema is permissive so that a malformed call is refused by the
 * HANDLER in the RFC's own shape. On `authorize` that is load-bearing rather than
 * stylistic: the caller is a browser mid-navigation, so a zod 400 would paint an
 * API error body across a member's tab.
 *
 * The two write leaves (`register`, `authorize`) insert a row per anonymous
 * request. The backend has no in-app rate limiter (ADR 0034 §21), so the control
 * is the WAF rate-based rule extended with a `/v1/oauth/` prefix, plus a
 * scheduled sweep that drains what never earns a grant.
 */
const oauthServerContracts = {
  metadata: {
    method: 'GET',
    path: '/.well-known/oauth-authorization-server',
    response: { 200: AuthorizationServerMetadataSchema },
    permission: 'PUBLIC',
    description:
      'RFC 8414 authorization-server metadata for the workspace MCP surface: the issuer, the authorize/token/revocation/registration endpoints, the supported scopes, and the PKCE method. Unauthenticated and cacheable.',
  },
  register: {
    method: 'POST',
    path: '/v1/oauth/register',
    body: RegisterOauthClientBodySchema,
    response: { 201: RegisterOauthClientResponseSchema, 400: OauthProtocolErrorSchema },
    permission: 'PUBLIC',
    description:
      "RFC 7591 dynamic client registration. A client POSTs its metadata and receives a client_id it mints nothing itself — which is what keeps a client_id out of a member's hands entirely. Always a PUBLIC client: no client_secret is ever issued. 400 invalid_redirect_uri for a redirect that is neither https nor an http loopback; 400 invalid_client_metadata for missing redirect_uris or an unsupported grant/response type.",
  },
  authorize: {
    method: 'GET',
    path: '/v1/oauth/authorize',
    query: AuthorizeOauthQuerySchema,
    // A navigation: the only outcome is a redirect, so there is no body to
    // describe. Which redirect depends on whether the request named a client and
    // a callback we may safely use — an unvalidated target means the member is
    // the only reader, and they get our own consent route with an error code.
    response: { 302: zod.void() },
    permission: 'PUBLIC',
    description:
      'RFC 6749 §4.1 authorization endpoint. Validates the client, the redirect URI, PKCE (S256 only) and the RFC 8707 resource, persists a single-use authorization request, and redirects the browser to the immoOS consent screen. A request naming an unknown client or an unregistered redirect URI is redirected NOWHERE the request asked for: the member is shown the refusal on our own screen.',
  },
  token: {
    method: 'POST',
    path: '/v1/oauth/token',
    body: OauthTokenBodySchema,
    response: { 200: OauthTokenResponseSchema, 400: OauthProtocolErrorSchema },
    permission: 'PUBLIC',
    description:
      'RFC 6749 §4.1.3 / §6 token endpoint, application/x-www-form-urlencoded. Redeems an authorization code (single-use, PKCE-verified, audience-bound) or rotates a refresh token. Replaying a code, or presenting a refresh token that was already rotated away, revokes every token issued under that grant. Every refusal is the RFC §5.2 body and never distinguishes which check failed.',
  },
  revoke: {
    method: 'POST',
    path: '/v1/oauth/revoke',
    body: RevokeOauthTokenBodySchema,
    response: { 200: zod.void() },
    permission: 'PUBLIC',
    description:
      "RFC 7009 revocation. Possession of the token is the entire authorisation — every client here is public, so there is no credential to check. Revoking a refresh token revokes the grant's access tokens with it. Always 200, including for an unknown, expired, already-revoked or malformed token, so the endpoint is not a token existence oracle.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const skillApprovalsContracts = {
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/skills/approvals',
    pathParams: SkillInstanceWorkspaceParamsSchema,
    response: { 200: ListPendingApprovalsResponseSchema },
    permission: 'workspace:assistant:skill-manage',
    description: "Every action of the caller's own skills that is waiting on their approval, soonest to expire first.",
  },
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/skills/approvals/:id',
    pathParams: ProposedActionPathParamsSchema,
    response: { 200: ProposedActionDetailSchema },
    permission: 'workspace:assistant:skill-manage',
    description: 'One proposed action in full, including the exact recorded call the member would be approving.',
  },
  approve: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/skills/approvals/:id/approve',
    pathParams: ProposedActionPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: ProposedActionDetailSchema },
    permission: 'workspace:assistant:skill-manage',
    description:
      'Execute the recorded call exactly as proposed, under the run’s original grant, and append the result to that run.',
  },
  reject: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/skills/approvals/:id/reject',
    pathParams: ProposedActionPathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: ProposedActionDetailSchema },
    permission: 'workspace:assistant:skill-manage',
    description: 'Decline the proposed action. It never executes.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const skillInstancesContracts = {
  reactivate: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/skills/instances/:id/reactivate',
    pathParams: SkillInstancePathParamsSchema,
    body: zod.object({}).strict(),
    response: { 200: SkillInstanceDetailSchema },
    permission: 'workspace:assistant:skill-manage',
    description:
      'Put a skill that paused itself after repeated failures back into service, clearing its failure count.',
  },
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/skills/instances',
    pathParams: SkillInstanceWorkspaceParamsSchema,
    response: { 200: ListSkillInstancesResponseSchema },
    permission: 'workspace:assistant:skill-manage',
    description:
      "The member's Skills area: their own skill instances with derived status, plus the active definitions still available to activate.",
  },
  // The closed set of events an EVENT trigger may name (IMM-436.6). Gated on
  // `skill-manage`, not `skill-read`: the only reason to read it is to configure
  // a trigger, and the save it feeds is a manage call.
  eventCatalogue: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/skills/event-catalogue',
    pathParams: SkillInstanceWorkspaceParamsSchema,
    response: { 200: ListSkillEventCatalogueResponseSchema },
    permission: 'workspace:assistant:skill-manage',
    description:
      'Every domain event a skill instance may be triggered by, grouped by the module that publishes it. Derived from the platform event registry and fixed at deploy time, so there is nothing here to write.',
  },
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/skills/instances/:id',
    pathParams: SkillInstancePathParamsSchema,
    response: { 200: SkillInstanceDetailSchema },
    permission: 'workspace:assistant:skill-manage',
    description:
      "One instance's configuration, grant state and derived status, plus the definition's tools annotated by what each may do without asking — the data the consent step renders.",
  },
  create: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/skills/instances',
    pathParams: SkillInstanceWorkspaceParamsSchema,
    body: CreateSkillInstanceBodySchema,
    response: { 201: CreateSkillInstanceResponseSchema },
    permission: 'workspace:assistant:skill-manage',
    description:
      'Configure a skill instance. It lands disabled: an administrator may prepare one for a colleague, but only the granting member can confirm the grant that activates it.',
  },
  update: {
    method: 'PATCH',
    path: '/v1/workspaces/:workspaceId/skills/instances/:id',
    pathParams: SkillInstancePathParamsSchema,
    body: UpdateSkillInstanceConfigBodySchema,
    response: { 200: SkillInstanceDetailSchema },
    permission: 'workspace:assistant:skill-manage',
    description:
      "Re-scope an instance's allowlist, trigger, schedule or settings. Applies from the next trigger evaluation onwards, with no deployment; optimistic-concurrency guarded.",
  },
  grant: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/skills/instances/:id/grant',
    pathParams: SkillInstancePathParamsSchema,
    body: SkillInstanceActionBodySchema,
    response: { 200: SkillInstanceDetailSchema },
    permission: 'workspace:assistant:skill-manage',
    description:
      'Confirm the grant and activate the instance. Personal: only the granting member may call this, never an administrator on their behalf.',
  },
  revoke: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/skills/instances/:id/revoke',
    pathParams: SkillInstancePathParamsSchema,
    body: SkillInstanceActionBodySchema,
    response: { 200: SkillInstanceDetailSchema },
    permission: 'workspace:assistant:skill-manage',
    description:
      'Revoke the grant. No further runs start, pending proposed actions are voided, and the run history stays readable — the row is never deleted. Idempotent.',
  },
  run: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/skills/instances/:id/run',
    pathParams: SkillInstancePathParamsSchema,
    body: TriggerSkillRunBodySchema,
    response: { 202: SkillRunSummarySchema },
    permission: 'workspace:assistant:skill-manage',
    description:
      "Run the skill now, under the granting member's identity and scope — the same bounds a triggered run gets. Pressing again while a run is in flight returns that run rather than starting a second.",
  },
  listRuns: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/skills/instances/:id/runs',
    pathParams: SkillInstancePathParamsSchema,
    response: { 200: ListSkillRunsResponseSchema },
    permission: 'workspace:assistant:skill-read',
    description: "The instance's run history, newest first: trigger, status, timing, token usage and cost.",
  },
  getRun: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/skills/instances/:id/runs/:runId',
    pathParams: SkillRunPathParamsSchema,
    response: { 200: SkillRunDetailSchema },
    permission: 'workspace:assistant:skill-read',
    description:
      'One run in full: its steps, every tool call with parameters and outcome, usage, cost, and the fingerprint of the sequence it performed.',
  },
  getState: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/skills/instances/:id/state',
    pathParams: SkillInstancePathParamsSchema,
    response: { 200: GetSkillInstanceStateResponseSchema },
    permission: 'workspace:assistant:skill-read',
    description: "The instance's continuity state — what the skill remembers between runs.",
  },
  resetState: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/skills/instances/:id/state',
    pathParams: SkillInstancePathParamsSchema,
    // Returns the (now empty) state rather than a bodyless 204, matching every
    // other DELETE in this contract and letting the panel re-render from the
    // response instead of re-fetching.
    response: { 200: GetSkillInstanceStateResponseSchema },
    permission: 'workspace:assistant:skill-manage',
    description: "Clear the instance's continuity state so its next run starts fresh.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const conversationsContracts = {
  create: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/conversations',
    pathParams: CreateConversationPathParamsSchema,
    body: CreateConversationBodySchema,
    response: { 200: CreateConversationResponseSchema },
    permission: 'workspace:assistant:conversation-use',
    description:
      'Start a new assistant conversation owned by the calling member. Created empty; a member may hold several concurrently. The title defaults to null and is derived from the first message later. API keys: only the browser extension’s key; any other key gets 403 identity.api-key.route-not-available.',
  },
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/conversations',
    pathParams: ListConversationsPathParamsSchema,
    query: ListConversationsQuerySchema,
    response: { 200: ListConversationsResponseSchema },
    permission: 'workspace:assistant:conversation-read',
    description:
      "List the calling member's own assistant conversations in the workspace, most-recently-active first, keyset-paginated (limit + opaque cursor). Conversations owned by other members are never returned. API keys: only the browser extension's key; any other key gets 403 identity.api-key.route-not-available.",
  },
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/conversations/:conversationId',
    pathParams: GetConversationPathParamsSchema,
    response: { 200: GetConversationResponseSchema },
    permission: 'workspace:assistant:conversation-read',
    description:
      'Fetch one of the calling member’s conversations by id. 404 when it does not exist or is owned by another member (not-found masks not-owned, so ownership is not probeable). API keys: only the browser extension’s key; any other key gets 403 identity.api-key.route-not-available.',
  },
  postMessage: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/conversations/:conversationId/messages',
    pathParams: PostMessagePathParamsSchema,
    body: PostMessageBodySchema,
    response: { 200: PostMessageResponseSchema },
    permission: 'workspace:assistant:conversation-use',
    description:
      'Append a USER message to one of the calling member’s conversations and bump its last-activity stamp. Returns the persisted message. No assistant reply is produced in this slice — the run engine arrives with IMM-307.2. 404 when the conversation is not the caller’s. API keys: only the browser extension’s key; any other key gets 403 identity.api-key.route-not-available.',
  },
  recordPageRead: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/conversations/:conversationId/page-reads',
    pathParams: RecordPageReadPathParamsSchema,
    body: RecordPageReadBodySchema,
    response: { 200: MessageSchema },
    permission: 'workspace:assistant:conversation-use',
    description:
      "Answer the assistant's request for the page open in the member's browser (IMM-1393). `READ` stores the page as a PAGE_READ message and queues the run that answers from it; `NOT_SUPPORTED` stores the notice assistant.page-read.not-supported and reads nothing. API keys: only the browser extension's key; any other key gets 403 identity.api-key.route-not-available. 404 assistant.conversation.not-found; 422 assistant.page-read.not-pending when the request is no longer the conversation's latest message.",
  },
  listMessages: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/conversations/:conversationId/messages',
    pathParams: ListMessagesPathParamsSchema,
    query: ListMessagesQuerySchema,
    response: { 200: ListMessagesResponseSchema },
    permission: 'workspace:assistant:conversation-read',
    description:
      'Return a member’s conversation history in append order, keyset-paginated (limit + opaque cursor). 404 when the conversation is not the caller’s. API keys: only the browser extension’s key; any other key gets 403 identity.api-key.route-not-available.',
  },
  listModels: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/assistant/models',
    pathParams: ListModelsPathParamsSchema,
    response: { 200: ListModelsResponseSchema },
    permission: 'workspace:assistant:conversation-read',
    description:
      'List the models a member may pick for a message, each with a display label and the default flag (IMM-351.1). The set is a platform-wide, config-driven allowlist; the composer reads it to populate the model selector. API keys: only the browser extension’s key; any other key gets 403 identity.api-key.route-not-available.',
  },
  stream: {
    kind: 'stream',
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/conversations/:conversationId/stream',
    pathParams: ConversationPathParamsSchema,
    permission: 'workspace:assistant:conversation-read',
    event: RunStreamEventSchema,
    description:
      "Server-Sent Events stream of live run progress (step indications + a terminal event) for one of the calling member's conversations. text/event-stream; the persisted message log remains authoritative. 404 when the conversation is not the caller's. API keys: only the browser extension's key; any other key gets 403 identity.api-key.route-not-available.",
  },
} as const satisfies Record<string, EndpointDefinition>;

const calendarContracts = {
  getConnection: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/calendar',
    pathParams: GetCalendarConnectionPathParamsSchema,
    response: { 200: CalendarConnectionSchema },
    permission: 'workspace:communications:calendar-read',
    description:
      "The calling member's calendar connection state (not connected / connected / expired) and the connected account (IMM-234.2).",
  },
  syncNow: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/calendar/sync',
    pathParams: SyncCalendarPathParamsSchema,
    response: { 200: SyncCalendarResponseSchema },
    permission: 'workspace:communications:calendar-read',
    description:
      "Catch up the acting member's calendar from their provider — cooldown-throttled; `skipped` when nothing was read (no grant, no realtime for its provider, expired, or within the cooldown) (IMM-1170.4).",
  },
  listEvents: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/calendar/events',
    pathParams: ListCalendarEventsPathParamsSchema,
    query: CalendarEventsQuerySchema,
    response: { 200: CalendarEventsResponseSchema },
    permission: 'workspace:communications:calendar-read',
    description:
      "Every appointment of the caller's own calendar overlapping the half-open window [from, to), at most 62 days wide. An expired grant still returns the stored appointments with canEdit false (IMM-234.2).",
  },
  createEvent: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/calendar/events',
    pathParams: CreateAppointmentPathParamsSchema,
    body: CreateAppointmentBodySchema,
    response: { 201: AppointmentWriteResponseSchema },
    permission: 'organization:communications:appointment-write',
    description:
      "Creates an appointment in the caller's own connected calendar and projects it into the read model. Written at the provider first: a provider failure stores nothing (IMM-234.4). Optional links are re-validated inside the caller's workspace before anything is written (IMM-234.5).",
  },
  updateEvent: {
    method: 'PATCH',
    path: '/v1/workspaces/:workspaceId/communications/calendar/events/:appointmentId',
    pathParams: UpdateAppointmentPathParamsSchema,
    body: UpdateAppointmentBodySchema,
    response: { 200: AppointmentWriteResponseSchema },
    permission: 'organization:communications:appointment-write',
    description:
      "Changes an appointment in the owner's connected calendar — the full form AND the grid drag. Written at the provider first on the SAME occurrence, so a provider failure changes nothing locally. Re-reading the occurrence before the write is what sets or clears the conflict flag (IMM-234.6).",
  },
  cancelEvent: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/calendar/events/:appointmentId/cancel',
    pathParams: CancelAppointmentPathParamsSchema,
    response: { 200: AppointmentSchema },
    permission: 'organization:communications:appointment-write',
    description:
      'Cancels an appointment at the provider, which re-notifies the attendees. Never a delete: the row survives as CANCELLED so it stays visible, marked „Abgesagt“, on every linked record (IMM-234.6).',
  },
  inviteProspectToViewing: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:prospectId/viewing-invitations',
    pathParams: InviteProspectToViewingPathParamsSchema,
    body: InviteProspectToViewingBodySchema,
    response: { 201: ViewingInvitationResponseSchema },
    permission: 'organization:deal:prospect-phase-advance',
    description:
      "Writes a Besichtigung to the caller's calendar without provider notifications, sends the Kontakt immoOS's own invitation mail with a calendar entry, and moves the Interessent to „Zu Termin eingeladen“ when it was earlier — in one transaction (IMM-1067.2).",
  },
  previewViewingInvitation: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/prospects/:prospectId/viewing-invitations/preview',
    pathParams: InviteProspectToViewingPathParamsSchema,
    body: InviteProspectToViewingBodySchema,
    response: { 200: ViewingInvitationPreviewSchema },
    permission: 'organization:deal:prospect-phase-advance',
    description:
      "Renders the invitation e-mail `inviteProspectToViewing` would send for this body, with the sent mail's own template and its images inlined as data URIs. Sends, stores and writes nothing. Refuses what the invite refuses before its provider write: 404 for an Interessent out of scope, 422 for a closed Kaufprozess or a Kontakt without an e-mail address. A read posted as a body because the body carries the free-text Hinweis (IMM-1483.2).",
  },
  dealSuggestions: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/calendar/deal-suggestions/:dealId',
    pathParams: GetDealSuggestionsPathParamsSchema,
    response: { 200: DealSuggestionsResponseSchema },
    permission: 'workspace:communications:calendar-read',
    description:
      "The Objekt and Kontakte of one Deal, offered for adoption into an appointment's links. A Deal outside the workspace answers the empty suggestion, never a 404 (IMM-234.5).",
  },
  upcomingForRecord: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/calendar/records/:recordType/:recordId/upcoming',
    pathParams: UpcomingPathParamsSchema,
    response: { 200: UpcomingAppointmentsResponseSchema },
    permission: 'workspace:communications:calendar-read',
    description:
      'The coming, non-private appointments of every member linked to one Kontakt, Objekt, Deal or Lead, soonest first, at most 20. An unknown or foreign record answers an empty list, never a 404 (IMM-234.8).',
  },
} as const satisfies Record<string, EndpointDefinition>;

const companyMailboxContracts = {
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes',
    pathParams: ListCompanyMailboxesPathParamsSchema,
    response: { 200: ListCompanyMailboxesResponseSchema },
    permission: 'workspace:communications:company-mailbox-read',
    description:
      "The workspace's company mailboxes with status and who connected them, ordered by address (IMM-264). Owner and admin only.",
  },
  contactThreads: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes/contact-threads',
    pathParams: ListCompanyMailboxContactThreadsPathParamsSchema,
    query: ListCompanyMailboxContactThreadsQuerySchema,
    response: { 200: ListCompanyMailboxContactThreadsResponseSchema },
    permission: 'workspace:communications:company-mailbox-read',
    description:
      "Company-mailbox conversations matched to one contact, newest first, at most 100 — the contact timeline's company entries. Never written to the activity store (IMM-264). Owner and admin only.",
  },
  connect: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes/connect',
    pathParams: InitiateMailboxConnectionPathParamsSchema,
    body: InitiateMailboxConnectionBodySchema,
    response: { 200: InitiateMailboxConnectionResponseSchema },
    permission: 'workspace:communications:company-mailbox-manage',
    description:
      'Start the Google sign-in that connects a company mailbox; returns the authorisation URL the browser is redirected to (IMM-264). Owner and admin only.',
  },
  completeConnect: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes/callback',
    pathParams: CompleteMailboxConnectionPathParamsSchema,
    body: CompleteMailboxConnectionBodySchema,
    response: { 200: CompleteCompanyMailboxConnectionResponseSchema },
    permission: 'workspace:communications:company-mailbox-manage',
    description:
      'Exchange the OAuth code for a company mailbox grant. Authenticated by the signed state, like the personal callback. On a reconnect state: 404 communications.company-mailbox.not-found | 422 communications.company-mailbox.reconnect-address-mismatch (IMM-264).',
  },
  reconnect: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes/:companyMailboxId/reconnect',
    pathParams: CompanyMailboxPathParamsSchema,
    body: InitiateMailboxConnectionBodySchema,
    response: { 200: InitiateMailboxConnectionResponseSchema },
    permission: 'workspace:communications:company-mailbox-manage',
    description:
      "Start the Google sign-in that reconnects this company mailbox; the sign-in must use the mailbox's own address. 404 communications.company-mailbox.not-found | 422 communications.mailbox.provider-not-supported (IMM-264). Owner and admin only.",
  },
  connectImap: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes/imap',
    pathParams: ListCompanyMailboxesPathParamsSchema,
    body: ConnectImapCompanyMailboxBodySchema,
    response: { 200: CompleteCompanyMailboxConnectionResponseSchema },
    permission: 'workspace:communications:company-mailbox-manage',
    description:
      'Check and connect a company mailbox over IMAP — receiving only, so only the IMAP login is checked; nothing is stored unless it succeeds (IMM-1173.9). 422 communications.imap-mailbox.login-refused | server-unreachable | unencrypted · communications.company-mailbox.already-connected | connected-as-personal · communications.mailbox.provider-not-supported · 429 communications.imap-mailbox.too-many-attempts. Owner and admin only.',
  },
  reconnectImap: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes/:companyMailboxId/imap',
    pathParams: CompanyMailboxPathParamsSchema,
    body: ReconnectImapCompanyMailboxBodySchema,
    response: { 200: CompleteCompanyMailboxConnectionResponseSchema },
    permission: 'workspace:communications:company-mailbox-manage',
    description:
      'Reconnect this IMAP company mailbox with a new password or server details; the address is the stored one. 404 communications.company-mailbox.not-found · 422 the IMAP check codes · communications.mailbox.provider-not-supported when the mailbox is not an IMAP one · 429 communications.imap-mailbox.too-many-attempts (IMM-1173.9). Owner and admin only.',
  },
  disconnect: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes/:companyMailboxId',
    pathParams: CompanyMailboxPathParamsSchema,
    response: { 200: DisconnectMailboxResponseSchema },
    permission: 'workspace:communications:company-mailbox-manage',
    description:
      'Disconnect a company mailbox exactly as a personal mailbox is: stop push, revoke at the provider, remove the connection. Synced conversations are kept, hidden until the same address is connected again. Idempotent (IMM-264). Owner and admin only.',
  },
  getThread: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes/:companyMailboxId/threads/:providerThreadId',
    pathParams: CompanyMailboxThreadPathParamsSchema,
    response: { 200: EmailThreadResponseSchema },
    permission: 'workspace:communications:company-mailbox-read',
    description:
      'One company-mailbox conversation with its messages in chronological order. 404 communications.email-thread.not-found unless the id names a company mailbox of this workspace holding the thread (IMM-264). Owner and admin only.',
  },
  assignContact: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes/:companyMailboxId/threads/:providerThreadId/contact',
    pathParams: CompanyMailboxThreadPathParamsSchema,
    body: AssignEmailThreadContactBodySchema,
    response: { 200: AssignEmailThreadContactResponseSchema },
    // The personal assign's WRITE gate; the presenter adds the company-read check.
    permission: 'organization:communications:mailbox-send',
    description:
      'Assign a company-mailbox thread to a workspace contact, exactly as the personal route does within that company mailbox; sender overrides are per company mailbox and no timeline event is published. 404 communications.email-thread.not-found | communications.email-thread.contact-not-found (IMM-264). Owner and admin only.',
  },
  removeContact: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes/:companyMailboxId/threads/:providerThreadId/contact',
    pathParams: CompanyMailboxThreadPathParamsSchema,
    response: { 200: RemoveEmailThreadContactResponseSchema },
    permission: 'organization:communications:mailbox-send',
    description: "Clear a company-mailbox thread's contact assignment (IMM-264). Owner and admin only.",
  },
  sync: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/company-mailboxes/:companyMailboxId/sync',
    pathParams: CompanyMailboxPathParamsSchema,
    response: { 200: SyncMailboxResponseSchema },
    permission: 'workspace:communications:company-mailbox-read',
    description:
      'Pull the latest mail of one company mailbox, cooldown-throttled like the personal sync. 404 communications.company-mailbox.not-found (IMM-264). Owner and admin only.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const mailboxContracts = {
  getConnection: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/mailbox',
    pathParams: GetMailboxConnectionPathParamsSchema,
    response: { 200: MailboxConnectionResponseSchema },
    permission: 'workspace:communications:mailbox-connect',
    description:
      "The calling member's mailbox connection state (none / active / expired) for the settings surface (IMM-86.3 / ADR 0018).",
  },
  connect: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/connect',
    pathParams: InitiateMailboxConnectionPathParamsSchema,
    body: InitiateMailboxConnectionBodySchema,
    response: { 200: InitiateMailboxConnectionResponseSchema },
    permission: 'workspace:communications:mailbox-connect',
    description:
      "Start the provider's OAuth flow (broker or immoOS's own client) for the caller's chosen provider; returns the authorization URL the browser is redirected to (IMM-86.3).",
  },
  discoverImap: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/imap/discover',
    pathParams: DiscoverImapServerPathParamsSchema,
    body: DiscoverImapServerBodySchema,
    response: { 200: DiscoverImapServerResponseSchema },
    permission: 'workspace:communications:mailbox-connect',
    description:
      "Recognise the IMAP provider of an address — by its domain, else by the domain's mail servers — and return its known server settings, or nulls (IMM-1173.1). 422 communications.mailbox.provider-not-supported while IMAP is not offered.",
  },
  connectImap: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/imap',
    pathParams: ConnectImapMailboxPathParamsSchema,
    body: ConnectImapMailboxBodySchema,
    response: { 200: CompleteMailboxConnectionResponseSchema },
    permission: 'workspace:communications:mailbox-connect',
    description:
      "Check and connect the caller's mailbox over IMAP: logs in to the IMAP server and authenticates at the SMTP server with the same credentials, and stores the connection only when both succeed — nothing is stored otherwise. Also reconnects an expired IMAP connection. 422 communications.imap-mailbox.login-refused | server-unreachable | sending-refused | unencrypted · communications.mailbox.connected-as-company-mailbox · communications.mailbox.provider-not-supported · 429 communications.imap-mailbox.too-many-attempts (IMM-1173.2).",
  },
  completeConnect: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/callback',
    pathParams: CompleteMailboxConnectionPathParamsSchema,
    body: CompleteMailboxConnectionBodySchema,
    response: { 200: CompleteMailboxConnectionResponseSchema },
    permission: 'workspace:communications:mailbox-connect',
    description:
      'Exchange the OAuth code returned by the provider for a grant and store it against the calling member (IMM-86.3).',
  },
  disconnect: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/communications/mailbox',
    pathParams: DisconnectMailboxPathParamsSchema,
    response: { 200: DisconnectMailboxResponseSchema },
    permission: 'workspace:communications:mailbox-disconnect',
    description: "Revoke the calling member's grant at the provider and remove it. Idempotent (IMM-86.3).",
  },
  getThread: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/threads/:providerThreadId',
    pathParams: GetEmailThreadPathParamsSchema,
    response: { 200: MailboxThreadResponseSchema },
    // Membership gate only (IMM-233.1.2): every member reads their OWN mailbox;
    // reading a colleague's thread additionally requires `mailbox-read`, enforced
    // in-handler on the resolved thread owner. Workspace-scoped throughout.
    permission: 'workspace:communications:mailbox-thread-read',
    description:
      "One of the caller's synced email threads with its messages in chronological order (metadata from the read-model). Own mailbox always; a colleague's thread requires workspace:communications:mailbox-read (IMM-233.1.2 / ADR 0018).",
  },
  placeThread: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/threads/:providerThreadId/placement',
    pathParams: GetEmailThreadPathParamsSchema,
    body: PlaceEmailThreadBodySchema,
    response: { 200: PlaceEmailThreadResponseSchema },
    permission: 'organization:communications:mailbox-send',
    description:
      "Place, move or confirm the caller's own conversation on a Kontakt, Lead, Deal or Objekt; the whole conversation follows, later replies included, and a placement by hand outranks every automatic one. applyToAllFromSender is for a Kontakt target only and reaches only unplaced conversations. 404 communications.email-thread.not-found | communications.email-thread.subject-not-found, 403 communications.email-thread.placement-forbidden (IMM-866.5).",
  },
  removeThreadPlacement: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/threads/:providerThreadId/placement',
    pathParams: GetEmailThreadPathParamsSchema,
    response: { 200: RemoveEmailThreadPlacementResponseSchema },
    permission: 'organization:communications:mailbox-send',
    description:
      "Take the caller's own conversation off its record; its e-mail activities leave the timeline and later mail is not re-placed automatically. Idempotent: removed is false when it was not placed. 404 communications.email-thread.not-found, 403 communications.email-thread.placement-forbidden (IMM-866.5).",
  },
  getThreadPlacementSuggestions: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/threads/:providerThreadId/placement/suggestions',
    pathParams: GetEmailThreadPathParamsSchema,
    response: { 200: EmailThreadPlacementSuggestionsResponseSchema },
    permission: 'workspace:communications:mailbox-thread-read',
    description:
      "The Kontakt the conversation's counterpart matches and, when that Kontakt has several candidates, the Deals and Objekte inside the caller's visible Standorte. 404 communications.email-thread.not-found (IMM-866.5).",
  },
  getMessageBody: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/messages/:messageId/body',
    pathParams: GetEmailMessageBodyPathParamsSchema,
    response: { 200: EmailMessageBodyResponseSchema },
    permission: 'workspace:communications:mailbox-thread-read',
    description:
      "A single message's body, fetched live from the owning mailbox on open and NEVER persisted — returned as HTML or a plain-text fallback (IMM-233.1.2 / ADR 0018).",
  },
  markMessageRead: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/messages/:messageId/read',
    pathParams: MarkEmailMessageReadPathParamsSchema,
    response: { 200: MarkEmailMessageReadResponseSchema },
    permission: 'workspace:communications:mailbox-thread-read',
    description:
      "Mark an inbound message read against the owning mailbox and update the read-model unread state. Idempotent — reflects the mailbox owner's state, not the viewer's (IMM-233.1.2 / ADR 0018).",
  },
  syncNow: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/sync',
    pathParams: SyncMailboxPathParamsSchema,
    response: { 200: SyncMailboxResponseSchema },
    permission: 'workspace:communications:mailbox-thread-read',
    description:
      "Actively pull the acting member's most recent messages from the provider into the read-model, covering the delivery lag of Nylas basic-subscription webhooks. Cooldown-throttled; `skipped` is true when nothing was pulled (no active grant, or within the cooldown) (IMM-233).",
  },
  downloadAttachment: {
    kind: 'stream',
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/messages/:messageId/attachments/:attachmentId',
    pathParams: DownloadEmailAttachmentPathParamsSchema,
    permission: 'workspace:communications:mailbox-thread-read',
    description:
      "Stream a message attachment's original bytes from the owning mailbox with its filename and content-type. The bytes are never retained by immoOS (IMM-233.1.2 / ADR 0018).",
  },
  send: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/send',
    pathParams: SendEmailPathParamsSchema,
    body: SendEmailBodySchema,
    response: { 200: SendEmailResponseSchema },
    // WRITE gate (IMM-233.2). Org-scoped `mailbox-send` — every org role holds it
    // (Viewer since IMM-1063); a member without it is refused with the ADR-0013 Forbidden shape.
    // The member always sends from their OWN mailbox (no send-on-behalf-of), so
    // this capability is the whole authorisation. Workspace-addressed path (email
    // is workspace-scoped); the resolver's primary-org fallback resolves the role.
    permission: 'organization:communications:mailbox-send',
    description:
      "Send an email from the calling member's own connected mailbox (member-as-actor, ADR 0018) — compose / reply / reply-all / forward. Materialises the outbound message into the read-model and auto-matches it to a workspace contact (primary To; exactly-one → assigned). 422 communications.mailbox.grant-not-active when the caller has no active grant (inline-blockable); 422 communications.mailbox.grant-mismatch when a supplied fromGrantId contradicts the resolved grant; 422 communications.mailbox.attachments-too-large when combined attachments exceed 25 MB (IMM-233.2); 422 communications.mailbox.personal-link-unavailable when the body links an Interessent's personal page (`personalLinkProspectId`) the caller cannot see or whose Widerrufsbelehrung was never sent — a fresh IMM-1065 link is issued per e-mail (IMM-1177.7); 422 communications.mailbox.send-refused when the provider permanently refuses the message before accepting it (details.reason, the provider's own words; nothing was sent) (IMM-1173.4).",
  },
  listMessages: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/messages',
    pathParams: ListMailboxMessagesPathParamsSchema,
    query: ListMailboxMessagesQuerySchema,
    response: { 200: ListMailboxMessagesResponseSchema },
    // Membership gate only (IMM-233.3): the Posteingang / Postausgang list the
    // caller's OWN mailbox — every query is workspace + acting-member scoped, so
    // there is no own-vs-foreign path here. Reuses 233.1.2's `mailbox-thread-read`.
    permission: 'workspace:communications:mailbox-thread-read',
    description:
      "The calling member's own Posteingang (direction=INBOUND) or Postausgang (direction=OUTBOUND) — a Gmail-style chronological cross-contact list over the already-synced read-model, newest first, keyset-paginated (limit + opaque cursor → \"Mehr laden\"). Optional `unread` filter (INBOUND only; ignored for OUTBOUND) and `accountId` account filter. Workspace + own-member scoped; a member never lists another member's mailbox. The workspace's company mailboxes are included for a workspace:communications:company-mailbox-read holder (IMM-233.3 / IMM-264 / ADR 0018). Optional `placement=UNPLACED` narrows to the caller's own personal conversations placed on no record (IMM-866.7).",
  },
  countUnplacedThreads: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/threads/unplaced-count',
    pathParams: CountUnplacedEmailThreadsPathParamsSchema,
    response: { 200: CountUnplacedEmailThreadsResponseSchema },
    permission: 'workspace:communications:mailbox-thread-read',
    description:
      "How many of the caller's own personal conversations are placed on no record and hold at least one received message — the Posteingang's „{count} nicht zugeordnet“. „Zuordnung prüfen“ conversations are placed and not counted; company-mailbox conversations are never counted; a disconnected mailbox's conversations are not counted (IMM-866.7).",
  },
  assignContact: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/threads/:providerThreadId/contact',
    pathParams: AssignEmailThreadContactPathParamsSchema,
    body: AssignEmailThreadContactBodySchema,
    response: { 200: AssignEmailThreadContactResponseSchema },
    // WRITE gate (IMM-233.4). The same org-scoped `mailbox-send` capability the
    // send surface uses — assignment is the other member-owned mailbox write and
    // needs it too: every org role holds it; the placement authority additionally
    // requires `activity-write` in the record's Standort, so a Viewer (and an
    // Assistant) is still refused there. The member assigns only their OWN thread (resolved + own-gated in-handler); a
    // colleague's thread is refused with the ADR-0013 Forbidden shape.
    permission: 'organization:communications:mailbox-send',
    description:
      "Manually assign one of the caller's own email threads to a workspace contact, marking the assignment MANUAL so a later auto-match never overwrites it, and projecting the thread onto the contact's activity timeline. With applyToAllFromSender, every OTHER of the caller's threads from the same sender address is assigned too, except those already MANUALLY assigned to a different contact (non-destructive). 404 communications.email-thread.not-found for an unknown thread; 404 communications.email-thread.contact-not-found for a contact outside the workspace (IMM-233.4).",
  },
  removeContact: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/communications/mailbox/threads/:providerThreadId/contact',
    pathParams: RemoveEmailThreadContactPathParamsSchema,
    response: { 200: RemoveEmailThreadContactResponseSchema },
    // Same WRITE gate as assignContact (IMM-233.4).
    permission: 'organization:communications:mailbox-send',
    description:
      "Remove the contact assignment from one of the caller's own email threads — the thread returns to unassigned and its entry is retracted from the contact's activity timeline. The email/thread itself is NOT deleted. Idempotent: removing an already-unassigned thread is a no-op. 404 communications.email-thread.not-found for an unknown thread (IMM-233.4).",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * A member connecting/disconnecting their own WhatsApp number from their
 * profile (IMM-1040.1). Internal to the web app, not published — no `docs` key.
 */
const myWhatsAppContracts = {
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/me/whatsapp',
    pathParams: MyWhatsAppPathParamsSchema,
    response: { 200: MyWhatsAppConnectionResponseSchema },
    permission: 'workspace:identity:profile-read',
    description:
      "The calling member's WhatsApp connection state: whether the channel is configured, the immoOS number to write to, and the connected number if any (IMM-1040.1).",
  },
  issueLinkCode: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/me/whatsapp/link-code',
    pathParams: MyWhatsAppPathParamsSchema,
    response: { 201: IssueWhatsAppLinkCodeResponseSchema },
    permission: 'workspace:identity:profile-update',
    description:
      'Issue a one-time code the caller sends from WhatsApp to bind that number to their account; superseding any code already open for them. 422 communications.whatsapp-link.already-connected when the caller already has an active number. 422 communications.whatsapp-link.channel-unavailable when WhatsApp is not configured. 403 for an API-key principal or under impersonation (IMM-1040.1).',
  },
  disconnect: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/me/whatsapp',
    pathParams: MyWhatsAppPathParamsSchema,
    response: { 204: zod.void() },
    permission: 'workspace:identity:profile-update',
    description:
      "End the caller's active WhatsApp connection and close any open code. Idempotent: disconnecting with no active connection is a 204 no-op. 403 for an API-key principal or under impersonation (IMM-1040.1).",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * A member connecting/disconnecting their own Telegram account from their
 * profile (IMM-1127.2). Internal to the web app, not published — no `docs` key.
 */
const myTelegramContracts = {
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/me/telegram',
    pathParams: MyTelegramPathParamsSchema,
    response: { 200: MyTelegramConnectionResponseSchema },
    permission: 'workspace:identity:profile-read',
    description:
      "The calling member's Telegram connection state: whether the channel is configured, the bot's username to write to, and the connected account if any (IMM-1127.2).",
  },
  issueLinkCode: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/me/telegram/link-code',
    pathParams: MyTelegramPathParamsSchema,
    response: { 201: IssueTelegramLinkCodeResponseSchema },
    permission: 'workspace:identity:profile-update',
    description:
      'Issue a one-time code the caller sends to the bot from Telegram to bind that account to their account; superseding any code already open for them. 422 communications.telegram-link.already-connected when the caller already has an active account. 422 communications.telegram-link.channel-unavailable when Telegram is not configured. 403 for an API-key principal or under impersonation (IMM-1127.2).',
  },
  disconnect: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/me/telegram',
    pathParams: MyTelegramPathParamsSchema,
    response: { 204: zod.void() },
    permission: 'workspace:identity:profile-update',
    description:
      "End the caller's active Telegram connection and close any open code. Idempotent: disconnecting with no active connection is a 204 no-op. 403 for an API-key principal or under impersonation (IMM-1127.2).",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The workspace's ImmoScout24 connection (IMM-1057.1). Internal to the web app,
 * not published — no `docs` key.
 */
const portalConnectionsContracts = {
  list: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/portal-connections',
    pathParams: PortalConnectionPathParamsSchema,
    response: { 200: ListPortalConnectionsResponseSchema },
    permission: 'workspace:property:portal-connection-read',
    description:
      "Every portal's connection for the workspace, one entry per portal in PORTALS order: whether the deployment offers it, its state, who connected it when, and for Kleinanzeigen and Immowelt the stored access without the password (IMM-246.1).",
  },
  saveAccess: {
    method: 'PUT',
    path: '/v1/workspaces/:workspaceId/portal-connections/:portal/access',
    pathParams: PortalAccessPathParamsSchema,
    body: SavePortalAccessBodySchema,
    response: { 200: PortalConnectionResponseSchema },
    permission: 'workspace:property:portal-connection-manage',
    description:
      "Store the workspace's FTP access to Kleinanzeigen or Immowelt after one sign-in with it. 422 property.portal-connection.sign-in-failed (nothing stored; the server's reply in details.serverMessage), .password-required, .unavailable. 403 for an API-key principal or under impersonation (IMM-246.1).",
  },
  removeAccess: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/portal-connections/:portal/access',
    pathParams: PortalAccessPathParamsSchema,
    response: { 204: zod.void() },
    permission: 'workspace:property:portal-connection-manage',
    description:
      'Remove the stored Kleinanzeigen or Immowelt access. Listings already on the portal stay there. Idempotent: 204 with nothing connected. 403 for an API-key principal or under impersonation (IMM-246.1).',
  },
  get: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/portal-connections/immoscout24',
    pathParams: PortalConnectionPathParamsSchema,
    response: { 200: PortalConnectionResponseSchema },
    permission: 'workspace:property:portal-connection-read',
    description:
      "The workspace's ImmoScout24 connection: whether the deployment offers it, its state, the IS24 account name and who connected it when. A workspace with no connection reads as NOT_CONNECTED (IMM-1057.1).",
  },
  start: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/portal-connections/immoscout24/connect',
    pathParams: PortalConnectionPathParamsSchema,
    response: { 200: StartPortalConnectionResponseSchema },
    permission: 'workspace:property:portal-connection-manage',
    description:
      'Begin the ImmoScout24 sign-in and return the address to send the browser to. 422 property.portal-connection.unavailable when the deployment has no ImmoScout24 credentials. 502 property.portal-connection.provider-failed when ImmoScout24 is unreachable. 403 for an API-key principal or under impersonation (IMM-1057.1).',
  },
  complete: {
    method: 'POST',
    path: '/v1/workspaces/:workspaceId/portal-connections/immoscout24/complete',
    pathParams: PortalConnectionPathParamsSchema,
    body: CompletePortalConnectionBodySchema,
    response: { 200: PortalConnectionResponseSchema },
    permission: 'workspace:property:portal-connection-manage',
    description:
      'Finish the ImmoScout24 sign-in with the values ImmoScout24 returned. 422 property.portal-connection.attempt-invalid for a state, token or member that does not match the pending attempt, or one older than 15 minutes. 422 property.portal-connection.consent-declined when no verifier came back. 502 property.portal-connection.provider-failed when ImmoScout24 is unreachable. 403 for an API-key principal or under impersonation (IMM-1057.1).',
  },
  remove: {
    method: 'DELETE',
    path: '/v1/workspaces/:workspaceId/portal-connections/immoscout24',
    pathParams: PortalConnectionPathParamsSchema,
    response: { 204: zod.void() },
    permission: 'workspace:property:portal-connection-manage',
    description:
      'Remove the stored ImmoScout24 credentials. Live listings stay online at ImmoScout24. Idempotent: disconnecting with no connection is a 204 no-op. 403 for an API-key principal or under impersonation (IMM-1057.1).',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * An Objekt's portal listings and its ImmoScout24 readiness (IMM-1057.3).
 * Internal to the web app, not published — no `docs` key.
 */
const portalListingsContracts = {
  list: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings',
    pathParams: PropertyPortalListingsPathParamsSchema,
    response: { 200: PropertyPortalListingsResponseSchema },
    permission: 'organization:property:property-read',
    description:
      "The Objekt's portal listings on every portal and the state of each of the workspace's portal connections (ImmoScout24, Kleinanzeigen and Immowelt). 404 property.property.not-found for a foreign or deleted Objekt.",
  },
  readiness: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/readiness',
    pathParams: PropertyPortalListingsPathParamsSchema,
    response: { 200: PortalReadinessResponseSchema },
    permission: 'organization:property:property-read',
    description:
      'Everything ImmoScout24 still needs before this Objekt can be published, split into blockers and hints, each with the section it is edited in. A pure read: nothing is sent to ImmoScout24. 404 property.property.not-found for a foreign or deleted Objekt.',
  },
  openImmoReadiness: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/:portal/readiness',
    pathParams: PropertyOpenImmoListingPathParamsSchema,
    response: { 200: PortalReadinessResponseSchema },
    permission: 'organization:property:property-read',
    description:
      'Everything Kleinanzeigen or Immowelt still needs before this Objekt can be published, split into blockers and hints. A pure read: nothing is sent to the portal. 404 property.property.not-found for a foreign or deleted Objekt.',
  },
  transmit: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/transmit',
    pathParams: PropertyPortalListingsPathParamsSchema,
    body: TransmitPortalListingsBodySchema,
    response: { 202: PropertyPortalListingsResponseSchema },
    permission: 'organization:property:portal-listing-publish',
    description:
      "Queue a transmission of this Objekt to ImmoScout24 under its responsible member's contact. The route repeats the readiness checks, records a pending transmission on the Objekt's listing rows and answers 202 with the same read model as the list; ImmoScout24 is called afterwards, off the request path.\n\nPass `attachPortalListingId` (digits only) to take over a listing that already exists on ImmoScout24 instead of creating a second one: the listing is looked up in the connected account, its id is stored on the Objekt's listing of that type, and the transmission then overwrites its texts, pictures and contact person. Every transmission removes pictures from the ImmoScout24 listing that immoOS did not upload. 422 property.portal-listing.attach-not-found, .attach-type-mismatch or .already-on-portal; 409 property.portal-listing.attach-taken or .attach-taken-unnumbered; 502 property.portal-connection.provider-failed when ImmoScout24 cannot be asked.\n\n422 property.portal-listing.property-not-active, .not-ready, .contact-incomplete, .responsible-member-inactive or .listing-type-changed, or property.portal-connection.not-connected or .expired; 409 property.portal-listing.operation-in-progress; 403 without the publish right; 404 property.property.not-found for a foreign or deleted Objekt.",
  },
  openImmoTransmit: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/:portal/transmit',
    pathParams: PropertyOpenImmoListingPathParamsSchema,
    body: TransmitOpenImmoListingBodySchema,
    response: { 202: PropertyPortalListingsResponseSchema },
    permission: 'organization:property:portal-listing-publish',
    description:
      "Queue an upload of this Objekt to Kleinanzeigen or Immowelt as one OpenImmo partial transfer. The route repeats the readiness checks, records a pending transmission on the Objekt's listing for the portal and answers 202 with the listings read; the portal's FTP server is called afterwards, off the request path. 422 property.portal-listing.property-not-active, .not-ready or .responsible-member-inactive, or property.portal-connection.not-connected or .expired; 409 property.portal-listing.operation-in-progress; 403 without the publish right; 404 property.property.not-found.",
  },
  withdraw: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/withdraw',
    pathParams: PropertyPortalListingsPathParamsSchema,
    response: { 202: PropertyPortalListingsResponseSchema },
    permission: 'organization:property:portal-listing-withdraw',
    description:
      "Queue the withdrawal of this Objekt's live ImmoScout24 listings. Withdrawing depublishes the listing and keeps its IS24 id and pictures, so publishing again reuses them. Accepted in any Objekt status. Answers 202 with the same read model as the list; ImmoScout24 is called afterwards, off the request path. 422 property.portal-listing.not-live, or property.portal-connection.not-connected or .expired; 409 property.portal-listing.operation-in-progress; 403 without the withdraw right; 404 property.property.not-found for a foreign or deleted Objekt.",
  },
  openImmoWithdraw: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/:portal/withdraw',
    pathParams: PropertyOpenImmoListingPathParamsSchema,
    response: { 202: PropertyPortalListingsResponseSchema },
    permission: 'organization:property:portal-listing-withdraw',
    description:
      "Queue the withdrawal of this Objekt's live Kleinanzeigen or Immowelt listing: an OpenImmo DELETE under the listing's ids. The listing keeps its ids and history, so publishing again reuses it. Accepted in any Objekt status. 422 property.portal-listing.not-live, or property.portal-connection.not-connected or .expired; 409 property.portal-listing.operation-in-progress; 403 without the withdraw right; 404 property.property.not-found.",
  },
  confirmBrowserRun: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-runs/:runId/confirm',
    pathParams: BrowserRunPathParamsSchema,
    body: ConfirmBrowserRunBodySchema,
    response: { 200: PropertyPortalListingsResponseSchema },
    permission: 'organization:property:portal-listing-publish',
    description:
      "Confirm a connected client's request to send this Objekt to ImmoScout24 or to publish its changes: the run becomes CONFIRMED and the requesting member's browser extension carries it out within 24 hours, or it lapses. Only the member whose client asked can confirm. For a request to put a draft online, `publicationOptions` names the options ImmoScout24 offers that the member ticked (at least one); a send or update takes none. Answers 200 with the same read model as the portal listings. 404 property.browser-run.not-found for a run of another Objekt or organisation and for a member other than the requester; 422 property.browser-run.publication-option-required (details.runId) for a go-live without options; 422 property.browser-run.publication-option-not-applicable (details.runId, details.kind) for options on a send or update; 422 property.browser-run.not-awaiting-confirmation (details.runId, details.status) once the run is decided, claimed or finished; 403 identity.api-key.route-not-available for an API key (only a member session decides), and without the publish right.",
  },
  declineBrowserRun: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-runs/:runId/decline',
    pathParams: BrowserRunPathParamsSchema,
    body: DecideBrowserRunBodySchema,
    response: { 200: PropertyPortalListingsResponseSchema },
    permission: 'organization:property:portal-listing-publish',
    description:
      "Decline a connected client's request: the run ends DECLINED and nothing is sent to ImmoScout24. Any member with the publish right can decline. Answers 200 with the same read model as the portal listings. 404 property.browser-run.not-found for a run of another Objekt or organisation; 422 property.browser-run.not-awaiting-confirmation (details.runId, details.status) once the run is decided, claimed or finished; 403 identity.api-key.route-not-available for an API key (only a member session decides), and without the publish right.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The browser extension's ImmoScout24 channel (IMM-1381, ADR 0064). Called with the member-bound `immo_` key
 * „Erweiterung verbinden“ issues. Internal to the extension, not published — no `docs` key.
 */
const browserPublishContracts = {
  listings: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/portal-listings/immoscout24/browser',
    pathParams: BrowserListingsPathParamsSchema,
    response: { 200: BrowserListingsResponseSchema },
    permission: 'workspace:property:property-read',
    description:
      "The caller's visible active Objekte with their ImmoScout24 listing rows (Scout-ID, status, live, public link), newest first. One call serves the extension's „Deine Inserate“ menu and its recognition of the Scout-ID in the open IS24 tab.",
  },
  job: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-job',
    pathParams: BrowserJobPathParamsSchema,
    query: BrowserJobQuerySchema,
    response: { 200: BrowserJobResponseSchema },
    permission: 'organization:property:portal-listing-browser-publish',
    description:
      'What the extension writes to ImmoScout24 for this Objekt: per IS24 listing the real estate payload, the marketing pictures as short-lived download links, the pictures already on IS24 and a fingerprint, plus the readiness without the connection blockers. `jobs` is empty while a blocker remains. `attachPortalListingId` targets an existing IS24 listing instead („Verknüpfen“). A pure read: nothing is sent to ImmoScout24. 409 property.portal-listing.operation-in-progress while an ImmoScout24 operation of the OAuth channel is pending on the Objekt; 409 property.portal-listing.attach-taken or .attach-taken-unnumbered when another Objekt holds that Scout-ID; 422 property.portal-listing.already-on-portal when the Objekt already holds a different one; 404 property.property.not-found.',
  },
  recordOutcome: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-outcome',
    pathParams: BrowserJobPathParamsSchema,
    body: RecordBrowserOutcomeBodySchema,
    response: { 200: RecordBrowserOutcomeResponseSchema },
    permission: 'organization:property:portal-listing-browser-publish',
    description:
      "Record what the extension wrote to ImmoScout24: the Scout-ID, the pictures it holds and the fingerprint of the job (`null` when IS24 did not keep every field, so a live listing reads as out of date). A new draft is recorded as transmitted and not live (it goes online only when the agent books it in ScoutManager); a live listing stays live. Recorded whatever the Objekt's status or readiness by now: IS24 already holds the write. Answers with the Objekt's ImmoScout24 rows as `listings` returns them. 422 property.portal-listing.already-on-portal; 409 property.portal-listing.operation-in-progress, .attach-taken or .attach-taken-unnumbered; 404 property.property.not-found.",
  },
  linkListing: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-link',
    pathParams: BrowserJobPathParamsSchema,
    body: LinkBrowserListingBodySchema,
    response: { 200: RecordBrowserOutcomeResponseSchema },
    permission: 'organization:property:portal-listing-browser-publish',
    description:
      "„Verknüpfen“: keep the Scout-ID of the agent's ImmoScout24 tab on this Objekt. Only the Scout-ID is stored — no status, transmission time, fingerprint or picture — so the listing reads as linked and differing until the extension writes it. Nothing is sent to ImmoScout24. The first of the Objekt's ImmoScout24 listing types without a Scout-ID takes it; linking the Scout-ID the Objekt already holds changes nothing. Answers with the Objekt's ImmoScout24 rows as `listings` returns them. 409 property.portal-listing.operation-in-progress, .attach-taken or .attach-taken-unnumbered; 422 property.portal-listing.already-on-portal or .attach-type-mismatch; 404 property.property.not-found.",
  },
  importOptions: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/portal-listings/immoscout24/browser-import',
    pathParams: BrowserListingsPathParamsSchema,
    response: { 200: BrowserImportOptionsResponseSchema },
    permission: 'workspace:property:property-read',
    description:
      "What the extension's import of ImmoScout24 listings needs (IMM-1484): the caller's visible Standorte with their active members, the default Standort (the caller's own first visible one), and every Scout-ID an Objekt of the workspace holds, so linked listings are never offered. A pure read.",
  },
  importPictureUploads: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/portal-listings/immoscout24/browser-import/picture-uploads',
    pathParams: BrowserImportPathParamsSchema,
    body: BrowserImportPictureUploadsBodySchema,
    response: { 200: BrowserImportPictureUploadsResponseSchema },
    permission: 'organization:property:property-create',
    description:
      'Presigned direct-to-S3 uploads for the pictures of an ImmoScout24 listing about to be imported (IMM-1484), in the order given; the property media image policy applies (JPEG, PNG, WebP; ≤ 20 MB). No Objekt and no media row exist yet: `importListing` confirms them. 403 property.browser-import.not-allowed without the browser-publish permission; 422 property.property-media.* for a refused type or size.',
  },
  importListing: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/portal-listings/immoscout24/browser-import',
    pathParams: BrowserImportPathParamsSchema,
    body: BrowserImportListingBodySchema,
    response: { 201: BrowserImportListingResponseSchema },
    permission: 'organization:property:property-create',
    description:
      "Import one ImmoScout24 listing as an Objekt (IMM-1484): immoOS maps the listing's ScoutManager data per the signed-off mapping, creates the Objekt in this Standort with `assignedMemberId` zuständig, numbered by immoOS, with the listing's own Objektnummer as its Referenznummer (left empty when another Objekt carries it), adds the uploaded pictures in order (published, cover and floor plans as on ImmoScout24) and keeps the Scout-ID on its ImmoScout24 listing as „Verknüpfen“ would. All or nothing; nothing is sent to ImmoScout24. 403 property.browser-import.not-allowed; 422 property.browser-import.unreadable-listing, property.browser-import.assignee-not-in-standort or property.property-media.*; 409 property.portal-listing.attach-taken or .attach-taken-unnumbered.",
  },
  runs: {
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/portal-listings/immoscout24/browser-runs',
    pathParams: BrowserListingsPathParamsSchema,
    response: { 200: BrowserRunQueueResponseSchema },
    permission: 'workspace:property:property-read',
    description:
      "The caller's confirmed client requests the extension still has to carry out, in visible organisations, unexpired, oldest confirmation first, at most 20. Polled by the extension on start-up, on connect and every 2 minutes.",
  },
  startRun: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-runs',
    pathParams: BrowserJobPathParamsSchema,
    body: StartBrowserRunBodySchema,
    response: { 201: StartBrowserRunResponseSchema },
    permission: 'organization:property:portal-listing-browser-publish',
    description:
      "Record a send or update the extension is about to carry out for this Objekt, started from a click in immoOS. The run is recorded RUNNING at once (`kind` is UPDATE when the Objekt already holds a Scout-ID, else SEND) and answers with the run and the job exactly as `job` would answer now. Nothing is recorded when refused: 404 property.property.not-found; 422 property.portal-listing.property-not-active, property.browser-run.api-connection-in-use (the workspace's ImmoScout24 connection is not NOT_CONNECTED) or property.portal-listing.not-ready (details.items); 409 property.portal-listing.operation-in-progress or property.browser-run.already-open (details.runId, details.status).",
  },
  goLiveJob: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-go-live-job',
    pathParams: BrowserJobPathParamsSchema,
    query: GoLiveJobQuerySchema,
    response: { 200: BrowserGoLiveJobResponseSchema },
    permission: 'organization:property:portal-listing-browser-publish',
    description:
      "The ScoutManager go-live pages for this Objekt's ImmoScout24 draft of one listing type, as steps the extension walks in the agent's browser. A pure read: nothing is sent or recorded. 404 property.property.not-found; 422 property.browser-run.not-a-draft (details.listingType) when the listing has no Scout-ID or is already live.",
  },
  startGoLiveRun: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-runs/go-live',
    pathParams: BrowserJobPathParamsSchema,
    body: StartGoLiveRunBodySchema,
    response: { 201: StartGoLiveRunResponseSchema },
    permission: 'organization:property:portal-listing-browser-publish',
    description:
      "Record that the extension is about to put this Objekt's ImmoScout24 draft online with the publication options the agent chose in immoOS. The run is recorded RUNNING at once (`kind` GO_LIVE) and answers with the run and the go-live steps. Nothing is recorded when refused: 404 property.property.not-found; 422 property.browser-run.not-a-draft (details.listingType), property.portal-listing.property-not-active or property.browser-run.api-connection-in-use; 409 property.browser-run.already-open (details.runId, details.status) or property.portal-listing.operation-in-progress.",
  },
  startEndListingRun: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-runs/end-listing',
    pathParams: BrowserJobPathParamsSchema,
    body: StartEndListingRunBodySchema,
    response: { 201: StartGoLiveRunResponseSchema },
    permission: 'organization:property:portal-listing-browser-publish',
    description:
      "Record that the extension is about to take this Objekt's ImmoScout24 listing offline (`kind` TAKE_OFFLINE) or delete it (`kind` DELETE), as the agent confirmed in immoOS. The run is recorded RUNNING at once and answers with the run and the ScoutManager steps; ImmoScout24's survey is always answered „Keine Angabe“. Only a live listing can be taken offline and only one that is not live can be deleted. Nothing is recorded when refused: 404 property.property.not-found; 422 property.browser-run.not-live (details.listingType) when the listing has no Scout-ID or, for TAKE_OFFLINE, is not live; property.browser-run.still-live (details.listingType) for a DELETE of a live listing; property.browser-run.api-connection-in-use. The Objekt need not be „In Vermarktung“; 409 property.browser-run.already-open (details.runId, details.status) or property.portal-listing.operation-in-progress.",
  },
  reportRunProgress: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-runs/:runId/progress',
    pathParams: BrowserRunPathParamsSchema,
    body: ReportBrowserRunProgressBodySchema,
    response: { 200: BrowserRunResponseSchema },
    permission: 'organization:property:portal-listing-browser-publish',
    description:
      "Report how many of the run's ImmoScout24 calls are done (`done` of `total`). Only the member who started the run can report on it. 404 property.browser-run.not-found for a run of another member, Objekt or organisation; 422 property.browser-run.not-running once the run is finished or has gone stale.",
  },
  finishRun: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-runs/:runId/finish',
    pathParams: BrowserRunPathParamsSchema,
    body: FinishBrowserRunBodySchema,
    response: { 200: BrowserRunResponseSchema },
    permission: 'organization:property:portal-listing-browser-publish',
    description:
      "Record how the run ended: DONE with the German names of the details ImmoScout24 did not keep, or FAILED with IS24_REFUSED (and ImmoScout24's own text as `portalMessage`), IS24_SIGNED_OUT or EXTENSION_FAILED. The listing itself is recorded through `recordOutcome`. 404 property.browser-run.not-found for a run of another member, Objekt or organisation; 422 property.browser-run.not-running once the run is finished or has gone stale.",
  },
  claimRun: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/portal-listings/immoscout24/browser-runs/:runId/claim',
    pathParams: BrowserRunPathParamsSchema,
    body: ClaimBrowserRunBodySchema,
    response: { 200: ClaimBrowserRunResponseSchema },
    permission: 'organization:property:portal-listing-browser-publish',
    description:
      "Take a confirmed client request: the run becomes RUNNING and the answer carries the job built now, never the data of the request's time. A go-live request answers `goLiveJob` (the ScoutManager go-live steps, built now) with `job: null`; the run's `publicationOptions` are what the member confirmed. A draft that went live or lost its Scout-ID meanwhile ends the run FAILED / NOT_READY with both null. The answer carries `job: null` when the Objekt is no longer active or ready; the run then ends FAILED with NOT_READY. Only the requesting member can claim. 404 property.browser-run.not-found for a run of another member, Objekt or organisation; 422 property.browser-run.not-confirmed (details.runId, details.status) when the run is not CONFIRMED, lapsed included, or property.browser-run.api-connection-in-use (the run stays CONFIRMED until it lapses).",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * An Objekt's web exposé: readiness, publish and withdraw (IMM-1161.2).
 * Internal to the web app, not published — no `docs` key. The state itself is read through `portalListings.list`.
 */
const webExposeContracts = {
  readiness: {
    method: 'GET',
    path: '/v1/organizations/:organizationId/properties/:id/web-expose/readiness',
    pathParams: PropertyPortalListingsPathParamsSchema,
    response: { 200: WebExposeReadinessResponseSchema },
    permission: 'organization:property:property-read',
    description:
      'Everything the web exposé still needs before this Objekt can be published, split into blockers and hints, each with the section it is edited in, and the link the exposé will have. A pure read. 404 property.property.not-found for a foreign or deleted Objekt.',
  },
  publish: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/web-expose/publish',
    pathParams: PropertyPortalListingsPathParamsSchema,
    body: PublishWebExposeBodySchema,
    response: { 200: PropertyPortalListingsResponseSchema },
    permission: 'organization:property:portal-listing-publish',
    description:
      "Publish this Objekt's web exposé, or publish a withdrawn one again under the same link. `showAddress` decides whether the street is shown. Answers 200 with the same read model as the portal listings. 422 property.web-expose.property-not-active, .not-for-sale or .not-ready (details.missingItems); 403 without the publish right; 404 property.property.not-found for a foreign or deleted Objekt.",
  },
  withdraw: {
    method: 'POST',
    path: '/v1/organizations/:organizationId/properties/:id/web-expose/withdraw',
    pathParams: PropertyPortalListingsPathParamsSchema,
    response: { 200: PropertyPortalListingsResponseSchema },
    permission: 'organization:property:portal-listing-withdraw',
    description:
      "Withdraw this Objekt's web exposé. The link stays the same, so publishing again makes it live again. Accepted in any Objekt status. Answers 200 with the same read model as the portal listings. 422 property.web-expose.not-published; 403 without the withdraw right; 404 property.property.not-found for a foreign or deleted Objekt.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const workspaceEventsContracts = {
  stream: {
    kind: 'stream',
    method: 'GET',
    path: '/v1/workspaces/:workspaceId/communications/stream',
    pathParams: WorkspaceEventStreamPathParamsSchema,
    // Member-implicit like the workspace-addressed list gates: every member may
    // open the stream, and `attachVisibleOrganizations` — not this literal —
    // decides which Standorte the connection may hear about. Same reasoning as
    // `workspace:communications:mailbox-thread-read`.
    permission: 'workspace:communications:event-stream-read',
    event: StreamEventSchema,
    description:
      "Server-Sent Events stream of workspace changes the calling member is allowed to see (ADR 0036). text/event-stream; best-effort only — the REST reads stay authoritative, there is no replay of events missed while disconnected. The connection's Standort scope is resolved at connect and held for its lifetime.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/** The website's inquiry intake (IMM-1123.1, ADR 0062), published by IMM-1123.4. */
const publicInquiryContracts = {
  submit: {
    method: 'POST',
    path: '/public/v1/workspaces/:workspaceSlug/inquiries',
    pathParams: zod.object({ workspaceSlug: zod.string().min(1) }),
    body: SubmitWebsiteInquiryBodySchema,
    response: { 204: zod.void(), 404: WireErrorSchema, 422: WireErrorSchema },
    permission: 'PUBLIC',
    docs: 'PUBLIC',
    description:
      'Send one buyer\'s inquiry about one property from the agency\'s own website straight into immoOS. **Needs no authentication and is callable from any origin** (CORS is open for POST, without credentials), so the form\'s own submit handler can call it from the browser.\n\n**URL.** `POST https://api.immoos.ai/public/v1/workspaces/{workspaceSlug}/inquiries`. `{workspaceSlug}` is your workspace\'s slug, the segment after `/w/` in the immoOS app URL (`app.immoos.ai/w/{workspaceSlug}/…`), the same slug the reference feed takes. The URL keeps working after the workspace is renamed. `propertyId` is the property\'s id, the last segment of its page in immoOS: `app.immoos.ai/w/{workspaceSlug}/properties/{propertyId}`.\n\n**Request.** JSON only: send `Content-Type: application/json`. `name`, `email` and `phone` are required, `message` is optional and is cut at 10 000 characters, and `propertyId` is required. `homepage` is the hidden check field: render it as a text input hidden from people (off-screen or `display: none`, with `tabindex="-1"`, `autocomplete="off"` and `aria-hidden="true"`), and send whatever it contains. A person leaves it empty.\n\n**Answers.**\n\n- `204` with no body: accepted. The same answer when `homepage` is filled, so a bot learns nothing.\n- `422 deal.inquiry.cannot-submit-required-fields-missing`: `error.details.missingFields` lists the empty fields, a subset of `name`, `email` and `phone`.\n- `422 deal.inquiry.cannot-submit-email-invalid`: `email` is not a valid e-mail address.\n- `404 deal.inquiry.property-not-found`: the slug names no workspace, `propertyId` is missing, or the property is not one of the workspace\'s. All of these answer alike, on purpose.\n- `415`: the body is not JSON, for example a plain form post (`application/x-www-form-urlencoded` or `multipart/form-data`).\n- `400 shared.request.invalid`: a field is longer than the schema allows.\n\n**What to show the buyer.** `error.message` is developer-facing English, never copy for the buyer. Branch on `error.code` and show its German sentence:\n\n- `deal.inquiry.cannot-submit-required-fields-missing`: „Bitte fülle alle Pflichtfelder aus: {Felder}.“ Build `{Felder}` from `details.missingFields`, joined with a comma: `name` → Name, `email` → E-Mail, `phone` → Telefon, for example „Name, Telefon“.\n- `deal.inquiry.cannot-submit-email-invalid`: „Bitte gib eine gültige E-Mail-Adresse an.“\n- `deal.inquiry.property-not-found`: „Dieses Objekt wurde nicht gefunden.“\n\n**Rate limit.** Requests are throttled per caller IP at the edge. Over the limit the answer is `429` with no body and no CORS headers, so the browser reports a failed request, not a response. Treat any failed request like a refusal: keep the form filled in and tell the buyer to try again later.\n\n**What happens next.** immoOS matches the sender to an existing contact by e-mail, or creates one. On a property for sale that is not archived, it creates a buyer prospect in the sales pipeline unless the contact already is an active one there. The inquiry is kept on the contact in every case.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The buyer's own page (IMM-1065.7). No `docs` marker: a `docs: 'PUBLIC'` leaf would publish the page and escalate
 * every invalid-link 404 to Sentry. The response map holds only 200 so 404 and 400 arrive as `ok: false`.
 */
const publicBrokerageAgreementContracts = {
  page: {
    method: 'POST',
    path: '/public/v1/brokerage-agreement/page',
    body: ServeBrokerageAgreementPageBodySchema,
    response: { 200: BrokerageAgreementPageSchema },
    permission: 'PUBLIC',
    description:
      "The Interessent's personal page behind a link from the Belehrung e-mail. The token travels in the body, never the URL. Answers OPEN with the filled legal texts and the served page's fingerprint, or INACTIVE; 404 deal.brokerage-agreement.link-invalid for every link that opens nothing. Not available to API keys.",
  },
  conclusion: {
    method: 'POST',
    path: '/public/v1/brokerage-agreement/conclusion',
    body: ConcludeBrokerageAgreementBodySchema,
    response: { 200: BrokerageAgreementPageSchema },
    permission: 'PUBLIC',
    description:
      "Concludes the Maklervertrag from the Interessent's page against the snapshot the page was served with (pageFingerprint). Answers the page in CONCLUDED, also when the contract was already concluded. 422 deal.brokerage-agreement.cannot-conclude-notice-not-read (only for a page served with the read tick) | -page-outdated | -inactive; 404 deal.brokerage-agreement.link-invalid. Not available to API keys.",
  },
  expose: {
    method: 'POST',
    path: '/public/v1/brokerage-agreement/expose',
    body: GetBrokerageExposeDownloadBodySchema,
    response: { 200: BrokerageExposeDownloadSchema },
    permission: 'PUBLIC',
    description:
      "A short-lived download URL for the Objekt's current Exposé Vollversion, from the Interessent's page. Re-checks on every call that the contract is concluded with the early-start request, not withdrawn, and that a Vollversion exists now. 422 deal.brokerage-agreement.expose-unavailable for every reason it is not released; 404 deal.brokerage-agreement.link-invalid. Not available to API keys.",
  },
  withdrawal: {
    method: 'POST',
    path: '/public/v1/brokerage-agreement/withdrawal',
    body: WithdrawBrokerageAgreementBodySchema,
    response: { 200: BrokerageAgreementPageSchema },
    permission: 'PUBLIC',
    description:
      "Records the Interessent's Widerruf (§ 356a BGB) from their page, with the server time of the request as its time of receipt. Answers the page in WITHDRAWN, also when the Widerruf was already recorded. 422 deal.brokerage-agreement.cannot-withdraw-required-fields-missing | -email-invalid | -period-ended | -not-concluded; 404 deal.brokerage-agreement.link-invalid. Not available to API keys.",
  },
  documents: {
    method: 'POST',
    path: '/public/v1/brokerage-agreement/documents',
    body: GetBuyerDataRoomBodySchema,
    response: { 200: BuyerDataRoomSchema },
    permission: 'PUBLIC',
    description:
      "The Datenraum section of the Interessent's page: NONE without a release, ENDED once it ended, ACTIVE with every document currently marked for buyers. Read on every call. 404 deal.brokerage-agreement.link-invalid. Not available to API keys.",
  },
  document: {
    method: 'POST',
    path: '/public/v1/brokerage-agreement/document',
    body: GetDataRoomDocumentUrlBodySchema,
    response: { 200: BrokerageExposeDownloadSchema },
    permission: 'PUBLIC',
    description:
      'A short-lived URL for one released document (inline for VIEW, attachment for DOWNLOAD) and one recorded access. Re-checks link, active release and the live mark on every call. 422 deal.data-room.cannot-open-document-release-ended; 404 deal.brokerage-agreement.link-invalid for every other reason. Not available to API keys.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/** No `docs` marker, as for the buyer page: a `docs: 'PUBLIC'` leaf would publish the route and escalate every invalid-link 404 to Sentry. */
const publicSigningContracts = {
  page: {
    method: 'POST',
    path: '/public/v1/signing/page',
    body: SigningTokenBodySchema,
    response: { 200: SigningPageSchema },
    permission: 'PUBLIC',
    description:
      "The Eigentümer's signing page behind the personal link from the invitation. The token travels in the body, never the URL. Answers OPEN with the declaration texts built from the request's snapshot and the served page's fingerprint, SIGNED for a signer who has signed, or INACTIVE (withdrawn request, Deal lost, won or deleted) with the header and title band only; 404 deal.signing-request.link-invalid for every link that opens nothing. Not available to API keys.",
  },
  document: {
    method: 'POST',
    path: '/public/v1/signing/document',
    body: SigningDocumentBodySchema,
    response: { 200: SigningDocumentSchema },
    permission: 'PUBLIC',
    description:
      "A short-lived URL for the PDF exactly as it was sent, inline for the page's document view or as an attachment under its sent file name. Only while the page is OPEN, or for a signer who has signed; 404 deal.signing-request.link-invalid otherwise. Not available to API keys.",
  },
  signature: {
    method: 'POST',
    path: '/public/v1/signing/signature',
    body: SignBodySchema,
    response: { 200: SigningPageSchema },
    permission: 'PUBLIC',
    description:
      'The Eigentümer signs on the page served with pageFingerprint. Records the typed name, the early-start choice (Maklervertrag only), server time, IP, browser and the link used, and e-mails the confirmation with the sent PDF. Idempotent per signer: a signer who has already signed gets the SIGNED page and nothing is written. 422 deal.signing-request.cannot-sign-name-missing | cannot-sign-inactive | cannot-sign-page-outdated; 404 deal.signing-request.link-invalid. Not available to API keys.',
  },
  revocation: {
    method: 'POST',
    path: '/public/v1/signing/revocation',
    body: SigningRevocationBodySchema,
    response: { 200: SigningPageSchema },
    permission: 'PUBLIC',
    description:
      "The Eigentümer revokes a signed Maklervertrag from their page (§ 356a BGB), with the server time of the request as its time of receipt. Recorded per signer; the request's status, the signed PDF and the Deal's phase and status do not change. Answers the SIGNED page carrying the revocation, also when it was already recorded; e-mails the receipt with the sent PDF. 422 deal.signing-request.cannot-revoke-not-signed | cannot-revoke-period-ended; 422 deal.brokerage-agreement.cannot-withdraw-required-fields-missing | cannot-withdraw-email-invalid; 404 deal.signing-request.link-invalid. Not available to API keys.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/** No `docs` marker, as for the buyer page: a `docs: 'PUBLIC'` leaf would publish the route and escalate prospect 404s. */
const publicWebExposeContracts = {
  page: {
    method: 'GET',
    path: '/public/v1/web-exposes/:webAddress/:objectNumber',
    pathParams: PublicWebExposePathParamsSchema,
    response: { 200: PublicWebExposePageSchema, 404: WireErrorSchema },
    permission: 'PUBLIC',
    description:
      "The published web exposé of one Objekt, addressed by the workspace's Web-Adresse and the Objekt-Nr. Needs no authentication. Only the marketing set: the published photos as short-lived URLs, the published videos and 360° tours, the key facts, the price, and the firm's contact, logos and imprint. Street and house number only when the agent chose to show them. 404 property.web-expose.unavailable, identical for every reason the page is not available. Never cached; noindex.",
  },
  recordOpening: {
    method: 'POST',
    path: '/public/v1/web-exposes/:webAddress/:objectNumber/openings',
    pathParams: PublicWebExposePathParamsSchema,
    response: { 204: zod.void() },
    permission: 'PUBLIC',
    description:
      'Count one opening of a published web exposé. Anonymous: nothing about the caller is stored. Always 204 — also when nothing was counted (unknown or withdrawn link, automated user agent) — so the answer reveals nothing.',
  },
} as const satisfies Record<string, EndpointDefinition>;

/** No `docs` marker, as for the page it is sent from. */
const publicWebExposeInquiryContracts = {
  submit: {
    method: 'POST',
    path: '/public/v1/web-exposes/:webAddress/:objectNumber/inquiries',
    pathParams: PublicWebExposePathParamsSchema,
    body: SubmitWebExposeInquiryBodySchema,
    response: { 204: zod.void(), 404: WireErrorSchema, 422: WireErrorSchema },
    permission: 'PUBLIC',
    description:
      'Submit a question („INQUIRY") or a full-exposé request („FULL_EXPOSE") from a published web exposé. Needs no authentication. Takes the inquiry in like a website inquiry with source WEB_EXPOSE. 204 with no body. 422 deal.inquiry.cannot-submit-required-fields-missing (details.missingFields) | deal.inquiry.cannot-submit-email-invalid; 404 deal.inquiry.property-not-found when the link names no published web exposé. Throttled per caller IP (429).',
  },
} as const satisfies Record<string, EndpointDefinition>;

/** No `docs` marker: a navigation sent from the docs.immoos.ai guide, not part of the partner API. */
const publicMicrosoftContracts = {
  adminConsent: {
    method: 'GET',
    path: '/public/v1/microsoft/admin-consent',
    // A navigation: the only outcome is a redirect (as `oauthServer.authorize`).
    response: { 302: zod.void() },
    permission: 'PUBLIC',
    description:
      "Sends a customer's Microsoft 365 administrator to Microsoft's admin-consent screen for immoOS's Entra app, so they can release immoOS for their organisation (IMM-1170.9). Carries no data; 404 when this deployment has no Microsoft app configured.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * Extracted from the `contracts` literal into a named const (IMM-556.4). See the
 * note above `contracts` for why every group is named rather than inline.
 */
const publicReferenceFeedContracts = {
  list: {
    method: 'GET',
    path: '/public/v1/workspaces/:workspaceSlug/references',
    pathParams: zod.object({ workspaceSlug: zod.string().min(1) }),
    query: zod.object({ organizationId: zod.string().uuid().optional() }),
    response: { 200: ReferenceFeedResponseSchema, 404: WireErrorSchema },
    permission: 'PUBLIC',
    docs: 'PUBLIC',
    description:
      "A workspace's published reference properties as a keyless JSON feed, keyed on the stable workspace slug suffix. **Needs no authentication and is callable from any origin** (CORS is open, without credentials). Returns the sold, homepage-consented references in a whitelist-only projection, newest sale first, hard-capped with the true `total` stated. Optional `organizationId` filter narrows to one organization (an unknown or foreign organization yields an empty list). Responses are cacheable for five minutes and carry an `ETag` (a matching `If-None-Match` gets a 304). The prefix is throttled per caller IP — excess requests get a 429. The address is limited to `postalCode` and `city`; the listing free-text fields (`description`, `locationDescription`, `features`) are returned exactly as the agent wrote them. 404 when the workspace slug resolves to no workspace.",
  },
  // IMM-408.3 — resolves an image URL the feed emits. A `kind: 'stream'` leaf:
  // binary image bytes, no JSON response body. Re-checks the double-flag consent
  // on every request; any miss (non-published property, foreign/unknown ids,
  // non-photo, unknown `variant`) is a bodyless 404 — no enumeration oracle.
  // `variant` is typed here but validated in the handler so a bad value 404s
  // rather than 400ing (the raw stream route does not zod-validate pathParams).
  image: {
    kind: 'stream',
    method: 'GET',
    path: '/public/v1/workspaces/:workspaceSlug/references/:propertyId/images/:mediaId/:variant',
    pathParams: zod.object({
      workspaceSlug: zod.string().min(1),
      propertyId: zod.string(),
      mediaId: zod.string(),
      variant: zod.enum(['original', 'thumb']),
    }),
    permission: 'PUBLIC',
    docs: 'PUBLIC',
    description:
      "Stream a published reference property's photo bytes (full-size `original` or `thumb` derivative, falling back to the original when absent), re-checking on every request that the owning property is a currently-published reference. **Needs no authentication and is callable from any origin.** Served with `Cache-Control: public, max-age=300` (ADR 0034 D6) so revocation propagates within five minutes; the public prefix is throttled per caller IP (429 on excess). 404 on any miss — no oracle.",
  },
} as const satisfies Record<string, EndpointDefinition>;

/**
 * The API contract tree: every endpoint, grouped by resource.
 *
 * EXPLICITLY ANNOTATED, and every group is a named const (IMM-556.4). Both are
 * forced by `tsc`: with an inferred `as const` type this object had grown past
 * the length the compiler will serialize into a `.d.ts`, so adding ONE route to
 * ANY group failed the build with TS7056 — a wall the next endpoint added would
 * have hit regardless of which feature added it.
 *
 * The annotation is `typeof` references only, so the emitted declaration stays
 * small while every leaf keeps its exact literal method / path / permission and
 * its schemas — which the route-builder's request-type inference depends on.
 * Adding a group means adding its line to the annotation too; the compiler will
 * tell you if you forget.
 */
export const contracts: {
  readonly auth: typeof authContracts;
  readonly sandboxAuth: typeof sandboxAuthContracts;
  readonly organizations: typeof organizationsContracts;
  readonly organizationOnboarding: typeof organizationOnboardingContracts;
  readonly members: typeof membersContracts;
  readonly invitations: typeof invitationsContracts;
  readonly memberProfiles: typeof memberProfilesContracts;
  readonly memberPreferences: typeof memberPreferencesContracts;
  readonly memberInvite: typeof memberInviteContracts;
  readonly memberOnboarding: typeof memberOnboardingContracts;
  readonly companyOnboarding: typeof companyOnboardingContracts;
  readonly company: typeof companyContracts;
  readonly webAddress: typeof webAddressContracts;
  readonly branding: typeof brandingContracts;
  readonly companySignature: typeof companySignatureContracts;
  readonly documentTemplates: typeof documentTemplatesContracts;
  readonly emailTemplates: typeof emailTemplatesContracts;
  readonly buyerContractTemplates: typeof buyerContractTemplatesContracts;
  readonly appHandoffs: typeof appHandoffsContracts;
  readonly apiKeys: typeof apiKeysContracts;
  readonly connectedClients: typeof connectedClientsContracts;
  readonly impersonation: typeof impersonationContracts;
  readonly mediaFiles: typeof mediaFilesContracts;
  readonly coverage: typeof coverageContracts;
  readonly leads: typeof leadsContracts;
  readonly leadPartners: typeof leadPartnersContracts;
  readonly properties: typeof propertiesContracts;
  readonly activities: typeof activitiesContracts;
  readonly deals: typeof dealsContracts;
  readonly dealDocuments: typeof dealDocumentsContracts;
  readonly dashboard: typeof dashboardContracts;
  readonly prospects: typeof prospectsContracts;
  readonly search: typeof searchContracts;
  readonly contacts: typeof contactsContracts;
  readonly companies: typeof companiesContracts;
  readonly companyContacts: typeof companyContactsContracts;
  readonly adminProperties: typeof adminPropertiesContracts;
  readonly adminPortalConnections: typeof adminPortalConnectionsContracts;
  readonly adminEmailTemplates: typeof adminEmailTemplatesContracts;
  readonly adminBuyerContractTemplates: typeof adminBuyerContractTemplatesContracts;
  readonly adminValuationRequests: typeof adminValuationContracts;
  readonly adminDeals: typeof adminDealContracts;
  readonly adminCompanies: typeof adminCompanyContracts;
  readonly adminAuth: typeof adminAuthContracts;
  readonly adminWorkspaces: typeof adminWorkspacesContracts;
  readonly adminWorkspaceRoles: typeof adminWorkspaceRolesContracts;
  readonly adminOrganizations: typeof adminOrganizationsContracts;
  readonly adminApiKeys: typeof adminApiKeyContracts;
  readonly adminApiCalls: typeof adminApiCallsContracts;
  readonly adminEmailLog: typeof adminEmailLogContracts;
  readonly adminMembers: typeof adminMembersContracts;
  readonly adminDocumentAnalysisSkills: typeof adminDocumentAnalysisSkillsContracts;
  readonly adminCodificationAdvisory: typeof adminCodificationAdvisoryContracts;
  readonly adminToolCatalog: typeof adminToolCatalogContracts;
  readonly adminAgentSkills: typeof adminAgentSkillsContracts;
  readonly adminSystemSkills: typeof adminSystemSkillsContracts;
  readonly fleetMachines: typeof fleetMachinesContracts;
  readonly fleetJobs: typeof fleetJobsContracts;
  readonly fleetQueue: typeof fleetQueueContracts;
  readonly fleetCommands: typeof fleetCommandsContracts;
  readonly fleetMetrics: typeof fleetMetricsContracts;
  readonly fleetLogs: typeof fleetLogsContracts;
  readonly adminFleetMetrics: typeof adminFleetMetricsContracts;
  readonly adminFleetJobs: typeof adminFleetJobsContracts;
  readonly adminFleetBoard: typeof adminFleetBoardContracts;
  readonly fleetLanes: typeof fleetLanesContracts;
  readonly adminFleetQuestions: typeof adminFleetQuestionsContracts;
  readonly fleetQuestions: typeof fleetQuestionsContracts;
  readonly adminFleetReports: typeof adminFleetReportsContracts;
  readonly fleetReports: typeof fleetReportsContracts;
  readonly adminFleetAssistant: typeof adminFleetAssistantContracts;
  readonly fleetAssistant: typeof fleetAssistantContracts;
  readonly fleetBoard: typeof fleetBoardContracts;
  readonly adminFleetLogs: typeof adminFleetLogsContracts;
  readonly adminWhatsApp: typeof adminWhatsAppContracts;
  readonly adminMessengerChats: typeof adminMessengerChatsContracts;
  readonly fleetTick: typeof fleetTickContracts;
  readonly fleetStream: typeof fleetStreamContracts;
  readonly fleetCommandOutcome: typeof fleetCommandOutcomeContracts;
  readonly adminFleetMachines: typeof adminFleetMachinesContracts;
  readonly adminFleetAccounts: typeof adminFleetAccountsContracts;
  readonly adminFleet: typeof adminFleetContracts;
  readonly adminFleetCommands: typeof adminFleetCommandsContracts;
  readonly adminFactKeys: typeof adminFactKeysContracts;
  readonly factKeyCatalogAgent: typeof factKeyCatalogAgentContracts;
  readonly adminAdmins: typeof adminAdminsContracts;
  readonly adminPreferences: typeof adminPreferencesContracts;
  readonly adminPushDevices: typeof adminPushDevicesContracts;
  readonly adminPushEvents: typeof adminPushEventsContracts;
  readonly adminLandiqDataRuns: typeof adminLandiqDataRunsContracts;
  readonly adminSandbox: typeof adminSandboxContracts;
  readonly adminMcpAccess: typeof adminMcpAccessContracts;
  readonly adminValuationAgentToken: typeof adminValuationAgentTokenContracts;
  readonly adminMcpSurface: typeof adminMcpSurfaceContracts;
  readonly adminConnectedClients: typeof adminConnectedClientContracts;
  readonly adminMcp: typeof adminMcpContracts;
  readonly mcp: typeof mcpContracts;
  readonly oauthProtectedResource: typeof oauthProtectedResourceContracts;
  readonly oauthServer: typeof oauthServerContracts;
  readonly skillApprovals: typeof skillApprovalsContracts;
  readonly skillInstances: typeof skillInstancesContracts;
  readonly conversations: typeof conversationsContracts;
  readonly mailbox: typeof mailboxContracts;
  readonly companyMailboxes: typeof companyMailboxContracts;
  readonly myWhatsApp: typeof myWhatsAppContracts;
  readonly myTelegram: typeof myTelegramContracts;
  readonly portalConnections: typeof portalConnectionsContracts;
  readonly portalListings: typeof portalListingsContracts;
  readonly browserPublish: typeof browserPublishContracts;
  readonly webExpose: typeof webExposeContracts;
  readonly calendar: typeof calendarContracts;
  readonly workspaceEvents: typeof workspaceEventsContracts;
  readonly valuation: typeof valuationContracts;
  readonly valuationAgent: typeof valuationAgentContracts;
  readonly valuationAgentPropertyFacts: typeof valuationAgentPropertyFactsContracts;
  readonly valuationAgentPropertyCompleteness: typeof valuationAgentPropertyCompletenessContracts;
  readonly valuationAgentPropertyCapture: typeof valuationAgentPropertyCaptureContracts;
  readonly publicReferenceFeed: typeof publicReferenceFeedContracts;
  readonly publicBrokerageAgreement: typeof publicBrokerageAgreementContracts;
  readonly publicSigning: typeof publicSigningContracts;
  readonly publicWebExpose: typeof publicWebExposeContracts;
  readonly publicWebExposeInquiries: typeof publicWebExposeInquiryContracts;
  readonly publicMicrosoft: typeof publicMicrosoftContracts;
  readonly publicInquiries: typeof publicInquiryContracts;
} = {
  auth: authContracts,
  sandboxAuth: sandboxAuthContracts,
  organizations: organizationsContracts,
  // Partner-facing onboarding surface (IMM-255.3). The partner is not a
  // workspace member — they exchange a magic-link token for an IdP (WorkOS)
  // session in two steps (ADR 0030): requestSessionCode verifies the token and
  // asks WorkOS to e-mail a one-time code, then exchangeSession redeems that
  // code for the session (which returns the onboarding id the token mapped
  // to). They then drive the lifecycle by addressing that onboarding by id
  // with the resulting bearer access token. The requireOnboardingParticipant
  // middleware loads the path `:onboardingId` and authorises by ownership (the
  // authenticated user's e-mail must equal the onboarding's location-manager
  // e-mail), so a tampered/other id fails the ownership check (403) —
  // cross-onboarding access is impossible.
  organizationOnboarding: organizationOnboardingContracts,
  members: membersContracts,
  invitations: invitationsContracts,
  memberProfiles: memberProfilesContracts,
  // Generic per-member key/value preference store. Self-scoped: the member is the
  // calling session member (`request.member.id()`), never in the path, so a
  // caller can only read/write their own. The value is arbitrary JSON — each view
  // owns its shape client-side — so new preferences need no backend change.
  memberPreferences: memberPreferencesContracts,
  // Public member-invite session surface (IMM-271). The invited member is not
  // yet authenticated — they exchange the IMM-2 invitation magic-link token for
  // a WorkOS session in two steps (ADR 0030, mirroring organizationOnboarding):
  // requestSessionCode verifies the token hash-only and asks WorkOS to e-mail a
  // one-time code, then exchangeSession redeems that code for a session (sealing
  // the refresh cookie). After the exchange the member rides the standard bearer
  // pipeline and the existing onboarding gate (GET /auth/me) routes them to the
  // profile wizard. No password is ever set.
  memberInvite: memberInviteContracts,
  memberOnboarding: memberOnboardingContracts,
  companyOnboarding: companyOnboardingContracts,
  company: companyContracts,
  webAddress: webAddressContracts,
  branding: brandingContracts,
  companySignature: companySignatureContracts,
  documentTemplates: documentTemplatesContracts,
  emailTemplates: emailTemplatesContracts,
  buyerContractTemplates: buyerContractTemplatesContracts,
  appHandoffs: appHandoffsContracts,
  apiKeys: apiKeysContracts,
  connectedClients: connectedClientsContracts,
  impersonation: impersonationContracts,
  mediaFiles: mediaFilesContracts,
  // IMM-415 — workspace-wide Einzugsgebiet coverage, read through the
  // authenticated `immo_`-key API (the read counterpart of the lead-push write
  // API). Workspace-addressed and workspace-scoped, distinct from the
  // org-addressed per-Standort coverage endpoints under `organizations.*`.
  // `docs: 'PUBLIC'` publishes it on the partner docs site alongside
  // `leads.create` (IMM-415.3); documenting it publicly does not weaken the key
  // gate. Adding it requires updating the leak-prevention test's expected set.
  //
  // Everything a partner reads says `coverage`: what they pull here is the
  // workspace's locations and the postal codes each one covers, and
  // „Einzugsgebiet" has no term of art in English they would guess. Renamed
  // from the earlier English term (breaking, recorded verbatim in
  // PUBLIC-API-CHANGELOG.md) one week after the endpoint first shipped, before
  // any integration consumed it.
  //
  // That includes this branch key, which is NOT purely internal: the docs
  // generator derives the reference's section heading from it, so the first pass
  // renamed the URL and left the published site's section heading on the old
  // term. The heading is now declared copy (`DOC_TAGS` in apps/api-docs), and
  // the key matches it. It also includes the permission literal, renamed to
  // `organization-coverage-read`/`-write` in a second pass: a customer picks
  // that grant off a checklist next to the endpoint name when issuing a key, so
  // the two cannot disagree.
  //
  // IMM-493.3 finished the job: the identity module's folders, classes, table
  // and error code carried the old vocabulary until then, on the reasoning that
  // it was internal and a partner never read it. That split had a running cost
  // — two words for one concept, and a live risk of the old term leaking back
  // into a partner-facing string, which is the exact defect the first pass had
  // to fix on the published docs site. There is now ONE English word for the
  // concept everywhere outside the shipped migration history.
  coverage: coverageContracts,
  leads: leadsContracts,
  leadPartners: leadPartnersContracts,
  properties: propertiesContracts,
  activities: activitiesContracts,
  deals: dealsContracts,
  dealDocuments: dealDocumentsContracts,
  dashboard: dashboardContracts,
  prospects: prospectsContracts,
  search: searchContracts,
  contacts: contactsContracts,
  companies: companiesContracts,
  companyContacts: companyContactsContracts,
  adminProperties: adminPropertiesContracts,
  adminPortalConnections: adminPortalConnectionsContracts,
  adminEmailTemplates: adminEmailTemplatesContracts,
  adminBuyerContractTemplates: adminBuyerContractTemplatesContracts,
  adminValuationRequests: adminValuationContracts,
  adminDeals: adminDealContracts,
  adminCompanies: adminCompanyContracts,
  adminAuth: adminAuthContracts,
  adminWorkspaces: adminWorkspacesContracts,
  adminWorkspaceRoles: adminWorkspaceRolesContracts,
  adminOrganizations: adminOrganizationsContracts,
  adminApiKeys: adminApiKeyContracts,
  adminApiCalls: adminApiCallsContracts,
  adminEmailLog: adminEmailLogContracts,
  adminMembers: adminMembersContracts,
  adminDocumentAnalysisSkills: adminDocumentAnalysisSkillsContracts,
  // The codification advisory (IMM-436.9). Operator-only: a workspace admin sees
  // their own run histories and costs, but cross-workspace spend patterns are
  // platform-operations information, not a customer's.
  adminCodificationAdvisory: adminCodificationAdvisoryContracts,
  // The tool catalogue an agent-skill definition's default allowlist is assembled
  // from (IMM-436.4). Backoffice-gated and read-only: the catalogue is fixed at
  // deploy time, so there is nothing here to write.
  adminToolCatalog: adminToolCatalogContracts,
  adminAgentSkills: adminAgentSkillsContracts,
  adminSystemSkills: adminSystemSkillsContracts,
  // Fleet machine registry (IMM-910.1, ADR 0047). The machine-facing leaves
  // have no browser or partner caller — the host component ships in
  // IMM-910.3 and the operator UI in IMM-910.7 — which is the legitimate
  // exception BL-050 names for a route shipped ahead of its consumer.
  fleetMachines: fleetMachinesContracts,
  fleetJobs: fleetJobsContracts,
  fleetQueue: fleetQueueContracts,
  fleetCommands: fleetCommandsContracts,
  fleetMetrics: fleetMetricsContracts,
  fleetLogs: fleetLogsContracts,
  adminFleetMetrics: adminFleetMetricsContracts,
  adminFleetJobs: adminFleetJobsContracts,
  adminFleetBoard: adminFleetBoardContracts,
  fleetLanes: fleetLanesContracts,
  adminFleetQuestions: adminFleetQuestionsContracts,
  fleetQuestions: fleetQuestionsContracts,
  adminFleetReports: adminFleetReportsContracts,
  fleetReports: fleetReportsContracts,
  adminFleetAssistant: adminFleetAssistantContracts,
  fleetAssistant: fleetAssistantContracts,
  fleetBoard: fleetBoardContracts,
  adminFleetLogs: adminFleetLogsContracts,
  adminWhatsApp: adminWhatsAppContracts,
  adminMessengerChats: adminMessengerChatsContracts,
  fleetTick: fleetTickContracts,
  fleetStream: fleetStreamContracts,
  fleetCommandOutcome: fleetCommandOutcomeContracts,
  adminFleetMachines: adminFleetMachinesContracts,
  adminFleetAccounts: adminFleetAccountsContracts,
  adminFleet: adminFleetContracts,
  adminFleetCommands: adminFleetCommandsContracts,
  adminFactKeys: adminFactKeysContracts,
  factKeyCatalogAgent: factKeyCatalogAgentContracts,
  adminAdmins: adminAdminsContracts,
  adminPreferences: adminPreferencesContracts,
  adminPushDevices: adminPushDevicesContracts,
  adminPushEvents: adminPushEventsContracts,
  adminLandiqDataRuns: adminLandiqDataRunsContracts,
  adminSandbox: adminSandboxContracts,
  adminMcpAccess: adminMcpAccessContracts,
  adminValuationAgentToken: adminValuationAgentTokenContracts,
  // Operator containment for the customer MCP surface (IMM-601.6) — the kill
  // switch over `mcp.invoke` below, not over the operator's own `adminMcp`.
  adminMcpSurface: adminMcpSurfaceContracts,
  adminConnectedClients: adminConnectedClientContracts,
  adminMcp: adminMcpContracts,
  // The workspace MCP surface (IMM-601.2): the same JSON-RPC dispatch shape as
  // `adminMcp`, gated by the implicit MCP-capable-key permission.
  mcp: mcpContracts,
  // Where a client gets a token for `mcp.invoke` (IMM-601.8.1, RFC 9728). The
  // only unauthenticated leaf outside `/public/v1/`, and the target of the
  // constant `WWW-Authenticate` challenge every MCP 401 carries.
  oauthProtectedResource: oauthProtectedResourceContracts,
  oauthServer: oauthServerContracts,
  // A skill's proposed actions — the writes it may not take without the granting
  // member's explicit say-so (IMM-436.5). Workspace-addressed like every other
  // skill route; the member is resolved from the principal, never from the body,
  // because approval is personal to the grant.
  skillApprovals: skillApprovalsContracts,
  skillInstances: skillInstancesContracts,
  conversations: conversationsContracts,
  mailbox: mailboxContracts,
  companyMailboxes: companyMailboxContracts,
  myWhatsApp: myWhatsAppContracts,
  myTelegram: myTelegramContracts,
  portalConnections: portalConnectionsContracts,
  portalListings: portalListingsContracts,
  browserPublish: browserPublishContracts,
  webExpose: webExposeContracts,
  calendar: calendarContracts,
  // Workspace event stream (IMM-516.2 / ADR 0036). One long-lived SSE connection
  // per open workspace surface, carrying only the events the connection's
  // server-resolved scope allows. Not on the public docs site: it is an app-only
  // surface with no JSON body.
  workspaceEvents: workspaceEventsContracts,
  // Bewertung (IMM-407). Owned by the valuation bounded context; the group is
  // assembled in `valuation/valuation.contracts.ts` and mounted here.
  valuation: valuationContracts,
  valuationAgent: valuationAgentContracts,
  valuationAgentPropertyFacts: valuationAgentPropertyFactsContracts,
  valuationAgentPropertyCompleteness: valuationAgentPropertyCompletenessContracts,
  valuationAgentPropertyCapture: valuationAgentPropertyCaptureContracts,
  // Public reference feed (IMM-408.2 / ADR 0034). The one unauthenticated
  // business-data surface: `permission: 'PUBLIC'` (the auth plugin no-ops
  // without a Bearer), served under the dedicated `/public/v1/` prefix —
  // intentionally NOT `/v1/` — behind the edge origin lock. Surfaced on the
  // public docs site (`docs: 'PUBLIC'`) per IMM-408.5.
  publicReferenceFeed: publicReferenceFeedContracts,
  // The buyer page: POST with the token in the body, own origins only (ADR 0059).
  publicBrokerageAgreement: publicBrokerageAgreementContracts,
  publicSigning: publicSigningContracts,
  // The prospect's web exposé (IMM-1161.4): GET from any origin, no credentials, noindex
  publicWebExpose: publicWebExposeContracts,
  publicWebExposeInquiries: publicWebExposeInquiryContracts,
  // The IT guide's admin-consent link (IMM-1170.9): a GET navigation from docs.immoos.ai, no data.
  publicMicrosoft: publicMicrosoftContracts,
  // The agency website's inquiry intake: POST from any origin, no credentials (ADR 0062).
  publicInquiries: publicInquiryContracts,
};

export type Contracts = typeof contracts;

export { WireErrorSchema };
